import React__default, { createContext, createElement, useContext, Component, Fragment, createRef, useState, useCallback, useEffect, memo, useMemo, useRef, useReducer } from 'react';
import { StyleSheet, css } from 'aphrodite/no-important';
import { Helmet } from 'react-helmet';
import ReactDOM from 'react-dom';

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

const SCREEN_SIZE = {
  LARGE: 'large',
  MEDIUM: 'medium',
  SMALL: 'small'
};
const TYPOGRAPHY_SIZE = {
  GIGANTIC: 'gigantic',
  HUGE: 'huge',
  LARGE: 'large',
  BIGGER: 'bigger',
  BIG: 'big',
  NORMAL: 'normal',
  TINY: 'tiny'
};
const ALIGNMENT_OPTIONS = {
  CENTER: 'CENTER',
  START: 'START'
};
const CONFIGURATION_TYPES = {
  COLOR: 'COLOR',
  TYPOGRAPHY: 'TYPOGRAPHY',
  NUMBER: 'NUMBER',
  BOX_SHADOW: 'BOX_SHADOW',
  BOOLEAN: 'BOOLEAN',
  ALIGNMENT: 'ALIGNMENT',
  RATIO: 'RATIO',
  SEMANTIC_TAG: 'SEMANTIC_TAG'
};

const stylesheetUrlTypeName = 'stylesheetUrl';
const fontFaceTypeName = 'fontFace';

class PaletteManager {
  constructor(palette) {
    _defineProperty(this, "palette", void 0);

    this.palette = palette;
  }

  getColorByKey(key) {
    return this.palette.colors[key];
  }

  getFonts() {
    return this.palette.fonts;
  }

  getFontSizeMap() {
    return { ...this.palette.fontSizeMap
    };
  }

}

function translatePropertyValueByType(instructions, {
  value,
  type
}) {
  if (typeof instructions === 'undefined') {
    throw new Error('must supply instructions to the transformation');
  }

  if (typeof instructions[type] !== 'function') {
    throw new Error(`there is no instruction function to type: ${type}`);
  }

  return instructions[type](value);
}

function translateProperty(instructions, property) {
  return {
    [property.name]: translatePropertyValueByType(instructions, property)
  };
}

function translateComponent(instructions, themeProperties) {
  return themeProperties.reduce((accumulator, componentProperty) => ({ ...accumulator,
    ...translateProperty(instructions, componentProperty)
  }), {});
}

function translate(instructions, configuration) {
  return Object.keys(configuration).reduce((accumulator, name) => ({ ...accumulator,
    [name]: translateComponent(instructions, configuration[name])
  }), {});
}

const noBorderBoxShadow = {
  borderWidthTypeNormal: 0,
  borderWidthTypeHover: 0,
  borderWidthTypeActive: 0,
  borderColorTypeNormal: '',
  borderColorTypeHover: '',
  borderColorTypeActive: ''
};
const elevationTypesMap = {
  LOW: '0 2px 4px 0 rgba(0,0,0,0.2)',
  HIGH: '0 8px 8px -8px rgba(0,0,0,0.2), 0 4px 20px 0px rgba(0,0,0,0.2), 0 12px 16px 0px rgba(0,0,0,0.2)',
  NONE: '0 0 0 0'
};
const getElevationType = key => elevationTypesMap[key];
const createStyleForBoxShadow = ({
  boxShadowTypeNormal = elevationTypesMap.NONE,
  boxShadowTypeHover,
  boxShadowTypeActive,
  boxShadowTypeDisable,
  borderBoxShadow = noBorderBoxShadow
}) => {
  const {
    borderColorTypeActive,
    borderColorTypeHover,
    borderColorTypeNormal,
    borderWidthTypeActive,
    borderWidthTypeHover,
    borderWidthTypeNormal
  } = borderBoxShadow;
  return StyleSheet.create({
    style: {
      transition: '0.2s box-shadow ease-in-out',
      willChange: 'box-shadow',
      boxShadow: `${boxShadowTypeNormal}, 0 0 0 ${borderWidthTypeNormal}px ${borderColorTypeNormal}`,
      ':hover': {
        boxShadow: `${boxShadowTypeHover || boxShadowTypeNormal}, 0 0 0 ${borderWidthTypeHover}px ${borderColorTypeHover}`
      },
      ':active': {
        boxShadow: `${boxShadowTypeActive || boxShadowTypeNormal}, 0 0 0 ${borderWidthTypeActive}px ${borderColorTypeActive}`
      },
      ':disabled': {
        boxShadow: boxShadowTypeDisable || boxShadowTypeNormal
      }
    }
  });
};

class ThemeManager {
  constructor(palette, themeConfiguration) {
    _defineProperty(this, "paletteManager", void 0);

    _defineProperty(this, "themeConfiguration", void 0);

    _defineProperty(this, "instructions", {
      [CONFIGURATION_TYPES.COLOR]: key => this.paletteManager.getColorByKey(key),
      [CONFIGURATION_TYPES.NUMBER]: key => key,
      [CONFIGURATION_TYPES.BOX_SHADOW]: key => getElevationType(key),
      [CONFIGURATION_TYPES.TYPOGRAPHY]: key => key,
      [CONFIGURATION_TYPES.BOOLEAN]: key => key,
      [CONFIGURATION_TYPES.ALIGNMENT]: key => key,
      [CONFIGURATION_TYPES.RATIO]: key => key,
      [CONFIGURATION_TYPES.SEMANTIC_TAG]: key => key
    });

    this.themeConfiguration = themeConfiguration;
    this.paletteManager = new PaletteManager(palette);
  }

  getConfiguration() {
    return { ...translate(this.instructions, this.themeConfiguration),
      fontSizeMap: this.paletteManager.getFontSizeMap()
    };
  }

}

const lineHeightRatioMap = {
  gigantic: {
    large: 1.4,
    medium: 1.375,
    small: 1.333
  },
  huge: {
    large: 1.375,
    medium: 1.333,
    small: 1.4
  },
  large: {
    large: 1.4,
    medium: 1.4,
    small: 1.5
  },
  bigger: {
    large: 1.5,
    medium: 1.5,
    small: 1.43
  },
  big: {
    large: 1.43,
    medium: 1.43,
    small: 1.333
  },
  normal: {
    large: 1.5,
    medium: 1.5,
    small: 1.43
  },
  tiny: {
    large: 1.333,
    medium: 1.333,
    small: 1.4
  }
};

const getFontSize = size => `${size}px`;

const getLineHeightByFontSize = (fontSize, size, screenSize) => `${Math.round(fontSize * lineHeightRatioMap[size][screenSize])}px`;

const translateFontSizes = (fontSizeMap, actualTypographySizesFontFamiliesMap) => {
  return Object.keys(fontSizeMap).reduce((sizesObj, current) => {
    return { ...sizesObj,
      [current]: {
        breakpoints: {
          [SCREEN_SIZE.LARGE]: {
            fontSize: getFontSize(fontSizeMap[current].screenSizes.large),
            lineHeight: getLineHeightByFontSize(fontSizeMap[current].screenSizes.large, current, SCREEN_SIZE.LARGE)
          },
          [SCREEN_SIZE.MEDIUM]: {
            fontSize: getFontSize(fontSizeMap[current].screenSizes.medium),
            lineHeight: getLineHeightByFontSize(fontSizeMap[current].screenSizes.medium, current, SCREEN_SIZE.MEDIUM)
          },
          [SCREEN_SIZE.SMALL]: {
            fontSize: getFontSize(fontSizeMap[current].screenSizes.small),
            lineHeight: getLineHeightByFontSize(fontSizeMap[current].screenSizes.small, current, SCREEN_SIZE.SMALL)
          }
        },
        style: {
          fontWeight: fontSizeMap[current].fontWeight,
          fontFamily: actualTypographySizesFontFamiliesMap[current]
        }
      }
    };
  }, {});
};
const createTypographySizesFontFamiliesMap = fontSizeMap => {
  return Object.keys(fontSizeMap).reduce((sizesObj, current) => {
    return { ...sizesObj,
      [current]: {
        fontFamily: fontSizeMap[current].fontFamily,
        defaultFontFamily: fontSizeMap[current].defaultFontFamily
      }
    };
  }, {});
};
const defaultWebFontsContext = {
  gigantic: 'inherit',
  huge: 'inherit',
  large: 'inherit',
  bigger: 'inherit',
  big: 'inherit',
  normal: 'inherit',
  tiny: 'inherit'
};

const ThemeContext = createContext({});
const WebFontsContext = createContext(defaultWebFontsContext);

const Theme = ({
  configuration,
  children
}) => {
  const actualTypographySizesFontFamiliesMap = useContext(WebFontsContext);
  const fontSizesStyles = translateFontSizes(configuration.fontSizeMap, actualTypographySizesFontFamiliesMap);
  const themeContext = { ...configuration,
    fontSizesStyles
  };
  return /*#__PURE__*/createElement(ThemeContext.Provider, {
    value: themeContext
  }, children);
};

const ThemeProviderWithFonts = WebFontsComponent => {
  const ThemeProviderWithFontsComponent = props => {
    const {
      palette,
      themeConfiguration,
      children
    } = props;
    const themeManager = new ThemeManager(palette, themeConfiguration);
    const configuration = themeManager.getConfiguration();
    const typographySizesFontFamiliesMap = createTypographySizesFontFamiliesMap(themeManager.paletteManager.getFontSizeMap());
    const fontsConfiguration = themeManager.paletteManager.getFonts();
    return /*#__PURE__*/createElement(WebFontsComponent, {
      typographySizesFontFamiliesMap: typographySizesFontFamiliesMap,
      fontsConfiguration: fontsConfiguration
    }, /*#__PURE__*/createElement(Theme, {
      configuration: configuration
    }, children));
  };

  return ThemeProviderWithFontsComponent;
};

class EmptyReporter {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars,@typescript-eslint/no-empty-function
  reportException(err, additionalData) {} // eslint-disable-next-line @typescript-eslint/no-unused-vars,@typescript-eslint/no-empty-function


  reportWarning(message) {}

}
const ErrorReporting = createContext(new EmptyReporter());

const baseUnit = 20;

/* eslint-disable */
const useTheme = key => {
  const reporter = React__default.useContext(ErrorReporting);
  const themeContext = React__default.useContext(ThemeContext);

  if (key && themeContext[key] || !key) {
    return { ...(key && themeContext[key]),
      fontSizesStyles: themeContext.fontSizesStyles,
      baseUnit
    };
  }

  reporter.reportWarning(`cannot find key: ${key} in configuration`);
  return {};
};

const HUGE_SCREEN_SIZE = 1440;
const LARGE_SCREEN_SIZE = 1080;
const MEDIUM_SCREEN_SIZE = 1079;
const SMALL_SCREEN_SIZE = 719;
const MEDIA_BREAKPOINTS = {
  large: `@media (min-width: ${LARGE_SCREEN_SIZE}px)`,
  medium: `@media (max-width: ${MEDIUM_SCREEN_SIZE}px)`,
  small: `@media (max-width:${SMALL_SCREEN_SIZE}px)`
};

const getLineHeightBySize = (fontSizesStyle, fontSize, screenSize) => {
  return parseFloat(fontSizesStyle[fontSize].breakpoints[screenSize].lineHeight);
};
const getStyleBySize = (fontSizesStyle, fontSizes) => {
  const {
    fontSizeLarge,
    fontSizeMedium,
    fontSizeSmall
  } = fontSizes;
  return StyleSheet.create({
    tagStyle: {
      [MEDIA_BREAKPOINTS.large]: { ...fontSizesStyle[fontSizeLarge].style,
        ...fontSizesStyle[fontSizeLarge].breakpoints.large
      },
      [MEDIA_BREAKPOINTS.medium]: { ...fontSizesStyle[fontSizeMedium].style,
        ...fontSizesStyle[fontSizeMedium].breakpoints.medium
      },
      [MEDIA_BREAKPOINTS.small]: { ...fontSizesStyle[fontSizeSmall].style,
        ...fontSizesStyle[fontSizeSmall].breakpoints.small
      },
      margin: 0
    }
  }).tagStyle;
};
const semanticCSS = (...styles) => styles;
const getFontSizesBySemanticTag = semanticTag => {
  switch (semanticTag) {
    case 'h1':
      return {
        fontSizeLarge: TYPOGRAPHY_SIZE.GIGANTIC,
        fontSizeMedium: TYPOGRAPHY_SIZE.GIGANTIC,
        fontSizeSmall: TYPOGRAPHY_SIZE.GIGANTIC
      };

    case 'h2':
      return {
        fontSizeLarge: TYPOGRAPHY_SIZE.HUGE,
        fontSizeMedium: TYPOGRAPHY_SIZE.HUGE,
        fontSizeSmall: TYPOGRAPHY_SIZE.HUGE
      };

    case 'h3':
      return {
        fontSizeLarge: TYPOGRAPHY_SIZE.LARGE,
        fontSizeMedium: TYPOGRAPHY_SIZE.LARGE,
        fontSizeSmall: TYPOGRAPHY_SIZE.LARGE
      };

    case 'h4':
      return {
        fontSizeLarge: TYPOGRAPHY_SIZE.BIGGER,
        fontSizeMedium: TYPOGRAPHY_SIZE.BIGGER,
        fontSizeSmall: TYPOGRAPHY_SIZE.BIGGER
      };

    case 'h5':
      return {
        fontSizeLarge: TYPOGRAPHY_SIZE.BIG,
        fontSizeMedium: TYPOGRAPHY_SIZE.BIG,
        fontSizeSmall: TYPOGRAPHY_SIZE.BIG
      };

    case 'h6':
      return {
        fontSizeLarge: TYPOGRAPHY_SIZE.TINY,
        fontSizeMedium: TYPOGRAPHY_SIZE.TINY,
        fontSizeSmall: TYPOGRAPHY_SIZE.TINY
      };

    case 'span':
      return {
        fontSizeLarge: TYPOGRAPHY_SIZE.NORMAL,
        fontSizeMedium: TYPOGRAPHY_SIZE.NORMAL,
        fontSizeSmall: TYPOGRAPHY_SIZE.NORMAL
      };

    case 'p':
    default:
      return {
        fontSizeLarge: TYPOGRAPHY_SIZE.NORMAL,
        fontSizeMedium: TYPOGRAPHY_SIZE.NORMAL,
        fontSizeSmall: TYPOGRAPHY_SIZE.NORMAL
      };
  }
};
const sanitizeAttributes = attributes => {
  const notAllowedAttributes = ['baseUnit', 'fontSizesStyles'];
  return Object.keys(attributes).reduce((newAttributes, attr) => {
    return { ...newAttributes,
      ...(notAllowedAttributes.includes(attr) ? null : {
        [attr]: attributes[attr]
      })
    };
  }, {});
};

const H1 = props => {
  const defaultFontSizes = getFontSizesBySemanticTag('h1');
  const {
    fontSizesStyles
  } = useTheme();
  const {
    children,
    styles = [],
    fontSizeSmall = defaultFontSizes.fontSizeSmall,
    fontSizeMedium = defaultFontSizes.fontSizeMedium,
    fontSizeLarge = defaultFontSizes.fontSizeLarge,
    ...attributes
  } = props;
  const styleBySize = getStyleBySize(fontSizesStyles, {
    fontSizeSmall,
    fontSizeMedium,
    fontSizeLarge
  });
  return /*#__PURE__*/createElement("h1", _extends({
    className: css(styleBySize, ...styles)
  }, sanitizeAttributes(attributes)), children);
};

const H2 = props => {
  const defaultFontSizes = getFontSizesBySemanticTag('h2');
  const {
    fontSizesStyles
  } = useTheme();
  const {
    children,
    styles = [],
    fontSizeSmall = defaultFontSizes.fontSizeSmall,
    fontSizeMedium = defaultFontSizes.fontSizeMedium,
    fontSizeLarge = defaultFontSizes.fontSizeLarge,
    ...attributes
  } = props;
  const styleBySize = getStyleBySize(fontSizesStyles, {
    fontSizeSmall,
    fontSizeMedium,
    fontSizeLarge
  });
  return /*#__PURE__*/createElement("h2", _extends({
    className: css(styleBySize, ...styles)
  }, sanitizeAttributes(attributes)), children);
};

const H4 = props => {
  const defaultFontSizes = getFontSizesBySemanticTag('h4');
  const {
    fontSizesStyles
  } = useTheme();
  const {
    children,
    styles = [],
    fontSizeSmall = defaultFontSizes.fontSizeSmall,
    fontSizeMedium = defaultFontSizes.fontSizeMedium,
    fontSizeLarge = defaultFontSizes.fontSizeLarge,
    ...attributes
  } = props;
  const styleBySize = getStyleBySize(fontSizesStyles, {
    fontSizeSmall,
    fontSizeMedium,
    fontSizeLarge
  });
  return /*#__PURE__*/createElement("h4", _extends({
    className: css(styleBySize, ...styles)
  }, sanitizeAttributes(attributes)), children);
};

const H5 = props => {
  const defaultFontSizes = getFontSizesBySemanticTag('h5');
  const {
    fontSizesStyles
  } = useTheme();
  const {
    children,
    styles = [],
    fontSizeSmall = defaultFontSizes.fontSizeSmall,
    fontSizeMedium = defaultFontSizes.fontSizeMedium,
    fontSizeLarge = defaultFontSizes.fontSizeLarge,
    ...attributes
  } = props;
  const styleBySize = getStyleBySize(fontSizesStyles, {
    fontSizeSmall,
    fontSizeMedium,
    fontSizeLarge
  });
  return /*#__PURE__*/createElement("h5", _extends({
    className: css(styleBySize, ...styles)
  }, sanitizeAttributes(attributes)), children);
};

const P = props => {
  const defaultFontSizes = getFontSizesBySemanticTag('p');
  const {
    fontSizesStyles
  } = useTheme();
  const {
    children,
    styles = [],
    fontSizeSmall = defaultFontSizes.fontSizeSmall,
    fontSizeMedium = defaultFontSizes.fontSizeMedium,
    fontSizeLarge = defaultFontSizes.fontSizeLarge,
    ...attributes
  } = props;
  const styleBySize = getStyleBySize(fontSizesStyles, {
    fontSizeSmall,
    fontSizeMedium,
    fontSizeLarge
  });
  return /*#__PURE__*/createElement("p", _extends({
    className: css(styleBySize, ...styles)
  }, sanitizeAttributes(attributes)), children);
};

const Span = props => {
  const defaultFontSizes = getFontSizesBySemanticTag('span');
  const {
    fontSizesStyles
  } = useTheme();
  const {
    children,
    styles = [],
    reference,
    fontSizeSmall = defaultFontSizes.fontSizeSmall,
    fontSizeMedium = defaultFontSizes.fontSizeMedium,
    fontSizeLarge = defaultFontSizes.fontSizeLarge,
    ...attributes
  } = props;
  const styleBySize = getStyleBySize(fontSizesStyles, {
    fontSizeSmall,
    fontSizeMedium,
    fontSizeLarge
  });
  return /*#__PURE__*/createElement("span", _extends({
    className: css(styleBySize, ...styles)
  }, sanitizeAttributes(attributes), {
    ref: reference
  }), children);
};

const getSectionStyle = (maxWidth = LARGE_SCREEN_SIZE) => {
  return StyleSheet.create({
    sectionStyle: {
      maxWidth: `${maxWidth}px`,
      width: '-webkit-fill-available'
    }
  }).sectionStyle;
};
const getLayoutComponentPadding = (paddingFactors = {
  large: 0.5,
  medium: 0.5,
  small: 0.5
}) => {
  return StyleSheet.create({
    padding: {
      [MEDIA_BREAKPOINTS.large]: {
        paddingLeft: `${paddingFactors.large * baseUnit}px`,
        paddingRight: `${paddingFactors.large * baseUnit}px`
      },
      [MEDIA_BREAKPOINTS.medium]: {
        paddingLeft: `${paddingFactors.medium * baseUnit}px`,
        paddingRight: `${paddingFactors.medium * baseUnit}px`
      },
      [MEDIA_BREAKPOINTS.small]: {
        paddingLeft: `${paddingFactors.small * baseUnit}px`,
        paddingRight: `${paddingFactors.small * baseUnit}px`
      }
    }
  }).padding;
};
const getBackgroundColorStyle = backgroundColor => {
  return StyleSheet.create({
    style: {
      backgroundColor
    }
  }).style;
};

const EventContext = createContext({
  category: 'default category'
});
const EventProvider = ({
  children,
  category: newCategory
}) => {
  const {
    category
  } = useContext(EventContext);
  const newValue = {
    category: newCategory || category
  };
  return /*#__PURE__*/createElement(EventContext.Provider, {
    value: newValue
  }, children);
};

const Link = ({
  children,
  onClick,
  ...props
}) => {
  const {
    category
  } = useContext(EventContext);

  const sendClickAnalytics = () => {
    if (typeof category !== 'undefined') {
      window.mmClientApi = window.mmClientApi || [];
      window.mmClientApi.push('analytics', {
        event_category: category,
        event_action: 'click',
        event_label: ''
      });
    }
  };

  const onClickHandler = (...args) => {
    if (onClick) {
      onClick(...args);
    }

    sendClickAnalytics();
  };

  return /*#__PURE__*/createElement("a", _extends({}, props, {
    onClick: onClickHandler
  }), children);
};

const baseStyles = {
  base: {
    flex: '0 0 auto',
    display: 'inline-flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    textDecoration: 'none',
    color: 'initial',
    fontFamily: 'inherit',
    height: '100%',
    ':visited': {
      textDecoration: 'none',
      color: 'initial'
    },
    ':active': {
      textDecoration: 'none',
      color: 'initial'
    },
    ':hover': {
      color: 'initial'
    }
  }
};
const generateLinkStyleFromTheme = (themedLinkStyle, additionalStyle = {}) => {
  return {
    style: {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      color: themedLinkStyle.idleColor,
      textDecoration: 'none',
      ':visited': {
        color: themedLinkStyle.idleColor
      },
      ':active': {
        color: themedLinkStyle.activeStateColor
      },
      [MEDIA_BREAKPOINTS.large]: {
        ':hover': {
          color: themedLinkStyle.hoverStateColor
        }
      },
      [MEDIA_BREAKPOINTS.medium]: {
        ':hover': {
          color: themedLinkStyle.idleColor
        }
      },
      [MEDIA_BREAKPOINTS.small]: {
        ':hover': {
          color: themedLinkStyle.idleColor
        }
      }
    },
    additionalStyle
  };
};

const combineStyleWithBase = (base, newStyle) => {
  return newStyle ? css(StyleSheet.create(base).base, StyleSheet.create(newStyle).style, StyleSheet.create(newStyle).additionalStyle) : css(StyleSheet.create(base).base);
};

const TextLinkComponent = props => {
  const {
    children,
    href,
    target,
    rel,
    linkStyle = null
  } = props;
  const finalStyle = combineStyleWithBase(baseStyles, linkStyle);
  return /*#__PURE__*/createElement(Link, {
    href: href,
    target: target,
    rel: rel,
    className: finalStyle
  }, children);
};

class ErrorBoundary extends Component {
  constructor(...args) {
    super(...args);

    _defineProperty(this, "state", {
      hasError: false
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  static getDerivedStateFromError(error) {
    return {
      hasError: true
    };
  }

  componentDidCatch(error, info) {
    const errorReporter = this.context;
    errorReporter.reportException(error, info);
  }

  render() {
    const {
      hasError
    } = this.state;
    const {
      children
    } = this.props;
    return hasError ? /*#__PURE__*/createElement("div", {
      className: "has-error"
    }) : /*#__PURE__*/createElement(Fragment, null, children);
  }

}
ErrorBoundary.contextType = ErrorReporting;

const createArrowStyles = () => StyleSheet.create({
  arrow: {
    [MEDIA_BREAKPOINTS.large]: {
      width: '20px',
      height: '20px'
    },
    [MEDIA_BREAKPOINTS.medium]: {
      width: '20px',
      height: '20px'
    },
    [MEDIA_BREAKPOINTS.small]: {
      width: '15px',
      height: '15px'
    }
  }
});

const ArrowSvg = props => {
  const {
    arrowColor,
    alt
  } = props;
  const arrowStyle = createArrowStyles();
  return /*#__PURE__*/createElement(ErrorBoundary, null, /*#__PURE__*/createElement("svg", {
    className: css(arrowStyle.arrow),
    "aria-label": alt,
    viewBox: "0 0 23 26",
    version: "1.1",
    xmlns: "http://www.w3.org/2000/svg"
  }, /*#__PURE__*/createElement("g", {
    stroke: "none",
    strokeWidth: "1",
    fill: "none",
    fillRule: "evenodd"
  }, /*#__PURE__*/createElement("g", {
    transform: "translate(-975.000000, -785.000000)",
    stroke: arrowColor,
    strokeWidth: "4"
  }, /*#__PURE__*/createElement("g", {
    transform: "translate(80.000000, 760.000000)"
  }, /*#__PURE__*/createElement("g", {
    transform: "translate(905.000000, 38.000000) rotate(180.000000) translate(-905.000000, -38.000000) translate(895.000000, 28.000000)"
  }, /*#__PURE__*/createElement("line", {
    x1: "20",
    y1: "10",
    x2: "0",
    y2: "10",
    strokeLinejoin: "bevel"
  }), /*#__PURE__*/createElement("polyline", {
    strokeLinecap: "square",
    points: "10 20 0 10 10 0"
  })))))));
};

const componentConfigurationKey = 'moreButton';

const createLinkTextStyles = (idleColor, hoverStateColor, activeStateColor) => StyleSheet.create({
  linkText: {
    [MEDIA_BREAKPOINTS.large]: {
      ':hover': {
        textDecoration: 'underline'
      }
    },
    [MEDIA_BREAKPOINTS.medium]: {
      ':hover': {
        color: 'initial'
      }
    },
    [MEDIA_BREAKPOINTS.small]: {
      ':hover': {
        color: 'initial'
      }
    }
  },
  moreButtonSvg: {
    fill: idleColor,
    width: '30px',
    height: '25px',
    ':hover': {
      fill: hoverStateColor
    },
    ':active': {
      fill: activeStateColor
    }
  },
  moreButtonStroke: {
    stroke: idleColor,
    ':hover': {
      stroke: hoverStateColor
    },
    ':active': {
      stroke: activeStateColor
    }
  }
});

const createMoreButtonText = (href, text, titleStyle, linkStyle) => {
  return /*#__PURE__*/createElement(TextLinkComponent, {
    href: href,
    linkStyle: linkStyle
  }, /*#__PURE__*/createElement(H4, {
    fontSizeLarge: TYPOGRAPHY_SIZE.BIGGER,
    fontSizeMedium: TYPOGRAPHY_SIZE.BIGGER,
    fontSizeSmall: TYPOGRAPHY_SIZE.BIGGER,
    styles: semanticCSS(titleStyle)
  }, text));
};

const MoreButton = props => {
  const {
    moreButtonSvg,
    moreButtonText
  } = props;
  const themeProps = useTheme(componentConfigurationKey);
  const {
    showArrow,
    idleColor,
    hoverStateColor,
    activeStateColor
  } = themeProps;
  const style = createLinkTextStyles(idleColor, hoverStateColor, activeStateColor);
  const additionalStyle = {
    justifyContent: 'flex-end',
    marginLeft: 'auto'
  };
  const linkStyle = generateLinkStyleFromTheme({
    idleColor,
    activeStateColor,
    hoverStateColor
  }, additionalStyle);

  if (moreButtonSvg) {
    const svg = moreButtonSvg.src.replace(/<g/g, `<g class="${css(style.moreButtonStroke)}"`).replace(/<svg/g, `<svg class="${css(style.moreButtonSvg)}"`);
    return /*#__PURE__*/createElement(TextLinkComponent, {
      href: moreButtonSvg.href,
      linkStyle: linkStyle
    }, /*#__PURE__*/createElement("div", {
      dangerouslySetInnerHTML: {
        __html: svg
      }
    }));
  }

  if (moreButtonText) {
    if (showArrow) {
      return /*#__PURE__*/createElement(TextLinkComponent, {
        href: moreButtonText.href,
        linkStyle: linkStyle
      }, /*#__PURE__*/createElement(ArrowSvg, {
        arrowColor: idleColor,
        alt: moreButtonText.text
      }));
    }

    return createMoreButtonText(moreButtonText.href, moreButtonText.text, style.linkText, linkStyle);
  }

  return null;
};

const componentConfigurationKey$1 = 'sectionHeader';

const createStyles = ({
  titleColor,
  titleBackgroundColor,
  borderTopColor,
  squareColor,
  showBorderTop,
  showBorderBottom,
  borderTopWidth,
  showStrikeThrowHorizontalLine,
  strikeThrowHorizontalLineColor,
  middleHorizontalLineColor,
  largeVerticalGap,
  mediumVerticalGap,
  smallVerticalGap,
  largeHorizontalGap,
  mediumHorizontalGap,
  smallHorizontalGap,
  largeWrapperGap,
  mediumWrapperGap,
  smallWrapperGap,
  descriptionColor,
  borderBottomColor,
  borderBottomWidth,
  largeMarginBottomFactor,
  mediumMarginBottomFactor,
  smallMarginBottomFactor
}) => {
  return StyleSheet.create({
    container: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'baseline',
      flexWrap: 'wrap',
      flexDirection: 'column',
      [MEDIA_BREAKPOINTS.large]: {
        marginBottom: `${baseUnit * largeMarginBottomFactor}px`
      },
      [MEDIA_BREAKPOINTS.medium]: {
        marginBottom: `${baseUnit * mediumMarginBottomFactor}px`
      },
      [MEDIA_BREAKPOINTS.small]: {
        marginBottom: `${baseUnit * smallMarginBottomFactor}px`
      }
    },
    titleItem: {
      flex: '1 1 auto',
      color: titleColor,
      display: 'inline-flex',
      alignItems: 'center',
      width: '100%',
      position: 'relative',
      zIndex: 0,
      borderTop: showBorderTop ? `${baseUnit * borderTopWidth}px solid ${borderTopColor}` : 'unset',
      borderBottom: showBorderBottom ? `${baseUnit * borderBottomWidth}px solid ${borderBottomColor}` : 'unset',
      backgroundColor: titleBackgroundColor,
      [MEDIA_BREAKPOINTS.large]: {
        padding: `${largeHorizontalGap * baseUnit}px ${largeWrapperGap * baseUnit}px ${largeHorizontalGap * baseUnit}px ${largeWrapperGap * baseUnit}px`
      },
      [MEDIA_BREAKPOINTS.medium]: {
        padding: `${mediumHorizontalGap * baseUnit}px ${mediumWrapperGap * baseUnit}px ${mediumHorizontalGap * baseUnit}px ${mediumWrapperGap * baseUnit}px`
      },
      [MEDIA_BREAKPOINTS.small]: {
        padding: `${smallHorizontalGap * baseUnit}px ${smallWrapperGap * baseUnit}px ${smallHorizontalGap * baseUnit}px ${smallWrapperGap * baseUnit}px`
      },
      ':before': { ...(showStrikeThrowHorizontalLine ? {
          content: '""',
          right: 0,
          left: 0,
          position: 'absolute',
          zIndex: -1,
          width: '100%',
          [MEDIA_BREAKPOINTS.large]: {
            borderTop: `${0.75 * baseUnit}px solid ${strikeThrowHorizontalLineColor}`
          },
          [MEDIA_BREAKPOINTS.medium]: {
            borderTop: `${0.5 * baseUnit}px solid ${strikeThrowHorizontalLineColor}`
          },
          [MEDIA_BREAKPOINTS.small]: {
            borderTop: `${0.5 * baseUnit}px solid ${strikeThrowHorizontalLineColor}`
          }
        } : null)
      }
    },
    square: {
      backgroundColor: squareColor,
      marginRight: `${baseUnit * 0.5}px`,
      [MEDIA_BREAKPOINTS.large]: {
        width: '28px',
        height: '28px'
      },
      [MEDIA_BREAKPOINTS.medium]: {
        width: '24px',
        height: '24px'
      },
      [MEDIA_BREAKPOINTS.small]: {
        width: '20px',
        height: '20px'
      }
    },
    middleHorizontalLineContainer: {
      flexGrow: 1,
      ':before': {
        content: '""',
        display: 'block',
        borderTop: `2px solid ${middleHorizontalLineColor}`,
        flex: '2 0 0',
        [MEDIA_BREAKPOINTS.large]: {
          marginRight: `${largeVerticalGap * baseUnit}px`,
          marginLeft: `${largeVerticalGap * baseUnit}px`
        },
        [MEDIA_BREAKPOINTS.medium]: {
          marginRight: `${mediumVerticalGap * baseUnit}px`,
          marginLeft: `${mediumVerticalGap * baseUnit}px`
        },
        [MEDIA_BREAKPOINTS.small]: {
          marginRight: `${smallVerticalGap * baseUnit}px`,
          marginLeft: `${smallVerticalGap * baseUnit}px`
        }
      }
    },
    description: {
      color: descriptionColor
    }
  });
};

const SectionHeader = props => {
  const themeProps = useTheme(componentConfigurationKey$1);
  const {
    children,
    paddingFactors,
    description,
    moreButtonText,
    moreButtonSvg
  } = props;
  const {
    showSquare,
    showMiddleHorizontalLine,
    descriptionFontSizeLarge,
    descriptionFontSizeMedium,
    descriptionFontSizeSmall
  } = themeProps;
  const style = createStyles(themeProps);
  const paddingStyle = getLayoutComponentPadding(paddingFactors);
  return /*#__PURE__*/createElement(ErrorBoundary, null, /*#__PURE__*/createElement("div", {
    className: css(paddingStyle, style.container)
  }, /*#__PURE__*/createElement("div", {
    className: css(style.titleItem)
  }, showSquare ? /*#__PURE__*/createElement("div", {
    className: css(style.square)
  }) : null, children, showMiddleHorizontalLine ? /*#__PURE__*/createElement("div", {
    className: css(style.middleHorizontalLineContainer)
  }) : null, /*#__PURE__*/createElement(MoreButton, {
    moreButtonText: moreButtonText,
    moreButtonSvg: moreButtonSvg
  })), description ? /*#__PURE__*/createElement(P, {
    styles: semanticCSS(style.description),
    fontSizeSmall: descriptionFontSizeSmall,
    fontSizeMedium: descriptionFontSizeMedium,
    fontSizeLarge: descriptionFontSizeLarge
  }, description) : null));
};

const PageDescription = ({
  description,
  favicon,
  pageUrl,
  title,
  ampURL,
  prevCanonicalUrl
}) => /*#__PURE__*/createElement(Helmet, null, /*#__PURE__*/createElement("title", null, title), /*#__PURE__*/createElement("meta", {
  charSet: "utf-8"
}), /*#__PURE__*/createElement("meta", {
  name: "description",
  content: description
}), /*#__PURE__*/createElement("link", {
  rel: "icon",
  href: favicon,
  type: "image/png"
}), /*#__PURE__*/createElement("link", {
  rel: "canonical",
  href: pageUrl
}), ampURL ? /*#__PURE__*/createElement("link", {
  rel: "amphtml",
  href: ampURL
}) : null, prevCanonicalUrl ? /*#__PURE__*/createElement("link", {
  rel: "prev",
  href: prevCanonicalUrl
}) : null);

const FacebookMeta = ({
  appId,
  pageId
}) => /*#__PURE__*/createElement(Helmet, null, appId ? /*#__PURE__*/createElement("meta", {
  property: "fb:app_id",
  content: appId
}) : null, pageId ? /*#__PURE__*/createElement("meta", {
  property: "fb:pages",
  content: pageId
}) : null);

const OpenGraph = ({
  title,
  siteName,
  image,
  pageUrl,
  imageWidth,
  imageHeight,
  description
}) => /*#__PURE__*/createElement(Helmet, null, /*#__PURE__*/createElement("meta", {
  property: "og:title",
  content: title
}), /*#__PURE__*/createElement("meta", {
  property: "og:site_name",
  content: siteName
}), image ? /*#__PURE__*/createElement("meta", {
  property: "og:image",
  content: image
}) : null, /*#__PURE__*/createElement("meta", {
  property: "og:url",
  content: pageUrl
}), imageWidth ? /*#__PURE__*/createElement("meta", {
  property: "og:image:width",
  content: `${imageWidth}`
}) : null, imageHeight ? /*#__PURE__*/createElement("meta", {
  property: "og:image:height",
  content: `${imageHeight}`
}) : null, /*#__PURE__*/createElement("meta", {
  property: "og:description",
  content: description
}));

const TwitterShare = ({
  title,
  description,
  account,
  image,
  pageUrl,
  imageWidth,
  imageHeight
}) => /*#__PURE__*/createElement(Helmet, null, /*#__PURE__*/createElement("meta", {
  property: "twitter:title",
  content: title
}), /*#__PURE__*/createElement("meta", {
  property: "twitter:description",
  content: description
}), image ? /*#__PURE__*/createElement("meta", {
  property: "twitter:image",
  content: image
}) : null, /*#__PURE__*/createElement("meta", {
  property: "twitter:url",
  content: pageUrl
}), /*#__PURE__*/createElement("meta", {
  property: "twitter:card",
  content: "summary_large_image"
}), imageWidth ? /*#__PURE__*/createElement("meta", {
  property: "twitter:image:width",
  content: `${imageWidth}`
}) : null, imageHeight ? /*#__PURE__*/createElement("meta", {
  property: "twitter:image:height",
  content: `${imageHeight}`
}) : null, account ? /*#__PURE__*/createElement("meta", {
  property: "twitter:site",
  content: account
}) : null);

const calculateImageUrl = (host, path, manipulation) => `${host + manipulation}/${path}`;

const isHighDprInLargeImage = (width, dpr) => width >= 600 && dpr > 2;

const createImageSrc = (host, path, width, aspectRatio, dpr) => {
  if (isHighDprInLargeImage(width, dpr)) {
    return createImageSrc(host, path, width, aspectRatio, 2);
  }

  return calculateImageUrl(host, path, `c_fill,w_${width * dpr},ar_${aspectRatio.x}:${aspectRatio.y},f_auto,q_auto,g_auto`);
};
const getImageSrcAndSrcSet = (imageAspectRatios, imageWidths, host, path) => {
  return {
    srcSetSmallScreen: `${createImageSrc(host, path, imageWidths.small, imageAspectRatios.small, 1)} 1x, ${createImageSrc(host, path, imageWidths.small, imageAspectRatios.small, 2)} 2x, ${createImageSrc(host, path, imageWidths.small, imageAspectRatios.small, 3)} 3x`,
    srcSetMediumScreen: `${createImageSrc(host, path, imageWidths.medium, imageAspectRatios.medium, 1)} 1x, ${createImageSrc(host, path, imageWidths.medium, imageAspectRatios.medium, 2)} 2x, ${createImageSrc(host, path, imageWidths.medium, imageAspectRatios.medium, 3)} 3x`,
    srcSetLargeScreen: `${createImageSrc(host, path, imageWidths.large, imageAspectRatios.large, 1)} 1x, ${createImageSrc(host, path, imageWidths.large, imageAspectRatios.large, 2)} 2x, ${createImageSrc(host, path, imageWidths.large, imageAspectRatios.large, 3)} 3x`,
    lowResUrl: createImageSrc(host, path, 16, {
      x: 16,
      y: 9
    }, 1),
    fallbackSrc: createImageSrc(host, path, imageWidths.medium, imageAspectRatios.medium, 1)
  };
};
const imageStyle = StyleSheet.create({
  base: {
    position: 'absolute',
    top: '0',
    left: '0',
    bottom: '0',
    minWidth: '100%',
    maxWidth: '100%',
    maxHeight: '100%',
    transition: 'opacity 0.2s ease-in-out',
    objectFit: 'cover',
    minHeight: '100%'
  }
});
const getImageCaption = (credit, caption) => {
  return [caption, credit].filter(value => {
    return typeof value === 'string' && value.trim() !== '';
  }).join(' | ');
};
const createImageWidths = (small, medium, large) => ({
  small,
  medium,
  large
});
const createImageHeightCalculationMethods = (small, medium, large) => ({
  small,
  medium,
  large
});
const createImageAspectRatios = (small, medium, large) => ({
  small,
  medium,
  large
});

const dateToIsoDate = date => {
  return new Date(date).toISOString();
};

const getAuthorsListSchema = authors => {
  if (authors.length === 1) {
    return {
      '@type': 'Person',
      name: authors[0].name
    };
  }

  return authors.map(author => ({
    '@type': 'Person',
    name: author.name
  }));
};

const getSchemaData = (canonical, title, image, datePublished, publisherName, logoUrl, dateModified) => {
  return {
    '@context': 'http://schema.org',
    '@type': 'NewsArticle',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': canonical
    },
    headline: title,
    image,
    datePublished: dateToIsoDate(datePublished),
    dateModified: dateToIsoDate(dateModified),
    publisher: {
      '@type': 'Organization',
      name: publisherName,
      logo: {
        '@type': 'ImageObject',
        url: logoUrl
      }
    }
  };
};
const GoogleNewsSchema = props => {
  const {
    canonical,
    title,
    image,
    datePublished,
    authors,
    publisherName,
    logoUrl,
    dateModified
  } = props;
  const schemaWithoutAuthors = getSchemaData(canonical, title, image, datePublished, publisherName, logoUrl, dateModified);
  const schema = authors ? { ...schemaWithoutAuthors,
    author: getAuthorsListSchema(authors)
  } : schemaWithoutAuthors;
  return /*#__PURE__*/createElement(Helmet, null, /*#__PURE__*/createElement("script", {
    type: "application/ld+json"
  }, JSON.stringify(schema)));
};

const numArticlesForSchema = 5;
const sortByDate = (article1, article2) => {
  const article1Time = new Date(article1.featuredAt);
  const article2Time = new Date(article2.featuredAt);
  if (article2Time > article1Time) return 1;
  if (article2Time < article1Time) return -1;
  return 0;
};

const getTopArticlesByDate = articles => articles && [...articles].sort(sortByDate).splice(0, Math.min(numArticlesForSchema, articles.length));

const getUniqueArticles = topArticles => {
  return topArticles.reduce((uniqueElements, element) => {
    if (!uniqueElements.find(item => item.id === element.id)) {
      return [...uniqueElements, element];
    }

    return uniqueElements;
  }, []);
};

const getSchema = (topArticles, publisherName, logoUrl) => ({
  '@context': 'http://schema.org',
  '@type': 'ItemList',
  itemListElement: topArticles && getUniqueArticles(topArticles).map((item, position) => {
    const imageUrl = createImageSrc(item.image.host, item.image.path, 720, {
      x: 16,
      y: 9
    }, 1);
    return {
      '@type': 'NewsArticle',
      position: position + 1,
      url: item.articleUrl,
      headline: item.title,
      image: imageUrl,
      datePublished: item.featuredAt,
      author: getAuthorsListSchema(item.authors),
      publisher: {
        '@type': 'Organization',
        name: publisherName,
        logo: {
          '@type': 'ImageObject',
          url: logoUrl
        }
      }
    };
  })
});

const ItemsListSchema = ({
  itemListElement,
  publisherName,
  logoUrl
}) => {
  const topArticles = getTopArticlesByDate(itemListElement);
  const scriptContent = getSchema(topArticles, publisherName, logoUrl);
  return /*#__PURE__*/createElement(Helmet, null, /*#__PURE__*/createElement("script", {
    type: "application/ld+json"
  }, JSON.stringify(scriptContent)));
};

const createImageAttributes = pageImage => {
  const imageAspectRatioX = 16;
  const imageAspectRatioY = 9;
  const aspectRatio = {
    x: imageAspectRatioX,
    y: imageAspectRatioY
  };
  const imageWidth = 720;
  const imageUrl = createImageSrc(pageImage.host, pageImage.path, imageWidth, aspectRatio, 1);
  const imageHeight = Number(imageWidth) * imageAspectRatioY / imageAspectRatioX;
  return {
    imageUrl,
    imageHeight,
    imageWidth
  };
};

const MetaHeaderTags = ({
  canonicalUrl,
  favicon,
  description,
  facebookAppId,
  facebookPageId,
  title,
  twitterAccount,
  siteName,
  pageImage,
  articles,
  logoUrl,
  ampURL,
  prevCanonicalUrl
}) => {
  const {
    imageHeight = null,
    imageUrl = null,
    imageWidth = null
  } = pageImage ? createImageAttributes(pageImage) : {};
  const metaDescription = description || '';
  return /*#__PURE__*/createElement(Fragment, null, /*#__PURE__*/createElement(PageDescription, {
    pageUrl: canonicalUrl,
    favicon: favicon,
    description: metaDescription,
    title: title,
    ampURL: ampURL,
    prevCanonicalUrl: prevCanonicalUrl
  }), /*#__PURE__*/createElement(FacebookMeta, {
    appId: facebookAppId,
    pageId: facebookPageId
  }), /*#__PURE__*/createElement(OpenGraph, {
    pageUrl: canonicalUrl,
    image: imageUrl,
    title: title,
    siteName: siteName,
    imageHeight: imageHeight,
    imageWidth: imageWidth,
    description: metaDescription
  }), /*#__PURE__*/createElement(TwitterShare, {
    title: title,
    description: metaDescription,
    account: twitterAccount,
    image: imageUrl,
    pageUrl: canonicalUrl,
    imageHeight: imageHeight,
    imageWidth: imageWidth
  }), /*#__PURE__*/createElement(ItemsListSchema, {
    publisherName: siteName,
    logoUrl: logoUrl,
    itemListElement: articles
  }));
};

const getCombinedContent = actualContents => {
  return actualContents.reduce((combinedContent, content, index) => {
    if (index === 0) {
      return content;
    }

    return `${combinedContent}, ${content}`;
  }, '');
};

const RobotsMeta = props => {
  const contents = ['max-snippet:70', 'max-image-preview:large', 'max-video-preview:20'];
  const {
    noIndex
  } = props;

  if (noIndex === true) {
    contents.push('noindex', 'nofollow');
  }

  return /*#__PURE__*/createElement(Helmet, null, /*#__PURE__*/createElement("meta", {
    name: "robots",
    content: getCombinedContent(contents)
  }));
};

const PostMetaHeaderTags = props => {
  const {
    canonicalUrl,
    favicon,
    pageDescription,
    facebookAppId,
    facebookPageId,
    pageTitle,
    twitterAccount,
    siteName,
    pageImage,
    logoUrl,
    authors,
    createdAt,
    updatedAt,
    noIndex,
    ampURL,
    prevCanonicalUrl,
    ogDescription,
    ogImage,
    ogTitle,
    twitterDescription,
    twitterImage,
    twitterTitle
  } = props;
  const pageImageUrl = createImageAttributes(pageImage).imageUrl;
  const facebookImageProperties = createImageAttributes(ogImage);
  const twitterImageProperties = createImageAttributes(twitterImage);
  return /*#__PURE__*/createElement(Fragment, null, /*#__PURE__*/createElement(PageDescription, {
    pageUrl: canonicalUrl,
    favicon: favicon,
    description: pageDescription,
    title: pageTitle,
    ampURL: ampURL,
    prevCanonicalUrl: prevCanonicalUrl
  }), /*#__PURE__*/createElement(FacebookMeta, {
    appId: facebookAppId,
    pageId: facebookPageId
  }), /*#__PURE__*/createElement(OpenGraph, {
    pageUrl: canonicalUrl,
    image: facebookImageProperties.imageUrl,
    title: ogTitle,
    siteName: siteName,
    imageHeight: facebookImageProperties.imageHeight,
    imageWidth: facebookImageProperties.imageWidth,
    description: ogDescription
  }), /*#__PURE__*/createElement(TwitterShare, {
    title: twitterTitle,
    description: twitterDescription,
    account: twitterAccount,
    image: twitterImageProperties.imageUrl,
    pageUrl: canonicalUrl,
    imageHeight: twitterImageProperties.imageHeight,
    imageWidth: twitterImageProperties.imageWidth
  }), /*#__PURE__*/createElement(GoogleNewsSchema, {
    canonical: canonicalUrl,
    title: pageTitle,
    image: pageImageUrl,
    datePublished: createdAt,
    dateModified: updatedAt,
    authors: authors,
    publisherName: siteName,
    logoUrl: logoUrl
  }), /*#__PURE__*/createElement(RobotsMeta, {
    noIndex: noIndex
  }));
};

function createScriptTag({
  type,
  source,
  async,
  name,
  attributes = {}
}) {
  if (type === 'URL' && source.length > 0) {
    return /*#__PURE__*/createElement("script", _extends({
      async: async,
      src: source,
      type: "text/javascript",
      key: name
    }, attributes));
  } // TODO - add html sanitization in the builder side or here...


  if (type === 'code' && source.length > 0) {
    return /*#__PURE__*/createElement("script", _extends({
      type: "text/javascript",
      key: name
    }, attributes), source);
  }

  return null;
}

const ExternalScriptsComponent = ({
  scripts
}) => {
  return /*#__PURE__*/createElement(Helmet, null, scripts.map(createScriptTag));
};
const ExternalScripts = props => /*#__PURE__*/createElement(ErrorBoundary, null, /*#__PURE__*/createElement(ExternalScriptsComponent, props));

const componentConfigurationKey$2 = 'decorationLine';
const decorationLineHeight = 2;

const getDecorationLineStyle = cardDecorationColor => {
  return StyleSheet.create({
    style: {
      height: `${decorationLineHeight}px`,
      width: '100%',
      backgroundColor: cardDecorationColor
    }
  });
};

const DecorationLine = () => {
  const {
    cardDecorationColor
  } = useTheme(componentConfigurationKey$2);
  const decorationLineStyle = getDecorationLineStyle(cardDecorationColor);
  return /*#__PURE__*/createElement("div", {
    className: css(decorationLineStyle.style)
  });
};

const ellipsify = lines => {
  return {
    style: {
      overflow: 'hidden',
      display: '-webkit-box',
      '-webkitLineClamp': `${lines}`,
      '-webkitBoxOrient': 'vertical'
    }
  };
};

const createStyles$1 = (borderColor, linksColor, linkHoverColor, baseUnit) => StyleSheet.create({
  sectionItem: {
    padding: `${baseUnit * 0.5}px 0`,
    margin: `0 ${baseUnit * 0.5}px`,

    /*
      width is calculation of 50% - margins. The list is 200% width
      therefore to fit the visual container we set the item width to 50%
     */
    width: `calc(50% - ${baseUnit}px)`,
    borderTop: `1px solid ${borderColor}`,
    flex: '0 0 auto',
    boxSizing: 'border-box',
    position: 'relative',
    ':first-child': {
      borderTop: 'none'
    },
    ':last-child': {
      [MEDIA_BREAKPOINTS.small]: {
        display: 'none'
      }
    }
  },
  link: {
    textDecoration: 'none',
    boxSizing: 'border-box',
    color: linksColor,
    ':visited': {
      color: linksColor,
      textDecoration: 'none'
    },
    ':active': {
      color: linksColor,
      textDecoration: 'none'
    },
    [MEDIA_BREAKPOINTS.large]: {
      ':hover': {
        color: linkHoverColor,
        textDecoration: 'none'
      }
    },
    [MEDIA_BREAKPOINTS.medium]: {
      ':hover': {
        color: 'initial'
      }
    },
    [MEDIA_BREAKPOINTS.small]: {
      '-webkitLineClamp': '2',
      ':hover': {
        color: 'initial'
      }
    }
  }
});

const componentConfigurationKey$3 = 'listItem';
const ListItem = ({
  title,
  articleUrl,
  numberOfLines = 2
}) => {
  const {
    borderColor,
    linkColor,
    linkHoverColor,
    baseUnit
  } = useTheme(componentConfigurationKey$3);
  const styles = createStyles$1(borderColor, linkColor, linkHoverColor, baseUnit); // @ts-ignore TS2345

  const {
    style
  } = StyleSheet.create(ellipsify(numberOfLines));
  return /*#__PURE__*/createElement(Link, {
    href: articleUrl,
    className: css(styles.link, styles.sectionItem),
    key: articleUrl
  }, /*#__PURE__*/createElement(H4, {
    styles: semanticCSS(style)
  }, title));
};

const LIST_WRAPPER_STYLE = {
  flex: '1',
  position: 'relative'
};
const LIST_STYLE = {
  position: 'absolute',
  top: '0',
  left: '0',
  bottom: '0',
  display: 'flex',
  flexDirection: 'column',
  flexWrap: 'wrap',
  width: '200%',
  // we preserve width for two columns here to be able to hide wrapping children
  minWidth: '200%'
};

const componentConfigurationKey$4 = 'listWithHeading';

const createStyles$2 = (titleColor, backgroundColor, baseUnit, borderWidth, borderRadius, borderColor) => StyleSheet.create({
  listWrapper: { ...LIST_WRAPPER_STYLE,
    backgroundColor,
    borderRadius: `${borderRadius}px`,
    border: `${borderWidth}px solid ${borderColor}`,
    overflow: 'hidden'
  },
  list: { ...LIST_STYLE,
    [MEDIA_BREAKPOINTS.small]: {
      position: 'relative'
    }
  },
  title: {
    color: titleColor,
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    padding: `${baseUnit / 2}px`,
    [MEDIA_BREAKPOINTS.large]: {
      padding: `${baseUnit / 2}px`
    },
    [MEDIA_BREAKPOINTS.medium]: {
      padding: `${baseUnit / 2}px`
    },
    [MEDIA_BREAKPOINTS.small]: {
      padding: `${baseUnit / 2}px`
    }
  }
});

const ListWithHeading = ({
  listProps = [],
  displayName = '',
  itemNumLines = 1
}) => {
  const {
    titleColor,
    backgroundColor,
    baseUnit,
    borderWidth,
    borderRadius,
    borderColor,
    boxShadowTypeNormal,
    boxShadowTypeActive,
    boxShadowTypeHover
  } = useTheme(componentConfigurationKey$4);
  const styles = createStyles$2(titleColor, backgroundColor, baseUnit, borderWidth, borderRadius, borderColor);
  const boxShadowStyle = createStyleForBoxShadow({
    boxShadowTypeNormal,
    boxShadowTypeHover,
    boxShadowTypeActive
  });

  const createHeading = () => /*#__PURE__*/createElement(H1, {
    styles: semanticCSS(styles.title)
  }, displayName);

  return /*#__PURE__*/createElement("div", {
    className: css(boxShadowStyle.style, styles.listWrapper)
  }, /*#__PURE__*/createElement(DecorationLine, null), /*#__PURE__*/createElement("div", {
    className: css(styles.list)
  }, createHeading(), listProps.map((listItemProps, index) => /*#__PURE__*/createElement(ListItem, _extends({}, listItemProps, {
    numberOfLines: itemNumLines,
    key: index
  })))));
};

const hex2rgba = (hex, alpha = 1) => {
  // @ts-ignore
  const [r, g, b] = hex.match(/\w\w/g).map(x => parseInt(x, 16));
  return `rgba(${r},${g},${b},${alpha})`;
};

const CardOverlayStyles = cardOverlayColor => {
  return StyleSheet.create({
    style: {
      width: '100%',
      height: '100%',
      position: 'absolute',
      bottom: '0px',
      background: `linear-gradient(0deg, ${hex2rgba(cardOverlayColor, 0.7)} 0%, rgba(0,0,0,0) 100%)`
    }
  });
};

const CardOverlay = ({
  overlayColor
}) => {
  const cardOverlayStyle = CardOverlayStyles(overlayColor);
  return /*#__PURE__*/createElement("div", {
    className: css(cardOverlayStyle.style)
  });
};

const intersectionObserverOptions = {
  root: null,
  rootMargin: '0px',
  threshold: [0, 0.05, 0.1]
};
const createIntersectionObserver = (callback, target) => {
  // @ts-ignore
  const observer = new window.IntersectionObserver(callback, intersectionObserverOptions);

  if (target) {
    observer.observe(target);
  }
};

const placeholderStyle = StyleSheet.create({
  style: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    transition: 'opacity 0.2s ease-in-out',
    willChange: 'opacity, transform'
  },
  initial: {
    opacity: 1
  },
  ready: {
    opacity: 0
  }
});
const defaultBlurRadius = 40;
class BlurredImageComponent extends Component {
  constructor(props) {
    super(props);

    _defineProperty(this, "img", void 0);

    _defineProperty(this, "canvasRef", void 0);

    _defineProperty(this, "drawImageOnCanvas", event => {
      const {
        blur
      } = this.props;
      const ctx = this.getCanvas().getContext('2d');
      const {
        width,
        height
      } = this.getDimensions();
      this.setCanvasDimensions(width, height);
      ctx.filter = `blur(${blur}px)`;
      ctx.drawImage(event.target, 0, 0, width, height);
    });

    _defineProperty(this, "removeCanvas", () => {
      this.setState({
        shouldShowCanvas: false
      });
    });

    this.state = {
      shouldShowCanvas: true
    };
    this.canvasRef = /*#__PURE__*/createRef();
  }

  componentDidMount() {
    this.createImage();
    const {
      getRefCallback
    } = this.props;

    if (typeof getRefCallback === 'function') {
      getRefCallback(this.canvasRef);
    }
  }

  componentWillUnmount() {
    if (this.img) {
      this.img.onload = null;
    }
  }

  getCanvas() {
    return this.canvasRef.current;
  }

  getDimensions() {
    return this.getCanvas().getBoundingClientRect();
  }

  setCanvasDimensions(width, height) {
    this.getCanvas().width = width;
    this.getCanvas().height = height;
  }

  createImage() {
    const {
      src
    } = this.props;
    this.img = new window.Image();
    this.img.onload = this.drawImageOnCanvas;
    this.img.src = src;
  }

  render() {
    const {
      status
    } = this.props;
    const {
      shouldShowCanvas
    } = this.state;
    return shouldShowCanvas ? /*#__PURE__*/createElement("canvas", {
      className: css(placeholderStyle.style, placeholderStyle[status]),
      ref: this.canvasRef,
      onTransitionEnd: this.removeCanvas
    }) : null;
  }

}
const BlurredImage = BlurredImageComponent;

const EagerImage = props => {
  const {
    fallbackSrc,
    alt,
    title,
    srcSetLargeScreen,
    srcSetMediumScreen,
    srcSetSmallScreen
  } = props;
  return /*#__PURE__*/createElement("picture", {
    className: css(imageStyle.base)
  }, /*#__PURE__*/createElement("source", {
    srcSet: srcSetSmallScreen,
    media: `(max-width: ${SMALL_SCREEN_SIZE}px)`
  }), /*#__PURE__*/createElement("source", {
    srcSet: srcSetMediumScreen,
    media: `(max-width: ${MEDIUM_SCREEN_SIZE}px)`
  }), /*#__PURE__*/createElement("source", {
    srcSet: srcSetLargeScreen,
    media: `(min-width: ${LARGE_SCREEN_SIZE}px)`
  }), /*#__PURE__*/createElement("img", {
    className: css(imageStyle.base),
    src: fallbackSrc,
    alt: alt,
    title: title
  }));
};

const statusLabels = {
  initial: 'initial',
  ready: 'ready'
};
const lazyImageStyle = StyleSheet.create({
  style: {
    willChange: 'opacity, transform'
  },
  initial: {
    opacity: 0
  },
  ready: {
    opacity: 1
  }
});
class LazyImageComponent extends Component {
  constructor(props) {
    super(props);

    _defineProperty(this, "observer", null);

    _defineProperty(this, "blurRef", void 0);

    _defineProperty(this, "imageRef", void 0);

    _defineProperty(this, "imageLargeSourceRef", void 0);

    _defineProperty(this, "imageMediumSourceRef", void 0);

    _defineProperty(this, "imageSmallSourceRef", void 0);

    _defineProperty(this, "getBlurredImageRef", ref => {
      this.blurRef = ref;
      this.observer = this.createIntersectionObserver();
    });

    _defineProperty(this, "intersectionCallback", entries => entries.filter(entry => entry.intersectionRatio > 0).forEach(() => this.loadImage()));

    this.imageRef = /*#__PURE__*/createRef();
    this.imageLargeSourceRef = /*#__PURE__*/createRef();
    this.imageMediumSourceRef = /*#__PURE__*/createRef();
    this.imageSmallSourceRef = /*#__PURE__*/createRef();
    this.blurRef = /*#__PURE__*/createRef();
    this.state = {
      status: statusLabels.initial
    };
  }

  componentWillUnmount() {
    this.observer = this.stopObservingIntersections();
  }

  getImageStyle() {
    const {
      status
    } = this.state;
    return css(imageStyle.base, lazyImageStyle[status], lazyImageStyle.style);
  }

  getBlurRadius() {
    return defaultBlurRadius;
  }

  createIntersectionObserver() {
    return createIntersectionObserver(this.intersectionCallback, this.blurRef.current);
  }

  stopObservingIntersections() {
    if (this.observer && this.blurRef.current) {
      this.observer.unobserve(this.blurRef.current);
    }
  }

  loadImage() {
    const {
      fallbackSrc,
      srcSetLargeScreen,
      srcSetMediumScreen,
      srcSetSmallScreen
    } = this.props;
    const img = this.imageRef.current;
    const imgLargeSourceRef = this.imageLargeSourceRef.current;
    const imgMediumSourceRef = this.imageMediumSourceRef.current;
    const imgSmallSourceRef = this.imageSmallSourceRef.current;

    img.onload = () => this.setState({
      status: statusLabels.ready
    });

    this.stopObservingIntersections();
    img.src = fallbackSrc;
    imgLargeSourceRef.srcset = srcSetLargeScreen;
    imgLargeSourceRef.media = `(min-width: ${LARGE_SCREEN_SIZE}px)`;
    imgMediumSourceRef.srcset = srcSetMediumScreen;
    imgMediumSourceRef.media = `(max-width: ${MEDIUM_SCREEN_SIZE}px)`;
    imgSmallSourceRef.srcset = srcSetSmallScreen;
    imgSmallSourceRef.media = `(max-width: ${SMALL_SCREEN_SIZE}px)`;
  }

  render() {
    const {
      status
    } = this.state;
    const {
      alt,
      title,
      lowResUrl
    } = this.props;
    return /*#__PURE__*/createElement(Fragment, null, /*#__PURE__*/createElement(BlurredImage, {
      src: lowResUrl,
      blur: this.getBlurRadius(),
      getRefCallback: this.getBlurredImageRef,
      status: status
    }), /*#__PURE__*/createElement("picture", {
      className: this.getImageStyle()
    }, /*#__PURE__*/createElement("source", {
      ref: this.imageSmallSourceRef
    }), /*#__PURE__*/createElement("source", {
      ref: this.imageMediumSourceRef
    }), /*#__PURE__*/createElement("source", {
      ref: this.imageLargeSourceRef
    }), /*#__PURE__*/createElement("img", {
      className: this.getImageStyle(),
      ref: this.imageRef,
      alt: alt,
      title: title
    })), /*#__PURE__*/createElement("noscript", null, /*#__PURE__*/createElement(EagerImage, this.props)));
  }

}
const LazyImage = LazyImageComponent;

const componentConfigThemeKey = 'imageWrapper';
const defaultImageHeightCalculationMethods = createImageHeightCalculationMethods('ASPECT_RATIO', 'ASPECT_RATIO', 'ASPECT_RATIO');
const getBaseStyle = (imageAspectRatios, backgroundColor, imageHeightCalculationMethods, transparent) => {
  const getBaseStyleByType = type => {
    switch (type) {
      case '100%_HEIGHT':
        return {
          paddingTop: 'unset',
          height: '100%'
        };

      case 'ASPECT_RATIO':
      default:
        return {
          [MEDIA_BREAKPOINTS.large]: {
            paddingTop: `calc(100% / (${imageAspectRatios.large.x / imageAspectRatios.large.y}))`
          },
          [MEDIA_BREAKPOINTS.medium]: {
            paddingTop: `calc(100% / (${imageAspectRatios.medium.x / imageAspectRatios.medium.y}))`
          },
          [MEDIA_BREAKPOINTS.small]: {
            paddingTop: `calc(100% / (${imageAspectRatios.small.x / imageAspectRatios.small.y}))`
          }
        };
    }
  };

  return StyleSheet.create({
    base: {
      backgroundColor: transparent ? 'transparent' : backgroundColor,
      position: 'relative',
      overflow: 'hidden',
      margin: 0,
      boxSizing: 'border-box',
      flex: '1 1 100%',
      minWidth: '0%',
      [MEDIA_BREAKPOINTS.large]: { ...getBaseStyleByType(imageHeightCalculationMethods.large)
      },
      [MEDIA_BREAKPOINTS.medium]: { ...getBaseStyleByType(imageHeightCalculationMethods.medium)
      },
      [MEDIA_BREAKPOINTS.small]: { ...getBaseStyleByType(imageHeightCalculationMethods.small)
      }
    }
  });
};
const ImageWrapper = props => {
  const {
    additionalStyle,
    dataId,
    children,
    imageHeightCalculationMethods = defaultImageHeightCalculationMethods,
    imageAspectRatios,
    transparent
  } = props;
  const {
    backgroundColor
  } = useTheme(componentConfigThemeKey);
  const baseStyle = getBaseStyle(imageAspectRatios, backgroundColor, imageHeightCalculationMethods, transparent);
  return /*#__PURE__*/createElement(ErrorBoundary, null, additionalStyle ? /*#__PURE__*/createElement("figure", {
    "data-id": dataId,
    className: css(additionalStyle.style)
  }, /*#__PURE__*/createElement("div", {
    className: css(baseStyle.base)
  }, children)) : /*#__PURE__*/createElement("figure", {
    "data-id": dataId,
    className: css(baseStyle.base)
  }, children));
};

const Image = props => {
  const {
    description,
    host,
    path,
    aspectRatio = {
      x: 16,
      y: 9
    },
    additionalStyle,
    lazyLoad,
    dataId,
    imageHeightCalculationMethods,
    transparent = false
  } = props;
  const {
    imageAspectRatios = createImageAspectRatios(aspectRatio, aspectRatio, aspectRatio),
    imageWidths
  } = props;
  const CalculatedImage = lazyLoad ? LazyImage : EagerImage;
  const {
    fallbackSrc,
    lowResUrl,
    srcSetLargeScreen,
    srcSetMediumScreen,
    srcSetSmallScreen
  } = getImageSrcAndSrcSet(imageAspectRatios, imageWidths, host, path);
  return /*#__PURE__*/createElement(ImageWrapper, {
    imageAspectRatios: imageAspectRatios,
    additionalStyle: additionalStyle,
    dataId: dataId,
    imageHeightCalculationMethods: imageHeightCalculationMethods,
    transparent: transparent
  }, /*#__PURE__*/createElement(CalculatedImage, {
    alt: description,
    title: description,
    fallbackSrc: fallbackSrc,
    lowResUrl: lowResUrl,
    srcSetLargeScreen: srcSetLargeScreen,
    srcSetMediumScreen: srcSetMediumScreen,
    srcSetSmallScreen: srcSetSmallScreen
  }));
};

const componentConfigThemeKey$1 = 'playIcon';

const playIconStyle = ({
  baseUnit,
  backgroundColor,
  triangleColor
}) => StyleSheet.create({
  wrapper: {
    height: '15%',
    minHeight: '24px',
    width: 'auto',
    position: 'absolute',
    top: `${baseUnit * 0.75}px`,
    left: `${baseUnit * 0.75}px`
  },
  circle: {
    fill: backgroundColor
  },
  polygon: {
    fill: triangleColor
  }
});

const PlayIcon = () => {
  const themeProps = useTheme(componentConfigThemeKey$1);
  const style = playIconStyle(themeProps);
  return /*#__PURE__*/React__default.createElement("svg", {
    className: css(style.wrapper),
    viewBox: "0 0 64 64",
    version: "1.1",
    xmlns: "http://www.w3.org/2000/svg"
  }, /*#__PURE__*/React__default.createElement("title", null, "Icon / Video"), /*#__PURE__*/React__default.createElement("desc", null, "Created with Sketch."), /*#__PURE__*/React__default.createElement("g", {
    stroke: "none",
    strokeWidth: "1",
    fill: "none",
    fillRule: "evenodd"
  }, /*#__PURE__*/React__default.createElement("g", null, /*#__PURE__*/React__default.createElement("circle", {
    className: css(style.circle),
    fill: "#FE5000",
    cx: "32",
    cy: "32",
    r: "32"
  }), /*#__PURE__*/React__default.createElement("polygon", {
    className: css(style.polygon),
    fill: "#FFFFFF",
    transform: "translate(34.000000, 32.000000) rotate(90.000000) translate(-34.000000, -32.000000) ",
    points: "34 20 46 44 22 44"
  }))));
};

const createStyle = ({
  borderRadius,
  paddingLarge = 0,
  paddingMedium = 0,
  paddingSmall = 0
}) => StyleSheet.create({
  wrapper: {
    display: 'flex',
    width: '100%',
    height: '100%',
    borderRadius: `${borderRadius}px`,
    boxSizing: 'border-box',
    background: 'transparent',
    [MEDIA_BREAKPOINTS.large]: {
      padding: `${paddingLarge * baseUnit}px`
    },
    [MEDIA_BREAKPOINTS.medium]: {
      padding: `${paddingMedium * baseUnit}px`
    },
    [MEDIA_BREAKPOINTS.small]: {
      padding: `${paddingSmall * baseUnit}px`
    }
  }
});

const WrapperForCard = props => {
  const {
    boxShadowStyle,
    children
  } = props;
  const style = createStyle(props);
  return /*#__PURE__*/createElement("article", {
    className: css(boxShadowStyle.style, style.wrapper)
  }, children);
};

const listAuthors = authors => {
  return authors.map(author => author.name).join(', ');
};
const showMetadataInAnyScreenSize = (showAuthor, showDate) => showAuthor || showDate;
const showAuthorInAnyScreenSize = (authors, showAuthorInLargeScreen, showAuthorInMediumScreen, showAuthorInSmallScreen) => authors && authors.length > 0 && (showAuthorInLargeScreen || showAuthorInMediumScreen || showAuthorInSmallScreen);
const showDateInAnyScreenSize = (showDateInLargeScreen, showDateInMediumScreen, showDateInSmallScreen) => showDateInLargeScreen || showDateInMediumScreen || showDateInSmallScreen;
const showDescriptionInAnyScreenSize = (showDescriptionInLargeScreen, showDescriptionInMediumScreen, showDescriptionInSmallScreen) => showDescriptionInLargeScreen || showDescriptionInMediumScreen || showDescriptionInSmallScreen;
const showPipeInAnyScreenSize = (showAuthor, showDate) => showAuthor && showDate;
const showMetadataInLargeScreenSize = (showAuthorInLargeScreen, showDateInLargeScreen) => showAuthorInLargeScreen || showDateInLargeScreen;
const showMetadataInMediumScreenSize = (showAuthorInMediumScreen, showDateInMediumScreen) => showAuthorInMediumScreen || showDateInMediumScreen;
const showMetadataInSmallScreenSize = (showAuthorInSmallScreen, showDateInSmallScreen) => showAuthorInSmallScreen || showDateInSmallScreen;
const shouldShowSponsored = (...params) => params.every(str => typeof str === 'string' && str.trim() !== '');

const DynamicSemanticTag = props => {
  const {
    semanticTag
  } = props;
  const defaultFontSizes = getFontSizesBySemanticTag(semanticTag);
  const {
    fontSizesStyles
  } = useTheme();
  const {
    children,
    styles = [],
    fontSizeSmall = defaultFontSizes.fontSizeSmall,
    fontSizeMedium = defaultFontSizes.fontSizeMedium,
    fontSizeLarge = defaultFontSizes.fontSizeLarge,
    ...attributes
  } = props;
  const styleBySize = getStyleBySize(fontSizesStyles, {
    fontSizeSmall,
    fontSizeMedium,
    fontSizeLarge
  });
  const SemanticTag = semanticTag;
  delete attributes.semanticTag;
  return /*#__PURE__*/createElement(SemanticTag, _extends({
    className: css(styleBySize, ...styles)
  }, attributes), children);
};

const componentConfigThemeKey$2 = 'sponsoredTextVertical';

const getStyles = ({
  baseUnit,
  sponsoredTextColor,
  sponsoredIconColor,
  alignment = ALIGNMENT_OPTIONS.CENTER
}) => StyleSheet.create({
  wrapper: {
    textAlign: alignment === ALIGNMENT_OPTIONS.CENTER ? 'center' : 'left',
    marginTop: `${baseUnit / 2}px`,
    [MEDIA_BREAKPOINTS.large]: {
      gridRowGap: `${baseUnit / 2}px`,
      marginTop: `${baseUnit / 2}px`
    },
    [MEDIA_BREAKPOINTS.medium]: {
      gridRowGap: `${baseUnit / 2}px`,
      marginTop: `${baseUnit / 2}px`
    },
    [MEDIA_BREAKPOINTS.small]: {
      gridRowGap: `${baseUnit / 4}px`,
      marginTop: `${baseUnit / 4}px`
    }
  },
  sponsored: {
    color: sponsoredTextColor
  },
  image: {
    marginTop: `${baseUnit / 2}px`,
    fill: sponsoredIconColor,
    [MEDIA_BREAKPOINTS.large]: {
      height: `${baseUnit * 2}px`
    },
    [MEDIA_BREAKPOINTS.medium]: {
      height: `${baseUnit * 2}px`
    },
    [MEDIA_BREAKPOINTS.small]: {
      height: `${baseUnit}px`
    }
  }
});

const checkLogoExists = logo => typeof logo === 'string' && logo.trim() !== '';

const addParamsToLogo = (logo, className) => {
  return logo.replace('<svg', `<svg class="${className}"`);
};

const SponsoredTextVertical = props => {
  const {
    sponsoredText,
    logo,
    brandName
  } = props;
  const themeProps = useTheme(componentConfigThemeKey$2);
  const styles = getStyles(themeProps);
  const hasLogo = checkLogoExists(logo);
  const text = hasLogo ? sponsoredText : `${sponsoredText} ${brandName}`;
  const {
    sponsorFontSizeLarge,
    sponsorFontSizeMedium,
    sponsorFontSizeSmall
  } = themeProps;
  return /*#__PURE__*/createElement("div", {
    className: css(styles.wrapper)
  }, /*#__PURE__*/createElement(P, {
    fontSizeLarge: sponsorFontSizeLarge,
    fontSizeMedium: sponsorFontSizeMedium,
    fontSizeSmall: sponsorFontSizeSmall,
    styles: semanticCSS(styles.sponsored)
  }, text), // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  hasLogo && /*#__PURE__*/createElement("div", {
    dangerouslySetInnerHTML: {
      __html: addParamsToLogo(logo, css(styles.image))
    }
  }));
};

const componentConfigThemeKey$3 = 'onTopCard';

const createCardStyles = (cardTextColorTypeNormal, cardTextColorTypeHover, cardTextColorTypeActive, borderRadius) => StyleSheet.create({
  card: {
    width: '100%',
    height: '100%',
    position: 'relative',
    textDecoration: 'none',
    color: cardTextColorTypeNormal,
    ':hover': {
      color: cardTextColorTypeHover
    },
    ':active': {
      color: cardTextColorTypeActive
    },
    display: 'block',
    '-webkit-mask-image': '-webkit-radial-gradient(white, black)',
    borderRadius: `${borderRadius}px`
  }
});

const createFooterStyle = metadataAlignment => StyleSheet.create({
  style: {
    justifySelf: metadataAlignment === ALIGNMENT_OPTIONS.CENTER ? 'center' : 'left'
  }
}).style;

const createMetadataStyle = (horizontalGapLarge, horizontalGapMedium, horizontalGapSmall, showMetadataInLargeScreen, showMetadataInMediumScreen, showMetadataInSmallScreen) => StyleSheet.create({
  style: {
    gridTemplateColumns: 'auto auto max-content',
    [MEDIA_BREAKPOINTS.large]: {
      display: showMetadataInLargeScreen ? 'inline-grid' : 'none',
      marginTop: `${baseUnit * horizontalGapLarge}px`
    },
    [MEDIA_BREAKPOINTS.medium]: {
      display: showMetadataInMediumScreen ? 'inline-grid' : 'none',
      marginTop: `${baseUnit * horizontalGapMedium}px`
    },
    [MEDIA_BREAKPOINTS.small]: {
      display: showMetadataInSmallScreen ? 'inline-grid' : 'none',
      marginTop: `${baseUnit * horizontalGapSmall}px`
    }
  }
}).style;

const createTitleAlignmentStyle = titleAlignment => {
  switch (titleAlignment) {
    case ALIGNMENT_OPTIONS.CENTER:
      return StyleSheet.create({
        style: {
          textAlign: 'center'
        }
      }).style;

    case ALIGNMENT_OPTIONS.START:
    default:
      return StyleSheet.create({
        style: {
          textAlign: 'left'
        }
      }).style;
  }
};

const createAuthorsStyle = (showAuthorSmallScreen, showAuthorMediumScreen, showAuthorLargeScreen, authorColor, numberOfLinesForAuthors) => StyleSheet.create({
  style: {
    color: authorColor,
    height: 'fit-content',
    alignSelf: 'flex-end',
    [MEDIA_BREAKPOINTS.large]: {
      marginRight: `${baseUnit * 0.25}px`,
      ...(showAuthorLargeScreen ? ellipsify(numberOfLinesForAuthors).style : {
        display: 'none'
      })
    },
    [MEDIA_BREAKPOINTS.medium]: {
      marginRight: `${baseUnit * 0.25}px`,
      ...(showAuthorMediumScreen ? ellipsify(numberOfLinesForAuthors).style : {
        display: 'none'
      })
    },
    [MEDIA_BREAKPOINTS.small]: {
      marginRight: `${baseUnit * 0.25}px`,
      ...(showAuthorSmallScreen ? ellipsify(numberOfLinesForAuthors).style : {
        display: 'none'
      })
    }
  }
}).style;

const createDateStyle = (showDateSmallScreen, showDateMediumScreen, showDateLargeScreen, dateColor) => StyleSheet.create({
  style: {
    color: dateColor,
    alignSelf: 'flex-end',
    [MEDIA_BREAKPOINTS.large]: {
      display: showDateLargeScreen ? 'inline-block' : 'none'
    },
    [MEDIA_BREAKPOINTS.medium]: {
      display: showDateMediumScreen ? 'inline-block' : 'none'
    },
    [MEDIA_BREAKPOINTS.small]: {
      display: showDateSmallScreen ? 'inline-block' : 'none'
    }
  }
}).style;

const createPipeStyle = (showDateSmallScreen, showDateMediumScreen, showDateLargeScreen, showAuthorSmallScreen, showAuthorMediumScreen, showAuthorLargeScreen, pipeColor) => StyleSheet.create({
  style: {
    color: pipeColor,
    alignSelf: 'flex-end',
    [MEDIA_BREAKPOINTS.large]: {
      display: showDateLargeScreen && showAuthorLargeScreen ? 'block' : 'none',
      marginRight: `${baseUnit * 0.25}px`
    },
    [MEDIA_BREAKPOINTS.medium]: {
      display: showDateMediumScreen && showAuthorMediumScreen ? 'block' : 'none',
      marginRight: `${baseUnit * 0.25}px`
    },
    [MEDIA_BREAKPOINTS.small]: {
      display: showDateSmallScreen && showAuthorSmallScreen ? 'block' : 'none',
      marginRight: `${baseUnit * 0.25}px`
    }
  }
}).style;

const createTextWrapperStyle = (horizontalGapLarge, horizontalGapMedium, horizontalGapSmall, verticalGapLarge, verticalGapMedium, verticalGapSmall) => StyleSheet.create({
  style: {
    overflow: 'hidden',
    width: '-webkit-fill-available',
    display: 'grid',
    background: 'transparent',
    position: 'absolute',
    bottom: 0,
    [MEDIA_BREAKPOINTS.large]: {
      margin: `${baseUnit * horizontalGapLarge}px ${baseUnit * verticalGapLarge}px`
    },
    [MEDIA_BREAKPOINTS.medium]: {
      margin: `${baseUnit * horizontalGapMedium}px ${baseUnit * verticalGapMedium}px`
    },
    [MEDIA_BREAKPOINTS.small]: {
      margin: `${baseUnit * horizontalGapSmall}px ${baseUnit * verticalGapSmall}px`
    }
  }
}).style;

const createMainCategoryStyle = (showMainCategoryInLargeScreen, showMainCategoryInMediumScreen, showMainCategoryInSmallScreen, mainCategoryHorizontalGapLarge, mainCategoryHorizontalGapMedium, mainCategoryHorizontalGapSmall, mainCategoryVerticalGapLarge, mainCategoryVerticalGapMedium, mainCategoryVerticalGapSmall, mainCategoryBackgroundColor, mainCategoryTextColor, mainCategoryAlignment, horizontalGapLarge, horizontalGapMedium, horizontalGapSmall) => StyleSheet.create({
  style: {
    backgroundColor: mainCategoryBackgroundColor,
    color: mainCategoryTextColor,
    width: 'fit-content',
    [MEDIA_BREAKPOINTS.large]: {
      display: showMainCategoryInLargeScreen ? 'block' : 'none',
      padding: `${mainCategoryHorizontalGapLarge * baseUnit}px ${mainCategoryVerticalGapLarge * baseUnit}px`,
      margin: mainCategoryAlignment === ALIGNMENT_OPTIONS.CENTER ? `${horizontalGapLarge * baseUnit}px auto` : `${horizontalGapLarge * baseUnit}px 0`
    },
    [MEDIA_BREAKPOINTS.medium]: {
      display: showMainCategoryInMediumScreen ? 'block' : 'none',
      padding: `${mainCategoryHorizontalGapMedium * baseUnit}px ${mainCategoryVerticalGapMedium * baseUnit}px`,
      margin: mainCategoryAlignment === ALIGNMENT_OPTIONS.CENTER ? `${horizontalGapMedium * baseUnit}px auto` : `${horizontalGapMedium * baseUnit}px 0`
    },
    [MEDIA_BREAKPOINTS.small]: {
      display: showMainCategoryInSmallScreen ? 'block' : 'none',
      padding: `${mainCategoryHorizontalGapSmall * baseUnit}px ${mainCategoryVerticalGapSmall * baseUnit}px`,
      margin: mainCategoryAlignment === ALIGNMENT_OPTIONS.CENTER ? `${horizontalGapSmall * baseUnit}px auto` : `${horizontalGapSmall * baseUnit}px 0`
    }
  }
}).style;

const OnTopCard = props => {
  const {
    title,
    image,
    articleUrl,
    coverType,
    imageHeightCalculationMethods,
    imageWidths,
    lazyLoad = false,
    authors,
    mainCategory,
    logo,
    sponsoredText,
    brandName,
    updatedAt
  } = props;
  const {
    cardTitleColorTypeNormal,
    cardTitleColorTypeHover,
    cardTitleColorTypeActive,
    boxShadowTypeNormal,
    boxShadowTypeActive,
    boxShadowTypeHover,
    borderRadius,
    borderColorTypeActive,
    borderColorTypeHover,
    borderColorTypeNormal,
    borderWidthTypeActive,
    borderWidthTypeHover,
    borderWidthTypeNormal,
    titleFontSizeLarge,
    titleFontSizeSmall,
    titleFontSizeMedium,
    showAuthorInSmallScreen,
    showAuthorInMediumScreen,
    showAuthorInLargeScreen,
    aspectRatioLarge,
    aspectRatioMedium,
    aspectRatioSmall,
    authorColor,
    metadataFontSizeLarge,
    metadataFontSizeMedium,
    metadataFontSizeSmall,
    metadataAlignment,
    titleAlignment,
    horizontalGapLarge,
    horizontalGapMedium,
    horizontalGapSmall,
    overlayColor,
    verticalGapLarge,
    verticalGapMedium,
    verticalGapSmall,
    wrapperGapLarge,
    wrapperGapMedium,
    wrapperGapSmall,
    titleSemanticTag,
    metadataSemanticTag,
    showDateInSmallScreen,
    showDateInMediumScreen,
    showDateInLargeScreen,
    dateColor,
    pipeColor,
    showMainCategoryInLargeScreen,
    showMainCategoryInMediumScreen,
    showMainCategoryInSmallScreen,
    mainCategoryHorizontalGapLarge,
    mainCategoryHorizontalGapMedium,
    mainCategoryHorizontalGapSmall,
    mainCategoryVerticalGapLarge,
    mainCategoryVerticalGapMedium,
    mainCategoryVerticalGapSmall,
    mainCategoryBackgroundColor,
    mainCategoryFontSizeLarge,
    mainCategoryFontSizeMedium,
    mainCategoryFontSizeSmall,
    mainCategorySemanticTag,
    mainCategoryTextColor,
    mainCategoryAlignment,
    numberOfLinesForTitle,
    numberOfLinesForAuthors
  } = useTheme(componentConfigThemeKey$3);
  const cardStyles = createCardStyles(cardTitleColorTypeNormal, cardTitleColorTypeHover, cardTitleColorTypeActive, borderRadius);
  const borderBoxShadow = {
    borderColorTypeActive,
    borderColorTypeHover,
    borderColorTypeNormal,
    borderWidthTypeActive,
    borderWidthTypeHover,
    borderWidthTypeNormal
  };
  const showSponsored = shouldShowSponsored(sponsoredText, brandName);
  const boxShadowStyle = createStyleForBoxShadow({
    boxShadowTypeNormal,
    boxShadowTypeHover,
    boxShadowTypeActive,
    borderBoxShadow
  });
  const titleAlignmentStyle = createTitleAlignmentStyle(titleAlignment);
  const showMetadataInLargeScreen = showMetadataInLargeScreenSize(showAuthorInLargeScreen, showDateInLargeScreen);
  const showMetadataInMediumScreen = showMetadataInMediumScreenSize(showAuthorInMediumScreen, showDateInMediumScreen);
  const showMetadataInSmallScreen = showMetadataInSmallScreenSize(showAuthorInSmallScreen, showDateInSmallScreen);
  const footerStyle = createFooterStyle(metadataAlignment);
  const metadataStyle = createMetadataStyle(horizontalGapLarge, horizontalGapMedium, horizontalGapSmall, showMetadataInLargeScreen, showMetadataInMediumScreen, showMetadataInSmallScreen);
  const authorsStyle = createAuthorsStyle(showAuthorInSmallScreen, showAuthorInMediumScreen, showAuthorInLargeScreen, authorColor, numberOfLinesForAuthors);
  const dateStyle = createDateStyle(showDateInSmallScreen, showDateInMediumScreen, showDateInLargeScreen, dateColor);
  const pipeStyle = createPipeStyle(showDateInSmallScreen, showDateInMediumScreen, showDateInLargeScreen, showAuthorInSmallScreen, showAuthorInMediumScreen, showAuthorInLargeScreen, pipeColor);
  const showAuthor = showAuthorInAnyScreenSize(authors, showAuthorInLargeScreen, showAuthorInMediumScreen, showAuthorInSmallScreen);
  const showDate = showDateInAnyScreenSize(showDateInLargeScreen, showDateInMediumScreen, showDateInSmallScreen);
  const showMainCategory = showMainCategoryInSmallScreen || showMainCategoryInMediumScreen || showMainCategoryInLargeScreen; // @ts-ignore TS2345

  const {
    style
  } = StyleSheet.create(ellipsify(numberOfLinesForTitle));
  const textWrapperStyle = createTextWrapperStyle(horizontalGapLarge, horizontalGapMedium, horizontalGapSmall, verticalGapLarge, verticalGapMedium, verticalGapSmall);

  const MainCategory = () => {
    const mainCategoryStyle = createMainCategoryStyle(showMainCategoryInLargeScreen, showMainCategoryInMediumScreen, showMainCategoryInSmallScreen, mainCategoryHorizontalGapLarge, mainCategoryHorizontalGapMedium, mainCategoryHorizontalGapSmall, mainCategoryVerticalGapLarge, mainCategoryVerticalGapMedium, mainCategoryVerticalGapSmall, mainCategoryBackgroundColor, mainCategoryTextColor, mainCategoryAlignment, horizontalGapLarge, horizontalGapMedium, horizontalGapSmall);
    return /*#__PURE__*/createElement(DynamicSemanticTag, {
      semanticTag: mainCategorySemanticTag,
      fontSizeLarge: mainCategoryFontSizeLarge,
      fontSizeMedium: mainCategoryFontSizeMedium,
      fontSizeSmall: mainCategoryFontSizeSmall,
      styles: semanticCSS(mainCategoryStyle)
    }, mainCategory);
  };

  return /*#__PURE__*/createElement(WrapperForCard, {
    boxShadowStyle: boxShadowStyle,
    borderRadius: borderRadius,
    paddingLarge: wrapperGapLarge,
    paddingMedium: wrapperGapMedium,
    paddingSmall: wrapperGapSmall
  }, /*#__PURE__*/createElement(Link, {
    className: css(cardStyles.card, boxShadowStyle.style),
    href: articleUrl
  }, /*#__PURE__*/createElement(Image, _extends({}, image, {
    lazyLoad: lazyLoad,
    imageAspectRatios: createImageAspectRatios(aspectRatioSmall, aspectRatioMedium, aspectRatioLarge),
    imageHeightCalculationMethods: imageHeightCalculationMethods,
    imageWidths: imageWidths
  })), /*#__PURE__*/createElement(CardOverlay, {
    overlayColor: overlayColor
  }), /*#__PURE__*/createElement("div", {
    className: css(textWrapperStyle)
  }, /*#__PURE__*/createElement("header", null, showMainCategory ? /*#__PURE__*/createElement(MainCategory, null) : null, /*#__PURE__*/createElement(DynamicSemanticTag, {
    semanticTag: titleSemanticTag,
    fontSizeLarge: titleFontSizeLarge,
    fontSizeMedium: titleFontSizeMedium,
    fontSizeSmall: titleFontSizeSmall,
    styles: semanticCSS(titleAlignmentStyle, style)
  }, title)), showMetadataInAnyScreenSize(showAuthor, showDate) ? /*#__PURE__*/createElement("footer", {
    className: css(footerStyle)
  }, /*#__PURE__*/createElement(DynamicSemanticTag, {
    semanticTag: metadataSemanticTag,
    fontSizeLarge: metadataFontSizeLarge,
    fontSizeMedium: metadataFontSizeMedium,
    fontSizeSmall: metadataFontSizeSmall,
    styles: semanticCSS(metadataStyle)
  }, showAuthor ? /*#__PURE__*/createElement("div", {
    className: css(authorsStyle)
  }, listAuthors(authors)) : null, showPipeInAnyScreenSize(showAuthor, showDate) ? /*#__PURE__*/createElement("div", {
    className: css(pipeStyle)
  }, "|") : null, showDate ? /*#__PURE__*/createElement("div", {
    className: css(dateStyle)
  }, updatedAt) : null)) : null, showSponsored ? /*#__PURE__*/createElement(SponsoredTextVertical, {
    sponsoredText: sponsoredText,
    brandName: brandName,
    logo: logo
  }) : null), coverType === 'video' ? /*#__PURE__*/createElement(PlayIcon, null) : null));
};

const createStyles$3 = baseUnit => StyleSheet.create({
  container: {
    display: 'flex',
    justifyContent: 'flex-start',
    flexWrap: 'wrap'
  },
  cardWrapper: {
    boxSizing: 'border-box',
    [MEDIA_BREAKPOINTS.large]: {
      paddingRight: `${baseUnit / 4}px`,
      flex: '0 0 66.67%'
    },
    [MEDIA_BREAKPOINTS.medium]: {
      paddingRight: `${baseUnit / 4}px`,
      flex: '0 0 66.67%'
    },
    [MEDIA_BREAKPOINTS.small]: {
      paddingRight: '0',
      flex: '0 0 100%',
      paddingBottom: `${baseUnit / 4}px`
    }
  },
  listWrapper: {
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    [MEDIA_BREAKPOINTS.large]: {
      paddingLeft: `${baseUnit / 4}px`,
      flex: '0 1 33.33%'
    },
    [MEDIA_BREAKPOINTS.medium]: {
      paddingLeft: `${baseUnit / 4}px`,
      flex: '0 1 33.33%'
    },
    [MEDIA_BREAKPOINTS.small]: {
      paddingLeft: '0',
      flex: '0 1 100%',
      paddingTop: `${baseUnit / 4}px`
    }
  }
});

const imageWidths = createImageWidths(360, 720, 720);
const HeadlinesArticleList = ({
  cardsProps,
  displayName,
  paddingFactors
}) => {
  const {
    baseUnit
  } = useTheme();
  const styles = createStyles$3(baseUnit);
  const paddingStyle = getLayoutComponentPadding(paddingFactors);
  const [onTopCardProps, ...listProps] = cardsProps;
  return /*#__PURE__*/createElement(ErrorBoundary, null, cardsProps.length > 0 && /*#__PURE__*/createElement("div", {
    className: css(paddingStyle, styles.container)
  }, /*#__PURE__*/createElement("div", {
    className: css(styles.cardWrapper)
  }, /*#__PURE__*/createElement(OnTopCard, _extends({}, onTopCardProps, {
    imageWidths: imageWidths
  }))), /*#__PURE__*/createElement("div", {
    className: css(styles.listWrapper)
  }, /*#__PURE__*/createElement(ListWithHeading, {
    listProps: listProps,
    displayName: displayName,
    itemNumLines: 2
  }))));
};

function useThemeOverride(override, key) {
  const themeContext = React__default.useContext(ThemeContext);
  return { ...themeContext,
    [key]: { ...themeContext[key],
      ...override
    }
  };
}

const style = StyleSheet.create({
  wrap: {
    position: 'relative',
    display: 'flex'
  }
});
const RelativeWrapper = ({
  children
}) => /*#__PURE__*/createElement("div", {
  className: css(style.wrap)
}, children);
const ConditionalRelativeWrap = ({
  shouldWarp,
  children
}) => shouldWarp ? /*#__PURE__*/createElement(RelativeWrapper, null, children) : /*#__PURE__*/createElement(Fragment, null, children);

const componentConfigThemeKey$4 = 'sponsoredTextHorizontal';

const getStyles$1 = ({
  baseUnit,
  sponsoredTextColor,
  brandNameColor,
  alignment
}) => StyleSheet.create({
  wrapper: {
    textAlign: alignment === ALIGNMENT_OPTIONS.CENTER ? 'center' : 'left',
    marginTop: `${baseUnit / 2}px`,
    height: 'fit-content'
  },
  sponsored: {
    color: sponsoredTextColor
  },
  brandName: {
    color: brandNameColor
  }
});

const SponsoredTextHorizontal = props => {
  const {
    sponsoredText,
    brandName
  } = props;
  const themeProps = useTheme(componentConfigThemeKey$4);
  const styles = getStyles$1(themeProps);
  const {
    sponsorFontSizeLarge,
    sponsorFontSizeMedium,
    sponsorFontSizeSmall
  } = themeProps;
  return /*#__PURE__*/createElement("div", {
    className: css(styles.wrapper)
  }, /*#__PURE__*/createElement(Span, {
    fontSizeLarge: sponsorFontSizeLarge,
    fontSizeMedium: sponsorFontSizeMedium,
    fontSizeSmall: sponsorFontSizeSmall,
    styles: semanticCSS(styles.sponsored)
  }, `${sponsoredText} `), /*#__PURE__*/createElement(Span, {
    fontSizeLarge: sponsorFontSizeLarge,
    fontSizeMedium: sponsorFontSizeMedium,
    fontSizeSmall: sponsorFontSizeSmall,
    styles: semanticCSS(styles.brandName)
  }, brandName));
};

const createTextWrapperStyle$1 = (horizontalGapLarge, horizontalGapMedium, horizontalGapSmall, verticalGapLarge, verticalGapMedium, verticalGapSmall) => StyleSheet.create({
  style: {
    overflow: 'hidden',
    width: '-webkit-fill-available',
    display: 'grid',
    gridTemplateRows: 'auto 1fr',
    [MEDIA_BREAKPOINTS.large]: {
      margin: `${baseUnit * horizontalGapLarge}px ${baseUnit * verticalGapLarge}px`
    },
    [MEDIA_BREAKPOINTS.medium]: {
      margin: `${baseUnit * horizontalGapMedium}px ${baseUnit * verticalGapMedium}px`
    },
    [MEDIA_BREAKPOINTS.small]: {
      margin: `${baseUnit * horizontalGapSmall}px ${baseUnit * verticalGapSmall}px`
    }
  }
}).style;

const createTopTextWrapperStyle = () => StyleSheet.create({
  style: {
    height: 'fit-content'
  }
}).style;

const createTitleStyle = numberOfLinesForTitle => {
  return StyleSheet.create({
    text: {
      overflow: 'hidden',
      height: 'fit-content',
      ...ellipsify(numberOfLinesForTitle).style
    }
  }).text;
};

const createDescriptionStyle = (showDescriptionLargeScreen, showDescriptionMediumScreen, showDescriptionSmallScreen, descriptionColor, numberOfLinesForDescription) => StyleSheet.create({
  style: {
    color: descriptionColor,
    height: 'fit-content',
    [MEDIA_BREAKPOINTS.large]: { ...(showDescriptionLargeScreen ? ellipsify(numberOfLinesForDescription).style : {
        display: 'none'
      })
    },
    [MEDIA_BREAKPOINTS.medium]: { ...(showDescriptionMediumScreen ? ellipsify(numberOfLinesForDescription).style : {
        display: 'none'
      })
    },
    [MEDIA_BREAKPOINTS.small]: { ...(showDescriptionSmallScreen ? ellipsify(numberOfLinesForDescription).style : {
        display: 'none'
      })
    }
  }
}).style;

const createBottomTextWrapperStyle = () => StyleSheet.create({
  style: {
    height: '100%',
    display: 'grid',
    alignItems: 'flex-end',
    gridTemplateRows: '1fr auto'
  }
}).style;

const createMetadataStyle$1 = (horizontalGapLarge, horizontalGapMedium, horizontalGapSmall, showMetadataInLargeScreen, showMetadataInMediumScreen, showMetadataInSmallScreen) => StyleSheet.create({
  style: {
    gridTemplateColumns: 'auto auto max-content',
    width: 'fit-content',
    [MEDIA_BREAKPOINTS.large]: {
      display: showMetadataInLargeScreen ? 'inline-grid' : 'none',
      marginTop: `${baseUnit * horizontalGapLarge}px`
    },
    [MEDIA_BREAKPOINTS.medium]: {
      display: showMetadataInMediumScreen ? 'inline-grid' : 'none',
      marginTop: `${baseUnit * horizontalGapMedium}px`
    },
    [MEDIA_BREAKPOINTS.small]: {
      display: showMetadataInSmallScreen ? 'inline-grid' : 'none',
      marginTop: `${baseUnit * horizontalGapSmall}px`
    }
  }
}).style;

const createAuthorsStyle$1 = (showAuthorSmallScreen, showAuthorMediumScreen, showAuthorLargeScreen, authorColor, numberOfLinesForAuthors) => StyleSheet.create({
  style: {
    color: authorColor,
    alignSelf: 'flex-end',
    [MEDIA_BREAKPOINTS.large]: {
      marginRight: `${baseUnit * 0.25}px`,
      ...(showAuthorLargeScreen ? ellipsify(numberOfLinesForAuthors).style : {
        display: 'none'
      })
    },
    [MEDIA_BREAKPOINTS.medium]: {
      marginRight: `${baseUnit * 0.25}px`,
      ...(showAuthorMediumScreen ? ellipsify(numberOfLinesForAuthors).style : {
        display: 'none'
      })
    },
    [MEDIA_BREAKPOINTS.small]: {
      marginRight: `${baseUnit * 0.25}px`,
      ...(showAuthorSmallScreen ? ellipsify(numberOfLinesForAuthors).style : {
        display: 'none'
      })
    }
  }
}).style;

const createPipeStyle$1 = (showDateSmallScreen, showDateMediumScreen, showDateLargeScreen, showAuthorSmallScreen, showAuthorMediumScreen, showAuthorLargeScreen, pipeColor) => StyleSheet.create({
  style: {
    color: pipeColor,
    alignSelf: 'flex-end',
    [MEDIA_BREAKPOINTS.large]: {
      display: showDateLargeScreen && showAuthorLargeScreen ? 'block' : 'none',
      marginRight: `${baseUnit * 0.25}px`
    },
    [MEDIA_BREAKPOINTS.medium]: {
      display: showDateMediumScreen && showAuthorMediumScreen ? 'block' : 'none',
      marginRight: `${baseUnit * 0.25}px`
    },
    [MEDIA_BREAKPOINTS.small]: {
      display: showDateSmallScreen && showAuthorSmallScreen ? 'block' : 'none',
      marginRight: `${baseUnit * 0.25}px`
    }
  }
}).style;

const createDateStyle$1 = (showDateSmallScreen, showDateMediumScreen, showDateLargeScreen, dateColor) => StyleSheet.create({
  style: {
    color: dateColor,
    alignSelf: 'flex-end',
    [MEDIA_BREAKPOINTS.large]: {
      display: showDateLargeScreen ? 'inline-block' : 'none'
    },
    [MEDIA_BREAKPOINTS.medium]: {
      display: showDateMediumScreen ? 'inline-block' : 'none'
    },
    [MEDIA_BREAKPOINTS.small]: {
      display: showDateSmallScreen ? 'inline-block' : 'none'
    }
  }
}).style;

const TextNextToImage = props => {
  const {
    children,
    title,
    brandName,
    authors,
    description,
    updatedAt,
    sponsoredText,
    horizontalGapLarge,
    horizontalGapMedium,
    horizontalGapSmall,
    verticalGapLarge,
    verticalGapMedium,
    verticalGapSmall,
    titleSemanticTag,
    titleFontSizeMedium,
    titleFontSizeSmall,
    titleFontSizeLarge,
    numberOfLinesForTitle,
    showDescriptionInLargeScreen,
    showDescriptionInMediumScreen,
    showDescriptionInSmallScreen,
    descriptionSemanticTag,
    descriptionFontSizeInSmallScreen,
    descriptionFontSizeInLargeScreen,
    descriptionFontSizeInMediumScreen,
    descriptionColor,
    numberOfLinesForDescription,
    showAuthorInLargeScreen,
    showAuthorInMediumScreen,
    showAuthorInSmallScreen,
    showDateInLargeScreen,
    showDateInMediumScreen,
    showDateInSmallScreen,
    metadataSemanticTag,
    metadataFontSizeLarge,
    metadataFontSizeMedium,
    metadataFontSizeSmall,
    showMetadataInLargeScreen,
    showMetadataInMediumScreen,
    showMetadataInSmallScreen,
    authorColor,
    numberOfLinesForAuthors,
    pipeColor,
    dateColor
  } = props;
  const textWrapperStyle = createTextWrapperStyle$1(horizontalGapLarge, horizontalGapMedium, horizontalGapSmall, verticalGapLarge, verticalGapMedium, verticalGapSmall);
  const topTextWrapperStyle = createTopTextWrapperStyle();
  const titleStyle = createTitleStyle(numberOfLinesForTitle);
  const descriptionStyle = createDescriptionStyle(showDescriptionInLargeScreen, showDescriptionInMediumScreen, showDescriptionInSmallScreen, descriptionColor, numberOfLinesForDescription);
  const bottomTextWrapperStyle = createBottomTextWrapperStyle();
  const metadataStyle = createMetadataStyle$1(horizontalGapLarge, horizontalGapMedium, horizontalGapSmall, showMetadataInLargeScreen, showMetadataInMediumScreen, showMetadataInSmallScreen);
  const authorsStyle = createAuthorsStyle$1(showAuthorInSmallScreen, showAuthorInMediumScreen, showAuthorInLargeScreen, authorColor, numberOfLinesForAuthors);
  const pipeStyle = createPipeStyle$1(showDateInSmallScreen, showDateInMediumScreen, showDateInLargeScreen, showAuthorInSmallScreen, showAuthorInMediumScreen, showAuthorInLargeScreen, pipeColor);
  const dateStyle = createDateStyle$1(showDateInSmallScreen, showDateInMediumScreen, showDateInLargeScreen, dateColor);
  const showAuthor = showAuthorInAnyScreenSize(authors, showAuthorInLargeScreen, showAuthorInMediumScreen, showAuthorInSmallScreen);
  const showDate = showDateInAnyScreenSize(showDateInLargeScreen, showDateInMediumScreen, showDateInSmallScreen);
  const showSponsored = shouldShowSponsored(sponsoredText, brandName);
  const showBottomText = showSponsored || showMetadataInAnyScreenSize(showAuthor, showDate);
  return /*#__PURE__*/createElement("div", {
    className: css(textWrapperStyle)
  }, /*#__PURE__*/createElement("div", {
    className: css(topTextWrapperStyle)
  }, /*#__PURE__*/createElement("header", null, children, /*#__PURE__*/createElement(DynamicSemanticTag, {
    semanticTag: titleSemanticTag,
    fontSizeLarge: titleFontSizeLarge,
    fontSizeMedium: titleFontSizeMedium,
    fontSizeSmall: titleFontSizeSmall,
    styles: semanticCSS(titleStyle)
  }, title)), showDescriptionInAnyScreenSize(showDescriptionInLargeScreen, showDescriptionInMediumScreen, showDescriptionInSmallScreen) ? /*#__PURE__*/createElement(DynamicSemanticTag, {
    semanticTag: descriptionSemanticTag,
    fontSizeLarge: descriptionFontSizeInLargeScreen,
    fontSizeMedium: descriptionFontSizeInMediumScreen,
    fontSizeSmall: descriptionFontSizeInSmallScreen,
    styles: semanticCSS(descriptionStyle)
  }, description) : null), showBottomText ? /*#__PURE__*/createElement("div", {
    className: css(bottomTextWrapperStyle)
  }, showMetadataInAnyScreenSize(showAuthor, showDate) ? /*#__PURE__*/createElement("footer", null, /*#__PURE__*/createElement(DynamicSemanticTag, {
    semanticTag: metadataSemanticTag,
    fontSizeLarge: metadataFontSizeLarge,
    fontSizeMedium: metadataFontSizeMedium,
    fontSizeSmall: metadataFontSizeSmall,
    styles: semanticCSS(metadataStyle)
  }, showAuthor ? /*#__PURE__*/createElement("div", {
    className: css(authorsStyle)
  }, listAuthors(authors)) : null, showPipeInAnyScreenSize(showAuthor, showDate) ? /*#__PURE__*/createElement("div", {
    className: css(pipeStyle)
  }, "|") : null, showDate ? /*#__PURE__*/createElement("div", {
    className: css(dateStyle)
  }, updatedAt) : null)) : null, showSponsored ? /*#__PURE__*/createElement(SponsoredTextHorizontal, {
    sponsoredText: sponsoredText,
    brandName: brandName
  }) : null) : null);
};

const componentConfigThemeKey$5 = 'verticalCard';

const createLinkStyle = (cardBackgroundColor, cardTextColorTypeNormal, cardTextColorTypeHover, cardTextColorTypeActive, borderRadius, showDecorationLine) => {
  return StyleSheet.create({
    wrapper: {
      width: '100%',
      height: '100%',
      textDecoration: 'none',
      color: cardTextColorTypeNormal,
      backgroundColor: cardBackgroundColor,
      '-webkit-mask-image': '-webkit-radial-gradient(white, black)',
      borderRadius: `${borderRadius}px`,
      position: 'relative',
      ':hover': {
        color: cardTextColorTypeHover
      },
      ':active': {
        color: cardTextColorTypeActive
      },
      display: 'grid',
      gridTemplateRows: showDecorationLine ? `min-content ${decorationLineHeight}px auto` : 'min-content auto'
    }
  }).wrapper;
};

const createMainCategoryStyle$1 = (showMainCategoryInLargeScreen, showMainCategoryInMediumScreen, showMainCategoryInSmallScreen, mainCategoryHorizontalGapLarge, mainCategoryHorizontalGapMedium, mainCategoryHorizontalGapSmall, mainCategoryVerticalGapLarge, mainCategoryVerticalGapMedium, mainCategoryVerticalGapSmall, mainCategoryBackgroundColor, mainCategoryTextColor) => StyleSheet.create({
  style: {
    backgroundColor: mainCategoryBackgroundColor,
    color: mainCategoryTextColor,
    position: 'absolute',
    bottom: 0,
    left: 0,
    [MEDIA_BREAKPOINTS.large]: {
      display: showMainCategoryInLargeScreen ? 'block' : 'none',
      padding: `${mainCategoryHorizontalGapLarge * baseUnit}px ${mainCategoryVerticalGapLarge * baseUnit}px`
    },
    [MEDIA_BREAKPOINTS.medium]: {
      display: showMainCategoryInMediumScreen ? 'block' : 'none',
      padding: `${mainCategoryHorizontalGapMedium * baseUnit}px ${mainCategoryVerticalGapMedium * baseUnit}px`
    },
    [MEDIA_BREAKPOINTS.small]: {
      display: showMainCategoryInSmallScreen ? 'block' : 'none',
      padding: `${mainCategoryHorizontalGapSmall * baseUnit}px ${mainCategoryVerticalGapSmall * baseUnit}px`
    }
  }
}).style;

const VerticalCard = props => {
  const {
    coverType,
    lazyLoad = true,
    showDecorationLine = true,
    title,
    articleUrl,
    image,
    imageWidths,
    authors,
    mainCategory,
    sponsoredText,
    brandName,
    description,
    updatedAt
  } = props;
  const {
    borderRadius,
    borderColorTypeActive,
    borderColorTypeHover,
    borderColorTypeNormal,
    borderWidthTypeActive,
    borderWidthTypeHover,
    borderWidthTypeNormal,
    boxShadowTypeNormal,
    boxShadowTypeHover,
    boxShadowTypeActive,
    cardBackgroundColor,
    cardTitleColorTypeNormal,
    cardTitleColorTypeHover,
    cardTitleColorTypeActive,
    titleFontSizeLarge,
    titleFontSizeSmall,
    titleFontSizeMedium,
    showAuthorInLargeScreen,
    showAuthorInMediumScreen,
    showAuthorInSmallScreen,
    aspectRatioLarge,
    aspectRatioMedium,
    aspectRatioSmall,
    authorColor,
    metadataFontSizeLarge,
    metadataFontSizeMedium,
    metadataFontSizeSmall,
    horizontalGapLarge,
    horizontalGapMedium,
    horizontalGapSmall,
    verticalGapLarge,
    verticalGapMedium,
    verticalGapSmall,
    wrapperGapLarge,
    wrapperGapMedium,
    wrapperGapSmall,
    titleSemanticTag,
    metadataSemanticTag,
    showDateInSmallScreen,
    showDateInMediumScreen,
    showDateInLargeScreen,
    dateColor,
    pipeColor,
    showMainCategoryInLargeScreen,
    showMainCategoryInMediumScreen,
    showMainCategoryInSmallScreen,
    mainCategoryHorizontalGapLarge,
    mainCategoryHorizontalGapMedium,
    mainCategoryHorizontalGapSmall,
    mainCategoryVerticalGapLarge,
    mainCategoryVerticalGapMedium,
    mainCategoryVerticalGapSmall,
    mainCategoryBackgroundColor,
    mainCategoryFontSizeLarge,
    mainCategoryFontSizeMedium,
    mainCategoryFontSizeSmall,
    mainCategorySemanticTag,
    mainCategoryTextColor,
    numberOfLinesForTitle,
    numberOfLinesForAuthors,
    showDescriptionInLargeScreen,
    showDescriptionInMediumScreen,
    showDescriptionInSmallScreen,
    numberOfLinesForDescription,
    descriptionColor,
    descriptionFontSizeInLargeScreen,
    descriptionFontSizeInMediumScreen,
    descriptionFontSizeInSmallScreen,
    descriptionSemanticTag
  } = useTheme(componentConfigThemeKey$5);
  const borderBoxShadow = {
    borderColorTypeActive,
    borderColorTypeHover,
    borderColorTypeNormal,
    borderWidthTypeActive,
    borderWidthTypeHover,
    borderWidthTypeNormal
  };
  const linkStyle = createLinkStyle(cardBackgroundColor, cardTitleColorTypeNormal, cardTitleColorTypeHover, cardTitleColorTypeActive, borderRadius, showDecorationLine);
  const boxShadowStyle = createStyleForBoxShadow({
    boxShadowTypeNormal,
    boxShadowTypeHover,
    boxShadowTypeActive,
    borderBoxShadow
  });
  const showMetadataInLargeScreen = showMetadataInLargeScreenSize(showAuthorInLargeScreen, showDateInLargeScreen);
  const showMetadataInMediumScreen = showMetadataInMediumScreenSize(showAuthorInMediumScreen, showDateInMediumScreen);
  const showMetadataInSmallScreen = showMetadataInSmallScreenSize(showAuthorInSmallScreen, showDateInSmallScreen);
  const showMainCategory = showMainCategoryInSmallScreen || showMainCategoryInMediumScreen || showMainCategoryInLargeScreen;

  const MainCategory = () => {
    const mainCategoryStyle = createMainCategoryStyle$1(showMainCategoryInLargeScreen, showMainCategoryInMediumScreen, showMainCategoryInSmallScreen, mainCategoryHorizontalGapLarge, mainCategoryHorizontalGapMedium, mainCategoryHorizontalGapSmall, mainCategoryVerticalGapLarge, mainCategoryVerticalGapMedium, mainCategoryVerticalGapSmall, mainCategoryBackgroundColor, mainCategoryTextColor);
    return /*#__PURE__*/createElement(DynamicSemanticTag, {
      semanticTag: mainCategorySemanticTag,
      fontSizeLarge: mainCategoryFontSizeLarge,
      fontSizeMedium: mainCategoryFontSizeMedium,
      fontSizeSmall: mainCategoryFontSizeSmall,
      styles: semanticCSS(mainCategoryStyle)
    }, mainCategory);
  };

  return /*#__PURE__*/createElement(WrapperForCard, {
    boxShadowStyle: boxShadowStyle,
    borderRadius: borderRadius,
    paddingLarge: wrapperGapLarge,
    paddingMedium: wrapperGapMedium,
    paddingSmall: wrapperGapSmall
  }, /*#__PURE__*/createElement(Link, {
    className: css(linkStyle, boxShadowStyle.style),
    href: articleUrl
  }, /*#__PURE__*/createElement(ConditionalRelativeWrap, {
    shouldWarp: coverType === 'video' || showMainCategory
  }, /*#__PURE__*/createElement(Image, _extends({}, image, {
    lazyLoad: lazyLoad,
    imageWidths: imageWidths,
    imageAspectRatios: createImageAspectRatios(aspectRatioSmall, aspectRatioMedium, aspectRatioLarge)
  })), coverType === 'video' ? /*#__PURE__*/createElement(PlayIcon, null) : null, showMainCategory ? /*#__PURE__*/createElement(MainCategory, null) : null), showDecorationLine ? /*#__PURE__*/createElement(DecorationLine, null) : null, /*#__PURE__*/createElement(TextNextToImage, {
    title,
    brandName,
    authors,
    description,
    updatedAt,
    sponsoredText,
    horizontalGapLarge,
    horizontalGapMedium,
    horizontalGapSmall,
    verticalGapLarge,
    verticalGapMedium,
    verticalGapSmall,
    titleSemanticTag,
    titleFontSizeMedium,
    titleFontSizeSmall,
    titleFontSizeLarge,
    numberOfLinesForTitle,
    showDescriptionInLargeScreen,
    showDescriptionInMediumScreen,
    showDescriptionInSmallScreen,
    descriptionSemanticTag,
    descriptionFontSizeInSmallScreen,
    descriptionFontSizeInLargeScreen,
    descriptionFontSizeInMediumScreen,
    descriptionColor,
    numberOfLinesForDescription,
    showAuthorInLargeScreen,
    showAuthorInMediumScreen,
    showAuthorInSmallScreen,
    showDateInLargeScreen,
    showDateInMediumScreen,
    showDateInSmallScreen,
    metadataSemanticTag,
    metadataFontSizeLarge,
    metadataFontSizeMedium,
    metadataFontSizeSmall,
    showMetadataInLargeScreen,
    showMetadataInMediumScreen,
    showMetadataInSmallScreen,
    authorColor,
    numberOfLinesForAuthors,
    pipeColor,
    dateColor
  })));
};

const componentConfigThemeKey$6 = 'threeThreeVC';
const maximumNumberOfCards = 6;

const getStyle = themeProps => {
  const {
    largeHorizontalGap,
    largeVerticalGap,
    mediumHorizontalGap,
    mediumVerticalGap,
    smallHorizontalGap
  } = themeProps;
  return StyleSheet.create({
    wrapper: {
      display: 'grid',
      marginBottom: `${baseUnit}px`,
      [MEDIA_BREAKPOINTS.large]: {
        gridTemplateColumns: '1fr 1fr 1fr',
        gridColumnGap: `${baseUnit * largeVerticalGap}px`,
        gridRowGap: `${baseUnit * largeHorizontalGap}px`
      },
      [MEDIA_BREAKPOINTS.medium]: {
        gridColumnGap: `${baseUnit * mediumVerticalGap}px`,
        gridTemplateColumns: '1fr 1fr',
        gridRowGap: `${baseUnit * mediumHorizontalGap}px`
      },
      [MEDIA_BREAKPOINTS.small]: {
        gridTemplateColumns: '1fr',
        gridRowGap: `${baseUnit * smallHorizontalGap}px`
      }
    }
  });
};

const imageWidths$1 = createImageWidths(360, 360, 360);
const ThreeThreeVC = props => {
  const {
    cards,
    paddingFactors
  } = props;
  const themeProps = useTheme(componentConfigThemeKey$6);
  const style = getStyle(themeProps);
  const paddingStyle = getLayoutComponentPadding(paddingFactors);
  const {
    titleFontSizeLarge,
    titleFontSizeMedium,
    titleFontSizeSmall,
    metadataFontSizeLarge,
    metadataFontSizeMedium,
    metadataFontSizeSmall
  } = themeProps;
  const themeContextVerticalCard = useThemeOverride({
    titleFontSizeLarge,
    titleFontSizeMedium,
    titleFontSizeSmall,
    metadataFontSizeLarge,
    metadataFontSizeMedium,
    metadataFontSizeSmall
  }, componentConfigThemeKey$5);
  return /*#__PURE__*/createElement(ErrorBoundary, null, cards && cards.length > 0 && /*#__PURE__*/createElement("div", {
    className: css(style.wrapper, paddingStyle)
  }, /*#__PURE__*/createElement(ThemeContext.Provider, {
    value: themeContextVerticalCard
  }, cards.map(card => /*#__PURE__*/createElement(VerticalCard, _extends({
    key: card.id
  }, card, {
    imageWidths: imageWidths$1
  }))).slice(0, maximumNumberOfCards))));
};

const componentConfigThemeKey$7 = 'horizontalCard';

const createStyle$1 = (cardBackgroundColor, cardTextColorTypeNormal, cardTextColorTypeHover, cardTextColorTypeActive, borderRadius, shouldReverseImageAlignment, cardProportionsLarge, cardProportionsMedium, cardProportionsSmall, lineHeightSmallScreen) => {
  return StyleSheet.create({
    text: {
      overflow: 'hidden',
      [MEDIA_BREAKPOINTS.large]: {
        height: 'fit-content'
      },
      [MEDIA_BREAKPOINTS.medium]: {
        height: 'fit-content'
      },
      [MEDIA_BREAKPOINTS.small]: {
        height: `${lineHeightSmallScreen * 3}px`
      }
    },
    wrapper: {
      width: '100%',
      height: '100%',
      textDecoration: 'none',
      color: cardTextColorTypeNormal,
      backgroundColor: cardBackgroundColor,
      '-webkit-mask-image': '-webkit-radial-gradient(white, black)',
      borderRadius: `${borderRadius}px`,
      position: 'relative',
      ':hover': {
        color: cardTextColorTypeHover
      },
      ':active': {
        color: cardTextColorTypeActive
      },
      display: 'grid',
      [MEDIA_BREAKPOINTS.large]: {
        gridTemplateColumns: shouldReverseImageAlignment ? `${cardProportionsLarge.y}fr ${cardProportionsLarge.x}fr` : `${cardProportionsLarge.x}fr ${cardProportionsLarge.y}fr`
      },
      [MEDIA_BREAKPOINTS.medium]: {
        gridTemplateColumns: shouldReverseImageAlignment ? `${cardProportionsMedium.y}fr ${cardProportionsMedium.x}fr` : `${cardProportionsMedium.x}fr ${cardProportionsMedium.y}fr`
      },
      [MEDIA_BREAKPOINTS.small]: {
        gridTemplateColumns: shouldReverseImageAlignment ? `${cardProportionsSmall.y}fr ${cardProportionsSmall.x}fr` : `${cardProportionsSmall.x}fr ${cardProportionsSmall.y}fr`
      }
    }
  });
};

const createMainCategoryStyle$2 = (showMainCategoryInLargeScreen, showMainCategoryInMediumScreen, showMainCategoryInSmallScreen, mainCategoryHorizontalGapLarge, mainCategoryHorizontalGapMedium, mainCategoryHorizontalGapSmall, mainCategoryVerticalGapLarge, mainCategoryVerticalGapMedium, mainCategoryVerticalGapSmall, mainCategoryBackgroundColor, mainCategoryTextColor, horizontalGapLarge, horizontalGapMedium, horizontalGapSmall) => StyleSheet.create({
  style: {
    backgroundColor: mainCategoryBackgroundColor,
    color: mainCategoryTextColor,
    width: 'fit-content',
    [MEDIA_BREAKPOINTS.large]: {
      display: showMainCategoryInLargeScreen ? 'block' : 'none',
      padding: `${mainCategoryHorizontalGapLarge * baseUnit}px ${mainCategoryVerticalGapLarge * baseUnit}px`,
      marginBottom: `${horizontalGapLarge * baseUnit}px`
    },
    [MEDIA_BREAKPOINTS.medium]: {
      display: showMainCategoryInMediumScreen ? 'block' : 'none',
      padding: `${mainCategoryHorizontalGapMedium * baseUnit}px ${mainCategoryVerticalGapMedium * baseUnit}px`,
      marginBottom: `${horizontalGapMedium * baseUnit}px`
    },
    [MEDIA_BREAKPOINTS.small]: {
      display: showMainCategoryInSmallScreen ? 'block' : 'none',
      padding: `${mainCategoryHorizontalGapSmall * baseUnit}px ${mainCategoryVerticalGapSmall * baseUnit}px`,
      marginBottom: `${horizontalGapSmall * baseUnit}px`
    }
  }
}).style;

const HorizontalCard = props => {
  const {
    title,
    image,
    articleUrl,
    coverType,
    lazyLoad = true,
    imageWidths,
    imageHeightCalculationMethods,
    authors,
    description,
    mainCategory,
    sponsoredText,
    brandName,
    updatedAt
  } = props;
  const {
    cardBackgroundColor,
    cardTitleColorTypeNormal,
    cardTitleColorTypeHover,
    cardTitleColorTypeActive,
    boxShadowTypeNormal,
    boxShadowTypeActive,
    boxShadowTypeHover,
    borderRadius,
    borderColorTypeActive,
    borderColorTypeHover,
    borderColorTypeNormal,
    borderWidthTypeActive,
    borderWidthTypeHover,
    borderWidthTypeNormal,
    titleFontSizeLarge,
    titleFontSizeSmall,
    titleFontSizeMedium,
    showAuthorInLargeScreen,
    showAuthorInMediumScreen,
    showAuthorInSmallScreen,
    showDateInLargeScreen,
    showDateInMediumScreen,
    showDateInSmallScreen,
    showDescriptionInLargeScreen,
    showDescriptionInMediumScreen,
    showDescriptionInSmallScreen,
    aspectRatioLarge,
    aspectRatioMedium,
    aspectRatioSmall,
    authorColor,
    dateColor,
    pipeColor,
    descriptionColor,
    metadataFontSizeLarge,
    metadataFontSizeMedium,
    metadataFontSizeSmall,
    descriptionFontSizeLarge,
    descriptionFontSizeMedium,
    descriptionFontSizeSmall,
    shouldReverseImageAlignment,
    horizontalGapLarge,
    horizontalGapMedium,
    horizontalGapSmall,
    verticalGapLarge,
    verticalGapMedium,
    verticalGapSmall,
    wrapperGapLarge,
    wrapperGapMedium,
    wrapperGapSmall,
    titleSemanticTag,
    descriptionSemanticTag,
    metadataSemanticTag,
    cardProportionsLarge,
    cardProportionsMedium,
    cardProportionsSmall,
    showMainCategoryInLargeScreen,
    showMainCategoryInMediumScreen,
    showMainCategoryInSmallScreen,
    mainCategoryHorizontalGapLarge,
    mainCategoryHorizontalGapMedium,
    mainCategoryHorizontalGapSmall,
    mainCategoryVerticalGapLarge,
    mainCategoryVerticalGapMedium,
    mainCategoryVerticalGapSmall,
    mainCategoryBackgroundColor,
    mainCategoryFontSizeLarge,
    mainCategoryFontSizeMedium,
    mainCategoryFontSizeSmall,
    mainCategorySemanticTag,
    mainCategoryTextColor,
    fontSizesStyles,
    numberOfLinesForTitle,
    numberOfLinesForDescription,
    numberOfLinesForAuthors
  } = useTheme(componentConfigThemeKey$7);
  const borderBoxShadow = {
    borderColorTypeActive,
    borderColorTypeHover,
    borderColorTypeNormal,
    borderWidthTypeActive,
    borderWidthTypeHover,
    borderWidthTypeNormal
  };
  const titleLineHeight = getLineHeightBySize(fontSizesStyles, titleFontSizeSmall, 'small');
  const boxShadowStyle = createStyleForBoxShadow({
    boxShadowTypeNormal,
    boxShadowTypeHover,
    boxShadowTypeActive,
    borderBoxShadow
  });
  const titleStyle = createStyle$1(cardBackgroundColor, cardTitleColorTypeNormal, cardTitleColorTypeHover, cardTitleColorTypeActive, borderRadius, shouldReverseImageAlignment, cardProportionsLarge, cardProportionsMedium, cardProportionsSmall, titleLineHeight);
  const showMetadataInLargeScreen = showMetadataInLargeScreenSize(showAuthorInLargeScreen, showDateInLargeScreen);
  const showMetadataInMediumScreen = showMetadataInMediumScreenSize(showAuthorInMediumScreen, showDateInMediumScreen);
  const showMetadataInSmallScreen = showMetadataInSmallScreenSize(showAuthorInSmallScreen, showDateInSmallScreen);
  const showMainCategory = showMainCategoryInSmallScreen || showMainCategoryInMediumScreen || showMainCategoryInLargeScreen;

  const MainCategory = () => {
    const mainCategoryStyle = createMainCategoryStyle$2(showMainCategoryInLargeScreen, showMainCategoryInMediumScreen, showMainCategoryInSmallScreen, mainCategoryHorizontalGapLarge, mainCategoryHorizontalGapMedium, mainCategoryHorizontalGapSmall, mainCategoryVerticalGapLarge, mainCategoryVerticalGapMedium, mainCategoryVerticalGapSmall, mainCategoryBackgroundColor, mainCategoryTextColor, horizontalGapLarge, horizontalGapMedium, horizontalGapSmall);
    return /*#__PURE__*/createElement(DynamicSemanticTag, {
      semanticTag: mainCategorySemanticTag,
      fontSizeLarge: mainCategoryFontSizeLarge,
      fontSizeMedium: mainCategoryFontSizeMedium,
      fontSizeSmall: mainCategoryFontSizeSmall,
      styles: semanticCSS(mainCategoryStyle)
    }, mainCategory);
  };

  const ImageElement = () => /*#__PURE__*/createElement(ConditionalRelativeWrap, {
    shouldWarp: coverType === 'video'
  }, /*#__PURE__*/createElement(Image, _extends({}, image, {
    lazyLoad: lazyLoad,
    imageWidths: imageWidths,
    imageAspectRatios: createImageAspectRatios(aspectRatioSmall, aspectRatioMedium, aspectRatioLarge),
    imageHeightCalculationMethods: imageHeightCalculationMethods
  })), coverType === 'video' ? /*#__PURE__*/createElement(PlayIcon, null) : null);

  const TextElement = () => /*#__PURE__*/createElement(TextNextToImage, {
    title,
    brandName,
    authors,
    description,
    updatedAt,
    sponsoredText,
    horizontalGapLarge,
    horizontalGapMedium,
    horizontalGapSmall,
    verticalGapLarge,
    verticalGapMedium,
    verticalGapSmall,
    titleSemanticTag,
    titleFontSizeMedium,
    titleFontSizeSmall,
    titleFontSizeLarge,
    numberOfLinesForTitle,
    showDescriptionInLargeScreen,
    showDescriptionInMediumScreen,
    showDescriptionInSmallScreen,
    descriptionSemanticTag,
    descriptionFontSizeInSmallScreen: descriptionFontSizeSmall,
    descriptionFontSizeInLargeScreen: descriptionFontSizeMedium,
    descriptionFontSizeInMediumScreen: descriptionFontSizeLarge,
    descriptionColor,
    numberOfLinesForDescription,
    showAuthorInLargeScreen,
    showAuthorInMediumScreen,
    showAuthorInSmallScreen,
    showDateInLargeScreen,
    showDateInMediumScreen,
    showDateInSmallScreen,
    metadataSemanticTag,
    metadataFontSizeLarge,
    metadataFontSizeMedium,
    metadataFontSizeSmall,
    showMetadataInLargeScreen,
    showMetadataInMediumScreen,
    showMetadataInSmallScreen,
    authorColor,
    numberOfLinesForAuthors,
    pipeColor,
    dateColor
  }, showMainCategory ? /*#__PURE__*/createElement(MainCategory, null) : null);

  const elements = [/*#__PURE__*/createElement(ImageElement, {
    key: "image"
  }), /*#__PURE__*/createElement(TextElement, {
    key: "text"
  })];
  return /*#__PURE__*/createElement(WrapperForCard, {
    boxShadowStyle: boxShadowStyle,
    borderRadius: borderRadius,
    paddingLarge: wrapperGapLarge,
    paddingMedium: wrapperGapMedium,
    paddingSmall: wrapperGapSmall
  }, /*#__PURE__*/createElement(Link, {
    className: css(titleStyle.wrapper, boxShadowStyle.style),
    href: articleUrl
  }, shouldReverseImageAlignment ? elements.reverse() : elements));
};

const componentConfigThemeKey$8 = 'sixHC';
const maximumNumberOfCards$1 = 6;

const getStyle$1 = themeProps => {
  const {
    largeHorizontalGap,
    mediumHorizontalGap,
    smallHorizontalGap
  } = themeProps;
  return StyleSheet.create({
    wrapper: {
      display: 'grid',
      gridTemplateColumns: '1fr',
      gridTemplateRows: 'auto',
      height: 'fit-content',
      [MEDIA_BREAKPOINTS.large]: {
        gridRowGap: `${baseUnit * largeHorizontalGap}px`
      },
      [MEDIA_BREAKPOINTS.medium]: {
        gridRowGap: `${baseUnit * mediumHorizontalGap}px`
      },
      [MEDIA_BREAKPOINTS.small]: {
        gridRowGap: `${baseUnit * smallHorizontalGap}px`
      }
    }
  });
};

const defaultImageWidths = createImageWidths(180, 180, 360);
const SixHC = props => {
  const {
    cards,
    paddingFactors,
    imageWidths = defaultImageWidths,
    expandable = false
  } = props;
  const themeProps = useTheme(componentConfigThemeKey$8);
  const style = getStyle$1(themeProps);
  const paddingStyle = getLayoutComponentPadding(paddingFactors);
  return /*#__PURE__*/createElement(ErrorBoundary, null, cards && cards.length > 0 && /*#__PURE__*/createElement("div", {
    className: css(paddingStyle, style.wrapper)
  }, expandable ? cards.map((card, index) => /*#__PURE__*/createElement(HorizontalCard, _extends({
    key: index
  }, card, {
    imageWidths: imageWidths
  }))) : cards.map((card, index) => /*#__PURE__*/createElement(HorizontalCard, _extends({
    key: index
  }, card, {
    imageWidths: imageWidths
  }))).slice(0, maximumNumberOfCards$1)));
};

const componentConfigThemeKey$9 = 'sixSix';

const isNewChunk = (index, chunkSize) => index % chunkSize === 0;

const getArticlesInChunks = (articles, chunkSize) => {
  let currentChunk = [];
  return articles.reduce((chunks, item, index) => {
    if (isNewChunk(index, chunkSize)) {
      currentChunk = [item];
      chunks.push(currentChunk);
    } else {
      currentChunk.push(item);
    }

    return chunks;
  }, []);
};

const getSixSixArticleListStyle = baseUnit => {
  return StyleSheet.create({
    verticalCardsGrid: {
      marginBottom: `${baseUnit}px`
    },
    horizontalCardsGrid: {
      marginBottom: `${baseUnit / 2}px`,
      marginTop: `${baseUnit / 2}px`
    }
  });
};

const SixSixArticleList = ({
  cardsProps
}) => {
  const {
    baseUnit,
    showDescriptionInLargeScreen,
    showDescriptionInMediumScreen
  } = useTheme(componentConfigThemeKey$9);
  const SixSixArticleListStyle = getSixSixArticleListStyle(baseUnit);
  const firstSectionStyle = SixSixArticleListStyle.verticalCardsGrid;
  const secondSectionStyle = SixSixArticleListStyle.horizontalCardsGrid;
  const alteredThemeData = {
    showDescriptionInLargeScreen,
    showDescriptionInMediumScreen
  };
  const secondSectionTheme = useThemeOverride(alteredThemeData, componentConfigThemeKey$7);

  const createGrids = (cards, index) => {
    return index % 2 === 0 ? /*#__PURE__*/createElement("div", {
      className: css(firstSectionStyle),
      key: index
    }, /*#__PURE__*/createElement(ThreeThreeVC, {
      cards: cards
    })) : /*#__PURE__*/createElement("div", {
      className: css(secondSectionStyle),
      key: index
    }, /*#__PURE__*/createElement(ThemeContext.Provider, {
      value: secondSectionTheme
    }, /*#__PURE__*/createElement(SixHC, {
      cards: cards
    })));
  };

  return /*#__PURE__*/createElement(ErrorBoundary, null, cardsProps && cardsProps.length > 0 && getArticlesInChunks(cardsProps, 6).map(createGrids));
};

const style$1 = {
  '-webkit-font-smoothing': 'antialiased',
  margin: 0
};

const componentConfigThemeKey$a = 'button';

const getStyle$2 = ({
  backgroundColorNormal,
  backgroundColorHover,
  backgroundColorActive,
  textColorNormal,
  textColorHover,
  textColorActive,
  borderRadius,
  baseUnit
}) => {
  return StyleSheet.create({
    button: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      cursor: 'pointer',
      outline: 'none',
      border: 'none',
      padding: `0 ${baseUnit}px`,
      borderRadius: `${borderRadius}px`,
      backgroundColor: backgroundColorNormal,
      minWidth: '120px',
      height: '40px',
      boxSizing: 'border-box',
      color: textColorNormal,
      ':hover': {
        backgroundColor: backgroundColorHover,
        color: textColorHover
      },
      ':active': {
        backgroundColor: backgroundColorActive,
        color: textColorActive
      }
    }
  });
};

const Button = props => {
  const themeProps = useTheme(componentConfigThemeKey$a);
  const {
    label,
    onClick
  } = props;
  const {
    fontSizesStyles,
    boxShadowTypeNormal,
    boxShadowTypeHover,
    boxShadowTypeActive,
    borderColorNormal,
    borderColorActive,
    borderColorHover,
    borderWidthNormal,
    borderWidthActive,
    borderWidthHover,
    fontSizeLarge,
    fontSizeMedium,
    fontSizeSmall
  } = themeProps;
  const buttonStyle = getStyle$2(themeProps);
  const borderBoxShadow = {
    borderColorTypeActive: borderColorActive,
    borderColorTypeHover: borderColorHover,
    borderColorTypeNormal: borderColorNormal,
    borderWidthTypeActive: borderWidthActive,
    borderWidthTypeHover: borderWidthHover,
    borderWidthTypeNormal: borderWidthNormal
  };
  const boxShadowStyle = createStyleForBoxShadow({
    boxShadowTypeNormal,
    boxShadowTypeHover,
    boxShadowTypeActive,
    borderBoxShadow
  });
  const textStyle = getStyleBySize(fontSizesStyles, {
    fontSizeSmall,
    fontSizeMedium,
    fontSizeLarge
  });
  return /*#__PURE__*/React__default.createElement("button", {
    type: "button",
    className: css(buttonStyle.button, boxShadowStyle.style, StyleSheet.create({
      style: style$1
    }).style, textStyle),
    onClick: onClick
  }, label);
};

const GLOBALS = '__GLOBAL_STYLES__';
const globalExtension = {
  selectorHandler: (selector, baseSelector, generateSubtreeStyles) => {
    return baseSelector.includes(GLOBALS) ? generateSubtreeStyles(selector) : null;
  }
};

const normalizeCss = () => ({
  a: {
    textDecoration: 'none'
  },
  figure: {
    margin: 0
  },
  ul: {
    margin: 0,
    padding: 0
  },
  li: {
    margin: 0,
    padding: 0
  },
  img: {
    borderStyle: 'none'
  },
  h1: {
    fontSize: '2em',
    margin: '0.67em 0',
    marginBlockEnd: 0,
    marginBlockStart: 0
  },
  hr: {
    boxSizing: 'content-box',
    height: 0
  },
  pre: {
    fontFamily: 'monospace, monospace',
    fontSize: '1em'
  },
  'abbr[title]': {
    borderBottom: 'none',
    textDecoration: 'underline'
  },
  strong: {
    fontWeight: 'bold'
  },
  b: {
    fontWeight: 'bolder'
  },
  samp: {
    fontFamily: 'monospace, monospace',
    fontSize: '1em'
  },
  kbd: {
    fontFamily: 'monospace, monospace',
    fontSize: '1em'
  },
  code: {
    fontFamily: 'monospace, monospace',
    fontSize: '1em'
  },
  small: {
    fontSize: '80%'
  },
  sup: {
    fontSize: '75%',
    lineHeight: 0,
    position: 'relative',
    verticalAlign: 'baseline',
    top: '-0.5em'
  },
  sub: {
    fontSize: '75%',
    lineHeight: 0,
    position: 'relative',
    verticalAlign: 'baseline',
    bottom: '-0.25em'
  },
  textarea: {
    fontFamily: 'inherit',
    fontSize: '100%',
    lineHeight: '1.15',
    margin: 0
  },
  select: {
    fontFamily: 'inherit',
    fontSize: '100%',
    lineHeight: '1.15',
    margin: 0,
    textTransform: 'none'
  },
  button: {
    fontFamily: 'inherit',
    fontSize: '100%',
    lineHeight: '1.15',
    margin: 0,
    overflow: 'visible',
    textTransform: 'none',
    padding: 0,
    border: 'unset',
    backgroundColor: 'unset',
    ':focus': {
      outline: 0
    }
  },
  input: {
    fontFamily: 'inherit',
    fontSize: '100%',
    lineHeight: '1.15',
    margin: 0
  },
  optgroup: {
    fontFamily: 'inherit',
    fontSize: '100%',
    lineHeight: '1.15',
    margin: 0
  },
  'button:-moz-focusring': {
    outline: '1px dotted ButtonText'
  },
  '[type="button"]:-moz-focusring': {
    outline: '1px dotted ButtonText'
  },
  '[type="submit"]:-moz-focusring': {
    outline: '1px dotted ButtonText'
  },
  '[type="reset"]:-moz-focusring': {
    outline: '1px dotted ButtonText'
  },
  fieldset: {
    padding: '0.35em 0.75em 0.625em'
  },
  legend: {
    padding: 0
  },
  progress: {
    verticalAlign: 'baseline'
  },
  details: {
    display: 'block'
  },
  summary: {
    display: 'list-item'
  },
  template: {
    display: 'none'
  },
  '[hidden]': {
    display: 'none'
  }
});

const componentConfigurationKey$5 = 'mobileTopBar';
const MobileTopBarColor = () => {
  const {
    backgroundColor
  } = useTheme(componentConfigurationKey$5);
  return /*#__PURE__*/createElement(ErrorBoundary, null, /*#__PURE__*/createElement(Helmet, null, /*#__PURE__*/createElement("meta", {
    name: "theme-color",
    content: backgroundColor
  })));
};

/* eslint no-underscore-dangle: [1, { "allowAfterThis": true }] */
const extended = StyleSheet.extend([globalExtension]); // This code causes aphrodite to ignore its caching system, and to create a new style object with every render
// The code is necessary due to a bug in aphrodite - the extended stylesheet seems to have issues with multiple re-renders

const potentialMemoryLeak = prefix => {
  return prefix + Math.random().toString(36).substring(7);
};

const AppContent = props => {
  const normalizedCss = normalizeCss();
  const combinedStyles = Object.keys(normalizedCss).reduce((acc, key) => {
    return { ...acc,
      [key]: { ...acc[key],
        ...normalizedCss[key]
      }
    };
  }, {});
  const styles = extended.StyleSheet.create({
    [GLOBALS]: combinedStyles
  }); // eslint-disable-next-line no-underscore-dangle

  if (!styles[GLOBALS]._name) {
    // eslint-disable-next-line no-underscore-dangle
    styles[GLOBALS]._name = potentialMemoryLeak(GLOBALS);
  }

  try {
    extended.css(styles[GLOBALS]);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(e);
  }

  const {
    children
  } = props;
  return /*#__PURE__*/createElement(Fragment, null, /*#__PURE__*/createElement(MobileTopBarColor, null), children);
};

/* eslint no-underscore-dangle: [1, { "allowAfterThis": true }] */
const extended$1 = StyleSheet.extend([globalExtension]); // This code causes aphrodite to ignore its caching system, and to create a new style object with every render
// The code is necessary due to a bug in aphrodite - the extended stylesheet seems to have issues with multiple re-renders

const potentialMemoryLeak$1 = () => {
  return GLOBALS + Math.random().toString(36).substring(7);
};

const getStyles$2 = () => StyleSheet.create({
  style: {
    maxWidth: `${LARGE_SCREEN_SIZE}px`,
    margin: 'auto'
  }
});

const AMPAppContent = props => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const applyGlobalStyles = (reporter, context) => {
    const styles = extended$1.StyleSheet.create({
      [GLOBALS]: { ...normalizeCss()
      }
    }); // eslint-disable-next-line no-underscore-dangle

    styles[GLOBALS]._name = potentialMemoryLeak$1();
    const componentStyle = getStyles$2();
    const {
      children
    } = props;
    return /*#__PURE__*/createElement(Fragment, null, /*#__PURE__*/createElement(Helmet, null, /*#__PURE__*/createElement("body", {
      className: extended$1.css(styles[GLOBALS])
    })), /*#__PURE__*/createElement(MobileTopBarColor, null), /*#__PURE__*/createElement("div", {
      className: css(componentStyle.style)
    }, children));
  };

  return /*#__PURE__*/createElement(ErrorReporting.Consumer, null, reporter => /*#__PURE__*/createElement(ThemeContext.Consumer, null, context => applyGlobalStyles()));
};

const createStyles$4 = baseUnit => StyleSheet.create({
  container: {
    display: 'flex',
    justifyContent: 'center',
    maxWidth: `${LARGE_SCREEN_SIZE}px`,
    margin: '0 auto',
    marginTop: `${baseUnit}px`,
    overflowAnchor: 'none'
  }
});

const LoadMoreButtonWrapper = ({
  children
}) => {
  const {
    baseUnit
  } = useTheme();
  const style = createStyles$4(baseUnit);
  return /*#__PURE__*/createElement("div", {
    className: css(style.container)
  }, children);
};

const primaryHeaderHeights = {
  large: 80,
  medium: 60,
  small: 60
};
const reducedHeaderFirstRowHeightFactors = {
  large: 1 / 2,
  medium: 2 / 3,
  small: 2 / 3
};
const reducedLogoHeightFactor = 0.5;
const reducedPrimaryHeaderHeights = {
  large: primaryHeaderHeights.large * reducedHeaderFirstRowHeightFactors.large,
  medium: primaryHeaderHeights.medium * reducedHeaderFirstRowHeightFactors.medium,
  small: primaryHeaderHeights.small * reducedHeaderFirstRowHeightFactors.small
};
const secondaryHeaderHeight = 40;
const notActiveLinkIdleColor = (isHoveredLink, hoverOtherLinksColors, idleColor) => isHoveredLink ? hoverOtherLinksColors : idleColor;
const getHeaderHeights = isSecondaryHeaderEnabled => isSecondaryHeaderEnabled ? {
  large: secondaryHeaderHeight + primaryHeaderHeights.large,
  medium: secondaryHeaderHeight + primaryHeaderHeights.medium,
  small: secondaryHeaderHeight + primaryHeaderHeights.small
} : primaryHeaderHeights;

const componentConfigThemeKeyForContent = 'mainContent';
const componentConfigThemeKeyForArticleContent = 'mainArticleContent';

const componentConfigThemeKeyForTemplateBody = 'templateBody';
const componentConfigThemeKeyForTemplateArticleBody = 'templateArticleBody';
const BodyTag = ({
  mainConfigThemeKey
}) => {
  const getBodyTagConfigThemeKey = () => {
    switch (mainConfigThemeKey) {
      case componentConfigThemeKeyForContent:
        return componentConfigThemeKeyForTemplateBody;

      case componentConfigThemeKeyForArticleContent:
        return componentConfigThemeKeyForTemplateArticleBody;

      default:
        return componentConfigThemeKeyForTemplateBody;
    }
  };

  const {
    backgroundColor
  } = useTheme(getBodyTagConfigThemeKey());
  return /*#__PURE__*/React__default.createElement(React__default.Fragment, null, /*#__PURE__*/React__default.createElement(Helmet, null, /*#__PURE__*/React__default.createElement("style", null, `body {background-color: ${backgroundColor};}`)));
};

const MainWrapperForDoubleHeaderNavigation = ({
  isSecondaryHeaderEnabled,
  children,
  configThemeKey
}) => {
  const {
    baseUnit,
    backgroundColor,
    maxWidth
  } = useTheme(configThemeKey);
  const headerHeights = getHeaderHeights(isSecondaryHeaderEnabled);
  const style = {
    backgroundColor,
    maxWidth: `${maxWidth}px`,
    margin: '0 auto',
    paddingBottom: `${baseUnit}px`,
    [MEDIA_BREAKPOINTS.large]: {
      paddingTop: `${headerHeights.large + baseUnit / 4}px`
    },
    [MEDIA_BREAKPOINTS.medium]: {
      paddingTop: `${headerHeights.medium + baseUnit / 4}px`
    },
    [MEDIA_BREAKPOINTS.small]: {
      paddingTop: `${headerHeights.small + baseUnit / 4}px`
    }
  };
  return /*#__PURE__*/React__default.createElement(React__default.Fragment, null, /*#__PURE__*/React__default.createElement(BodyTag, {
    mainConfigThemeKey: configThemeKey
  }), /*#__PURE__*/React__default.createElement("main", {
    className: css(StyleSheet.create({
      style
    }).style)
  }, children));
};

const componentConfigThemeKey$b = 'headerWithSubMenus';

const useHeaderHeights = () => {
  const themeContextForHeaderWithSubMenus = useTheme(componentConfigThemeKey$b);
  return {
    large: themeContextForHeaderWithSubMenus.firstRowHeightInLargeScreen,
    medium: themeContextForHeaderWithSubMenus.firstRowHeightInMediumScreen + themeContextForHeaderWithSubMenus.secondRowHeightInMediumScreen,
    small: themeContextForHeaderWithSubMenus.firstRowHeightInSmallScreen + themeContextForHeaderWithSubMenus.secondRowHeightInSmallScreen
  };
};
const useFirstRowHeaderHeights = () => {
  const themeContextForHeaderWithSubMenus = useTheme(componentConfigThemeKey$b);
  return {
    large: themeContextForHeaderWithSubMenus.firstRowHeightInLargeScreen,
    medium: themeContextForHeaderWithSubMenus.firstRowHeightInMediumScreen,
    small: themeContextForHeaderWithSubMenus.firstRowHeightInSmallScreen
  };
};

const MainWrapperForDefaultNavigation = ({
  children,
  configThemeKey
}) => {
  const {
    baseUnit,
    backgroundColor,
    paddingFactor,
    maxWidth
  } = useTheme(configThemeKey);
  const headerHeights = useHeaderHeights();
  const style = {
    backgroundColor,
    maxWidth: `${maxWidth}px`,
    margin: '0 auto',
    paddingBottom: `${baseUnit}px`,
    [MEDIA_BREAKPOINTS.large]: {
      marginTop: `${headerHeights.large * baseUnit + baseUnit * paddingFactor}px`
    },
    [MEDIA_BREAKPOINTS.medium]: {
      marginTop: `${headerHeights.medium * baseUnit + baseUnit * paddingFactor}px`
    },
    [MEDIA_BREAKPOINTS.small]: {
      marginTop: `${headerHeights.small * baseUnit + baseUnit * paddingFactor}px`
    }
  };
  return /*#__PURE__*/React__default.createElement(React__default.Fragment, null, /*#__PURE__*/React__default.createElement(BodyTag, {
    mainConfigThemeKey: configThemeKey
  }), /*#__PURE__*/React__default.createElement("main", {
    className: css(StyleSheet.create({
      style
    }).style)
  }, children));
};

const AMPMainWrapper = ({
  isSecondaryHeaderEnabled,
  children
}) => {
  const {
    baseUnit,
    backgroundColor
  } = useTheme(componentConfigThemeKeyForArticleContent);
  const headerHeights = getHeaderHeights(isSecondaryHeaderEnabled);
  const style = {
    backgroundColor,
    paddingBottom: `${baseUnit}px`,
    [MEDIA_BREAKPOINTS.large]: {
      paddingTop: `${headerHeights.large + baseUnit / 4}px`
    },
    [MEDIA_BREAKPOINTS.medium]: {
      paddingTop: `${headerHeights.medium + baseUnit / 4}px`
    },
    [MEDIA_BREAKPOINTS.small]: {
      paddingTop: `${headerHeights.small + baseUnit / 4}px`
    }
  };
  return /*#__PURE__*/React__default.createElement("main", {
    className: css(StyleSheet.create({
      style
    }).style)
  }, children);
};

const footerSectionPadding = baseUnit => ({
  padding: `${baseUnit}px 0`
});
const generateSeparatorSections = separatorStyle => (acc, section, index, arr) => {
  const res = section;
  return index < arr.length - 1 ? [...acc, res, /*#__PURE__*/createElement("div", {
    key: index,
    className: css(separatorStyle)
  })] : [...acc, res];
};

const componentConfigurationKey$6 = 'linkButton';

const createStyle$2 = (color, hoverColor) => StyleSheet.create({
  button: {
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    textAlign: 'start',
    color,
    ':hover': {
      color: hoverColor
    },
    ':active': {
      color
    },
    ':visited': {
      color
    },
    ':focus': {
      outline: 'none'
    }
  }
});

const LinkButton = props => {
  const {
    text,
    onClick,
    color,
    hoverColor
  } = props;
  const {
    fontSizeLarge,
    fontSizeMedium,
    fontSizeSmall,
    fontSizesStyles
  } = useTheme(componentConfigurationKey$6);
  /* eslint-disable-next-line no-new-func */

  const onClickFunction = new Function(onClick);
  const style = createStyle$2(color, hoverColor);
  const fontStyles = getStyleBySize(fontSizesStyles, {
    fontSizeLarge,
    fontSizeMedium,
    fontSizeSmall
  });
  return /*#__PURE__*/createElement("button", {
    type: "button",
    onClick: () => onClickFunction(),
    className: css(style.button, fontStyles)
  }, text);
};

const componentConfigurationKey$7 = 'footerLinksSection';

const getStyle$3 = theme => {
  const {
    baseUnit
  } = theme;
  const padding = footerSectionPadding(baseUnit);
  return StyleSheet.create({
    section: { ...padding
    },
    title: {
      textAlign: 'center',
      color: theme.color
    },
    gridWrapper: {
      display: 'flex',
      justifyContent: 'center'
    },
    linksWrapper: {
      display: 'inline-grid',
      marginTop: `${baseUnit * 0.5}px`,
      [MEDIA_BREAKPOINTS.large]: {
        gridTemplateColumns: 'auto auto auto auto',
        width: '100%',
        justifyContent: 'space-between',
        gridGap: `${baseUnit * 0.5}px`
      },
      [MEDIA_BREAKPOINTS.medium]: {
        gridTemplateColumns: 'auto auto auto',
        gridGap: `${baseUnit * 0.5}px ${baseUnit * 4}px`
      },
      [MEDIA_BREAKPOINTS.small]: {
        gridTemplateColumns: 'auto auto',
        gridGap: `${baseUnit * 0.5}px ${baseUnit * 2}px`
      }
    }
  });
};

const FooterLinksSection = props => {
  const finalProps = { ...useTheme(componentConfigurationKey$7),
    ...props
  };
  const {
    title,
    links,
    buttons,
    hoverStateColor,
    idleColor
  } = finalProps;
  const style = getStyle$3(finalProps);
  const linkStyle = generateLinkStyleFromTheme({ ...finalProps
  });
  return /*#__PURE__*/createElement("div", {
    className: css(style.section)
  }, /*#__PURE__*/createElement(H4, {
    styles: semanticCSS(style.title)
  }, title), /*#__PURE__*/createElement("div", {
    className: css(style.gridWrapper)
  }, /*#__PURE__*/createElement("div", {
    className: css(style.linksWrapper)
  }, links ? links.map((navigationLink, index) => /*#__PURE__*/createElement(TextLinkComponent, {
    key: index,
    href: navigationLink.link.href,
    linkStyle: linkStyle
  }, /*#__PURE__*/createElement(P, null, navigationLink.link.text))) : null, buttons ? buttons.map((button, index) => /*#__PURE__*/createElement(LinkButton, {
    key: index,
    text: button.text,
    onClick: button.onClick,
    color: idleColor,
    hoverColor: hoverStateColor
  })) : null)));
};

const componentConfigurationKey$8 = 'bottomFooterBar';

const getBottomFooterBarStyle = ({
  backgroundColor,
  baseUnit,
  textColor
}) => StyleSheet.create({
  style: {
    color: textColor,
    padding: `${baseUnit * 0.5}px ${baseUnit}px`,
    boxSizing: 'border-box',
    textAlign: 'center',
    margin: '0 auto',
    maxWidth: `${HUGE_SCREEN_SIZE}px`
  },
  wrapper: {
    backgroundColor
  }
});

const BottomFooterBar = props => {
  const themeProps = useTheme(componentConfigurationKey$8);
  const bottomFooterBarStyle = getBottomFooterBarStyle(themeProps);
  const {
    linkFontSizeLarge,
    linkFontSizeMedium,
    linkFontSizeSmall,
    textColor
  } = themeProps;
  const {
    year,
    rightsReservedText,
    poweredByLink
  } = props;
  const linkStyle = generateLinkStyleFromTheme({
    idleColor: textColor,
    activeStateColor: textColor,
    hoverStateColor: textColor
  });
  return /*#__PURE__*/createElement("div", {
    className: css(bottomFooterBarStyle.wrapper)
  }, /*#__PURE__*/createElement(P, {
    styles: semanticCSS(bottomFooterBarStyle.style),
    fontSizeLarge: linkFontSizeLarge,
    fontSizeMedium: linkFontSizeMedium,
    fontSizeSmall: linkFontSizeSmall
  }, `© ${year} `, /*#__PURE__*/createElement(TextLinkComponent, {
    href: poweredByLink.href,
    linkStyle: linkStyle,
    rel: "noopener",
    target: "_blank"
  }, poweredByLink.text), ` - ${rightsReservedText}`));
};

const componentConfigurationKey$9 = 'footerSocialLinks';

const getSocialLinksStyle = (baseUnit, titleColor, circleColor, numberOfColumns, socialLinksColor, socialLinksHoverColor) => {
  const padding = footerSectionPadding(baseUnit);
  return StyleSheet.create({
    section: { ...padding
    },
    title: {
      textAlign: 'center',
      color: titleColor
    },
    storesWrapper: {
      display: 'grid',
      gridTemplateColumns: `repeat(${numberOfColumns}, 1fr)`,
      gridGap: `${baseUnit * 0.5}px`,
      marginTop: `${baseUnit * 0.5}px`
    },
    circle: {
      width: '40px',
      height: '40px',
      backgroundColor: circleColor,
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    svgCircle: {
      fill: socialLinksColor,
      height: '16px',
      ':hover': {
        fill: socialLinksHoverColor
      }
    }
  });
};

const createStoreItem = circleStyle => (socialLink, index) => {
  return /*#__PURE__*/createElement(TextLinkComponent, {
    href: socialLink.href,
    key: index,
    rel: "noopener",
    target: "_blank"
  }, /*#__PURE__*/createElement("div", {
    className: css(circleStyle)
  }, /*#__PURE__*/createElement("img", {
    alt: socialLink.alt,
    src: socialLink.src,
    height: "16"
  })));
};

const createStoreItemSvg = (svgStyle, circleStyle) => (socialLink, index) => {
  const svg = socialLink.src.replace(/<svg/g, `<svg class="${css(svgStyle)}"`);
  return /*#__PURE__*/createElement(TextLinkComponent, {
    href: socialLink.href,
    key: index,
    rel: "noopener",
    target: "_blank"
  }, /*#__PURE__*/createElement("div", {
    className: css(circleStyle),
    "aria-label": socialLink.alt,
    dangerouslySetInnerHTML: {
      __html: svg
    }
  }));
};

const maxSocialItemsPerRow = 8;
const FooterSocialLinks = props => {
  const {
    socialSection,
    oldSocialSection
  } = props;
  const {
    baseUnit,
    titleColor,
    circleColor,
    socialLinksColor,
    socialLinksHoverColor,
    titleFontSizeLarge,
    titleFontSizeMedium,
    titleFontSizeSmall
  } = useTheme(componentConfigurationKey$9); // eslint-disable-next-line no-nested-ternary

  const socialSectionLength = socialSection ? socialSection.imageLinks.length : oldSocialSection ? oldSocialSection.imageLinks.length : 0;
  const style = getSocialLinksStyle(baseUnit, titleColor, circleColor, Math.min(socialSectionLength, maxSocialItemsPerRow), socialLinksColor, socialLinksHoverColor);
  return socialSection ? /*#__PURE__*/createElement("div", {
    className: css(style.section)
  }, /*#__PURE__*/createElement(H4, {
    styles: semanticCSS(style.title),
    fontSizeLarge: titleFontSizeLarge,
    fontSizeMedium: titleFontSizeMedium,
    fontSizeSmall: titleFontSizeSmall
  }, socialSection && socialSection.title), /*#__PURE__*/createElement("div", {
    className: css(style.storesWrapper)
  }, socialSection && socialSection.imageLinks.map(createStoreItemSvg(style.svgCircle, style.circle)))) : /*#__PURE__*/createElement("div", {
    className: css(style.section)
  }, /*#__PURE__*/createElement(H4, {
    styles: semanticCSS(style.title),
    fontSizeLarge: titleFontSizeLarge,
    fontSizeMedium: titleFontSizeMedium,
    fontSizeSmall: titleFontSizeSmall
  }, oldSocialSection && oldSocialSection.title), /*#__PURE__*/createElement("div", {
    className: css(style.storesWrapper)
  }, oldSocialSection && oldSocialSection.imageLinks.map(createStoreItem(style.circle))));
};

const componentConfigurationKey$a = 'footerMobileStores';

const getStyle$4 = (theme, numColumns = 2) => {
  const {
    baseUnit
  } = theme;
  const padding = footerSectionPadding(baseUnit);
  const columns = Array(numColumns).fill('auto').join(' ');
  return StyleSheet.create({
    section: { ...padding
    },
    title: {
      textAlign: 'center',
      color: theme.color
    },
    storesWrapper: {
      display: 'grid',
      gridTemplateColumns: columns,
      justifyContent: 'center',
      gridGap: `${baseUnit * 0.5}px`,
      marginTop: `${baseUnit * 0.5}px`
    },
    storeItem: {
      backgroundColor: 'wheat'
    }
  });
};

const createStoreItem$1 = (storeLink, index) => {
  return /*#__PURE__*/createElement(TextLinkComponent, {
    href: storeLink.href,
    key: index,
    target: "_blank",
    rel: "noopener"
  }, /*#__PURE__*/createElement("img", {
    alt: storeLink.alt,
    src: storeLink.src,
    height: "40"
  }));
};

const FooterMobileStores = props => {
  const {
    title,
    imageLinks
  } = props;
  const themeProps = useTheme(componentConfigurationKey$a);
  const style = getStyle$4(themeProps, imageLinks.length);
  const {
    titleFontSizeLarge,
    titleFontSizeMedium,
    titleFontSizeSmall
  } = themeProps;
  return /*#__PURE__*/createElement(ErrorBoundary, null, /*#__PURE__*/createElement("div", {
    className: css(style.section)
  }, /*#__PURE__*/createElement(H4, {
    styles: semanticCSS(style.title),
    fontSizeLarge: titleFontSizeLarge,
    fontSizeMedium: titleFontSizeMedium,
    fontSizeSmall: titleFontSizeSmall
  }, title), /*#__PURE__*/createElement("div", {
    className: css(style.storesWrapper)
  }, imageLinks.map(createStoreItem$1))));
};

const getSeparatorStyle = (borderTopColor, separatorHeight) => {
  return StyleSheet.create({
    style: {
      height: `${separatorHeight}px`,
      width: '100%',
      backgroundColor: borderTopColor,
      [MEDIA_BREAKPOINTS.large]: {
        display: 'none'
      },
      [MEDIA_BREAKPOINTS.medium]: {
        display: 'block'
      },
      [MEDIA_BREAKPOINTS.small]: {
        display: 'block'
      }
    }
  });
};

const getWrapperStyle = numberOfSections => {
  return StyleSheet.create({
    style: {
      display: 'grid',
      justifyItems: 'center',
      [MEDIA_BREAKPOINTS.large]: {
        gridTemplateColumns: `repeat(${numberOfSections}, 1fr)`
      },
      [MEDIA_BREAKPOINTS.medium]: {
        gridTemplateColumns: '1fr'
      },
      [MEDIA_BREAKPOINTS.small]: {
        gridTemplateColumns: '1fr'
      }
    }
  });
};

const getSocialSection = (socialSection, oldSocialSection) => {
  // eslint-disable-next-line no-nested-ternary
  return socialSection ? /*#__PURE__*/createElement(FooterSocialLinks, {
    key: "social-links",
    socialSection: socialSection
  }) : oldSocialSection ? /*#__PURE__*/createElement(FooterSocialLinks, {
    key: "social-links",
    oldSocialSection: oldSocialSection
  }) : null;
};

const getAppsSection = appsSection => {
  return appsSection ? /*#__PURE__*/createElement(FooterMobileStores, _extends({
    key: "mobile-links"
  }, appsSection)) : null;
};

const AdditionalFooterSections = props => {
  const {
    socialSection,
    oldSocialSection,
    appsSection,
    separatorColor,
    separatorHeight
  } = props;
  const additionalFooterSections = [getSocialSection(socialSection, oldSocialSection), getAppsSection(appsSection)].filter(section => section !== null);
  return additionalFooterSections.length === 0 ? null : /*#__PURE__*/createElement("div", {
    className: css(getWrapperStyle(additionalFooterSections.length).style)
  }, additionalFooterSections.reduce(generateSeparatorSections(getSeparatorStyle(separatorColor, separatorHeight).style), []));
};

const getAdditionalFooterSection = (socialSection, oldSocialSection, appsSection, separatorColor, separatorHeight) => {
  if (socialSection === null && oldSocialSection === null && appsSection === null) return null;
  return /*#__PURE__*/createElement(AdditionalFooterSections, {
    key: "additional-links",
    oldSocialSection: oldSocialSection,
    socialSection: socialSection,
    appsSection: appsSection,
    separatorColor: separatorColor,
    separatorHeight: separatorHeight
  });
};

const componentConfigurationKey$b = 'footer';
const SEPARATOR_HEIGHT = 1;

const getFooterStyle = theme => {
  const {
    baseUnit,
    borderTopColor,
    backgroundColor
  } = theme;
  return StyleSheet.create({
    footer: {
      borderTop: `solid 2px ${borderTopColor}`,
      [MEDIA_BREAKPOINTS.large]: {
        padding: `0 ${baseUnit}px`
      },
      [MEDIA_BREAKPOINTS.medium]: {
        padding: `0 ${baseUnit}px`
      },
      [MEDIA_BREAKPOINTS.small]: {
        padding: `0 ${baseUnit * 0.75}px`
      },
      margin: '0 auto',
      maxWidth: `${HUGE_SCREEN_SIZE}px`
    },
    footerWrapper: {
      backgroundColor
    },
    footerContent: {
      margin: 'auto',
      [MEDIA_BREAKPOINTS.large]: {
        maxWidth: '852px'
      }
    }
  });
};

const getSeparatorStyle$1 = (separatorColor, separatorHeight) => {
  return StyleSheet.create({
    style: {
      height: `${separatorHeight}px`,
      backgroundColor: separatorColor
    }
  });
};

const getLinksSection = linksSection => {
  return linksSection ? /*#__PURE__*/createElement(FooterLinksSection, _extends({
    key: "footer-links"
  }, linksSection)) : null;
};

const getFooterItems = (linksSection, socialSection, oldSocialSection, appsSection, separatorColor, separatorHeight) => {
  return [getLinksSection(linksSection), getAdditionalFooterSection(socialSection, oldSocialSection, appsSection, separatorColor, separatorHeight)].filter(section => section !== null).reduce(generateSeparatorSections(getSeparatorStyle$1(separatorColor, separatorHeight).style), []);
};

const OldFooter = props => {
  const themeProps = useTheme(componentConfigurationKey$b);
  const footerComponentStyle = getFooterStyle(themeProps);
  const {
    linksSection,
    socialSection,
    oldSocialSection,
    appsSection,
    bottomFooterBar
  } = props;
  const {
    separatorColor
  } = themeProps;
  return /*#__PURE__*/createElement(ErrorBoundary, null, /*#__PURE__*/createElement("footer", null, /*#__PURE__*/createElement("div", {
    className: css(footerComponentStyle.footerWrapper)
  }, /*#__PURE__*/createElement("div", {
    className: css(footerComponentStyle.footer)
  }, /*#__PURE__*/createElement("div", {
    className: css(footerComponentStyle.footerContent)
  }, getFooterItems(linksSection, socialSection, oldSocialSection, appsSection, separatorColor, SEPARATOR_HEIGHT)))), /*#__PURE__*/createElement(BottomFooterBar, bottomFooterBar)));
};

const AnimationDirection = {
  UP: 'foldingUp',
  DOWN: 'foldingDown',
  NONE: 'none'
};

const reducedLogoHeightFactor$1 = 0.5;

const calculateImageLogoAspectRatio = logoElement => {
  return {
    x: logoElement.getBoundingClientRect().width,
    y: logoElement.getBoundingClientRect().height
  };
};

const calculateVideoLogoAspectRatio = logoElement => {
  return {
    x: logoElement.videoWidth,
    y: logoElement.videoHeight
  };
};

const useAnimationLogoHeightsAndWidths = initialLogoHeight => {
  const [logoAspectRatio, setLogoAspectRatio] = React__default.useState(null);
  const {
    baseUnit
  } = useTheme();
  const ref = React__default.useCallback(node => {
    if (node) {
      if (node.tagName === 'VIDEO') {
        node.addEventListener('loadedmetadata', () => {
          setLogoAspectRatio(calculateVideoLogoAspectRatio(node));
        });
      } else {
        setLogoAspectRatio(calculateImageLogoAspectRatio(node));
      }
    }
  }, []);
  const animationLogoHeights = {
    height: baseUnit * initialLogoHeight,
    reducedHeight: baseUnit * initialLogoHeight * reducedLogoHeightFactor$1
  };
  const animationLogoWidths = {
    width: logoAspectRatio ? animationLogoHeights.height * (logoAspectRatio.x / logoAspectRatio.y) : -1,
    reducedWidth: logoAspectRatio ? animationLogoHeights.reducedHeight * (logoAspectRatio.x / logoAspectRatio.y) : -1
  };
  return [ref, { ...animationLogoHeights,
    ...animationLogoWidths
  }];
};

const getLogoStyle = (logoHeightInMediumScreen, logoHeightInSmallScreen, menuAnimation, reduceHeightAnimationDuration, largeLogoHeightsAndWidths) => {
  return StyleSheet.create({
    base: {
      display: 'inline-flex',
      [MEDIA_BREAKPOINTS.large]: {
        height: menuAnimation === AnimationDirection.UP ? `${largeLogoHeightsAndWidths.reducedHeight}px` : `${largeLogoHeightsAndWidths.height}px`,
        width: menuAnimation === AnimationDirection.UP ? `${largeLogoHeightsAndWidths.reducedWidth}px` : `${largeLogoHeightsAndWidths.width}px`
      },
      [MEDIA_BREAKPOINTS.medium]: {
        height: `${logoHeightInMediumScreen * baseUnit}px`,
        width: 'auto'
      },
      [MEDIA_BREAKPOINTS.small]: {
        height: `${logoHeightInSmallScreen * baseUnit}px`,
        width: 'auto'
      },
      alignItems: 'center',
      transition: `${reduceHeightAnimationDuration}ms`
    },
    image: {
      height: '100%'
    }
  });
};

const getImageResource = svg => {
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
};

const Logo = props => {
  const {
    href,
    alt,
    src,
    logoHeightInLargeScreen,
    logoHeightInMediumScreen,
    logoHeightInSmallScreen,
    menuAnimation = AnimationDirection.NONE,
    reduceHeightAnimationDuration = 0
  } = props;
  const [ref, largeLogoHeightsAndWidths] = useAnimationLogoHeightsAndWidths(logoHeightInLargeScreen);
  const style = getLogoStyle(logoHeightInMediumScreen, logoHeightInSmallScreen, menuAnimation, reduceHeightAnimationDuration, largeLogoHeightsAndWidths);
  return /*#__PURE__*/React__default.createElement(React__default.Fragment, null, /*#__PURE__*/React__default.createElement(Link, {
    className: css(style.base),
    href: href
  }, /*#__PURE__*/React__default.createElement("img", {
    ref: ref,
    className: css(style.image),
    alt: alt,
    src: getImageResource(src)
  })));
};

const componentConfigurationKey$c = 'footerLinksSection';

const getStyle$5 = theme => {
  const {
    baseUnit,
    largePaddingFactor,
    mediumPaddingFactor,
    smallPaddingFactor
  } = theme;
  return StyleSheet.create({
    section: {
      [MEDIA_BREAKPOINTS.large]: {
        paddingTop: `${largePaddingFactor * baseUnit}px`,
        paddingBottom: `${largePaddingFactor * baseUnit}px`
      },
      [MEDIA_BREAKPOINTS.medium]: {
        paddingTop: `${mediumPaddingFactor * baseUnit}px`,
        paddingBottom: `${mediumPaddingFactor * baseUnit}px`
      },
      [MEDIA_BREAKPOINTS.small]: {
        paddingTop: `${smallPaddingFactor * baseUnit}px`,
        paddingBottom: `${smallPaddingFactor * baseUnit}px`
      },
      display: 'flex',
      justifyContent: 'center'
    },
    linksWrapper: {
      display: 'inline-grid',
      [MEDIA_BREAKPOINTS.large]: {
        gridTemplateColumns: 'auto auto auto auto',
        gridGap: `${baseUnit * 0.5}px ${baseUnit * 4}px`
      },
      [MEDIA_BREAKPOINTS.medium]: {
        gridTemplateColumns: 'auto auto auto',
        gridGap: `${baseUnit * 0.5}px ${baseUnit * 4}px`
      },
      [MEDIA_BREAKPOINTS.small]: {
        gridTemplateColumns: 'auto auto',
        gridGap: `${baseUnit * 0.5}px ${baseUnit * 2}px`
      }
    }
  });
};

const FooterLinksSection$1 = props => {
  const themeProps = useTheme(componentConfigurationKey$c);
  const {
    links,
    buttons
  } = props;
  const style = getStyle$5(themeProps);
  const {
    fontSizesStyles,
    activeStateColor,
    hoverStateColor,
    idleColor,
    linkFontSizeLarge,
    linkFontSizeMedium,
    linkFontSizeSmall
  } = themeProps;
  const fontStyles = getStyleBySize(fontSizesStyles, {
    fontSizeLarge: linkFontSizeLarge,
    fontSizeMedium: linkFontSizeMedium,
    fontSizeSmall: linkFontSizeSmall
  });
  const linkColorsStyle = generateLinkStyleFromTheme({
    activeStateColor,
    hoverStateColor,
    idleColor
  });
  return /*#__PURE__*/createElement("div", {
    className: css(style.section)
  }, /*#__PURE__*/createElement("div", {
    className: css(style.linksWrapper)
  }, links ? links.map((navigationLink, index) => /*#__PURE__*/createElement("div", {
    key: index,
    className: css(fontStyles)
  }, /*#__PURE__*/createElement(TextLinkComponent, {
    linkStyle: linkColorsStyle,
    href: navigationLink.link.href
  }, navigationLink.link.text))) : null, buttons ? buttons.map((button, index) => /*#__PURE__*/createElement(LinkButton, {
    key: index,
    text: button.text,
    onClick: button.onClick,
    color: idleColor,
    hoverColor: hoverStateColor
  })) : null));
};

const componentConfigurationKey$d = 'footerWithLogo';

const createStyles$5 = (themeProps, separatorHeight) => {
  const {
    baseUnit,
    backgroundColor,
    separatorColor,
    largePaddingFactor,
    mediumPaddingFactor,
    smallPaddingFactor
  } = themeProps;
  return StyleSheet.create({
    footerWrapper: {
      backgroundColor,
      [MEDIA_BREAKPOINTS.large]: {
        paddingTop: `${largePaddingFactor * baseUnit}px`,
        paddingBottom: `${largePaddingFactor * baseUnit}px`
      },
      [MEDIA_BREAKPOINTS.medium]: {
        paddingTop: `${mediumPaddingFactor * baseUnit}px`,
        paddingBottom: `${mediumPaddingFactor * baseUnit}px`
      },
      [MEDIA_BREAKPOINTS.small]: {
        paddingTop: `${smallPaddingFactor * baseUnit}px`,
        paddingBottom: `${smallPaddingFactor * baseUnit}px`
      }
    },
    footerContent: {
      [MEDIA_BREAKPOINTS.large]: {
        padding: `0 ${baseUnit}px`
      },
      [MEDIA_BREAKPOINTS.medium]: {
        padding: `0 ${baseUnit}px`
      },
      [MEDIA_BREAKPOINTS.small]: {
        padding: `0 ${baseUnit * 0.75}px`
      },
      margin: '0 auto',
      maxWidth: `${HUGE_SCREEN_SIZE}px`
    },
    logoSeparator: {
      height: `${separatorHeight}px`,
      width: '100%',
      backgroundColor: separatorColor,
      ':first-child': {
        marginRight: `${baseUnit}px`
      },
      ':last-child': {
        marginLeft: `${baseUnit}px`
      }
    },
    logoWrapper: {
      paddingTop: `${baseUnit}px`,
      display: 'flex',
      alignItems: 'center'
    }
  });
};

const getSeparatorStyle$2 = (separatorColor, separatorHeight) => {
  return StyleSheet.create({
    separator: {
      height: `${separatorHeight}px`,
      backgroundColor: separatorColor
    }
  });
};

const logoBaseUnitHeight = 1.2;
const SEPARATOR_HEIGHT$1 = 1;

const getLinksSection$1 = linksSection => {
  return linksSection ? /*#__PURE__*/createElement(FooterLinksSection$1, _extends({
    key: "footer-links"
  }, linksSection)) : null;
};

const getFooterItems$1 = (linksSection, oldSocialSection, socialSection, appsSection, separatorColor, separatorHeight) => {
  const separatorStyle = getSeparatorStyle$2(separatorColor, separatorHeight).separator;
  return [getLinksSection$1(linksSection), getAdditionalFooterSection(socialSection, oldSocialSection, appsSection, separatorColor, separatorHeight)].filter(section => section !== null).reduce(generateSeparatorSections(separatorStyle), []);
};

const Footer = props => {
  const {
    linksSection,
    socialSection,
    oldSocialSection,
    appsSection,
    logo,
    bottomFooterBar
  } = props;
  const themeProps = useTheme(componentConfigurationKey$d);
  const {
    separatorColor
  } = themeProps;
  const styles = createStyles$5(themeProps, SEPARATOR_HEIGHT$1);
  return /*#__PURE__*/createElement(ErrorBoundary, null, /*#__PURE__*/createElement("footer", {
    className: css(styles.footerWrapper)
  }, /*#__PURE__*/createElement("div", {
    className: css(styles.footerContent)
  }, /*#__PURE__*/createElement("div", {
    className: css(styles.logoWrapper)
  }, /*#__PURE__*/createElement("div", {
    className: css(styles.logoSeparator)
  }), logo && /*#__PURE__*/createElement(Logo, _extends({}, logo, {
    logoHeightInSmallScreen: logoBaseUnitHeight,
    logoHeightInMediumScreen: logoBaseUnitHeight,
    logoHeightInLargeScreen: logoBaseUnitHeight
  })), /*#__PURE__*/createElement("div", {
    className: css(styles.logoSeparator)
  })), getFooterItems$1(linksSection, oldSocialSection, socialSection, appsSection, separatorColor, SEPARATOR_HEIGHT$1)), /*#__PURE__*/createElement(BottomFooterBar, bottomFooterBar)));
};

const componentConfigThemeKey$c = 'primaryLinks';
const radiusBorder = 4;

const getLinkStyle = (activeBorderBottom, isActive = false) => {
  return StyleSheet.create({
    link: {
      height: '100%',
      width: '100%',
      display: 'inline-flex',
      alignItems: 'center',
      position: 'relative',
      ':before': isActive ? {
        height: `${radiusBorder}px`,
        bottom: 0,
        left: 0,
        position: 'absolute',
        right: 0,
        content: "''",
        backgroundColor: activeBorderBottom
      } : {}
    }
  });
};

const getContainerStyle = baseUnit => {
  return StyleSheet.create({
    linksContainer: {
      display: 'grid',
      gridAutoFlow: 'column',
      height: '100%',
      gridColumnGap: `${baseUnit}px`
    }
  });
};

const additionalStyle = {
  [MEDIA_BREAKPOINTS.large]: {
    display: 'block'
  },
  [MEDIA_BREAKPOINTS.medium]: {
    display: 'none'
  },
  [MEDIA_BREAKPOINTS.small]: {
    display: 'none'
  }
};

const getContentStyle = (isActiveLink, fontSizesStyles) => {
  const fontSizesActive = {
    fontSizeSmall: TYPOGRAPHY_SIZE.BIGGER,
    fontSizeMedium: TYPOGRAPHY_SIZE.BIGGER,
    fontSizeLarge: TYPOGRAPHY_SIZE.BIGGER
  };
  const fontSizesNotActive = {
    fontSizeSmall: TYPOGRAPHY_SIZE.NORMAL,
    fontSizeMedium: TYPOGRAPHY_SIZE.NORMAL,
    fontSizeLarge: TYPOGRAPHY_SIZE.NORMAL
  };
  return isActiveLink ? getStyleBySize(fontSizesStyles, fontSizesActive) : getStyleBySize(fontSizesStyles, fontSizesNotActive);
};

const createLink = ({
  idleColor,
  activeStateColor,
  hoverStateColor,
  activeBorderBottom,
  hoverOtherLinksColors,
  fontSizesStyles
}, isHoveredLink, pagePath) => (linkData, index) => {
  const isActiveLink = linkData.link.href === pagePath;
  const style = getLinkStyle(activeBorderBottom, isActiveLink);
  const calculatedIdleColor = isActiveLink ? activeStateColor : notActiveLinkIdleColor(isHoveredLink, hoverOtherLinksColors, idleColor);
  const linkStyle = generateLinkStyleFromTheme({
    idleColor: calculatedIdleColor,
    activeStateColor,
    hoverStateColor
  }, additionalStyle);
  const contentStyle = getContentStyle(isActiveLink, fontSizesStyles);
  return /*#__PURE__*/React__default.createElement(TextLinkComponent, {
    key: index,
    href: linkData.link.href,
    linkStyle: linkStyle
  }, /*#__PURE__*/React__default.createElement(Span, {
    styles: semanticCSS(contentStyle, style.link)
  }, linkData.link.text));
};

const PrimaryLinks = props => {
  const [isHoveredLink, setIsHoveredLink] = useState(false);
  const themeProps = useTheme(componentConfigThemeKey$c);
  const {
    links,
    pagePath
  } = props;
  const {
    baseUnit
  } = themeProps;
  const style = getContainerStyle(baseUnit);
  return /*#__PURE__*/React__default.createElement(ErrorBoundary, null, /*#__PURE__*/React__default.createElement("div", {
    className: css(style.linksContainer),
    onMouseEnter: () => setIsHoveredLink(true),
    onMouseLeave: () => setIsHoveredLink(false)
  }, links.map(createLink(themeProps, isHoveredLink, pagePath))));
};

const createLogoHeights = (small, medium, large) => ({
  small,
  medium,
  large
});
const defaultLogoHeights = createLogoHeights(24, 24, 24);

const getLogoStyle$1 = (logoHeights, menuAnimation, reduceHeightAnimationDuration) => {
  return StyleSheet.create({
    base: {
      [MEDIA_BREAKPOINTS.large]: {
        height: menuAnimation === AnimationDirection.UP ? `${logoHeights.large * reducedLogoHeightFactor}px` : `${logoHeights.large}px`
      },
      [MEDIA_BREAKPOINTS.medium]: {
        height: `${logoHeights.medium}px`
      },
      [MEDIA_BREAKPOINTS.small]: {
        height: `${logoHeights.small}px`
      },
      alignItems: 'center',
      paddingRight: `${baseUnit}px`,
      transition: `${reduceHeightAnimationDuration}ms`
    },
    largeSvg: {
      [MEDIA_BREAKPOINTS.large]: {
        display: 'inline-flex'
      },
      [MEDIA_BREAKPOINTS.medium]: {
        display: 'none'
      },
      [MEDIA_BREAKPOINTS.small]: {
        display: 'none'
      }
    },
    smallSvg: {
      [MEDIA_BREAKPOINTS.large]: {
        display: 'none'
      },
      [MEDIA_BREAKPOINTS.medium]: {
        display: 'inline-flex'
      },
      [MEDIA_BREAKPOINTS.small]: {
        display: 'inline-flex'
      }
    },
    image: {
      height: '100%'
    }
  });
};

const getImageResource$1 = svg => {
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
};

const Logo$1 = props => {
  const {
    href,
    alt,
    svgLarge,
    svgSmall,
    logoHeights = defaultLogoHeights,
    menuAnimation = AnimationDirection.NONE,
    reduceHeightAnimationDuration = 0
  } = props;
  const style = getLogoStyle$1(logoHeights, menuAnimation, reduceHeightAnimationDuration);
  return /*#__PURE__*/React__default.createElement(React__default.Fragment, null, /*#__PURE__*/React__default.createElement(Link, {
    className: css(style.largeSvg, style.base),
    href: href
  }, /*#__PURE__*/React__default.createElement("img", {
    className: css(style.image),
    alt: alt,
    src: getImageResource$1(svgLarge)
  })), /*#__PURE__*/React__default.createElement(Link, {
    className: css(style.smallSvg, style.base),
    href: href
  }, /*#__PURE__*/React__default.createElement("img", {
    className: css(style.image),
    alt: alt,
    src: getImageResource$1(svgSmall)
  })));
};

let Directions;

(function (Directions) {
  Directions["UP"] = "180deg";
  Directions["DOWN"] = "0deg";
  Directions["LEFT"] = "90deg";
  Directions["RIGHT"] = "270deg";
})(Directions || (Directions = {}));

const getStyle$6 = direction => StyleSheet.create({
  caret: {
    transform: `rotate(${direction})`,
    transition: 'transform 400ms'
  }
});

const ChevronIcon = props => {
  const {
    direction = Directions.DOWN,
    color = '#FFF'
  } = props;
  return /*#__PURE__*/React__default.createElement("svg", {
    className: css(getStyle$6(direction).caret),
    width: "16",
    height: "16",
    viewBox: "0 0 16 16",
    xmlns: "http://www.w3.org/2000/svg"
  }, /*#__PURE__*/React__default.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, /*#__PURE__*/React__default.createElement("path", {
    d: "M11,8.09909655 L6.5,13 L5,11.3663655 L8.09099026,8 L5,4.63363448 L6.5,3 L11,7.90090345 L10.9090097,8 L11,8.09909655 Z",
    fill: color,
    fillRule: "nonzero",
    transform: "rotate(90 8 8)"
  })));
};

const getWrapperStyle$1 = baseUnit => StyleSheet.create({
  wrapper: {
    [MEDIA_BREAKPOINTS.large]: {
      padding: `${baseUnit / 4}px 0`
    }
  }
});

const getContentStyle$1 = (configThemeKey, hovering, isActive, fontSizesStyles, fontSize) => {
  switch (configThemeKey) {
    case 'editionsMenu':
      {
        return getStyleBySize(fontSizesStyles, {
          fontSizeSmall: TYPOGRAPHY_SIZE.BIGGER,
          fontSizeMedium: TYPOGRAPHY_SIZE.BIGGER,
          fontSizeLarge: TYPOGRAPHY_SIZE.BIGGER
        });
      }

    case 'moreLinks':
      {
        const fontSizesForActive = {
          fontSizeSmall: TYPOGRAPHY_SIZE.BIGGER,
          fontSizeMedium: TYPOGRAPHY_SIZE.BIGGER,
          fontSizeLarge: TYPOGRAPHY_SIZE.BIGGER
        };
        const fontSizesForNotActive = {
          fontSizeSmall: fontSize,
          fontSizeMedium: fontSize,
          fontSizeLarge: fontSize
        };
        return getStyleBySize(fontSizesStyles, isActive ? fontSizesForActive : fontSizesForNotActive);
      }

    default:
      return getStyleBySize(fontSizesStyles, {
        fontSizeSmall: TYPOGRAPHY_SIZE.NORMAL,
        fontSizeMedium: TYPOGRAPHY_SIZE.NORMAL,
        fontSizeLarge: TYPOGRAPHY_SIZE.NORMAL
      });
  }
};

const DropDownMenuLink = props => {
  const {
    linkColor,
    hoverColor,
    activeColor,
    href,
    text,
    baseUnit,
    isActive,
    configThemeKey,
    fontSizesStyles,
    fontSize
  } = props;
  const [hovering, setHovering] = useState(false);
  const wrapperStyle = getWrapperStyle$1(baseUnit);
  const contentStyle = getContentStyle$1(configThemeKey, hovering, isActive, fontSizesStyles, fontSize);
  const linkStyle = generateLinkStyleFromTheme({
    idleColor: isActive ? activeColor : linkColor,
    activeStateColor: activeColor,
    hoverStateColor: isActive ? activeColor : hoverColor
  }, {
    width: '100%'
  });
  return /*#__PURE__*/React__default.createElement("li", {
    onMouseEnter: () => setHovering(true),
    onMouseLeave: () => setHovering(false),
    className: css(wrapperStyle.wrapper)
  }, /*#__PURE__*/React__default.createElement(TextLinkComponent, {
    href: href,
    linkStyle: linkStyle
  }, /*#__PURE__*/React__default.createElement(Span, {
    styles: semanticCSS(contentStyle)
  }, text)));
};

const getStyle$7 = theme => {
  const {
    baseUnit,
    menuBackgroundColor
  } = theme;
  return StyleSheet.create({
    menuWrapper: {
      position: 'absolute',
      top: '100%',
      width: '100%',
      minWidth: '160px',
      boxSizing: 'border-box',
      background: menuBackgroundColor,
      listStyle: 'none',
      padding: 0,
      margin: 0,
      display: 'flex',
      flexDirection: 'column',
      zIndex: 100,
      [MEDIA_BREAKPOINTS.large]: {
        padding: `${baseUnit / 4}px ${baseUnit / 2}px`
      }
    }
  });
};

const getLinksList = (menuLinks, linkColor, hoverColor, activeColor, baseUnit, configThemeKey, activeEndpoint, fontSizesStyles, fontSize) => {
  const isActive = navigationLink => navigationLink.link.href === activeEndpoint;

  return menuLinks.map((navigationLink, index) => /*#__PURE__*/React__default.createElement(DropDownMenuLink, {
    key: index,
    href: navigationLink.link.href,
    text: navigationLink.link.text,
    linkColor: linkColor,
    hoverColor: hoverColor,
    activeColor: activeColor,
    baseUnit: baseUnit,
    isActive: isActive(navigationLink),
    configThemeKey: configThemeKey,
    fontSizesStyles: fontSizesStyles,
    fontSize: fontSize
  }));
};

const DropDownMenu = props => {
  const {
    menuLinks,
    configThemeKey,
    activeEndpoint
  } = props;
  const themeProps = useTheme(configThemeKey);
  const {
    boxShadowTypeNormal,
    baseUnit,
    fontSizesStyles,
    linkColor,
    hoverColor,
    activeColor,
    fontSize = TYPOGRAPHY_SIZE.NORMAL
  } = themeProps;
  const style = getStyle$7(themeProps);
  const boxShadowStyle = createStyleForBoxShadow({
    boxShadowTypeNormal
  });
  return /*#__PURE__*/React__default.createElement(ErrorBoundary, null, /*#__PURE__*/React__default.createElement("ul", {
    className: css(style.menuWrapper, boxShadowStyle.style)
  }, getLinksList(menuLinks, linkColor, hoverColor, activeColor, baseUnit, configThemeKey, activeEndpoint, fontSizesStyles, fontSize)));
};

const componentConfigThemeKey$d = 'editions';

const getEditionsStyle = color => {
  return StyleSheet.create({
    editionsWrapper: {
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      position: 'relative',
      [MEDIA_BREAKPOINTS.medium]: {
        display: 'none'
      },
      [MEDIA_BREAKPOINTS.small]: {
        display: 'none'
      }
    },
    editions: {
      alignItems: 'center',
      display: 'flex',
      justifyContent: 'space-between',
      width: '144px',
      color,
      textDecoration: 'none',
      backgroundColor: 'transparent',
      border: 'none',
      outline: 'none',
      ':hover': {
        cursor: 'pointer'
      }
    }
  });
};

const getActiveEdition = (editions, editionEndpoint) => {
  return editions.find(edition => edition.link.href === editionEndpoint) || editions[0];
};
const Editions = props => {
  const {
    editions,
    editionEndpoint
  } = props;
  const {
    color
  } = useTheme(componentConfigThemeKey$d);
  const style = getEditionsStyle(color);
  const activeEdition = getActiveEdition(editions, editionEndpoint);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const editionsClickHandler = useCallback(() => {
    setIsMenuOpen(!isMenuOpen);
  }, [isMenuOpen]);
  return /*#__PURE__*/React__default.createElement(ErrorBoundary, null, /*#__PURE__*/React__default.createElement("div", {
    className: css(style.editionsWrapper)
  }, /*#__PURE__*/React__default.createElement("button", {
    type: "button",
    className: css(style.editions),
    onClick: editionsClickHandler
  }, /*#__PURE__*/React__default.createElement(H4, null, activeEdition.link.text), /*#__PURE__*/React__default.createElement(ChevronIcon, {
    direction: isMenuOpen ? Directions.UP : Directions.DOWN,
    color: color
  })), isMenuOpen && /*#__PURE__*/React__default.createElement(DropDownMenu, {
    menuLinks: editions,
    configThemeKey: "editionsMenu",
    activeEndpoint: editionEndpoint
  })));
};

const componentConfigThemeKey$e = 'ctaButton';
const height = 32;

const getStyle$8 = ({
  backgroundColorNormal,
  textColorNormal,
  borderColorNormal,
  backgroundColorHover,
  textColorHover,
  borderColorHover,
  backgroundColorActive,
  textColorActive,
  borderColorActive,
  baseUnit,
  borderRadius
}) => {
  return StyleSheet.create({
    ctaButton: {
      borderRadius: `${borderRadius}px`,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: backgroundColorNormal,
      color: textColorNormal,
      border: `${borderColorNormal} 1px solid`,
      cursor: 'pointer',
      borderWidth: '1px',
      textDecoration: 'none',
      height: `${height}px`,
      ':hover': {
        backgroundColor: backgroundColorHover,
        color: textColorHover,
        border: `${borderColorHover} 1px solid`
      },
      ':active': {
        backgroundColor: backgroundColorActive,
        color: textColorActive,
        border: `${borderColorActive} 1px solid`
      },
      padding: `0 ${baseUnit / 2}px`
    }
  });
};

const CtaButton = props => {
  const {
    href,
    text
  } = props;
  const themeProps = useTheme(componentConfigThemeKey$e);
  const {
    fontSizesStyles
  } = themeProps;
  const style = getStyle$8(themeProps);
  const linkTextStyle = getStyleBySize(fontSizesStyles, {
    fontSizeSmall: TYPOGRAPHY_SIZE.NORMAL,
    fontSizeMedium: TYPOGRAPHY_SIZE.NORMAL,
    fontSizeLarge: TYPOGRAPHY_SIZE.NORMAL
  });
  return /*#__PURE__*/React__default.createElement(ErrorBoundary, null, /*#__PURE__*/React__default.createElement(Link, {
    className: css(style.ctaButton, linkTextStyle),
    href: href
  }, text));
};

const componentConfigThemeKey$f = 'menuIcon';

const getMenuIconStyle = (isOpen, color) => StyleSheet.create({
  style: {
    height: '16px'
  },
  wrapper: {
    width: '16px',
    height: '16px',
    position: 'relative',
    transition: '.5s ease-in-out',
    cursor: 'pointer'
  },
  span: {
    display: 'block',
    position: 'absolute',
    height: '2px',
    width: '100%',
    backgroundColor: color,
    opacity: 1,
    left: 0,
    transition: '.25s ease-in-out',
    transformOrigin: 'right center'
  },
  firstSpan: (() => {
    return isOpen ? {
      transform: 'rotate(-45deg)',
      width: '130%',
      left: '-6px',
      top: 0
    } : {
      top: 0
    };
  })(),
  secondSpan: (() => {
    return isOpen ? {
      top: '7px',
      transform: 'scaleX(0.1)',
      opacity: 0
    } : {
      top: '7px'
    };
  })(),
  thirdSpan: (() => {
    return isOpen ? {
      transform: 'rotate(45deg)',
      top: '14px',
      width: '130%',
      left: '-6px'
    } : {
      top: '14px'
    };
  })()
});

const MenuIcon = props => {
  const {
    isMenuOpen,
    onClick
  } = props;
  const {
    color
  } = useTheme(componentConfigThemeKey$f);
  const style = getMenuIconStyle(isMenuOpen, color);
  return /*#__PURE__*/React__default.createElement(ErrorBoundary, null, /*#__PURE__*/React__default.createElement("div", {
    className: css(style.wrapper),
    onClick: onClick
  }, /*#__PURE__*/React__default.createElement("span", {
    className: css(style.span, style.firstSpan)
  }), /*#__PURE__*/React__default.createElement("span", {
    className: css(style.span, style.secondSpan)
  }), /*#__PURE__*/React__default.createElement("span", {
    className: css(style.span, style.thirdSpan)
  })));
};

function _extends$1() {
  _extends$1 = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends$1.apply(this, arguments);
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _inheritsLoose(subClass, superClass) {
  subClass.prototype = Object.create(superClass.prototype);
  subClass.prototype.constructor = subClass;
  subClass.__proto__ = superClass;
}

function unwrapExports (x) {
	return x && x.__esModule && Object.prototype.hasOwnProperty.call(x, 'default') ? x['default'] : x;
}

function createCommonjsModule(fn, module) {
	return module = { exports: {} }, fn(module, module.exports), module.exports;
}

var reactIs_production_min = createCommonjsModule(function (module, exports) {

Object.defineProperty(exports, "__esModule", {
  value: !0
});
var b = "function" === typeof Symbol && Symbol.for,
    c = b ? Symbol.for("react.element") : 60103,
    d = b ? Symbol.for("react.portal") : 60106,
    e = b ? Symbol.for("react.fragment") : 60107,
    f = b ? Symbol.for("react.strict_mode") : 60108,
    g = b ? Symbol.for("react.profiler") : 60114,
    h = b ? Symbol.for("react.provider") : 60109,
    k = b ? Symbol.for("react.context") : 60110,
    l = b ? Symbol.for("react.async_mode") : 60111,
    m = b ? Symbol.for("react.concurrent_mode") : 60111,
    n = b ? Symbol.for("react.forward_ref") : 60112,
    p = b ? Symbol.for("react.suspense") : 60113,
    q = b ? Symbol.for("react.suspense_list") : 60120,
    r = b ? Symbol.for("react.memo") : 60115,
    t = b ? Symbol.for("react.lazy") : 60116,
    v = b ? Symbol.for("react.fundamental") : 60117,
    w = b ? Symbol.for("react.responder") : 60118,
    x = b ? Symbol.for("react.scope") : 60119;

function y(a) {
  if ("object" === typeof a && null !== a) {
    var u = a.$$typeof;

    switch (u) {
      case c:
        switch (a = a.type, a) {
          case l:
          case m:
          case e:
          case g:
          case f:
          case p:
            return a;

          default:
            switch (a = a && a.$$typeof, a) {
              case k:
              case n:
              case t:
              case r:
              case h:
                return a;

              default:
                return u;
            }

        }

      case d:
        return u;
    }
  }
}

function z(a) {
  return y(a) === m;
}

exports.typeOf = y;
exports.AsyncMode = l;
exports.ConcurrentMode = m;
exports.ContextConsumer = k;
exports.ContextProvider = h;
exports.Element = c;
exports.ForwardRef = n;
exports.Fragment = e;
exports.Lazy = t;
exports.Memo = r;
exports.Portal = d;
exports.Profiler = g;
exports.StrictMode = f;
exports.Suspense = p;

exports.isValidElementType = function (a) {
  return "string" === typeof a || "function" === typeof a || a === e || a === m || a === g || a === f || a === p || a === q || "object" === typeof a && null !== a && (a.$$typeof === t || a.$$typeof === r || a.$$typeof === h || a.$$typeof === k || a.$$typeof === n || a.$$typeof === v || a.$$typeof === w || a.$$typeof === x);
};

exports.isAsyncMode = function (a) {
  return z(a) || y(a) === l;
};

exports.isConcurrentMode = z;

exports.isContextConsumer = function (a) {
  return y(a) === k;
};

exports.isContextProvider = function (a) {
  return y(a) === h;
};

exports.isElement = function (a) {
  return "object" === typeof a && null !== a && a.$$typeof === c;
};

exports.isForwardRef = function (a) {
  return y(a) === n;
};

exports.isFragment = function (a) {
  return y(a) === e;
};

exports.isLazy = function (a) {
  return y(a) === t;
};

exports.isMemo = function (a) {
  return y(a) === r;
};

exports.isPortal = function (a) {
  return y(a) === d;
};

exports.isProfiler = function (a) {
  return y(a) === g;
};

exports.isStrictMode = function (a) {
  return y(a) === f;
};

exports.isSuspense = function (a) {
  return y(a) === p;
};
});

unwrapExports(reactIs_production_min);
var reactIs_production_min_1 = reactIs_production_min.typeOf;
var reactIs_production_min_2 = reactIs_production_min.AsyncMode;
var reactIs_production_min_3 = reactIs_production_min.ConcurrentMode;
var reactIs_production_min_4 = reactIs_production_min.ContextConsumer;
var reactIs_production_min_5 = reactIs_production_min.ContextProvider;
var reactIs_production_min_6 = reactIs_production_min.Element;
var reactIs_production_min_7 = reactIs_production_min.ForwardRef;
var reactIs_production_min_8 = reactIs_production_min.Fragment;
var reactIs_production_min_9 = reactIs_production_min.Lazy;
var reactIs_production_min_10 = reactIs_production_min.Memo;
var reactIs_production_min_11 = reactIs_production_min.Portal;
var reactIs_production_min_12 = reactIs_production_min.Profiler;
var reactIs_production_min_13 = reactIs_production_min.StrictMode;
var reactIs_production_min_14 = reactIs_production_min.Suspense;
var reactIs_production_min_15 = reactIs_production_min.isValidElementType;
var reactIs_production_min_16 = reactIs_production_min.isAsyncMode;
var reactIs_production_min_17 = reactIs_production_min.isConcurrentMode;
var reactIs_production_min_18 = reactIs_production_min.isContextConsumer;
var reactIs_production_min_19 = reactIs_production_min.isContextProvider;
var reactIs_production_min_20 = reactIs_production_min.isElement;
var reactIs_production_min_21 = reactIs_production_min.isForwardRef;
var reactIs_production_min_22 = reactIs_production_min.isFragment;
var reactIs_production_min_23 = reactIs_production_min.isLazy;
var reactIs_production_min_24 = reactIs_production_min.isMemo;
var reactIs_production_min_25 = reactIs_production_min.isPortal;
var reactIs_production_min_26 = reactIs_production_min.isProfiler;
var reactIs_production_min_27 = reactIs_production_min.isStrictMode;
var reactIs_production_min_28 = reactIs_production_min.isSuspense;

var reactIs_development = createCommonjsModule(function (module, exports) {

if (process.env.NODE_ENV !== "production") {
  (function () {

    Object.defineProperty(exports, '__esModule', {
      value: true
    }); // The Symbol used to tag the ReactElement-like types. If there is no native Symbol
    // nor polyfill, then a plain number is used for performance.

    var hasSymbol = typeof Symbol === 'function' && Symbol.for;
    var REACT_ELEMENT_TYPE = hasSymbol ? Symbol.for('react.element') : 0xeac7;
    var REACT_PORTAL_TYPE = hasSymbol ? Symbol.for('react.portal') : 0xeaca;
    var REACT_FRAGMENT_TYPE = hasSymbol ? Symbol.for('react.fragment') : 0xeacb;
    var REACT_STRICT_MODE_TYPE = hasSymbol ? Symbol.for('react.strict_mode') : 0xeacc;
    var REACT_PROFILER_TYPE = hasSymbol ? Symbol.for('react.profiler') : 0xead2;
    var REACT_PROVIDER_TYPE = hasSymbol ? Symbol.for('react.provider') : 0xeacd;
    var REACT_CONTEXT_TYPE = hasSymbol ? Symbol.for('react.context') : 0xeace; // TODO: We don't use AsyncMode or ConcurrentMode anymore. They were temporary
    // (unstable) APIs that have been removed. Can we remove the symbols?

    var REACT_ASYNC_MODE_TYPE = hasSymbol ? Symbol.for('react.async_mode') : 0xeacf;
    var REACT_CONCURRENT_MODE_TYPE = hasSymbol ? Symbol.for('react.concurrent_mode') : 0xeacf;
    var REACT_FORWARD_REF_TYPE = hasSymbol ? Symbol.for('react.forward_ref') : 0xead0;
    var REACT_SUSPENSE_TYPE = hasSymbol ? Symbol.for('react.suspense') : 0xead1;
    var REACT_SUSPENSE_LIST_TYPE = hasSymbol ? Symbol.for('react.suspense_list') : 0xead8;
    var REACT_MEMO_TYPE = hasSymbol ? Symbol.for('react.memo') : 0xead3;
    var REACT_LAZY_TYPE = hasSymbol ? Symbol.for('react.lazy') : 0xead4;
    var REACT_FUNDAMENTAL_TYPE = hasSymbol ? Symbol.for('react.fundamental') : 0xead5;
    var REACT_RESPONDER_TYPE = hasSymbol ? Symbol.for('react.responder') : 0xead6;
    var REACT_SCOPE_TYPE = hasSymbol ? Symbol.for('react.scope') : 0xead7;

    function isValidElementType(type) {
      return typeof type === 'string' || typeof type === 'function' || // Note: its typeof might be other than 'symbol' or 'number' if it's a polyfill.
      type === REACT_FRAGMENT_TYPE || type === REACT_CONCURRENT_MODE_TYPE || type === REACT_PROFILER_TYPE || type === REACT_STRICT_MODE_TYPE || type === REACT_SUSPENSE_TYPE || type === REACT_SUSPENSE_LIST_TYPE || typeof type === 'object' && type !== null && (type.$$typeof === REACT_LAZY_TYPE || type.$$typeof === REACT_MEMO_TYPE || type.$$typeof === REACT_PROVIDER_TYPE || type.$$typeof === REACT_CONTEXT_TYPE || type.$$typeof === REACT_FORWARD_REF_TYPE || type.$$typeof === REACT_FUNDAMENTAL_TYPE || type.$$typeof === REACT_RESPONDER_TYPE || type.$$typeof === REACT_SCOPE_TYPE);
    }
    /**
     * Forked from fbjs/warning:
     * https://github.com/facebook/fbjs/blob/e66ba20ad5be433eb54423f2b097d829324d9de6/packages/fbjs/src/__forks__/warning.js
     *
     * Only change is we use console.warn instead of console.error,
     * and do nothing when 'console' is not supported.
     * This really simplifies the code.
     * ---
     * Similar to invariant but only logs a warning if the condition is not met.
     * This can be used to log issues in development environments in critical
     * paths. Removing the logging code for production environments will keep the
     * same logic and follow the same code paths.
     */


    var lowPriorityWarningWithoutStack = function () {};

    {
      var printWarning = function (format) {
        for (var _len = arguments.length, args = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
          args[_key - 1] = arguments[_key];
        }

        var argIndex = 0;
        var message = 'Warning: ' + format.replace(/%s/g, function () {
          return args[argIndex++];
        });

        if (typeof console !== 'undefined') {
          console.warn(message);
        }

        try {
          // --- Welcome to debugging React ---
          // This error was thrown as a convenience so that you can use this stack
          // to find the callsite that caused this warning to fire.
          throw new Error(message);
        } catch (x) {}
      };

      lowPriorityWarningWithoutStack = function (condition, format) {
        if (format === undefined) {
          throw new Error('`lowPriorityWarningWithoutStack(condition, format, ...args)` requires a warning ' + 'message argument');
        }

        if (!condition) {
          for (var _len2 = arguments.length, args = new Array(_len2 > 2 ? _len2 - 2 : 0), _key2 = 2; _key2 < _len2; _key2++) {
            args[_key2 - 2] = arguments[_key2];
          }

          printWarning.apply(void 0, [format].concat(args));
        }
      };
    }
    var lowPriorityWarningWithoutStack$1 = lowPriorityWarningWithoutStack;

    function typeOf(object) {
      if (typeof object === 'object' && object !== null) {
        var $$typeof = object.$$typeof;

        switch ($$typeof) {
          case REACT_ELEMENT_TYPE:
            var type = object.type;

            switch (type) {
              case REACT_ASYNC_MODE_TYPE:
              case REACT_CONCURRENT_MODE_TYPE:
              case REACT_FRAGMENT_TYPE:
              case REACT_PROFILER_TYPE:
              case REACT_STRICT_MODE_TYPE:
              case REACT_SUSPENSE_TYPE:
                return type;

              default:
                var $$typeofType = type && type.$$typeof;

                switch ($$typeofType) {
                  case REACT_CONTEXT_TYPE:
                  case REACT_FORWARD_REF_TYPE:
                  case REACT_LAZY_TYPE:
                  case REACT_MEMO_TYPE:
                  case REACT_PROVIDER_TYPE:
                    return $$typeofType;

                  default:
                    return $$typeof;
                }

            }

          case REACT_PORTAL_TYPE:
            return $$typeof;
        }
      }

      return undefined;
    } // AsyncMode is deprecated along with isAsyncMode


    var AsyncMode = REACT_ASYNC_MODE_TYPE;
    var ConcurrentMode = REACT_CONCURRENT_MODE_TYPE;
    var ContextConsumer = REACT_CONTEXT_TYPE;
    var ContextProvider = REACT_PROVIDER_TYPE;
    var Element = REACT_ELEMENT_TYPE;
    var ForwardRef = REACT_FORWARD_REF_TYPE;
    var Fragment = REACT_FRAGMENT_TYPE;
    var Lazy = REACT_LAZY_TYPE;
    var Memo = REACT_MEMO_TYPE;
    var Portal = REACT_PORTAL_TYPE;
    var Profiler = REACT_PROFILER_TYPE;
    var StrictMode = REACT_STRICT_MODE_TYPE;
    var Suspense = REACT_SUSPENSE_TYPE;
    var hasWarnedAboutDeprecatedIsAsyncMode = false; // AsyncMode should be deprecated

    function isAsyncMode(object) {
      {
        if (!hasWarnedAboutDeprecatedIsAsyncMode) {
          hasWarnedAboutDeprecatedIsAsyncMode = true;
          lowPriorityWarningWithoutStack$1(false, 'The ReactIs.isAsyncMode() alias has been deprecated, ' + 'and will be removed in React 17+. Update your code to use ' + 'ReactIs.isConcurrentMode() instead. It has the exact same API.');
        }
      }
      return isConcurrentMode(object) || typeOf(object) === REACT_ASYNC_MODE_TYPE;
    }

    function isConcurrentMode(object) {
      return typeOf(object) === REACT_CONCURRENT_MODE_TYPE;
    }

    function isContextConsumer(object) {
      return typeOf(object) === REACT_CONTEXT_TYPE;
    }

    function isContextProvider(object) {
      return typeOf(object) === REACT_PROVIDER_TYPE;
    }

    function isElement(object) {
      return typeof object === 'object' && object !== null && object.$$typeof === REACT_ELEMENT_TYPE;
    }

    function isForwardRef(object) {
      return typeOf(object) === REACT_FORWARD_REF_TYPE;
    }

    function isFragment(object) {
      return typeOf(object) === REACT_FRAGMENT_TYPE;
    }

    function isLazy(object) {
      return typeOf(object) === REACT_LAZY_TYPE;
    }

    function isMemo(object) {
      return typeOf(object) === REACT_MEMO_TYPE;
    }

    function isPortal(object) {
      return typeOf(object) === REACT_PORTAL_TYPE;
    }

    function isProfiler(object) {
      return typeOf(object) === REACT_PROFILER_TYPE;
    }

    function isStrictMode(object) {
      return typeOf(object) === REACT_STRICT_MODE_TYPE;
    }

    function isSuspense(object) {
      return typeOf(object) === REACT_SUSPENSE_TYPE;
    }

    exports.typeOf = typeOf;
    exports.AsyncMode = AsyncMode;
    exports.ConcurrentMode = ConcurrentMode;
    exports.ContextConsumer = ContextConsumer;
    exports.ContextProvider = ContextProvider;
    exports.Element = Element;
    exports.ForwardRef = ForwardRef;
    exports.Fragment = Fragment;
    exports.Lazy = Lazy;
    exports.Memo = Memo;
    exports.Portal = Portal;
    exports.Profiler = Profiler;
    exports.StrictMode = StrictMode;
    exports.Suspense = Suspense;
    exports.isValidElementType = isValidElementType;
    exports.isAsyncMode = isAsyncMode;
    exports.isConcurrentMode = isConcurrentMode;
    exports.isContextConsumer = isContextConsumer;
    exports.isContextProvider = isContextProvider;
    exports.isElement = isElement;
    exports.isForwardRef = isForwardRef;
    exports.isFragment = isFragment;
    exports.isLazy = isLazy;
    exports.isMemo = isMemo;
    exports.isPortal = isPortal;
    exports.isProfiler = isProfiler;
    exports.isStrictMode = isStrictMode;
    exports.isSuspense = isSuspense;
  })();
}
});

unwrapExports(reactIs_development);
var reactIs_development_1 = reactIs_development.typeOf;
var reactIs_development_2 = reactIs_development.AsyncMode;
var reactIs_development_3 = reactIs_development.ConcurrentMode;
var reactIs_development_4 = reactIs_development.ContextConsumer;
var reactIs_development_5 = reactIs_development.ContextProvider;
var reactIs_development_6 = reactIs_development.Element;
var reactIs_development_7 = reactIs_development.ForwardRef;
var reactIs_development_8 = reactIs_development.Fragment;
var reactIs_development_9 = reactIs_development.Lazy;
var reactIs_development_10 = reactIs_development.Memo;
var reactIs_development_11 = reactIs_development.Portal;
var reactIs_development_12 = reactIs_development.Profiler;
var reactIs_development_13 = reactIs_development.StrictMode;
var reactIs_development_14 = reactIs_development.Suspense;
var reactIs_development_15 = reactIs_development.isValidElementType;
var reactIs_development_16 = reactIs_development.isAsyncMode;
var reactIs_development_17 = reactIs_development.isConcurrentMode;
var reactIs_development_18 = reactIs_development.isContextConsumer;
var reactIs_development_19 = reactIs_development.isContextProvider;
var reactIs_development_20 = reactIs_development.isElement;
var reactIs_development_21 = reactIs_development.isForwardRef;
var reactIs_development_22 = reactIs_development.isFragment;
var reactIs_development_23 = reactIs_development.isLazy;
var reactIs_development_24 = reactIs_development.isMemo;
var reactIs_development_25 = reactIs_development.isPortal;
var reactIs_development_26 = reactIs_development.isProfiler;
var reactIs_development_27 = reactIs_development.isStrictMode;
var reactIs_development_28 = reactIs_development.isSuspense;

var reactIs = createCommonjsModule(function (module) {

if (process.env.NODE_ENV === 'production') {
  module.exports = reactIs_production_min;
} else {
  module.exports = reactIs_development;
}
});

/*
object-assign
(c) Sindre Sorhus
@license MIT
*/
/* eslint-disable no-unused-vars */

var getOwnPropertySymbols = Object.getOwnPropertySymbols;
var hasOwnProperty = Object.prototype.hasOwnProperty;
var propIsEnumerable = Object.prototype.propertyIsEnumerable;

function toObject(val) {
  if (val === null || val === undefined) {
    throw new TypeError('Object.assign cannot be called with null or undefined');
  }

  return Object(val);
}

function shouldUseNative() {
  try {
    if (!Object.assign) {
      return false;
    } // Detect buggy property enumeration order in older V8 versions.
    // https://bugs.chromium.org/p/v8/issues/detail?id=4118


    var test1 = new String('abc'); // eslint-disable-line no-new-wrappers

    test1[5] = 'de';

    if (Object.getOwnPropertyNames(test1)[0] === '5') {
      return false;
    } // https://bugs.chromium.org/p/v8/issues/detail?id=3056


    var test2 = {};

    for (var i = 0; i < 10; i++) {
      test2['_' + String.fromCharCode(i)] = i;
    }

    var order2 = Object.getOwnPropertyNames(test2).map(function (n) {
      return test2[n];
    });

    if (order2.join('') !== '0123456789') {
      return false;
    } // https://bugs.chromium.org/p/v8/issues/detail?id=3056


    var test3 = {};
    'abcdefghijklmnopqrst'.split('').forEach(function (letter) {
      test3[letter] = letter;
    });

    if (Object.keys(Object.assign({}, test3)).join('') !== 'abcdefghijklmnopqrst') {
      return false;
    }

    return true;
  } catch (err) {
    // We don't expect any of the above to throw, but better to be safe.
    return false;
  }
}

var objectAssign = shouldUseNative() ? Object.assign : function (target, source) {
  var from;
  var to = toObject(target);
  var symbols;

  for (var s = 1; s < arguments.length; s++) {
    from = Object(arguments[s]);

    for (var key in from) {
      if (hasOwnProperty.call(from, key)) {
        to[key] = from[key];
      }
    }

    if (getOwnPropertySymbols) {
      symbols = getOwnPropertySymbols(from);

      for (var i = 0; i < symbols.length; i++) {
        if (propIsEnumerable.call(from, symbols[i])) {
          to[symbols[i]] = from[symbols[i]];
        }
      }
    }
  }

  return to;
};

/**
 * Copyright (c) 2013-present, Facebook, Inc.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

var ReactPropTypesSecret = 'SECRET_DO_NOT_PASS_THIS_OR_YOU_WILL_BE_FIRED';
var ReactPropTypesSecret_1 = ReactPropTypesSecret;

var printWarning = function () {};

if (process.env.NODE_ENV !== 'production') {
  var ReactPropTypesSecret$1 = ReactPropTypesSecret_1;

  var loggedTypeFailures = {};
  var has = Function.call.bind(Object.prototype.hasOwnProperty);

  printWarning = function (text) {
    var message = 'Warning: ' + text;

    if (typeof console !== 'undefined') {
      console.error(message);
    }

    try {
      // --- Welcome to debugging React ---
      // This error was thrown as a convenience so that you can use this stack
      // to find the callsite that caused this warning to fire.
      throw new Error(message);
    } catch (x) {}
  };
}
/**
 * Assert that the values match with the type specs.
 * Error messages are memorized and will only be shown once.
 *
 * @param {object} typeSpecs Map of name to a ReactPropType
 * @param {object} values Runtime values that need to be type-checked
 * @param {string} location e.g. "prop", "context", "child context"
 * @param {string} componentName Name of the component for error messages.
 * @param {?Function} getStack Returns the component stack.
 * @private
 */


function checkPropTypes(typeSpecs, values, location, componentName, getStack) {
  if (process.env.NODE_ENV !== 'production') {
    for (var typeSpecName in typeSpecs) {
      if (has(typeSpecs, typeSpecName)) {
        var error; // Prop type validation may throw. In case they do, we don't want to
        // fail the render phase where it didn't fail before. So we log it.
        // After these have been cleaned up, we'll let them throw.

        try {
          // This is intentionally an invariant that gets caught. It's the same
          // behavior as without this statement except with a better message.
          if (typeof typeSpecs[typeSpecName] !== 'function') {
            var err = Error((componentName || 'React class') + ': ' + location + ' type `' + typeSpecName + '` is invalid; ' + 'it must be a function, usually from the `prop-types` package, but received `' + typeof typeSpecs[typeSpecName] + '`.');
            err.name = 'Invariant Violation';
            throw err;
          }

          error = typeSpecs[typeSpecName](values, typeSpecName, componentName, location, null, ReactPropTypesSecret$1);
        } catch (ex) {
          error = ex;
        }

        if (error && !(error instanceof Error)) {
          printWarning((componentName || 'React class') + ': type specification of ' + location + ' `' + typeSpecName + '` is invalid; the type checker ' + 'function must return `null` or an `Error` but returned a ' + typeof error + '. ' + 'You may have forgotten to pass an argument to the type checker ' + 'creator (arrayOf, instanceOf, objectOf, oneOf, oneOfType, and ' + 'shape all require an argument).');
        }

        if (error instanceof Error && !(error.message in loggedTypeFailures)) {
          // Only monitor this failure once because there tends to be a lot of the
          // same error.
          loggedTypeFailures[error.message] = true;
          var stack = getStack ? getStack() : '';
          printWarning('Failed ' + location + ' type: ' + error.message + (stack != null ? stack : ''));
        }
      }
    }
  }
}
/**
 * Resets warning cache when testing.
 *
 * @private
 */


checkPropTypes.resetWarningCache = function () {
  if (process.env.NODE_ENV !== 'production') {
    loggedTypeFailures = {};
  }
};

var checkPropTypes_1 = checkPropTypes;

var has$1 = Function.call.bind(Object.prototype.hasOwnProperty);

var printWarning$1 = function () {};

if (process.env.NODE_ENV !== 'production') {
  printWarning$1 = function (text) {
    var message = 'Warning: ' + text;

    if (typeof console !== 'undefined') {
      console.error(message);
    }

    try {
      // --- Welcome to debugging React ---
      // This error was thrown as a convenience so that you can use this stack
      // to find the callsite that caused this warning to fire.
      throw new Error(message);
    } catch (x) {}
  };
}

function emptyFunctionThatReturnsNull() {
  return null;
}

var factoryWithTypeCheckers = function (isValidElement, throwOnDirectAccess) {
  /* global Symbol */
  var ITERATOR_SYMBOL = typeof Symbol === 'function' && Symbol.iterator;
  var FAUX_ITERATOR_SYMBOL = '@@iterator'; // Before Symbol spec.

  /**
   * Returns the iterator method function contained on the iterable object.
   *
   * Be sure to invoke the function with the iterable as context:
   *
   *     var iteratorFn = getIteratorFn(myIterable);
   *     if (iteratorFn) {
   *       var iterator = iteratorFn.call(myIterable);
   *       ...
   *     }
   *
   * @param {?object} maybeIterable
   * @return {?function}
   */

  function getIteratorFn(maybeIterable) {
    var iteratorFn = maybeIterable && (ITERATOR_SYMBOL && maybeIterable[ITERATOR_SYMBOL] || maybeIterable[FAUX_ITERATOR_SYMBOL]);

    if (typeof iteratorFn === 'function') {
      return iteratorFn;
    }
  }
  /**
   * Collection of methods that allow declaration and validation of props that are
   * supplied to React components. Example usage:
   *
   *   var Props = require('ReactPropTypes');
   *   var MyArticle = React.createClass({
   *     propTypes: {
   *       // An optional string prop named "description".
   *       description: Props.string,
   *
   *       // A required enum prop named "category".
   *       category: Props.oneOf(['News','Photos']).isRequired,
   *
   *       // A prop named "dialog" that requires an instance of Dialog.
   *       dialog: Props.instanceOf(Dialog).isRequired
   *     },
   *     render: function() { ... }
   *   });
   *
   * A more formal specification of how these methods are used:
   *
   *   type := array|bool|func|object|number|string|oneOf([...])|instanceOf(...)
   *   decl := ReactPropTypes.{type}(.isRequired)?
   *
   * Each and every declaration produces a function with the same signature. This
   * allows the creation of custom validation functions. For example:
   *
   *  var MyLink = React.createClass({
   *    propTypes: {
   *      // An optional string or URI prop named "href".
   *      href: function(props, propName, componentName) {
   *        var propValue = props[propName];
   *        if (propValue != null && typeof propValue !== 'string' &&
   *            !(propValue instanceof URI)) {
   *          return new Error(
   *            'Expected a string or an URI for ' + propName + ' in ' +
   *            componentName
   *          );
   *        }
   *      }
   *    },
   *    render: function() {...}
   *  });
   *
   * @internal
   */


  var ANONYMOUS = '<<anonymous>>'; // Important!
  // Keep this list in sync with production version in `./factoryWithThrowingShims.js`.

  var ReactPropTypes = {
    array: createPrimitiveTypeChecker('array'),
    bool: createPrimitiveTypeChecker('boolean'),
    func: createPrimitiveTypeChecker('function'),
    number: createPrimitiveTypeChecker('number'),
    object: createPrimitiveTypeChecker('object'),
    string: createPrimitiveTypeChecker('string'),
    symbol: createPrimitiveTypeChecker('symbol'),
    any: createAnyTypeChecker(),
    arrayOf: createArrayOfTypeChecker,
    element: createElementTypeChecker(),
    elementType: createElementTypeTypeChecker(),
    instanceOf: createInstanceTypeChecker,
    node: createNodeChecker(),
    objectOf: createObjectOfTypeChecker,
    oneOf: createEnumTypeChecker,
    oneOfType: createUnionTypeChecker,
    shape: createShapeTypeChecker,
    exact: createStrictShapeTypeChecker
  };
  /**
   * inlined Object.is polyfill to avoid requiring consumers ship their own
   * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Object/is
   */

  /*eslint-disable no-self-compare*/

  function is(x, y) {
    // SameValue algorithm
    if (x === y) {
      // Steps 1-5, 7-10
      // Steps 6.b-6.e: +0 != -0
      return x !== 0 || 1 / x === 1 / y;
    } else {
      // Step 6.a: NaN == NaN
      return x !== x && y !== y;
    }
  }
  /*eslint-enable no-self-compare*/

  /**
   * We use an Error-like object for backward compatibility as people may call
   * PropTypes directly and inspect their output. However, we don't use real
   * Errors anymore. We don't inspect their stack anyway, and creating them
   * is prohibitively expensive if they are created too often, such as what
   * happens in oneOfType() for any type before the one that matched.
   */


  function PropTypeError(message) {
    this.message = message;
    this.stack = '';
  } // Make `instanceof Error` still work for returned errors.


  PropTypeError.prototype = Error.prototype;

  function createChainableTypeChecker(validate) {
    if (process.env.NODE_ENV !== 'production') {
      var manualPropTypeCallCache = {};
      var manualPropTypeWarningCount = 0;
    }

    function checkType(isRequired, props, propName, componentName, location, propFullName, secret) {
      componentName = componentName || ANONYMOUS;
      propFullName = propFullName || propName;

      if (secret !== ReactPropTypesSecret_1) {
        if (throwOnDirectAccess) {
          // New behavior only for users of `prop-types` package
          var err = new Error('Calling PropTypes validators directly is not supported by the `prop-types` package. ' + 'Use `PropTypes.checkPropTypes()` to call them. ' + 'Read more at http://fb.me/use-check-prop-types');
          err.name = 'Invariant Violation';
          throw err;
        } else if (process.env.NODE_ENV !== 'production' && typeof console !== 'undefined') {
          // Old behavior for people using React.PropTypes
          var cacheKey = componentName + ':' + propName;

          if (!manualPropTypeCallCache[cacheKey] && // Avoid spamming the console because they are often not actionable except for lib authors
          manualPropTypeWarningCount < 3) {
            printWarning$1('You are manually calling a React.PropTypes validation ' + 'function for the `' + propFullName + '` prop on `' + componentName + '`. This is deprecated ' + 'and will throw in the standalone `prop-types` package. ' + 'You may be seeing this warning due to a third-party PropTypes ' + 'library. See https://fb.me/react-warning-dont-call-proptypes ' + 'for details.');
            manualPropTypeCallCache[cacheKey] = true;
            manualPropTypeWarningCount++;
          }
        }
      }

      if (props[propName] == null) {
        if (isRequired) {
          if (props[propName] === null) {
            return new PropTypeError('The ' + location + ' `' + propFullName + '` is marked as required ' + ('in `' + componentName + '`, but its value is `null`.'));
          }

          return new PropTypeError('The ' + location + ' `' + propFullName + '` is marked as required in ' + ('`' + componentName + '`, but its value is `undefined`.'));
        }

        return null;
      } else {
        return validate(props, propName, componentName, location, propFullName);
      }
    }

    var chainedCheckType = checkType.bind(null, false);
    chainedCheckType.isRequired = checkType.bind(null, true);
    return chainedCheckType;
  }

  function createPrimitiveTypeChecker(expectedType) {
    function validate(props, propName, componentName, location, propFullName, secret) {
      var propValue = props[propName];
      var propType = getPropType(propValue);

      if (propType !== expectedType) {
        // `propValue` being instance of, say, date/regexp, pass the 'object'
        // check, but we can offer a more precise error message here rather than
        // 'of type `object`'.
        var preciseType = getPreciseType(propValue);
        return new PropTypeError('Invalid ' + location + ' `' + propFullName + '` of type ' + ('`' + preciseType + '` supplied to `' + componentName + '`, expected ') + ('`' + expectedType + '`.'));
      }

      return null;
    }

    return createChainableTypeChecker(validate);
  }

  function createAnyTypeChecker() {
    return createChainableTypeChecker(emptyFunctionThatReturnsNull);
  }

  function createArrayOfTypeChecker(typeChecker) {
    function validate(props, propName, componentName, location, propFullName) {
      if (typeof typeChecker !== 'function') {
        return new PropTypeError('Property `' + propFullName + '` of component `' + componentName + '` has invalid PropType notation inside arrayOf.');
      }

      var propValue = props[propName];

      if (!Array.isArray(propValue)) {
        var propType = getPropType(propValue);
        return new PropTypeError('Invalid ' + location + ' `' + propFullName + '` of type ' + ('`' + propType + '` supplied to `' + componentName + '`, expected an array.'));
      }

      for (var i = 0; i < propValue.length; i++) {
        var error = typeChecker(propValue, i, componentName, location, propFullName + '[' + i + ']', ReactPropTypesSecret_1);

        if (error instanceof Error) {
          return error;
        }
      }

      return null;
    }

    return createChainableTypeChecker(validate);
  }

  function createElementTypeChecker() {
    function validate(props, propName, componentName, location, propFullName) {
      var propValue = props[propName];

      if (!isValidElement(propValue)) {
        var propType = getPropType(propValue);
        return new PropTypeError('Invalid ' + location + ' `' + propFullName + '` of type ' + ('`' + propType + '` supplied to `' + componentName + '`, expected a single ReactElement.'));
      }

      return null;
    }

    return createChainableTypeChecker(validate);
  }

  function createElementTypeTypeChecker() {
    function validate(props, propName, componentName, location, propFullName) {
      var propValue = props[propName];

      if (!reactIs.isValidElementType(propValue)) {
        var propType = getPropType(propValue);
        return new PropTypeError('Invalid ' + location + ' `' + propFullName + '` of type ' + ('`' + propType + '` supplied to `' + componentName + '`, expected a single ReactElement type.'));
      }

      return null;
    }

    return createChainableTypeChecker(validate);
  }

  function createInstanceTypeChecker(expectedClass) {
    function validate(props, propName, componentName, location, propFullName) {
      if (!(props[propName] instanceof expectedClass)) {
        var expectedClassName = expectedClass.name || ANONYMOUS;
        var actualClassName = getClassName(props[propName]);
        return new PropTypeError('Invalid ' + location + ' `' + propFullName + '` of type ' + ('`' + actualClassName + '` supplied to `' + componentName + '`, expected ') + ('instance of `' + expectedClassName + '`.'));
      }

      return null;
    }

    return createChainableTypeChecker(validate);
  }

  function createEnumTypeChecker(expectedValues) {
    if (!Array.isArray(expectedValues)) {
      if (process.env.NODE_ENV !== 'production') {
        if (arguments.length > 1) {
          printWarning$1('Invalid arguments supplied to oneOf, expected an array, got ' + arguments.length + ' arguments. ' + 'A common mistake is to write oneOf(x, y, z) instead of oneOf([x, y, z]).');
        } else {
          printWarning$1('Invalid argument supplied to oneOf, expected an array.');
        }
      }

      return emptyFunctionThatReturnsNull;
    }

    function validate(props, propName, componentName, location, propFullName) {
      var propValue = props[propName];

      for (var i = 0; i < expectedValues.length; i++) {
        if (is(propValue, expectedValues[i])) {
          return null;
        }
      }

      var valuesString = JSON.stringify(expectedValues, function replacer(key, value) {
        var type = getPreciseType(value);

        if (type === 'symbol') {
          return String(value);
        }

        return value;
      });
      return new PropTypeError('Invalid ' + location + ' `' + propFullName + '` of value `' + String(propValue) + '` ' + ('supplied to `' + componentName + '`, expected one of ' + valuesString + '.'));
    }

    return createChainableTypeChecker(validate);
  }

  function createObjectOfTypeChecker(typeChecker) {
    function validate(props, propName, componentName, location, propFullName) {
      if (typeof typeChecker !== 'function') {
        return new PropTypeError('Property `' + propFullName + '` of component `' + componentName + '` has invalid PropType notation inside objectOf.');
      }

      var propValue = props[propName];
      var propType = getPropType(propValue);

      if (propType !== 'object') {
        return new PropTypeError('Invalid ' + location + ' `' + propFullName + '` of type ' + ('`' + propType + '` supplied to `' + componentName + '`, expected an object.'));
      }

      for (var key in propValue) {
        if (has$1(propValue, key)) {
          var error = typeChecker(propValue, key, componentName, location, propFullName + '.' + key, ReactPropTypesSecret_1);

          if (error instanceof Error) {
            return error;
          }
        }
      }

      return null;
    }

    return createChainableTypeChecker(validate);
  }

  function createUnionTypeChecker(arrayOfTypeCheckers) {
    if (!Array.isArray(arrayOfTypeCheckers)) {
      process.env.NODE_ENV !== 'production' ? printWarning$1('Invalid argument supplied to oneOfType, expected an instance of array.') : void 0;
      return emptyFunctionThatReturnsNull;
    }

    for (var i = 0; i < arrayOfTypeCheckers.length; i++) {
      var checker = arrayOfTypeCheckers[i];

      if (typeof checker !== 'function') {
        printWarning$1('Invalid argument supplied to oneOfType. Expected an array of check functions, but ' + 'received ' + getPostfixForTypeWarning(checker) + ' at index ' + i + '.');
        return emptyFunctionThatReturnsNull;
      }
    }

    function validate(props, propName, componentName, location, propFullName) {
      for (var i = 0; i < arrayOfTypeCheckers.length; i++) {
        var checker = arrayOfTypeCheckers[i];

        if (checker(props, propName, componentName, location, propFullName, ReactPropTypesSecret_1) == null) {
          return null;
        }
      }

      return new PropTypeError('Invalid ' + location + ' `' + propFullName + '` supplied to ' + ('`' + componentName + '`.'));
    }

    return createChainableTypeChecker(validate);
  }

  function createNodeChecker() {
    function validate(props, propName, componentName, location, propFullName) {
      if (!isNode(props[propName])) {
        return new PropTypeError('Invalid ' + location + ' `' + propFullName + '` supplied to ' + ('`' + componentName + '`, expected a ReactNode.'));
      }

      return null;
    }

    return createChainableTypeChecker(validate);
  }

  function createShapeTypeChecker(shapeTypes) {
    function validate(props, propName, componentName, location, propFullName) {
      var propValue = props[propName];
      var propType = getPropType(propValue);

      if (propType !== 'object') {
        return new PropTypeError('Invalid ' + location + ' `' + propFullName + '` of type `' + propType + '` ' + ('supplied to `' + componentName + '`, expected `object`.'));
      }

      for (var key in shapeTypes) {
        var checker = shapeTypes[key];

        if (!checker) {
          continue;
        }

        var error = checker(propValue, key, componentName, location, propFullName + '.' + key, ReactPropTypesSecret_1);

        if (error) {
          return error;
        }
      }

      return null;
    }

    return createChainableTypeChecker(validate);
  }

  function createStrictShapeTypeChecker(shapeTypes) {
    function validate(props, propName, componentName, location, propFullName) {
      var propValue = props[propName];
      var propType = getPropType(propValue);

      if (propType !== 'object') {
        return new PropTypeError('Invalid ' + location + ' `' + propFullName + '` of type `' + propType + '` ' + ('supplied to `' + componentName + '`, expected `object`.'));
      } // We need to check all keys in case some are required but missing from
      // props.


      var allKeys = objectAssign({}, props[propName], shapeTypes);

      for (var key in allKeys) {
        var checker = shapeTypes[key];

        if (!checker) {
          return new PropTypeError('Invalid ' + location + ' `' + propFullName + '` key `' + key + '` supplied to `' + componentName + '`.' + '\nBad object: ' + JSON.stringify(props[propName], null, '  ') + '\nValid keys: ' + JSON.stringify(Object.keys(shapeTypes), null, '  '));
        }

        var error = checker(propValue, key, componentName, location, propFullName + '.' + key, ReactPropTypesSecret_1);

        if (error) {
          return error;
        }
      }

      return null;
    }

    return createChainableTypeChecker(validate);
  }

  function isNode(propValue) {
    switch (typeof propValue) {
      case 'number':
      case 'string':
      case 'undefined':
        return true;

      case 'boolean':
        return !propValue;

      case 'object':
        if (Array.isArray(propValue)) {
          return propValue.every(isNode);
        }

        if (propValue === null || isValidElement(propValue)) {
          return true;
        }

        var iteratorFn = getIteratorFn(propValue);

        if (iteratorFn) {
          var iterator = iteratorFn.call(propValue);
          var step;

          if (iteratorFn !== propValue.entries) {
            while (!(step = iterator.next()).done) {
              if (!isNode(step.value)) {
                return false;
              }
            }
          } else {
            // Iterator will provide entry [k,v] tuples rather than values.
            while (!(step = iterator.next()).done) {
              var entry = step.value;

              if (entry) {
                if (!isNode(entry[1])) {
                  return false;
                }
              }
            }
          }
        } else {
          return false;
        }

        return true;

      default:
        return false;
    }
  }

  function isSymbol(propType, propValue) {
    // Native Symbol.
    if (propType === 'symbol') {
      return true;
    } // falsy value can't be a Symbol


    if (!propValue) {
      return false;
    } // 19.4.3.5 Symbol.prototype[@@toStringTag] === 'Symbol'


    if (propValue['@@toStringTag'] === 'Symbol') {
      return true;
    } // Fallback for non-spec compliant Symbols which are polyfilled.


    if (typeof Symbol === 'function' && propValue instanceof Symbol) {
      return true;
    }

    return false;
  } // Equivalent of `typeof` but with special handling for array and regexp.


  function getPropType(propValue) {
    var propType = typeof propValue;

    if (Array.isArray(propValue)) {
      return 'array';
    }

    if (propValue instanceof RegExp) {
      // Old webkits (at least until Android 4.0) return 'function' rather than
      // 'object' for typeof a RegExp. We'll normalize this here so that /bla/
      // passes PropTypes.object.
      return 'object';
    }

    if (isSymbol(propType, propValue)) {
      return 'symbol';
    }

    return propType;
  } // This handles more types than `getPropType`. Only used for error messages.
  // See `createPrimitiveTypeChecker`.


  function getPreciseType(propValue) {
    if (typeof propValue === 'undefined' || propValue === null) {
      return '' + propValue;
    }

    var propType = getPropType(propValue);

    if (propType === 'object') {
      if (propValue instanceof Date) {
        return 'date';
      } else if (propValue instanceof RegExp) {
        return 'regexp';
      }
    }

    return propType;
  } // Returns a string that is postfixed to a warning about an invalid type.
  // For example, "undefined" or "of type array"


  function getPostfixForTypeWarning(value) {
    var type = getPreciseType(value);

    switch (type) {
      case 'array':
      case 'object':
        return 'an ' + type;

      case 'boolean':
      case 'date':
      case 'regexp':
        return 'a ' + type;

      default:
        return type;
    }
  } // Returns class name of the object, if any.


  function getClassName(propValue) {
    if (!propValue.constructor || !propValue.constructor.name) {
      return ANONYMOUS;
    }

    return propValue.constructor.name;
  }

  ReactPropTypes.checkPropTypes = checkPropTypes_1;
  ReactPropTypes.resetWarningCache = checkPropTypes_1.resetWarningCache;
  ReactPropTypes.PropTypes = ReactPropTypes;
  return ReactPropTypes;
};

function emptyFunction() {}

function emptyFunctionWithReset() {}

emptyFunctionWithReset.resetWarningCache = emptyFunction;

var factoryWithThrowingShims = function () {
  function shim(props, propName, componentName, location, propFullName, secret) {
    if (secret === ReactPropTypesSecret_1) {
      // It is still safe when called from React.
      return;
    }

    var err = new Error('Calling PropTypes validators directly is not supported by the `prop-types` package. ' + 'Use PropTypes.checkPropTypes() to call them. ' + 'Read more at http://fb.me/use-check-prop-types');
    err.name = 'Invariant Violation';
    throw err;
  }
  shim.isRequired = shim;

  function getShim() {
    return shim;
  }
  // Keep this list in sync with production version in `./factoryWithTypeCheckers.js`.

  var ReactPropTypes = {
    array: shim,
    bool: shim,
    func: shim,
    number: shim,
    object: shim,
    string: shim,
    symbol: shim,
    any: shim,
    arrayOf: getShim,
    element: shim,
    elementType: shim,
    instanceOf: getShim,
    node: shim,
    objectOf: getShim,
    oneOf: getShim,
    oneOfType: getShim,
    shape: getShim,
    exact: getShim,
    checkPropTypes: emptyFunctionWithReset,
    resetWarningCache: emptyFunction
  };
  ReactPropTypes.PropTypes = ReactPropTypes;
  return ReactPropTypes;
};

var propTypes = createCommonjsModule(function (module) {
/**
 * Copyright (c) 2013-present, Facebook, Inc.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */
if (process.env.NODE_ENV !== 'production') {
  var ReactIs = reactIs; // By explicitly using `prop-types` you are opting into new development behavior.
  // http://fb.me/prop-types-in-prod


  var throwOnDirectAccess = true;
  module.exports = factoryWithTypeCheckers(ReactIs.isElement, throwOnDirectAccess);
} else {
  // By explicitly using `prop-types` you are opting into new production behavior.
  // http://fb.me/prop-types-in-prod
  module.exports = factoryWithThrowingShims();
}
});

function hasClass(element, className) {
  if (element.classList) return !!className && element.classList.contains(className);
  return (" " + (element.className.baseVal || element.className) + " ").indexOf(" " + className + " ") !== -1;
}

function addClass(element, className) {
  if (element.classList) element.classList.add(className);else if (!hasClass(element, className)) if (typeof element.className === 'string') element.className = element.className + " " + className;else element.setAttribute('class', (element.className && element.className.baseVal || '') + " " + className);
}

function replaceClassName(origClass, classToRemove) {
  return origClass.replace(new RegExp("(^|\\s)" + classToRemove + "(?:\\s|$)", 'g'), '$1').replace(/\s+/g, ' ').replace(/^\s*|\s*$/g, '');
}

function removeClass(element, className) {
  if (element.classList) {
    element.classList.remove(className);
  } else if (typeof element.className === 'string') {
    element.className = replaceClassName(element.className, className);
  } else {
    element.setAttribute('class', replaceClassName(element.className && element.className.baseVal || '', className));
  }
}

var config = {
  disabled: false
};

var timeoutsShape = process.env.NODE_ENV !== 'production' ? propTypes.oneOfType([propTypes.number, propTypes.shape({
  enter: propTypes.number,
  exit: propTypes.number,
  appear: propTypes.number
}).isRequired]) : null;
var classNamesShape = process.env.NODE_ENV !== 'production' ? propTypes.oneOfType([propTypes.string, propTypes.shape({
  enter: propTypes.string,
  exit: propTypes.string,
  active: propTypes.string
}), propTypes.shape({
  enter: propTypes.string,
  enterDone: propTypes.string,
  enterActive: propTypes.string,
  exit: propTypes.string,
  exitDone: propTypes.string,
  exitActive: propTypes.string
})]) : null;

var TransitionGroupContext = React__default.createContext(null);

var UNMOUNTED = 'unmounted';
var EXITED = 'exited';
var ENTERING = 'entering';
var ENTERED = 'entered';
var EXITING = 'exiting';
/**
 * The Transition component lets you describe a transition from one component
 * state to another _over time_ with a simple declarative API. Most commonly
 * it's used to animate the mounting and unmounting of a component, but can also
 * be used to describe in-place transition states as well.
 *
 * ---
 *
 * **Note**: `Transition` is a platform-agnostic base component. If you're using
 * transitions in CSS, you'll probably want to use
 * [`CSSTransition`](https://reactcommunity.org/react-transition-group/css-transition)
 * instead. It inherits all the features of `Transition`, but contains
 * additional features necessary to play nice with CSS transitions (hence the
 * name of the component).
 *
 * ---
 *
 * By default the `Transition` component does not alter the behavior of the
 * component it renders, it only tracks "enter" and "exit" states for the
 * components. It's up to you to give meaning and effect to those states. For
 * example we can add styles to a component when it enters or exits:
 *
 * ```jsx
 * import { Transition } from 'react-transition-group';
 *
 * const duration = 300;
 *
 * const defaultStyle = {
 *   transition: `opacity ${duration}ms ease-in-out`,
 *   opacity: 0,
 * }
 *
 * const transitionStyles = {
 *   entering: { opacity: 1 },
 *   entered:  { opacity: 1 },
 *   exiting:  { opacity: 0 },
 *   exited:  { opacity: 0 },
 * };
 *
 * const Fade = ({ in: inProp }) => (
 *   <Transition in={inProp} timeout={duration}>
 *     {state => (
 *       <div style={{
 *         ...defaultStyle,
 *         ...transitionStyles[state]
 *       }}>
 *         I'm a fade Transition!
 *       </div>
 *     )}
 *   </Transition>
 * );
 * ```
 *
 * There are 4 main states a Transition can be in:
 *  - `'entering'`
 *  - `'entered'`
 *  - `'exiting'`
 *  - `'exited'`
 *
 * Transition state is toggled via the `in` prop. When `true` the component
 * begins the "Enter" stage. During this stage, the component will shift from
 * its current transition state, to `'entering'` for the duration of the
 * transition and then to the `'entered'` stage once it's complete. Let's take
 * the following example (we'll use the
 * [useState](https://reactjs.org/docs/hooks-reference.html#usestate) hook):
 *
 * ```jsx
 * function App() {
 *   const [inProp, setInProp] = useState(false);
 *   return (
 *     <div>
 *       <Transition in={inProp} timeout={500}>
 *         {state => (
 *           // ...
 *         )}
 *       </Transition>
 *       <button onClick={() => setInProp(true)}>
 *         Click to Enter
 *       </button>
 *     </div>
 *   );
 * }
 * ```
 *
 * When the button is clicked the component will shift to the `'entering'` state
 * and stay there for 500ms (the value of `timeout`) before it finally switches
 * to `'entered'`.
 *
 * When `in` is `false` the same thing happens except the state moves from
 * `'exiting'` to `'exited'`.
 */

var Transition = /*#__PURE__*/function (_React$Component) {
  _inheritsLoose(Transition, _React$Component);

  function Transition(props, context) {
    var _this;

    _this = _React$Component.call(this, props, context) || this;
    var parentGroup = context; // In the context of a TransitionGroup all enters are really appears

    var appear = parentGroup && !parentGroup.isMounting ? props.enter : props.appear;
    var initialStatus;
    _this.appearStatus = null;

    if (props.in) {
      if (appear) {
        initialStatus = EXITED;
        _this.appearStatus = ENTERING;
      } else {
        initialStatus = ENTERED;
      }
    } else {
      if (props.unmountOnExit || props.mountOnEnter) {
        initialStatus = UNMOUNTED;
      } else {
        initialStatus = EXITED;
      }
    }

    _this.state = {
      status: initialStatus
    };
    _this.nextCallback = null;
    return _this;
  }

  Transition.getDerivedStateFromProps = function getDerivedStateFromProps(_ref, prevState) {
    var nextIn = _ref.in;

    if (nextIn && prevState.status === UNMOUNTED) {
      return {
        status: EXITED
      };
    }

    return null;
  }; // getSnapshotBeforeUpdate(prevProps) {
  //   let nextStatus = null
  //   if (prevProps !== this.props) {
  //     const { status } = this.state
  //     if (this.props.in) {
  //       if (status !== ENTERING && status !== ENTERED) {
  //         nextStatus = ENTERING
  //       }
  //     } else {
  //       if (status === ENTERING || status === ENTERED) {
  //         nextStatus = EXITING
  //       }
  //     }
  //   }
  //   return { nextStatus }
  // }


  var _proto = Transition.prototype;

  _proto.componentDidMount = function componentDidMount() {
    this.updateStatus(true, this.appearStatus);
  };

  _proto.componentDidUpdate = function componentDidUpdate(prevProps) {
    var nextStatus = null;

    if (prevProps !== this.props) {
      var status = this.state.status;

      if (this.props.in) {
        if (status !== ENTERING && status !== ENTERED) {
          nextStatus = ENTERING;
        }
      } else {
        if (status === ENTERING || status === ENTERED) {
          nextStatus = EXITING;
        }
      }
    }

    this.updateStatus(false, nextStatus);
  };

  _proto.componentWillUnmount = function componentWillUnmount() {
    this.cancelNextCallback();
  };

  _proto.getTimeouts = function getTimeouts() {
    var timeout = this.props.timeout;
    var exit, enter, appear;
    exit = enter = appear = timeout;

    if (timeout != null && typeof timeout !== 'number') {
      exit = timeout.exit;
      enter = timeout.enter; // TODO: remove fallback for next major

      appear = timeout.appear !== undefined ? timeout.appear : enter;
    }

    return {
      exit: exit,
      enter: enter,
      appear: appear
    };
  };

  _proto.updateStatus = function updateStatus(mounting, nextStatus) {
    if (mounting === void 0) {
      mounting = false;
    }

    if (nextStatus !== null) {
      // nextStatus will always be ENTERING or EXITING.
      this.cancelNextCallback();
      var node = ReactDOM.findDOMNode(this);

      if (nextStatus === ENTERING) {
        this.performEnter(node, mounting);
      } else {
        this.performExit(node);
      }
    } else if (this.props.unmountOnExit && this.state.status === EXITED) {
      this.setState({
        status: UNMOUNTED
      });
    }
  };

  _proto.performEnter = function performEnter(node, mounting) {
    var _this2 = this;

    var enter = this.props.enter;
    var appearing = this.context ? this.context.isMounting : mounting;
    var timeouts = this.getTimeouts();
    var enterTimeout = appearing ? timeouts.appear : timeouts.enter; // no enter animation skip right to ENTERED
    // if we are mounting and running this it means appear _must_ be set

    if (!mounting && !enter || config.disabled) {
      this.safeSetState({
        status: ENTERED
      }, function () {
        _this2.props.onEntered(node);
      });
      return;
    }

    this.props.onEnter(node, appearing);
    this.safeSetState({
      status: ENTERING
    }, function () {
      _this2.props.onEntering(node, appearing);

      _this2.onTransitionEnd(node, enterTimeout, function () {
        _this2.safeSetState({
          status: ENTERED
        }, function () {
          _this2.props.onEntered(node, appearing);
        });
      });
    });
  };

  _proto.performExit = function performExit(node) {
    var _this3 = this;

    var exit = this.props.exit;
    var timeouts = this.getTimeouts(); // no exit animation skip right to EXITED

    if (!exit || config.disabled) {
      this.safeSetState({
        status: EXITED
      }, function () {
        _this3.props.onExited(node);
      });
      return;
    }

    this.props.onExit(node);
    this.safeSetState({
      status: EXITING
    }, function () {
      _this3.props.onExiting(node);

      _this3.onTransitionEnd(node, timeouts.exit, function () {
        _this3.safeSetState({
          status: EXITED
        }, function () {
          _this3.props.onExited(node);
        });
      });
    });
  };

  _proto.cancelNextCallback = function cancelNextCallback() {
    if (this.nextCallback !== null) {
      this.nextCallback.cancel();
      this.nextCallback = null;
    }
  };

  _proto.safeSetState = function safeSetState(nextState, callback) {
    // This shouldn't be necessary, but there are weird race conditions with
    // setState callbacks and unmounting in testing, so always make sure that
    // we can cancel any pending setState callbacks after we unmount.
    callback = this.setNextCallback(callback);
    this.setState(nextState, callback);
  };

  _proto.setNextCallback = function setNextCallback(callback) {
    var _this4 = this;

    var active = true;

    this.nextCallback = function (event) {
      if (active) {
        active = false;
        _this4.nextCallback = null;
        callback(event);
      }
    };

    this.nextCallback.cancel = function () {
      active = false;
    };

    return this.nextCallback;
  };

  _proto.onTransitionEnd = function onTransitionEnd(node, timeout, handler) {
    this.setNextCallback(handler);
    var doesNotHaveTimeoutOrListener = timeout == null && !this.props.addEndListener;

    if (!node || doesNotHaveTimeoutOrListener) {
      setTimeout(this.nextCallback, 0);
      return;
    }

    if (this.props.addEndListener) {
      this.props.addEndListener(node, this.nextCallback);
    }

    if (timeout != null) {
      setTimeout(this.nextCallback, timeout);
    }
  };

  _proto.render = function render() {
    var status = this.state.status;

    if (status === UNMOUNTED) {
      return null;
    }

    var _this$props = this.props,
        children = _this$props.children,
        childProps = _objectWithoutPropertiesLoose(_this$props, ["children"]); // filter props for Transtition


    delete childProps.in;
    delete childProps.mountOnEnter;
    delete childProps.unmountOnExit;
    delete childProps.appear;
    delete childProps.enter;
    delete childProps.exit;
    delete childProps.timeout;
    delete childProps.addEndListener;
    delete childProps.onEnter;
    delete childProps.onEntering;
    delete childProps.onEntered;
    delete childProps.onExit;
    delete childProps.onExiting;
    delete childProps.onExited;

    if (typeof children === 'function') {
      // allows for nested Transitions
      return /*#__PURE__*/React__default.createElement(TransitionGroupContext.Provider, {
        value: null
      }, children(status, childProps));
    }

    var child = React__default.Children.only(children);
    return (
      /*#__PURE__*/
      // allows for nested Transitions
      React__default.createElement(TransitionGroupContext.Provider, {
        value: null
      }, /*#__PURE__*/React__default.cloneElement(child, childProps))
    );
  };

  return Transition;
}(React__default.Component);

Transition.contextType = TransitionGroupContext;
Transition.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * A `function` child can be used instead of a React element. This function is
   * called with the current transition status (`'entering'`, `'entered'`,
   * `'exiting'`, `'exited'`), which can be used to apply context
   * specific props to a component.
   *
   * ```jsx
   * <Transition in={this.state.in} timeout={150}>
   *   {state => (
   *     <MyComponent className={`fade fade-${state}`} />
   *   )}
   * </Transition>
   * ```
   */
  children: propTypes.oneOfType([propTypes.func.isRequired, propTypes.element.isRequired]).isRequired,

  /**
   * Show the component; triggers the enter or exit states
   */
  in: propTypes.bool,

  /**
   * By default the child component is mounted immediately along with
   * the parent `Transition` component. If you want to "lazy mount" the component on the
   * first `in={true}` you can set `mountOnEnter`. After the first enter transition the component will stay
   * mounted, even on "exited", unless you also specify `unmountOnExit`.
   */
  mountOnEnter: propTypes.bool,

  /**
   * By default the child component stays mounted after it reaches the `'exited'` state.
   * Set `unmountOnExit` if you'd prefer to unmount the component after it finishes exiting.
   */
  unmountOnExit: propTypes.bool,

  /**
   * Normally a component is not transitioned if it is shown when the
   * `<Transition>` component mounts. If you want to transition on the first
   * mount set `appear` to `true`, and the component will transition in as soon
   * as the `<Transition>` mounts.
   *
   * > **Note**: there are no special appear states like `appearing`/`appeared`, this prop
   * > only adds an additional enter transition. However, in the
   * > `<CSSTransition>` component that first enter transition does result in
   * > additional `.appear-*` classes, that way you can choose to style it
   * > differently.
   */
  appear: propTypes.bool,

  /**
   * Enable or disable enter transitions.
   */
  enter: propTypes.bool,

  /**
   * Enable or disable exit transitions.
   */
  exit: propTypes.bool,

  /**
   * The duration of the transition, in milliseconds.
   * Required unless `addEndListener` is provided.
   *
   * You may specify a single timeout for all transitions:
   *
   * ```jsx
   * timeout={500}
   * ```
   *
   * or individually:
   *
   * ```jsx
   * timeout={{
   *  appear: 500,
   *  enter: 300,
   *  exit: 500,
   * }}
   * ```
   *
   * - `appear` defaults to the value of `enter`
   * - `enter` defaults to `0`
   * - `exit` defaults to `0`
   *
   * @type {number | { enter?: number, exit?: number, appear?: number }}
   */
  timeout: function timeout(props) {
    var pt = timeoutsShape;
    if (!props.addEndListener) pt = pt.isRequired;

    for (var _len = arguments.length, args = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
      args[_key - 1] = arguments[_key];
    }

    return pt.apply(void 0, [props].concat(args));
  },

  /**
   * Add a custom transition end trigger. Called with the transitioning
   * DOM node and a `done` callback. Allows for more fine grained transition end
   * logic. **Note:** Timeouts are still used as a fallback if provided.
   *
   * ```jsx
   * addEndListener={(node, done) => {
   *   // use the css transitionend event to mark the finish of a transition
   *   node.addEventListener('transitionend', done, false);
   * }}
   * ```
   */
  addEndListener: propTypes.func,

  /**
   * Callback fired before the "entering" status is applied. An extra parameter
   * `isAppearing` is supplied to indicate if the enter stage is occurring on the initial mount
   *
   * @type Function(node: HtmlElement, isAppearing: bool) -> void
   */
  onEnter: propTypes.func,

  /**
   * Callback fired after the "entering" status is applied. An extra parameter
   * `isAppearing` is supplied to indicate if the enter stage is occurring on the initial mount
   *
   * @type Function(node: HtmlElement, isAppearing: bool)
   */
  onEntering: propTypes.func,

  /**
   * Callback fired after the "entered" status is applied. An extra parameter
   * `isAppearing` is supplied to indicate if the enter stage is occurring on the initial mount
   *
   * @type Function(node: HtmlElement, isAppearing: bool) -> void
   */
  onEntered: propTypes.func,

  /**
   * Callback fired before the "exiting" status is applied.
   *
   * @type Function(node: HtmlElement) -> void
   */
  onExit: propTypes.func,

  /**
   * Callback fired after the "exiting" status is applied.
   *
   * @type Function(node: HtmlElement) -> void
   */
  onExiting: propTypes.func,

  /**
   * Callback fired after the "exited" status is applied.
   *
   * @type Function(node: HtmlElement) -> void
   */
  onExited: propTypes.func // Name the function so it is clearer in the documentation

} : {};

function noop() {}

Transition.defaultProps = {
  in: false,
  mountOnEnter: false,
  unmountOnExit: false,
  appear: false,
  enter: true,
  exit: true,
  onEnter: noop,
  onEntering: noop,
  onEntered: noop,
  onExit: noop,
  onExiting: noop,
  onExited: noop
};
Transition.UNMOUNTED = 0;
Transition.EXITED = 1;
Transition.ENTERING = 2;
Transition.ENTERED = 3;
Transition.EXITING = 4;

var _addClass = function addClass$1(node, classes) {
  return node && classes && classes.split(' ').forEach(function (c) {
    return addClass(node, c);
  });
};

var removeClass$1 = function removeClass$1(node, classes) {
  return node && classes && classes.split(' ').forEach(function (c) {
    return removeClass(node, c);
  });
};
/**
 * A transition component inspired by the excellent
 * [ng-animate](http://www.nganimate.org/) library, you should use it if you're
 * using CSS transitions or animations. It's built upon the
 * [`Transition`](https://reactcommunity.org/react-transition-group/transition)
 * component, so it inherits all of its props.
 *
 * `CSSTransition` applies a pair of class names during the `appear`, `enter`,
 * and `exit` states of the transition. The first class is applied and then a
 * second `*-active` class in order to activate the CSS transition. After the
 * transition, matching `*-done` class names are applied to persist the
 * transition state.
 *
 * ```jsx
 * function App() {
 *   const [inProp, setInProp] = useState(false);
 *   return (
 *     <div>
 *       <CSSTransition in={inProp} timeout={200} classNames="my-node">
 *         <div>
 *           {"I'll receive my-node-* classes"}
 *         </div>
 *       </CSSTransition>
 *       <button type="button" onClick={() => setInProp(true)}>
 *         Click to Enter
 *       </button>
 *     </div>
 *   );
 * }
 * ```
 *
 * When the `in` prop is set to `true`, the child component will first receive
 * the class `example-enter`, then the `example-enter-active` will be added in
 * the next tick. `CSSTransition` [forces a
 * reflow](https://github.com/reactjs/react-transition-group/blob/5007303e729a74be66a21c3e2205e4916821524b/src/CSSTransition.js#L208-L215)
 * between before adding the `example-enter-active`. This is an important trick
 * because it allows us to transition between `example-enter` and
 * `example-enter-active` even though they were added immediately one after
 * another. Most notably, this is what makes it possible for us to animate
 * _appearance_.
 *
 * ```css
 * .my-node-enter {
 *   opacity: 0;
 * }
 * .my-node-enter-active {
 *   opacity: 1;
 *   transition: opacity 200ms;
 * }
 * .my-node-exit {
 *   opacity: 1;
 * }
 * .my-node-exit-active {
 *   opacity: 0;
 *   transition: opacity 200ms;
 * }
 * ```
 *
 * `*-active` classes represent which styles you want to animate **to**.
 *
 * **Note**: If you're using the
 * [`appear`](http://reactcommunity.org/react-transition-group/transition#Transition-prop-appear)
 * prop, make sure to define styles for `.appear-*` classes as well.
 */


var CSSTransition = /*#__PURE__*/function (_React$Component) {
  _inheritsLoose(CSSTransition, _React$Component);

  function CSSTransition() {
    var _this;

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this = _React$Component.call.apply(_React$Component, [this].concat(args)) || this;
    _this.appliedClasses = {
      appear: {},
      enter: {},
      exit: {}
    };

    _this.onEnter = function (node, appearing) {
      _this.removeClasses(node, 'exit');

      _this.addClass(node, appearing ? 'appear' : 'enter', 'base');

      if (_this.props.onEnter) {
        _this.props.onEnter(node, appearing);
      }
    };

    _this.onEntering = function (node, appearing) {
      var type = appearing ? 'appear' : 'enter';

      _this.addClass(node, type, 'active');

      if (_this.props.onEntering) {
        _this.props.onEntering(node, appearing);
      }
    };

    _this.onEntered = function (node, appearing) {
      var type = appearing ? 'appear' : 'enter';

      _this.removeClasses(node, type);

      _this.addClass(node, type, 'done');

      if (_this.props.onEntered) {
        _this.props.onEntered(node, appearing);
      }
    };

    _this.onExit = function (node) {
      _this.removeClasses(node, 'appear');

      _this.removeClasses(node, 'enter');

      _this.addClass(node, 'exit', 'base');

      if (_this.props.onExit) {
        _this.props.onExit(node);
      }
    };

    _this.onExiting = function (node) {
      _this.addClass(node, 'exit', 'active');

      if (_this.props.onExiting) {
        _this.props.onExiting(node);
      }
    };

    _this.onExited = function (node) {
      _this.removeClasses(node, 'exit');

      _this.addClass(node, 'exit', 'done');

      if (_this.props.onExited) {
        _this.props.onExited(node);
      }
    };

    _this.getClassNames = function (type) {
      var classNames = _this.props.classNames;
      var isStringClassNames = typeof classNames === 'string';
      var prefix = isStringClassNames && classNames ? classNames + "-" : '';
      var baseClassName = isStringClassNames ? "" + prefix + type : classNames[type];
      var activeClassName = isStringClassNames ? baseClassName + "-active" : classNames[type + "Active"];
      var doneClassName = isStringClassNames ? baseClassName + "-done" : classNames[type + "Done"];
      return {
        baseClassName: baseClassName,
        activeClassName: activeClassName,
        doneClassName: doneClassName
      };
    };

    return _this;
  }

  var _proto = CSSTransition.prototype;

  _proto.addClass = function addClass(node, type, phase) {
    var className = this.getClassNames(type)[phase + "ClassName"];

    if (type === 'appear' && phase === 'done') {
      className += " " + this.getClassNames('enter').doneClassName;
    } // This is for to force a repaint,
    // which is necessary in order to transition styles when adding a class name.


    if (phase === 'active') {
      /* eslint-disable no-unused-expressions */
      node && node.scrollTop;
    }

    this.appliedClasses[type][phase] = className;

    _addClass(node, className);
  };

  _proto.removeClasses = function removeClasses(node, type) {
    var _this$appliedClasses$ = this.appliedClasses[type],
        baseClassName = _this$appliedClasses$.base,
        activeClassName = _this$appliedClasses$.active,
        doneClassName = _this$appliedClasses$.done;
    this.appliedClasses[type] = {};

    if (baseClassName) {
      removeClass$1(node, baseClassName);
    }

    if (activeClassName) {
      removeClass$1(node, activeClassName);
    }

    if (doneClassName) {
      removeClass$1(node, doneClassName);
    }
  };

  _proto.render = function render() {
    var _this$props = this.props,
        _ = _this$props.classNames,
        props = _objectWithoutPropertiesLoose(_this$props, ["classNames"]);

    return /*#__PURE__*/React__default.createElement(Transition, _extends$1({}, props, {
      onEnter: this.onEnter,
      onEntered: this.onEntered,
      onEntering: this.onEntering,
      onExit: this.onExit,
      onExiting: this.onExiting,
      onExited: this.onExited
    }));
  };

  return CSSTransition;
}(React__default.Component);

CSSTransition.defaultProps = {
  classNames: ''
};
CSSTransition.propTypes = process.env.NODE_ENV !== "production" ? _extends$1({}, Transition.propTypes, {
  /**
   * The animation classNames applied to the component as it appears, enters,
   * exits or has finished the transition. A single name can be provided and it
   * will be suffixed for each stage: e.g.
   *
   * `classNames="fade"` applies `fade-appear`, `fade-appear-active`,
   * `fade-appear-done`, `fade-enter`, `fade-enter-active`, `fade-enter-done`,
   * `fade-exit`, `fade-exit-active`, and `fade-exit-done`.
   *
   * **Note**: `fade-appear-done` and `fade-enter-done` will _both_ be applied.
   * This allows you to define different behavior for when appearing is done and
   * when regular entering is done, using selectors like
   * `.fade-enter-done:not(.fade-appear-done)`. For example, you could apply an
   * epic entrance animation when element first appears in the DOM using
   * [Animate.css](https://daneden.github.io/animate.css/). Otherwise you can
   * simply use `fade-enter-done` for defining both cases.
   *
   * Each individual classNames can also be specified independently like:
   *
   * ```js
   * classNames={{
   *  appear: 'my-appear',
   *  appearActive: 'my-active-appear',
   *  appearDone: 'my-done-appear',
   *  enter: 'my-enter',
   *  enterActive: 'my-active-enter',
   *  enterDone: 'my-done-enter',
   *  exit: 'my-exit',
   *  exitActive: 'my-active-exit',
   *  exitDone: 'my-done-exit',
   * }}
   * ```
   *
   * If you want to set these classes using CSS Modules:
   *
   * ```js
   * import styles from './styles.css';
   * ```
   *
   * you might want to use camelCase in your CSS file, that way could simply
   * spread them instead of listing them one by one:
   *
   * ```js
   * classNames={{ ...styles }}
   * ```
   *
   * @type {string | {
   *  appear?: string,
   *  appearActive?: string,
   *  appearDone?: string,
   *  enter?: string,
   *  enterActive?: string,
   *  enterDone?: string,
   *  exit?: string,
   *  exitActive?: string,
   *  exitDone?: string,
   * }}
   */
  classNames: classNamesShape,

  /**
   * A `<Transition>` callback fired immediately after the 'enter' or 'appear' class is
   * applied.
   *
   * @type Function(node: HtmlElement, isAppearing: bool)
   */
  onEnter: propTypes.func,

  /**
   * A `<Transition>` callback fired immediately after the 'enter-active' or
   * 'appear-active' class is applied.
   *
   * @type Function(node: HtmlElement, isAppearing: bool)
   */
  onEntering: propTypes.func,

  /**
   * A `<Transition>` callback fired immediately after the 'enter' or
   * 'appear' classes are **removed** and the `done` class is added to the DOM node.
   *
   * @type Function(node: HtmlElement, isAppearing: bool)
   */
  onEntered: propTypes.func,

  /**
   * A `<Transition>` callback fired immediately after the 'exit' class is
   * applied.
   *
   * @type Function(node: HtmlElement)
   */
  onExit: propTypes.func,

  /**
   * A `<Transition>` callback fired immediately after the 'exit-active' is applied.
   *
   * @type Function(node: HtmlElement)
   */
  onExiting: propTypes.func,

  /**
   * A `<Transition>` callback fired immediately after the 'exit' classes
   * are **removed** and the `exit-done` class is added to the DOM node.
   *
   * @type Function(node: HtmlElement)
   */
  onExited: propTypes.func
}) : {};

const animationFactor = 100;
const menuEnterDuration = animationFactor * 2;
const menuExitDuration = animationFactor * 3;
const menuExitDelay = animationFactor * 3;
const menuEnterTimeOut = menuEnterDuration;
const menuExitTimeOut = menuExitDuration + menuExitDelay;
const linkEnterDuration = animationFactor * 2;
const linkEnterDelay = animationFactor;
const linkExitDuration = animationFactor * 3;
const linkExitDelay = animationFactor * 3;
const linkEnterTimeOut = linkEnterDuration + linkEnterDelay;
const linkExitTimeOut = linkExitDuration + linkExitDelay;
const chevronIconEnterDuration = animationFactor * 3;
const chevronIconEnterDelay = animationFactor * 3;
const chevronIconExitDuration = animationFactor * 3;
const chevronIconEnterTimeOut = chevronIconEnterDuration + chevronIconEnterDelay;
const chevronIconExitTimeOut = chevronIconExitDuration;
const subLinkEnterDuration = animationFactor * 2;
const subLinksTotalDelay = animationFactor * 2;
const subLinkEnterTimeOut = subLinkEnterDuration + subLinksTotalDelay;

const componentConfigThemeKey$g = 'menuSubLink';

const getMenuSubLinkStyle = () => StyleSheet.create({
  style: {
    display: 'flex',
    alignItems: 'flex-start',
    flexDirection: 'column'
  }
});

const MenuSubLink = props => {
  const {
    href,
    text
  } = props;
  const {
    idleColor,
    linkFontSizeMedium,
    linkFontSizeSmall
  } = useTheme(componentConfigThemeKey$g);
  const linkStyle = generateLinkStyleFromTheme({
    idleColor
  });
  const style = getMenuSubLinkStyle();
  return /*#__PURE__*/React__default.createElement("li", {
    className: css(style.style)
  }, /*#__PURE__*/React__default.createElement(TextLinkComponent, {
    href: href,
    linkStyle: linkStyle
  }, /*#__PURE__*/React__default.createElement(P, {
    fontSizeMedium: linkFontSizeMedium,
    fontSizeSmall: linkFontSizeSmall
  }, text)));
};

const MAX_SUB_LINKS_FOR_ONE_COLUMN = 10;

const getSubLinkStyle = (index, currentSubLinkDelay) => StyleSheet.create({
  enter: {
    opacity: 0
  },
  enterActive: {
    opacity: 1,
    transition: `opacity ${subLinkEnterDuration}ms ease-out`,
    transitionDelay: `${currentSubLinkDelay * index}ms`
  }
});

const createMenuSubLinks = (numberOfSubLinks, isLinkOpen) => (menuLink, index) => {
  const style = getSubLinkStyle(index, subLinksTotalDelay / numberOfSubLinks);
  return /*#__PURE__*/React__default.createElement(CSSTransition, {
    key: index,
    in: isLinkOpen,
    unmountOnExit: true,
    mountOnEnter: true,
    timeout: {
      enter: subLinkEnterTimeOut,
      exit: 0
    },
    classNames: {
      enter: css(style.enter),
      enterActive: css(style.enterActive)
    }
  }, /*#__PURE__*/React__default.createElement(MenuSubLink, {
    href: menuLink.link.href,
    text: menuLink.link.text
  }));
};

const getSubLinksWrapperStyle = (numberOfLinks, baseUnit) => {
  const showTwoColumns = numberOfLinks > MAX_SUB_LINKS_FOR_ONE_COLUMN;
  return StyleSheet.create({
    wrapper: {
      display: 'grid',
      padding: 0,
      gridRowGap: `${baseUnit}px`,
      gridColumnGap: `${baseUnit}px`,
      gridTemplateColumns: showTwoColumns ? '1fr 1fr' : '1fr',
      gridTemplateRows: showTwoColumns ? `repeat(${Math.round(numberOfLinks / 2)}, auto)` : 'auto',
      marginRight: `${baseUnit}px`,
      marginLeft: `${baseUnit * 1.5}px`
    }
  });
};

const MenuSubLinks = props => {
  const {
    isLinkOpen,
    links
  } = props;
  const {
    baseUnit
  } = useTheme();
  const numberOfLinks = links.length;
  const style = getSubLinksWrapperStyle(numberOfLinks, baseUnit);
  return /*#__PURE__*/React__default.createElement("ul", {
    className: css(style.wrapper)
  }, links.map(createMenuSubLinks(numberOfLinks, isLinkOpen)));
};

const componentConfigThemeKey$h = 'menuLink';
const getMenuLinkStyle = (baseUnit, linkColor) => StyleSheet.create({
  style: {
    color: linkColor,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    [MEDIA_BREAKPOINTS.medium]: {
      margin: `${baseUnit}px`
    },
    [MEDIA_BREAKPOINTS.small]: {
      margin: `${baseUnit}px`
    }
  },
  enter: {
    opacity: 0
  },
  enterActive: {
    opacity: 1,
    transition: `opacity ${chevronIconEnterDuration}ms ease-out`,
    transitionDelay: `${chevronIconEnterDelay}ms`
  },
  exit: {
    opacity: 1
  },
  exitActive: {
    opacity: 0,
    transition: `opacity ${chevronIconExitDuration}ms ease-in`
  },
  chevronIcon: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row-reverse'
  }
});
const MenuLink = props => {
  const {
    idleColor,
    activeStateColor,
    baseUnit,
    linkFontSizeMedium,
    linkFontSizeSmall
  } = useTheme(componentConfigThemeKey$h);
  const {
    link,
    onClick,
    index,
    isLinkOpen,
    childrenLinks,
    startChevronIconAnimation
  } = props;
  const linkColor = isLinkOpen ? activeStateColor : idleColor;
  const linkStyle = generateLinkStyleFromTheme({
    idleColor: linkColor
  });
  const style = getMenuLinkStyle(baseUnit, linkColor);
  const doSubLinksExist = childrenLinks && childrenLinks.length !== 0;
  return /*#__PURE__*/React__default.createElement(React__default.Fragment, null, /*#__PURE__*/React__default.createElement("li", null, /*#__PURE__*/React__default.createElement("div", {
    className: css(style.style)
  }, /*#__PURE__*/React__default.createElement(TextLinkComponent, {
    href: link.href,
    linkStyle: linkStyle
  }, /*#__PURE__*/React__default.createElement(H4, {
    fontSizeMedium: linkFontSizeMedium,
    fontSizeSmall: linkFontSizeSmall
  }, link.text)), doSubLinksExist ? /*#__PURE__*/React__default.createElement("div", {
    className: css(style.chevronIcon),
    onClick: () => onClick(index)
  }, /*#__PURE__*/React__default.createElement(CSSTransition, {
    in: startChevronIconAnimation,
    unmountOnExit: true,
    mountOnEnter: true,
    timeout: {
      enter: chevronIconEnterTimeOut,
      exit: chevronIconExitTimeOut
    },
    classNames: {
      enter: css(style.enter),
      enterActive: css(style.enterActive),
      exit: css(style.exit),
      exitActive: css(style.exitActive)
    }
  }, /*#__PURE__*/React__default.createElement(ChevronIcon, {
    direction: isLinkOpen ? Directions.UP : Directions.DOWN,
    color: linkColor
  }))) : null), doSubLinksExist && isLinkOpen ? /*#__PURE__*/React__default.createElement(MenuSubLinks, {
    links: childrenLinks,
    isLinkOpen: isLinkOpen
  }) : null));
};

const createStyle$3 = (baseUnit, linkColor) => StyleSheet.create({
  button: {
    color: linkColor,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    [MEDIA_BREAKPOINTS.medium]: {
      margin: `${baseUnit}px`
    },
    [MEDIA_BREAKPOINTS.small]: {
      margin: `${baseUnit}px`
    }
  }
});

const MenuButton = props => {
  const {
    onClick,
    text
  } = props;
  /* eslint-disable-next-line no-new-func */

  const onClickFunction = new Function(onClick);
  const {
    idleColor,
    baseUnit,
    fontSizesStyles,
    linkFontSizeMedium,
    linkFontSizeSmall
  } = useTheme(componentConfigThemeKey$h);
  const style = createStyle$3(baseUnit, idleColor);
  const styleByFontSize = getStyleBySize(fontSizesStyles, {
    fontSizeLarge: linkFontSizeMedium,
    fontSizeMedium: linkFontSizeMedium,
    fontSizeSmall: linkFontSizeSmall
  });
  return /*#__PURE__*/createElement("button", {
    className: css(style.button, styleByFontSize),
    type: "button",
    onClick: () => onClickFunction()
  }, text);
};

const componentConfigThemeKey$i = 'menuLinks';

const getMenuLinksStyle = (backgroundColor, dividerColor, baseUnit) => StyleSheet.create({
  style: {
    padding: 0,
    margin: 0,
    backgroundColor,
    position: 'fixed',
    width: '100%',
    zIndex: 2,
    left: 0,
    overflowY: 'auto',
    [MEDIA_BREAKPOINTS.medium]: {
      top: `${primaryHeaderHeights.medium}px`,
      height: `calc(100vh - ${primaryHeaderHeights.medium}px)`
    },
    [MEDIA_BREAKPOINTS.small]: {
      top: `${primaryHeaderHeights.small}px`,
      height: `calc(100vh - ${primaryHeaderHeights.small}px)`
    }
  },
  enter: {
    opacity: 0,
    transform: 'translate(-100%)'
  },
  enterActive: {
    opacity: 1,
    transform: 'translate(0)',
    transition: `transform ${menuEnterDuration}ms, opacity ${menuEnterDuration}ms`,
    transitionTimingFunction: 'ease-out'
  },
  exit: {
    opacity: 1,
    transform: 'translate(0)'
  },
  exitActive: {
    opacity: 0,
    transform: 'translate(-100%)',
    transition: `transform ${menuExitDuration}ms ease-in , opacity ${menuExitDuration}ms ease-in`,
    transitionDelay: `${menuExitDelay}ms`
  },
  divider: {
    backgroundColor: dividerColor,
    height: '1px',
    [MEDIA_BREAKPOINTS.medium]: {
      margin: `${baseUnit}px`
    },
    [MEDIA_BREAKPOINTS.small]: {
      margin: `${baseUnit}px`
    }
  }
});

const getMenuLinkStyle$1 = () => StyleSheet.create({
  enter: {
    opacity: 0.5,
    transform: 'translate(-100%)'
  },
  enterActive: {
    opacity: 1,
    transform: 'translate(0)',
    transition: `transform ${linkEnterDuration}ms ease-out, opacity ${linkEnterDuration}ms ease-in`,
    transitionDelay: `${linkEnterDelay}ms`
  },
  exit: {
    transform: 'translate(0)',
    opacity: 1
  },
  exitActive: {
    opacity: 0.5,
    transform: 'translate(-100%)',
    transition: `transform ${linkExitDuration}ms ease-in, opacity ${linkExitDuration}ms ease-in`,
    transitionDelay: `${linkExitDelay}ms`
  }
});

const MenuLinkAnimationWrapper = props => {
  const {
    children,
    shouldAppear
  } = props;
  const style = getMenuLinkStyle$1();
  return /*#__PURE__*/createElement(CSSTransition, {
    unmountOnExit: true,
    mountOnEnter: true,
    in: shouldAppear,
    timeout: {
      enter: linkEnterTimeOut,
      exit: linkExitTimeOut
    },
    classNames: {
      enter: css(style.enter),
      enterActive: css(style.enterActive),
      exit: css(style.exit),
      exitActive: css(style.exitActive)
    }
  }, children);
};

const createMenuLink = (onClick, currentOpenLink, shouldAppear, startIndex = 0) => (menuLink, index) => {
  return /*#__PURE__*/createElement(MenuLinkAnimationWrapper, {
    shouldAppear: shouldAppear,
    key: index
  }, state => /*#__PURE__*/createElement(MenuLink, {
    key: index,
    link: menuLink.link,
    childrenLinks: menuLink.childrenLinks,
    index: startIndex + index,
    onClick: onClick,
    isLinkOpen: currentOpenLink !== -1 && currentOpenLink === startIndex + index,
    startChevronIconAnimation: state === 'entering' || state === 'entered'
  }));
};
const navigationLinkToMenuLinkDataProps = navigationLink => ({
  link: navigationLink.link,
  childrenLinks: navigationLink.children ? navigationLink.children.map(navigationLinkToMenuLinkDataProps) : null
});
const createEditionLink = (onClick, currentOpenLink, shouldAppear, startIndex = 0) => (navigationLink, index) => {
  return /*#__PURE__*/createElement(MenuLinkAnimationWrapper, {
    shouldAppear: shouldAppear,
    key: index
  }, state => /*#__PURE__*/createElement(MenuLink, {
    key: index,
    link: navigationLink.link,
    childrenLinks: navigationLink.children ? navigationLink.children.map(navigationLinkToMenuLinkDataProps) : null,
    index: startIndex + index,
    onClick: onClick,
    isLinkOpen: currentOpenLink !== -1 && currentOpenLink === startIndex + index,
    startChevronIconAnimation: state === 'entering' || state === 'entered'
  }));
};

const createMenuButton = shouldAppear => ({
  text,
  onClick
}, key) => /*#__PURE__*/createElement(MenuLinkAnimationWrapper, {
  shouldAppear: shouldAppear,
  key: key
}, /*#__PURE__*/createElement(MenuButton, {
  text: text,
  onClick: onClick
}));

const createEditionsLinks = (editions, editionEndpoint) => {
  const activeEdition = getActiveEdition(editions, editionEndpoint);
  return [{
    link: {
      href: activeEdition.link.href,
      text: activeEdition.link.text
    },
    children: editions.map(edition => ({
      link: {
        href: edition.link.href,
        text: edition.link.text
      },
      children: null
    }))
  }];
};
const MenuLinks = props => {
  const {
    menuLinks,
    editions,
    isMenuOpen,
    onClick,
    currentOpenLink,
    editionEndpoint,
    buttons = []
  } = props;
  const {
    backgroundColor,
    dividerColor,
    baseUnit
  } = useTheme(componentConfigThemeKey$i);
  const style = getMenuLinksStyle(backgroundColor, dividerColor, baseUnit);
  const editionsLinks = editions ? createEditionsLinks(editions, editionEndpoint) : [];
  return /*#__PURE__*/createElement(ErrorBoundary, null, /*#__PURE__*/createElement(CSSTransition, {
    in: isMenuOpen,
    unmountOnExit: true,
    mountOnEnter: true,
    timeout: {
      enter: menuEnterTimeOut,
      exit: menuExitTimeOut
    },
    classNames: {
      enter: css(style.enter),
      enterActive: css(style.enterActive),
      exit: css(style.exit),
      exitActive: css(style.exitActive)
    }
  }, state => /*#__PURE__*/createElement("ul", {
    className: css(style.style)
  }, menuLinks.map(createMenuLink(onClick, currentOpenLink, state === 'entering' || state === 'entered')), buttons.map(createMenuButton(state === 'entering' || state === 'entered')), /*#__PURE__*/createElement("div", {
    className: css(style.divider)
  }), editionsLinks.map(createEditionLink(onClick, currentOpenLink, state === 'entering' || state === 'entered', menuLinks.length)))));
};

const getMenuStyle = () => StyleSheet.create({
  style: {
    [MEDIA_BREAKPOINTS.large]: {
      display: 'none'
    },
    [MEDIA_BREAKPOINTS.medium]: {
      display: 'inline-flex'
    },
    [MEDIA_BREAKPOINTS.small]: {
      display: 'inline-flex'
    }
  }
});

const Menu = props => {
  const [state, setState] = useState({
    isMenuOpen: false,
    currentOpenLink: -1
  });
  const {
    isMenuOpen,
    currentOpenLink
  } = state;

  const clickOnMenuIcon = () => {
    setState({
      isMenuOpen: !isMenuOpen,
      currentOpenLink: isMenuOpen ? currentOpenLink : -1
    });
  };

  const clickOnLinkDropDown = index => {
    setState({
      isMenuOpen,
      currentOpenLink: currentOpenLink === index ? -1 : index
    });
  };

  const {
    links,
    editions,
    editionEndpoint,
    buttons
  } = props;
  const style = getMenuStyle();
  return /*#__PURE__*/React__default.createElement("div", {
    className: css(style.style)
  }, /*#__PURE__*/React__default.createElement(MenuIcon, {
    isMenuOpen: isMenuOpen,
    onClick: clickOnMenuIcon
  }), /*#__PURE__*/React__default.createElement(MenuLinks, {
    menuLinks: links,
    buttons: buttons,
    editions: editions,
    isMenuOpen: isMenuOpen,
    onClick: clickOnLinkDropDown,
    currentOpenLink: currentOpenLink,
    editionEndpoint: editionEndpoint
  }));
};

const injectArticleBlockByIndexEvent = 'injectArticleBlockByIndexEvent';
const viewabilityArticleBlockEvent = 'viewabilityArticleBlockEvent';
const injectVideoLogoSuccessfully = 'injectVideoLogoSuccessfully';
const setEventEmitter = (eventName, eventHandler) => {
  if (window.__mmClientApiSubscriptions__ && window.__mmClientApiSubscriptions__.get(eventName)) {
    window.__mmClientApiSubscriptions__.get(eventName).forEach(params => {
      try {
        eventHandler(params);
      } catch (e) {
        console.error(`ClientAPI Error. The error is in callback for one of the following events: "${injectArticleBlockByIndexEvent}", "${viewabilityArticleBlockEvent}" or "${injectVideoLogoSuccessfully}". Please check the callback value.`, e);
      }
    });
  }
};

const embeddedVideoLogoPaddingInPx = 20;

const getInjectedVideoLogoStyle = (reduceHeightAnimationDuration, menuAnimation) => {
  return StyleSheet.create({
    wrapper: {
      [MEDIA_BREAKPOINTS.large]: {
        height: menuAnimation === AnimationDirection.UP ? `${primaryHeaderHeights.large * reducedLogoHeightFactor}px` : `${primaryHeaderHeights.large}px`
      },
      [MEDIA_BREAKPOINTS.medium]: {
        height: `${primaryHeaderHeights.medium}px`
      },
      [MEDIA_BREAKPOINTS.small]: {
        height: `${primaryHeaderHeights.small}px`
      },
      transition: `${reduceHeightAnimationDuration}ms`
    },
    video: {
      height: '100%',
      alignItems: 'center',
      display: 'inline-flex',
      margin: `0 -${embeddedVideoLogoPaddingInPx}px`
    },
    source: {
      height: '100%'
    }
  });
};

const VideoLogo = props => {
  const {
    videoPath,
    href,
    reduceHeightAnimationDuration = 0,
    menuAnimation = AnimationDirection.NONE
  } = props;
  const style = getInjectedVideoLogoStyle(reduceHeightAnimationDuration, menuAnimation);
  React__default.useEffect(() => {
    setEventEmitter(injectVideoLogoSuccessfully, callback => callback());
  }, [videoPath]);
  return /*#__PURE__*/React__default.createElement(Link, {
    className: css(style.wrapper),
    href: href
  }, /*#__PURE__*/React__default.createElement("video", {
    className: css(style.video),
    loop: true,
    autoPlay: true,
    muted: true
  }, /*#__PURE__*/React__default.createElement("source", {
    className: css(style.source),
    src: videoPath,
    type: "video/mp4"
  })));
};

const componentConfigThemeKey$j = 'primaryHeader';

const getStyle$9 = (baseUnit, backgroundColor, menuAnimation, reduceHeightAnimationDuration) => {
  return StyleSheet.create({
    headerWrapper: {
      position: 'relative',
      backgroundColor,
      zIndex: 100
    },
    header: {
      [MEDIA_BREAKPOINTS.large]: {
        height: menuAnimation === AnimationDirection.UP ? `${reducedPrimaryHeaderHeights.large}px` : `${primaryHeaderHeights.large}px`
      },
      [MEDIA_BREAKPOINTS.medium]: {
        height: `${primaryHeaderHeights.medium}px`
      },
      [MEDIA_BREAKPOINTS.small]: {
        height: `${primaryHeaderHeights.small}px`
      },
      display: 'flex',
      justifyContent: 'space-between',
      maxWidth: `${HUGE_SCREEN_SIZE}px`,
      margin: '0 auto',
      transition: `${reduceHeightAnimationDuration}ms`
    },
    itemsBasicContainer: {
      display: 'grid',
      gridAutoFlow: 'column',
      height: '100%',
      width: 'fit-content',
      alignItems: 'center',
      gridColumnGap: `${baseUnit}px`
    },
    itemsRightContainer: {
      [MEDIA_BREAKPOINTS.large]: {
        paddingRight: `${baseUnit}px`
      },
      [MEDIA_BREAKPOINTS.medium]: {
        paddingRight: `${baseUnit / 2}px`
      },
      [MEDIA_BREAKPOINTS.small]: {
        paddingRight: `${baseUnit / 2}px`
      }
    },
    itemsLeftContainer: {
      paddingLeft: `${baseUnit}px`
    }
  });
};

const PrimaryHeader = props => {
  const {
    links,
    logo,
    editions,
    cta,
    menu,
    videoLogo,
    pagePath,
    editionEndpoint,
    menuAnimation = AnimationDirection.NONE,
    reduceHeightAnimationDuration = 0
  } = props;
  const {
    baseUnit,
    backgroundColor,
    boxShadowTypeNormal
  } = useTheme(componentConfigThemeKey$j);
  const style = getStyle$9(baseUnit, backgroundColor, menuAnimation, reduceHeightAnimationDuration);
  const boxShadowStyle = createStyleForBoxShadow({
    boxShadowTypeNormal
  });
  return /*#__PURE__*/React__default.createElement(ErrorBoundary, null, /*#__PURE__*/React__default.createElement("div", {
    className: css(style.headerWrapper, boxShadowStyle.style)
  }, /*#__PURE__*/React__default.createElement("div", {
    className: css(style.header)
  }, /*#__PURE__*/React__default.createElement("div", {
    className: css(style.itemsBasicContainer, style.itemsLeftContainer)
  }, menu ? /*#__PURE__*/React__default.createElement(Menu, {
    links: menu.links,
    editions: editions,
    editionEndpoint: editionEndpoint,
    buttons: menu.buttons
  }) : null, videoLogo ? /*#__PURE__*/React__default.createElement(VideoLogo, _extends({}, videoLogo, {
    href: logo.href,
    menuAnimation: menuAnimation,
    reduceHeightAnimationDuration: reduceHeightAnimationDuration
  })) : /*#__PURE__*/React__default.createElement(Logo$1, _extends({}, logo, {
    logoHeights: createLogoHeights(30, 30, 40),
    menuAnimation: menuAnimation,
    reduceHeightAnimationDuration: reduceHeightAnimationDuration
  })), links ? /*#__PURE__*/React__default.createElement(PrimaryLinks, {
    links: links,
    pagePath: pagePath
  }) : null), /*#__PURE__*/React__default.createElement("div", {
    className: css(style.itemsBasicContainer, style.itemsRightContainer)
  }, cta ? /*#__PURE__*/React__default.createElement(CtaButton, cta) : null, editions ? /*#__PURE__*/React__default.createElement(Editions, {
    editions: editions,
    editionEndpoint: editionEndpoint
  }) : null))));
};

const componentConfigThemeKey$k = 'moreLinks';

const getStyle$a = theme => {
  const {
    titleColor,
    menuBackground
  } = theme;
  return StyleSheet.create({
    wrapper: {
      position: 'relative',
      height: '100%',
      display: 'flex'
    },
    button: {
      display: 'flex',
      alignItems: 'center',
      backgroundColor: 'transparent',
      border: 'none',
      outline: 'none'
    },
    buttonTitle: {
      color: titleColor,
      marginRight: '9px'
    },
    menuBody: {
      listStyle: 'none',
      width: '100%',
      position: 'absolute',
      backgroundColor: menuBackground
    }
  });
};

const MoreLinks = props => {
  const {
    moreLinksText,
    links,
    pagePath
  } = props;
  const themeProps = useTheme(componentConfigThemeKey$k);
  const {
    fontSize
  } = themeProps;
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const toggleMenu = useCallback(() => {
    setIsMenuOpen(!isMenuOpen);
  }, [isMenuOpen]);
  const style = getStyle$a(themeProps);
  const chevronDirection = isMenuOpen ? Directions.UP : Directions.DOWN;
  return /*#__PURE__*/createElement("div", {
    className: css(style.wrapper)
  }, /*#__PURE__*/createElement("button", {
    type: "button",
    className: css(style.button),
    onClick: toggleMenu
  }, /*#__PURE__*/createElement(Span, {
    styles: semanticCSS(style.buttonTitle),
    fontSizeLarge: fontSize,
    fontSizeMedium: fontSize,
    fontSizeSmall: fontSize
  }, moreLinksText), /*#__PURE__*/createElement(ChevronIcon, {
    direction: chevronDirection
  })), isMenuOpen && /*#__PURE__*/createElement(DropDownMenu, {
    configThemeKey: "moreLinks",
    menuLinks: links,
    highlightActive: true,
    activeEndpoint: pagePath
  }));
};

const componentConfigThemeKey$l = 'secondaryLink';

const getTextStyle = isLastLink => {
  return StyleSheet.create({
    link: {
      width: 'max-content',
      [MEDIA_BREAKPOINTS.medium]: {
        paddingRight: isLastLink ? `${baseUnit * 2}px` : 0
      },
      [MEDIA_BREAKPOINTS.small]: {
        paddingRight: isLastLink ? `${baseUnit * 2}px` : 0
      }
    }
  });
};

const isLastLink = (index, linksLength) => index + 1 === linksLength;

const getContentStyle$2 = (isActiveLink, fontSizesStyles, fontSize) => {
  const fontSizesActive = {
    fontSizeSmall: TYPOGRAPHY_SIZE.BIGGER,
    fontSizeMedium: TYPOGRAPHY_SIZE.BIGGER,
    fontSizeLarge: TYPOGRAPHY_SIZE.BIGGER
  };
  const fontSizesNotActive = {
    fontSizeSmall: fontSize,
    fontSizeMedium: fontSize,
    fontSizeLarge: fontSize
  };
  return isActiveLink ? getStyleBySize(fontSizesStyles, fontSizesActive) : getStyleBySize(fontSizesStyles, fontSizesNotActive);
};

const SecondaryLink = props => {
  const {
    linksLength,
    index,
    isHoveredLink,
    pagePath,
    linkData,
    maxVisibleLinks = 6
  } = props;
  const {
    activeStateColor,
    hoverOtherLinksColors,
    idleColor,
    hoverStateColor,
    fontSizesStyles,
    fontSize
  } = useTheme(componentConfigThemeKey$l);
  const isActiveLink = linkData.link.href === pagePath;
  const calculatedIdleColor = isActiveLink ? activeStateColor : notActiveLinkIdleColor(isHoveredLink, hoverOtherLinksColors, idleColor);
  const calculatedHoverColor = isActiveLink ? activeStateColor : hoverStateColor;
  const linkStyleBasedOnScreen = linksLength > maxVisibleLinks && index >= maxVisibleLinks - 1 ? {
    [MEDIA_BREAKPOINTS.large]: {
      display: 'none'
    },
    [MEDIA_BREAKPOINTS.small]: {
      display: 'inline-flex'
    },
    [MEDIA_BREAKPOINTS.medium]: {
      display: 'inline-flex'
    }
  } : {};
  const linkStyle = generateLinkStyleFromTheme({
    idleColor: calculatedIdleColor,
    activeStateColor,
    hoverStateColor: calculatedHoverColor
  }, linkStyleBasedOnScreen);
  const style = getTextStyle(isLastLink(index, linksLength));
  const contentStyle = getContentStyle$2(isActiveLink, fontSizesStyles, fontSize);
  return /*#__PURE__*/createElement(TextLinkComponent, {
    href: linkData.link.href,
    linkStyle: linkStyle
  }, /*#__PURE__*/createElement(Span, {
    styles: semanticCSS(contentStyle, style.link)
  }, linkData.link.text));
};

const getContainerStyle$1 = baseUnit => {
  return StyleSheet.create({
    container: {
      display: 'grid',
      gridAutoFlow: 'column',
      alignItems: 'center',
      height: '100%',
      gridColumnGap: `${baseUnit}px`,
      [MEDIA_BREAKPOINTS.medium]: {
        overflowX: 'auto'
      },
      [MEDIA_BREAKPOINTS.small]: {
        overflowX: 'auto'
      }
    },
    dropDownMenuWrapper: {
      height: '100%',
      [MEDIA_BREAKPOINTS.large]: {
        display: 'block'
      },
      [MEDIA_BREAKPOINTS.small]: {
        display: 'none'
      },
      [MEDIA_BREAKPOINTS.medium]: {
        display: 'none'
      }
    }
  });
};
const SecondaryLinks = props => {
  const {
    links,
    moreLinksText,
    maxVisibleLinks = 6,
    pagePath
  } = props;
  const {
    baseUnit
  } = useTheme();
  const [isHoveredLink, setIsHoveredLink] = React__default.useState(false);
  const style = getContainerStyle$1(baseUnit);
  const moreLinks = links.length > maxVisibleLinks ? links.slice(maxVisibleLinks - 1) : [];
  return /*#__PURE__*/React__default.createElement("div", {
    className: css(style.container)
  }, /*#__PURE__*/React__default.createElement("div", {
    className: css(style.container),
    onMouseEnter: () => setIsHoveredLink(true),
    onMouseLeave: () => setIsHoveredLink(false)
  }, links.map((linkData, index) => /*#__PURE__*/React__default.createElement(SecondaryLink, {
    key: index,
    linkData: linkData,
    index: index,
    linksLength: links.length,
    isHoveredLink: isHoveredLink,
    pagePath: pagePath,
    maxVisibleLinks: maxVisibleLinks
  }))), /*#__PURE__*/React__default.createElement("div", {
    className: css(style.dropDownMenuWrapper)
  }, moreLinks.length > 0 && /*#__PURE__*/React__default.createElement(MoreLinks, {
    moreLinksText: moreLinksText,
    links: moreLinks,
    pagePath: pagePath
  })));
};

const componentConfigThemeKey$m = 'secondaryHeader';
const maxVisibleLinksOnLargeAndXLarge = 6;

const getStyle$b = (baseUnit, backgroundColor, animationDuration) => {
  return StyleSheet.create({
    headerWrapper: {
      backgroundColor,
      zIndex: 50,
      position: 'relative'
    },
    header: {
      height: `${secondaryHeaderHeight}px`,
      display: 'grid',
      gridAutoFlow: 'column',
      width: '100%',
      alignItems: 'center',
      justifyContent: 'flex-start',
      gridColumnGap: `${baseUnit}px`,
      paddingLeft: `${baseUnit}px`,
      maxWidth: `${HUGE_SCREEN_SIZE}px`,
      margin: '0 auto',
      boxSizing: 'border-box'
    },
    foldingUp: {
      transition: `${animationDuration}ms`,
      [MEDIA_BREAKPOINTS.medium]: {
        transform: 'translateY(-100%)'
      },
      [MEDIA_BREAKPOINTS.small]: {
        transform: 'translateY(-100%)'
      }
    },
    foldingDown: {
      transition: `${animationDuration}ms`,
      [MEDIA_BREAKPOINTS.medium]: {
        transform: 'translateY(0)',
        transitionDelay: '200ms',
        transition: `${animationDuration}ms`
      },
      [MEDIA_BREAKPOINTS.small]: {
        transform: 'translateY(0)',
        transitionDelay: '200ms'
      }
    }
  });
};

const getClassWithAnimations = (animation, baseStyle, boxShadow) => {
  switch (animation) {
    case AnimationDirection.UP:
      return css(baseStyle.headerWrapper, boxShadow.style, baseStyle.foldingUp);

    case AnimationDirection.DOWN:
      return css(baseStyle.headerWrapper, boxShadow.style, baseStyle.foldingDown);

    default:
      return css(baseStyle.headerWrapper, boxShadow.style);
  }
};

const SecondaryHeader = props => {
  const {
    links,
    logo,
    moreLinksText,
    pagePath,
    animationDuration = 0,
    animation = AnimationDirection.NONE
  } = props;
  const {
    baseUnit,
    backgroundColor,
    boxShadowTypeNormal
  } = useTheme(componentConfigThemeKey$m);
  const style = getStyle$b(baseUnit, backgroundColor, animationDuration);
  const boxShadowStyle = createStyleForBoxShadow({
    boxShadowTypeNormal
  });
  const combinedStyle = getClassWithAnimations(animation, style, boxShadowStyle);
  return /*#__PURE__*/React__default.createElement(ErrorBoundary, null, /*#__PURE__*/React__default.createElement("div", {
    className: combinedStyle
  }, /*#__PURE__*/React__default.createElement("div", {
    className: css(style.header)
  }, logo ? /*#__PURE__*/React__default.createElement(Logo$1, logo) : null, links ? /*#__PURE__*/React__default.createElement(SecondaryLinks, {
    links: links,
    maxVisibleLinks: maxVisibleLinksOnLargeAndXLarge,
    moreLinksText: moreLinksText,
    pagePath: pagePath
  }) : null)));
};

const getStyle$c = isSecondaryHeaderEnabled => {
  const heights = getHeaderHeights(isSecondaryHeaderEnabled);
  return StyleSheet.create({
    anchor: {
      position: 'fixed',
      [MEDIA_BREAKPOINTS.large]: {
        top: heights.large
      },
      [MEDIA_BREAKPOINTS.medium]: {
        top: heights.medium
      },
      [MEDIA_BREAKPOINTS.small]: {
        top: heights.small
      },
      left: 0,
      right: 0,
      height: 0,
      transition: '800ms'
    },
    foldingUp: {
      [MEDIA_BREAKPOINTS.medium]: {
        top: 0
      },
      [MEDIA_BREAKPOINTS.small]: {
        top: 0
      }
    },
    foldingDown: {
      [MEDIA_BREAKPOINTS.medium]: {
        top: heights.medium
      },
      [MEDIA_BREAKPOINTS.small]: {
        top: heights.small
      }
    }
  });
};

const MontiAnchor = props => {
  const {
    isSecondaryHeaderEnabled,
    menuAnimation
  } = props;
  const style = getStyle$c(isSecondaryHeaderEnabled);
  let finalStyle;

  switch (menuAnimation) {
    case 'foldingUp':
      finalStyle = css(style.anchor, style.foldingUp);
      break;

    case 'foldingDown':
      finalStyle = css(style.anchor, style.foldingDown);
      break;

    default:
      finalStyle = css(style.anchor);
  }

  return /*#__PURE__*/createElement(ErrorBoundary, null, /*#__PURE__*/createElement("div", {
    className: `${finalStyle} video-top-anchor`
  }));
};

const animationDuration = 200;
const HeaderAnimationContextInitialValue = {
  menuFoldState: AnimationDirection.NONE,
  animationDuration
};
const HeaderAnimationContext = React__default.createContext(HeaderAnimationContextInitialValue);
const HeaderAnimation = ({
  children,
  headerHeights
}) => {
  const firstMenuFoldThreshold = 640;
  const secondMenuFoldThreshold = 100;
  const scrollUpThreshold = -400;
  const [scrollTicking, setScrollTicking] = useState(false);
  const [scrolledUpPixels, setScrolledUpPixels] = useState(0);
  const [menuFirstFolded, setMenuFirstFolded] = useState(false);
  const [menuFoldState, setMenuFoldState] = useState(HeaderAnimationContextInitialValue.menuFoldState);

  const isScrollingUp = deltaY => {
    return deltaY < 0;
  };

  const isScrollingDown = deltaY => {
    return deltaY > 0;
  };

  const getMenuAnimation = originalScrollPosition => {
    const currentScrollPosition = window.scrollY;
    const deltaY = currentScrollPosition - originalScrollPosition;

    if (currentScrollPosition > firstMenuFoldThreshold && !menuFirstFolded) {
      setMenuFirstFolded(true);
      return AnimationDirection.UP;
    }

    if (isScrollingUp(deltaY)) {
      setScrolledUpPixels(scrolledUpPixels + deltaY);

      if (scrolledUpPixels < scrollUpThreshold || currentScrollPosition <= headerHeights.small || currentScrollPosition <= headerHeights.medium) {
        return AnimationDirection.DOWN;
      }
    }

    if (isScrollingDown(deltaY) && menuFirstFolded && currentScrollPosition > secondMenuFoldThreshold) {
      setScrolledUpPixels(0);
      return AnimationDirection.UP;
    }

    return menuFoldState;
  };

  const scrollHandler = () => {
    if (!scrollTicking) {
      const originalY = window.scrollY;
      setScrollTicking(true);
      setTimeout(() => {
        const menuAnimation = getMenuAnimation(originalY);

        if (menuAnimation !== menuFoldState) {
          setMenuFoldState(menuAnimation);
        }

        setScrollTicking(false);
      }, 100);
    }
  };

  useEffect(() => {
    window.addEventListener('scroll', scrollHandler);
    return () => window.removeEventListener('scroll', scrollHandler);
  });
  return /*#__PURE__*/React__default.createElement(HeaderAnimationContext.Provider, {
    value: {
      menuFoldState,
      animationDuration
    }
  }, children);
};

const getStyle$d = animationDuration => {
  return StyleSheet.create({
    header: {
      position: 'fixed',
      width: '100%',
      zIndex: 1500,
      top: 0
    },
    foldingUp: {
      [MEDIA_BREAKPOINTS.medium]: {
        transition: `${animationDuration}ms`,
        transform: 'translateY(-100%)',
        transitionDelay: `${animationDuration}ms`
      },
      [MEDIA_BREAKPOINTS.small]: {
        transition: `${animationDuration}ms`,
        transform: 'translateY(-100%)',
        transitionDelay: `${animationDuration}ms`
      }
    },
    foldingDown: {
      [MEDIA_BREAKPOINTS.medium]: {
        transform: 'translateY(0)'
      },
      [MEDIA_BREAKPOINTS.small]: {
        transform: 'translateY(0)'
      }
    }
  });
};

const HeaderComponent = props => {
  const {
    menuFoldState,
    animationDuration
  } = React__default.useContext(HeaderAnimationContext);
  const {
    primaryHeader,
    secondaryHeader,
    pagePath,
    editionEndpoint
  } = props;
  const {
    links,
    logo,
    editions,
    cta,
    menu,
    videoLogo
  } = primaryHeader;

  const generateSecondaryHeader = () => {
    if (!secondaryHeader) {
      return null;
    }

    return /*#__PURE__*/React__default.createElement(SecondaryHeader, {
      moreLinksText: secondaryHeader.moreLinksText,
      links: secondaryHeader.links,
      logo: secondaryHeader.logo,
      pagePath: pagePath,
      animation: menuFoldState,
      animationDuration: animationDuration
    });
  };

  const getHeaderStyleWithAnimation = () => {
    const style = getStyle$d(animationDuration);

    if (menuFoldState === AnimationDirection.NONE) {
      return css(style.header);
    }

    const animationStyle = menuFoldState === AnimationDirection.UP ? style.foldingUp : style.foldingDown;
    return css(style.header, animationStyle);
  };

  return /*#__PURE__*/React__default.createElement(ErrorBoundary, null, /*#__PURE__*/React__default.createElement(MontiAnchor, {
    isSecondaryHeaderEnabled: !!secondaryHeader,
    menuAnimation: menuFoldState
  }), /*#__PURE__*/React__default.createElement("header", {
    className: getHeaderStyleWithAnimation()
  }, /*#__PURE__*/React__default.createElement(PrimaryHeader, {
    links,
    logo,
    editions,
    cta,
    menu,
    videoLogo,
    pagePath,
    editionEndpoint,
    menuAnimation: menuFoldState,
    reduceHeightAnimationDuration: animationDuration
  }), generateSecondaryHeader()));
};
const Header = props => {
  const {
    secondaryHeader
  } = props;
  const headerHeights = getHeaderHeights(secondaryHeader !== null);
  return /*#__PURE__*/React__default.createElement(ErrorBoundary, null, /*#__PURE__*/React__default.createElement(HeaderAnimation, {
    headerHeights: headerHeights
  }, /*#__PURE__*/React__default.createElement(HeaderComponent, props)));
};

const componentConfigThemeKey$n = 'errorPage';

const createErrorStyle = theme => {
  const {
    baseUnit,
    titleColor,
    textColor,
    buttonTextColorNormal,
    buttonTextColorHover,
    buttonTextColorActive,
    backgroundColorNormal,
    backgroundColorHover,
    backgroundColorActive,
    borderRadius
  } = theme;
  return StyleSheet.create({
    wrapperStyle: {
      paddingTop: `${2 * baseUnit}px`
    },
    title: {
      textAlign: 'center',
      color: titleColor
    },
    secondaryTitle: {
      textAlign: 'center',
      color: titleColor,
      [MEDIA_BREAKPOINTS.large]: {
        marginTop: 0
      },
      [MEDIA_BREAKPOINTS.medium]: {
        marginTop: `${baseUnit / 2}px`
      },
      [MEDIA_BREAKPOINTS.small]: {
        marginTop: `${baseUnit / 2}px`
      }
    },
    content: {
      textAlign: 'center',
      color: textColor
    },
    buttonWrapper: {
      display: 'flex',
      justifyContent: 'center',
      textAlign: 'center',
      marginTop: `${baseUnit}px`
    },
    button: {
      display: 'flex',
      height: '40px',
      paddingRight: `${baseUnit}px`,
      paddingLeft: `${baseUnit}px`,
      alignItems: 'center',
      color: buttonTextColorNormal,
      ':hover': {
        backgroundColor: backgroundColorHover,
        color: buttonTextColorHover
      },
      ':active': {
        backgroundColor: backgroundColorActive,
        color: buttonTextColorActive
      },
      backgroundColor: backgroundColorNormal,
      borderRadius: `${borderRadius}px`,
      textDecoration: 'none'
    }
  });
};

const createImageErrorStyle = theme => {
  const {
    baseUnit
  } = theme;
  return StyleSheet.create({
    style: {
      margin: `0px auto ${baseUnit}px auto`,
      [MEDIA_BREAKPOINTS.large]: {
        width: '300px',
        height: '300px'
      },
      [MEDIA_BREAKPOINTS.medium]: {
        width: '180px',
        height: '180px'
      },
      [MEDIA_BREAKPOINTS.small]: {
        width: '180px',
        height: '180px'
      }
    }
  });
};

const imageWidths$2 = createImageWidths(360, 360, 360);
const Error$1 = props => {
  const {
    title,
    image,
    secondaryTitle,
    content,
    button
  } = props;
  const themeProps = useTheme(componentConfigThemeKey$n);
  const aspectRatio = {
    x: 1,
    y: 1
  };
  const errorStyle = createErrorStyle(themeProps);
  const imageErrorStyle = createImageErrorStyle(themeProps);
  const {
    fontSizesStyles,
    boxShadowTypeNormal,
    boxShadowTypeHover,
    boxShadowTypeActive,
    borderColorNormal,
    borderColorActive,
    borderColorHover,
    borderWidthNormal,
    borderWidthActive,
    borderWidthHover,
    buttonFontSizeLarge,
    buttonFontSizeMedium,
    buttonFontSizeSmall
  } = themeProps;
  const borderBoxShadow = {
    borderColorTypeActive: borderColorActive,
    borderColorTypeHover: borderColorHover,
    borderColorTypeNormal: borderColorNormal,
    borderWidthTypeActive: borderWidthActive,
    borderWidthTypeHover: borderWidthHover,
    borderWidthTypeNormal: borderWidthNormal
  };
  const boxShadowStyle = createStyleForBoxShadow({
    boxShadowTypeNormal,
    boxShadowTypeHover,
    boxShadowTypeActive,
    borderBoxShadow
  });
  const textStyle = getStyleBySize(fontSizesStyles, {
    fontSizeSmall: buttonFontSizeSmall,
    fontSizeMedium: buttonFontSizeMedium,
    fontSizeLarge: buttonFontSizeLarge
  });
  return /*#__PURE__*/createElement(ErrorBoundary, null, /*#__PURE__*/createElement("div", {
    className: css(errorStyle.wrapperStyle)
  }, /*#__PURE__*/createElement("div", {
    className: css(imageErrorStyle.style)
  }, /*#__PURE__*/createElement(Image, _extends({}, image, {
    additionalStyle: imageErrorStyle,
    aspectRatio: aspectRatio,
    imageWidths: imageWidths$2,
    transparent: true
  }))), /*#__PURE__*/createElement(H1, {
    styles: semanticCSS(errorStyle.title)
  }, title), /*#__PURE__*/createElement(P, {
    styles: semanticCSS(errorStyle.secondaryTitle)
  }, secondaryTitle), /*#__PURE__*/createElement(P, {
    styles: semanticCSS(errorStyle.content)
  }, content), /*#__PURE__*/createElement("div", {
    className: css(errorStyle.buttonWrapper)
  }, /*#__PURE__*/createElement(Link, {
    href: button.href,
    className: css(errorStyle.button, boxShadowStyle.style)
  }, /*#__PURE__*/createElement(Span, {
    className: css(textStyle)
  }, button.text)))));
};

const createStyle$4 = () => StyleSheet.create({
  ad: {
    display: 'flex',
    justifyContent: 'center'
  }
});

const Ad = /*#__PURE__*/memo(({
  id,
  additionalStyle
}) => {
  const style = createStyle$4();
  return id ? /*#__PURE__*/createElement("div", {
    id: id,
    className: additionalStyle ? css(style.ad, additionalStyle.style) : css(style.ad)
  }) : null;
});

function getIntersectionThresholds() {
  const thresholds = [];

  for (let i = 0; i <= 1.0; i += 0.01) {
    thresholds.push(i);
  }

  return thresholds;
}

const options = {
  root: null,
  threshold: getIntersectionThresholds()
};

function isIntersectionAtBottomOfElementBiggerThanViewPort(subscriptionThreshold, entry) {
  return subscriptionThreshold === 1 && entry.boundingClientRect.bottom === entry.intersectionRect.bottom;
}

function isIntersectingAboveThreshold(subscriptionThreshold, entry) {
  return subscriptionThreshold < entry.intersectionRatio;
}

const observerCallback = subscriptions => (e, observer) => {
  e.forEach(entry => {
    if (entry.isIntersecting) {
      const elementSubscriptions = subscriptions.get(entry.target) || [];
      elementSubscriptions.filter(sub => isIntersectingAboveThreshold(sub.threshold, entry) || isIntersectionAtBottomOfElementBiggerThanViewPort(sub.threshold, entry)).forEach(sub => {
        observer.unobserve(entry.target);
        sub.handler(entry.target);
        delete elementSubscriptions[elementSubscriptions.findIndex(item => item === sub)];
      });
    }
  });
};

const ViewabilityHandlerContext = createContext({});
const useViewabilityHandler = (handler, threshold) => {
  const ref = useRef(null);
  const context = useContext(ViewabilityHandlerContext);
  const {
    intersectionObserver,
    viewabilityHandlers
  } = context;
  useEffect(() => {
    if (!ref.current || !intersectionObserver || !viewabilityHandlers) return;
    const element = ref.current;
    const subscription = {
      handler,
      threshold
    };
    viewabilityHandlers.set(element, [...(viewabilityHandlers.get(element) || []), subscription]);
    element.classList.add('viewablity-block');
    intersectionObserver.observe(element);
  }, [handler, intersectionObserver, threshold, viewabilityHandlers]);
  return ref;
};
const ViewabilityProvider = ({
  children
}) => {
  const {
    intersectionObserver,
    viewabilityHandlers
  } = useContext(ViewabilityHandlerContext);
  const context = useMemo(() => {
    const newViewabilityHandlers = viewabilityHandlers || new WeakMap();
    return {
      viewabilityHandlers: newViewabilityHandlers,
      intersectionObserver: typeof IntersectionObserver !== 'undefined' && (intersectionObserver || new IntersectionObserver(observerCallback(newViewabilityHandlers), options)) || undefined
    };
  }, [intersectionObserver, viewabilityHandlers]);

  if (typeof intersectionObserver !== 'undefined' && typeof viewabilityHandlers !== 'undefined' || typeof IntersectionObserver === 'undefined') {
    return /*#__PURE__*/createElement(Fragment, null, children);
  }

  return /*#__PURE__*/createElement(ViewabilityHandlerContext.Provider, {
    value: context
  }, children);
};

const Article = ({
  children,
  articleStyleClassName
}) => {
  const viewabilityCallback = useCallback(() => {
    // @ts-ignore
    window.mmClientApi = window.mmClientApi || []; // @ts-ignore

    window.mmClientApi.push('analytics', {
      event_category: 'Article',
      event_action: 'article end',
      event_label: ''
    });
  }, []);
  const ref = useViewabilityHandler(viewabilityCallback, 1);
  return /*#__PURE__*/createElement("article", {
    ref: ref,
    className: articleStyleClassName
  }, children);
};

const createStyle$5 = () => StyleSheet.create({
  style: {
    margin: '0 auto',
    maxWidth: '100%',
    minWidth: '100%',
    [MEDIA_BREAKPOINTS.large]: {
      paddingLeft: '80px',
      maxWidth: 'calc(100% - 80px)',
      minWidth: 'calc(100% - 80px)'
    }
  }
});

const PostMainContent = ({
  children
}) => {
  return /*#__PURE__*/createElement(ViewabilityProvider, null, /*#__PURE__*/createElement(Article, {
    articleStyleClassName: css(createStyle$5().style)
  }, children));
};

const componentConfigurationKey$e = 'postMainContent';

const createStyle$6 = backgroundColor => StyleSheet.create({
  style: {
    backgroundColor,
    margin: '0 auto',
    maxWidth: '100%',
    minWidth: '100%'
  }
});

const FullWidthPostMainContent = ({
  children
}) => {
  const {
    backgroundColor
  } = useTheme(componentConfigurationKey$e);
  return /*#__PURE__*/createElement(ViewabilityProvider, null, /*#__PURE__*/createElement(Article, {
    articleStyleClassName: css(createStyle$6(backgroundColor).style)
  }, children));
};

const createStyle$7 = baseUnit => {
  return StyleSheet.create({
    sidebar: {
      gridRowEnd: 'span 2',
      [MEDIA_BREAKPOINTS.large]: {
        gridRowEnd: 'span 2',
        padding: `${baseUnit}px`
      },
      [MEDIA_BREAKPOINTS.medium]: {
        display: 'none'
      },
      [MEDIA_BREAKPOINTS.small]: {
        display: 'none'
      }
    }
  });
};

const createAdditionalStyleForStickyAd = (headerHeights, baseUnit) => {
  return StyleSheet.create({
    style: {
      position: 'sticky',
      paddingTop: `${baseUnit}px`,
      [MEDIA_BREAKPOINTS.large]: {
        top: headerHeights.large
      },
      [MEDIA_BREAKPOINTS.medium]: {
        top: headerHeights.medium
      },
      [MEDIA_BREAKPOINTS.small]: {
        top: headerHeights.small
      }
    }
  });
};

const PostAside = props => {
  const {
    adId1,
    adId2
  } = props;
  const {
    baseUnit
  } = useTheme();
  const style = createStyle$7(baseUnit);
  const headerHeights = getHeaderHeights(false);
  return /*#__PURE__*/createElement("aside", {
    className: css(style.sidebar)
  }, /*#__PURE__*/createElement(Ad, {
    id: adId1
  }), /*#__PURE__*/createElement(Ad, {
    id: adId2,
    additionalStyle: createAdditionalStyleForStickyAd(headerHeights, baseUnit)
  }));
};

const getStyles$3 = baseUnit => {
  return StyleSheet.create({
    wrapper: {
      display: 'grid',
      [MEDIA_BREAKPOINTS.large]: {
        gridTemplateColumns: '660px 300px',
        gridColumnGap: `${baseUnit * 4}px`,
        paddingLeft: `${baseUnit * 4}px`
      },
      [MEDIA_BREAKPOINTS.medium]: {
        display: 'block',
        paddingLeft: '90px',
        paddingRight: '20px'
      },
      [MEDIA_BREAKPOINTS.small]: {
        display: 'block',
        padding: 0
      }
    }
  });
};

const PostContentWithAside = props => {
  const {
    adId1,
    adId2,
    children
  } = props;
  const {
    baseUnit
  } = useTheme();
  const styles = getStyles$3(baseUnit);
  return /*#__PURE__*/createElement("div", {
    className: css(styles.wrapper)
  }, /*#__PURE__*/createElement("div", null, children), /*#__PURE__*/createElement(PostAside, {
    adId1: adId1,
    adId2: adId2
  }));
};

const createStyle$8 = () => StyleSheet.create({
  style: {
    overflow: 'hidden',
    maxWidth: '1440px',
    [MEDIA_BREAKPOINTS.large]: {
      width: '100%'
    },
    [MEDIA_BREAKPOINTS.medium]: {
      width: '100%'
    },
    [MEDIA_BREAKPOINTS.small]: {
      width: 'unset'
    }
  }
});

const PostWithoutSidebarMainContent = ({
  children
}) => {
  return /*#__PURE__*/createElement(ViewabilityProvider, null, /*#__PURE__*/createElement(Article, {
    articleStyleClassName: css(createStyle$8().style)
  }, children));
};

const componentConfigurationKey$f = 'postMainContent';

const createEmbedPostStyle = backgroundColor => StyleSheet.create({
  style: {
    backgroundColor,
    overflow: 'hidden',
    maxWidth: '100%',
    minWidth: '100%'
  }
});

const EmbedPostMainContent = ({
  children
}) => {
  const {
    backgroundColor
  } = useTheme(componentConfigurationKey$f);
  return /*#__PURE__*/createElement(ViewabilityProvider, null, /*#__PURE__*/createElement(Article, {
    articleStyleClassName: css(createEmbedPostStyle(backgroundColor).style)
  }, children));
};

const createStyle$9 = baseUnit => {
  return StyleSheet.create({
    sidebar: {
      gridRowEnd: 'span 2',
      [MEDIA_BREAKPOINTS.large]: {
        gridRowEnd: 'span 2',
        padding: `${baseUnit}px`
      },
      [MEDIA_BREAKPOINTS.medium]: {
        display: 'none'
      },
      [MEDIA_BREAKPOINTS.small]: {
        display: 'none'
      }
    }
  });
};

const createAdditionalStyleForStickyAd$1 = (headerHeights, baseUnit) => {
  return StyleSheet.create({
    style: {
      position: 'sticky',
      paddingTop: `${baseUnit}px`,
      [MEDIA_BREAKPOINTS.large]: {
        top: headerHeights.large
      },
      [MEDIA_BREAKPOINTS.medium]: {
        top: headerHeights.medium
      },
      [MEDIA_BREAKPOINTS.small]: {
        top: headerHeights.small
      }
    }
  });
};

const PostSidebar = props => {
  const {
    adId1,
    adId2,
    isSecondaryHeaderEnabled
  } = props;
  const {
    baseUnit
  } = useTheme();
  const style = createStyle$9(baseUnit);
  const headerHeights = getHeaderHeights(isSecondaryHeaderEnabled);
  return /*#__PURE__*/createElement("div", {
    className: css(style.sidebar)
  }, /*#__PURE__*/createElement(Ad, {
    id: adId1
  }), /*#__PURE__*/createElement(Ad, {
    id: adId2,
    additionalStyle: createAdditionalStyleForStickyAd$1(headerHeights, baseUnit)
  }));
};

const getStyle$e = () => {
  return StyleSheet.create({
    wrapper: {
      maxWidth: `${LARGE_SCREEN_SIZE}px`,
      margin: 'auto',
      [MEDIA_BREAKPOINTS.large]: {
        display: 'grid',
        gridTemplateColumns: '2fr 1fr'
      },
      [MEDIA_BREAKPOINTS.medium]: {
        width: '75%',
        display: 'block'
      },
      [MEDIA_BREAKPOINTS.small]: {
        width: '100%',
        display: 'block'
      }
    }
  });
};

const ContentAndSidebar = props => {
  const {
    children
  } = props;
  const style = getStyle$e();
  return /*#__PURE__*/createElement(ErrorBoundary, null, /*#__PURE__*/createElement("div", {
    className: css(style.wrapper)
  }, children));
};

const horizontalGap = 2;

const getStyle$f = (maxWidth, mediumWidth = 800) => {
  return StyleSheet.create({
    wrapper: {
      maxWidth: `${maxWidth}px`,
      margin: 'auto',
      display: 'grid',
      gridRowGap: `${horizontalGap * baseUnit}px`,
      [MEDIA_BREAKPOINTS.medium]: {
        maxWidth: `${mediumWidth}px`,
        width: '100%'
      },
      [MEDIA_BREAKPOINTS.small]: {
        width: '100%'
      }
    }
  });
};

const ContentWithoutSidebar = props => {
  const {
    children,
    maxWidth,
    mediumWidth
  } = props;
  const style = getStyle$f(maxWidth, mediumWidth);
  return /*#__PURE__*/createElement(ErrorBoundary, null, /*#__PURE__*/createElement("div", {
    className: css(style.wrapper)
  }, children));
};

const getStyle$g = color => StyleSheet.create({
  author: {
    color
  }
});

const Author = props => {
  const {
    name,
    link,
    color,
    prefix = ''
  } = props;
  const style = getStyle$g(color);

  if (link) {
    return /*#__PURE__*/createElement(Fragment, null, prefix, /*#__PURE__*/createElement(Link, {
      href: link,
      className: css(style.author)
    }, name));
  }

  return /*#__PURE__*/createElement(Fragment, null, `${prefix}${name}`);
};

const getPrefix = index => {
  return index > 0 ? ', ' : '';
};

const AuthorsList = props => {
  const {
    authors,
    by,
    color
  } = props;
  return /*#__PURE__*/createElement(Fragment, null, `${by} `, authors && authors.map((author, index) => /*#__PURE__*/createElement(Author, _extends({
    key: index
  }, author, {
    color: color,
    prefix: getPrefix(index)
  }))));
};

const componentConfigurationKey$g = 'articleTitle';

const createStyle$a = ({
  titleColor,
  metaDataColor,
  baseUnit
}) => StyleSheet.create({
  wrapper: {
    position: 'relative'
  },
  title: {
    color: titleColor,
    marginTop: `${baseUnit}px`,
    marginBottom: `${baseUnit * 0.5}px`
  },
  metaData: {
    color: metaDataColor
  },
  sidesPadding: {
    marginTop: `${baseUnit * 0.5}px`,
    marginLeft: 0,
    marginRight: 0,
    [MEDIA_BREAKPOINTS.small]: {
      marginLeft: `${baseUnit}px`,
      marginRight: `${baseUnit}px`
    }
  }
});

const shouldDisplayAuthors = authors => {
  return authors && authors.length > 0;
};

const ArticleTitle = props => {
  const {
    title,
    by,
    updatedAtDateFormatted,
    updatedAtTimeAndDateFormatted,
    children,
    authors
  } = props;
  const themeProps = useTheme(componentConfigurationKey$g);
  const {
    metaDataColor,
    showTime,
    showTimestamp
  } = themeProps;
  const style = createStyle$a(themeProps);
  const hasAuthors = shouldDisplayAuthors(authors);
  const separator = hasAuthors ? ' | ' : '';
  const updatedAtFormatted = showTime ? updatedAtTimeAndDateFormatted : updatedAtDateFormatted;
  return /*#__PURE__*/createElement(ErrorBoundary, null, /*#__PURE__*/createElement(H1, {
    styles: semanticCSS(style.title, style.sidesPadding)
  }, title), /*#__PURE__*/createElement("div", {
    className: css(style.wrapper)
  }, /*#__PURE__*/createElement(H4, {
    styles: semanticCSS(style.metaData, style.sidesPadding)
  }, hasAuthors && /*#__PURE__*/createElement(AuthorsList, {
    by: by,
    authors: authors,
    color: metaDataColor
  }), showTimestamp ? `${separator}${updatedAtFormatted}` : null), children));
};

const componentConfigurationKey$h = 'articleTitleWithoutMetaData';

const createStyle$b = ({
  titleColor,
  baseUnit,
  horizontalPaddingFactorLarge,
  horizontalPaddingFactorMedium,
  horizontalPaddingFactorSmall
}) => StyleSheet.create({
  wrapper: {
    position: 'relative'
  },
  title: {
    color: titleColor,
    marginTop: `${baseUnit}px`,
    marginBottom: `${baseUnit * 0.5}px`,
    [MEDIA_BREAKPOINTS.small]: {
      padding: `0 ${baseUnit * horizontalPaddingFactorSmall}px`
    },
    [MEDIA_BREAKPOINTS.medium]: {
      padding: `0 ${baseUnit * horizontalPaddingFactorMedium}px`
    },
    [MEDIA_BREAKPOINTS.large]: {
      padding: `0 ${baseUnit * horizontalPaddingFactorLarge}px`
    }
  },
  sidesPadding: {
    marginTop: `${baseUnit * 0.5}px`,
    marginLeft: 0,
    marginRight: 0,
    [MEDIA_BREAKPOINTS.small]: {
      marginLeft: `${baseUnit}px`,
      marginRight: `${baseUnit}px`
    }
  }
});

const ArticleTitleWithoutMetaData = props => {
  const {
    title
  } = props;
  const themeProps = useTheme(componentConfigurationKey$h);
  const style = createStyle$b(themeProps);
  return /*#__PURE__*/createElement(ErrorBoundary, null, /*#__PURE__*/createElement(H1, {
    styles: semanticCSS(style.title, style.sidesPadding)
  }, title));
};

const componentConfigThemeKey$o = 'imageWithFigcaptionWrapper';
const getStyle$h = (aspectRatio, baseUnit, figcaptionTextColor, backgroundColor) => StyleSheet.create({
  base: {
    backgroundColor,
    position: 'relative',
    overflow: 'hidden',
    margin: 0,
    boxSizing: 'border-box',
    flex: '1 1 100%',
    minWidth: '0%',
    paddingTop: `calc(100% / (${aspectRatio.x / aspectRatio.y}))`
  },
  figcaption: {
    color: figcaptionTextColor,
    margin: `${baseUnit * 0.25}px`
  }
});
const getFigcaptionStyle = (baseUnit, borderColor, showBorderBottom) => {
  return showBorderBottom ? StyleSheet.create({
    style: {
      display: 'flex',
      alignItems: 'center',
      borderBottom: `1px solid ${borderColor}`,
      marginBottom: `${baseUnit}px`,
      [MEDIA_BREAKPOINTS.large]: {
        paddingTop: `${baseUnit}px`,
        paddingBottom: `${baseUnit}px`,
        width: '100%'
      },
      [MEDIA_BREAKPOINTS.medium]: {
        paddingTop: `${baseUnit * 0.5}px`,
        paddingBottom: `${baseUnit / 2}px`
      },
      [MEDIA_BREAKPOINTS.small]: {
        paddingTop: `${baseUnit * 0.5}px`,
        paddingBottom: `${baseUnit / 2}px`
      }
    }
  }) : StyleSheet.create({
    style: {}
  });
};
const ImageWithFigcaptionWrapper = ({
  additionalStyle,
  aspectRatio,
  figcaption,
  dataId,
  children
}) => {
  const {
    fontSizesStyles,
    baseUnit,
    figcaptionTextColor,
    backgroundColor,
    borderColor,
    showBorderBottom
  } = useTheme(componentConfigThemeKey$o);
  const imageWrapperStyle = getStyle$h(aspectRatio, baseUnit, figcaptionTextColor, backgroundColor);
  const figcaptionText = getStyleBySize(fontSizesStyles, {
    fontSizeSmall: TYPOGRAPHY_SIZE.NORMAL,
    fontSizeMedium: TYPOGRAPHY_SIZE.NORMAL,
    fontSizeLarge: TYPOGRAPHY_SIZE.NORMAL
  });
  const figcaptionStyle = getFigcaptionStyle(baseUnit, borderColor, showBorderBottom).style;
  return /*#__PURE__*/createElement("figure", {
    "data-id": dataId,
    className: css(additionalStyle.style)
  }, /*#__PURE__*/createElement("div", {
    className: css(imageWrapperStyle.base)
  }, children), /*#__PURE__*/createElement("figcaption", {
    className: css(figcaptionText, imageWrapperStyle.figcaption, figcaptionStyle)
  }, figcaption));
};

const ImageWithFigcaption = props => {
  const {
    host,
    path,
    description,
    aspectRatio = {
      x: 16,
      y: 9
    },
    additionalStyle,
    lazyLoad,
    figcaption,
    dataId,
    imageWidths
  } = props;
  const CalculatedImage = lazyLoad ? LazyImage : EagerImage;
  const {
    fallbackSrc,
    lowResUrl,
    srcSetLargeScreen,
    srcSetMediumScreen,
    srcSetSmallScreen
  } = getImageSrcAndSrcSet(createImageAspectRatios(aspectRatio, aspectRatio, aspectRatio), imageWidths, host, path);
  return /*#__PURE__*/createElement(ImageWithFigcaptionWrapper, {
    aspectRatio: aspectRatio,
    additionalStyle: additionalStyle,
    figcaption: figcaption,
    dataId: dataId
  }, /*#__PURE__*/createElement(CalculatedImage, {
    fallbackSrc: fallbackSrc,
    title: description,
    lowResUrl: lowResUrl,
    alt: description,
    srcSetLargeScreen: srcSetLargeScreen,
    srcSetMediumScreen: srcSetMediumScreen,
    srcSetSmallScreen: srcSetSmallScreen
  }));
};

const componentConfigurationKey$i = 'iframe';

const createStyle$c = backgroundColor => StyleSheet.create({
  style: {
    backgroundColor
  }
});

const StyleForMontiElement = () => {
  return (
    /*#__PURE__*/

    /* eslint-disable-next-line react/no-danger */
    createElement("style", {
      dangerouslySetInnerHTML: {
        __html: `
  .monti-placeholder-mm-article-player {
    margin: 0px;
  }
`
      }
    })
  );
};

const Jw = props => {
  const {
    html,
    additionalStyle,
    siteName,
    templateName,
    videoId,
    dataId
  } = props;
  const {
    backgroundColor
  } = useTheme(componentConfigurationKey$i);
  const JwComponentStyle = createStyle$c(backgroundColor);
  const figureRef = useRef();
  useEffect(() => {
    const initMonti = () => {
      if (figureRef.current !== null && figureRef.current.firstElementChild) {
        const jwHtmlRef = figureRef.current.firstElementChild; // @ts-ignore TS2339

        window.monti().initiate({
          config_name: `Editor Embedded player in ${siteName}`,
          uniq_key: 'mm-article-player',
          publisher_contribution: siteName,
          content_id: videoId,
          content_type: 'curated',
          player_pos: jwHtmlRef,
          category_name: templateName,
          monetization: {
            vpaid_mode: 'ENABLED',
            strategy: 'on_player_load',
            ad_type: 'auto_dynamic_tag',
            ad_request_timeout: '10000',
            ad_break_slots: {
              on: [0]
            }
          },
          brand_color: '#EEEEEE',
          playback_method: 'none',
          powered_by_strip: false
        });
      }
    }; // @ts-ignore TS2339


    if (typeof window.monti !== 'undefined') {
      initMonti();
    } else {
      window.addEventListener('mplayerPlayerLoaded', initMonti);
    }
  }, [figureRef, siteName, templateName, videoId]);
  return /*#__PURE__*/createElement(ErrorBoundary, null, /*#__PURE__*/createElement(Helmet, null, /*#__PURE__*/createElement("script", {
    async: true,
    defer: true,
    src: "https://www.oo-syringe.com/prod/players/mplayer_wrapper_v3_latest.js"
  })), /*#__PURE__*/createElement("figure", {
    ref: figureRef,
    "data-id": dataId,
    className: css(JwComponentStyle.style, additionalStyle.style),
    dangerouslySetInnerHTML: {
      __html: html
    }
  }), /*#__PURE__*/createElement(StyleForMontiElement, null));
};

const componentConfigurationKey$j = 'iframe';

const createIframeWrapperStyle = backgroundColor => StyleSheet.create({
  style: {
    backgroundColor,
    paddingTop: `calc(100% / (${16 / 9}))`,
    position: 'relative'
  }
});

const createIframeStyle = () => StyleSheet.create({
  style: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%'
  }
});

const parseIframeHtml = html => {
  const iframeStyle = createIframeStyle();
  return html.replace(/<iframe/, `<iframe class="${css(iframeStyle.style)}"`);
};

const Iframe = props => {
  const {
    html,
    additionalStyle,
    dataId
  } = props;
  const {
    backgroundColor
  } = useTheme(componentConfigurationKey$j);
  const iframeWrapperStyle = createIframeWrapperStyle(backgroundColor);
  return /*#__PURE__*/createElement(ErrorBoundary, null, /*#__PURE__*/createElement("div", {
    className: css(additionalStyle.style)
  }, /*#__PURE__*/createElement("figure", {
    "data-id": dataId,
    className: css(iframeWrapperStyle.style),
    dangerouslySetInnerHTML: {
      __html: parseIframeHtml(html)
    }
  })));
};

const componentConfigurationKey$k = 'mmPlayer';

const createMMPlayerWrapperStyle = backgroundColor => StyleSheet.create({
  style: {
    backgroundColor,
    paddingTop: `calc(100% / (${16 / 9}))`,
    position: 'relative',
    margin: 0
  }
});

const createMMPlayerStyle = () => StyleSheet.create({
  style: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%'
  }
});

const MMPlayer = props => {
  const {
    html,
    additionalStyle,
    dataId
  } = props;
  const {
    backgroundColor
  } = useTheme(componentConfigurationKey$k);
  const MMPlayerWrapperStyle = createMMPlayerWrapperStyle(backgroundColor);
  const MMPlayerStyle = createMMPlayerStyle();
  return /*#__PURE__*/createElement(ErrorBoundary, null, /*#__PURE__*/createElement(Helmet, null, /*#__PURE__*/createElement("script", {
    defer: true,
    src: "https://vms-players.minutemediaservices.com/mplayer_embed_mount.js"
  })), /*#__PURE__*/createElement("figure", {
    "data-id": dataId,
    className: css(additionalStyle.style)
  }, /*#__PURE__*/createElement("div", {
    className: css(MMPlayerWrapperStyle.style)
  }, /*#__PURE__*/createElement("div", {
    className: css(MMPlayerStyle.style),
    dangerouslySetInnerHTML: {
      __html: html
    }
  }))));
};

const createBlockStyle = (defaultBlocksWidths, paddingOverride = {
  largePadding: 1,
  mediumPadding: 1,
  smallPadding: 1
}) => {
  const {
    largeWidth,
    mediumWidth,
    smallWidth
  } = defaultBlocksWidths;
  const {
    largePadding,
    mediumPadding,
    smallPadding
  } = paddingOverride;
  return {
    display: 'block',
    marginLeft: 'auto',
    marginRight: 'auto',
    maxWidth: '100%',
    boxSizing: 'border-box',
    [MEDIA_BREAKPOINTS.large]: {
      width: `${largeWidth}px`,
      paddingRight: `${baseUnit * largePadding}px`,
      paddingLeft: `${baseUnit * largePadding}px`
    },
    [MEDIA_BREAKPOINTS.medium]: {
      width: `${mediumWidth}px`,
      paddingRight: `${baseUnit * mediumPadding}px`,
      paddingLeft: `${baseUnit * mediumPadding}px`
    },
    [MEDIA_BREAKPOINTS.small]: {
      width: `${smallWidth}px`,
      paddingRight: `${baseUnit * smallPadding}px`,
      paddingLeft: `${baseUnit * smallPadding}px`
    }
  };
};

const getMainImageStyle = () => {
  return StyleSheet.create({
    style: {
      marginTop: `${baseUnit}px`,
      marginBottom: `${baseUnit * 0.25}px`
    }
  });
};

const getCoverStyles = defaultBlocksWidths => {
  return StyleSheet.create({
    wrapper: {
      position: 'relative',
      ...createBlockStyle(defaultBlocksWidths)
    }
  });
};

const getBlockStyle = () => {
  return StyleSheet.create({
    style: {
      position: 'absolute',
      top: '0',
      left: '0',
      width: '100%',
      margin: 0
    }
  });
};

const imageWidths$3 = createImageWidths(540, 720, 720);

const getMainImage = (image, credit, caption, style) => {
  const figCaption = getImageCaption(credit, caption);
  return figCaption ? /*#__PURE__*/createElement(ImageWithFigcaption, _extends({}, image, {
    figcaption: figCaption,
    additionalStyle: style,
    imageWidths: imageWidths$3
  })) : /*#__PURE__*/createElement(Image, _extends({}, image, {
    additionalStyle: style,
    imageWidths: imageWidths$3
  }));
};

const getMedia = (media, siteName, templateName) => {
  const additionalStyle = getBlockStyle();
  const {
    type
  } = media;

  switch (type) {
    case 'jw':
      {
        const {
          html,
          videoId
        } = media;
        return /*#__PURE__*/createElement(Jw, {
          html: html,
          additionalStyle: additionalStyle,
          videoId: videoId,
          siteName: siteName,
          templateName: templateName
        });
      }

    case 'youtube':
      {
        const {
          html
        } = media;
        return /*#__PURE__*/createElement(Iframe, {
          html: html,
          additionalStyle: additionalStyle
        });
      }

    case 'mmPlayer':
      {
        const {
          html
        } = media;
        return /*#__PURE__*/createElement(MMPlayer, {
          html: html,
          additionalStyle: additionalStyle
        });
      }

    default:
      return null;
  }
};

const ArticleMainImage = ({
  image,
  media,
  siteName,
  templateName,
  defaultBlocksWidths
}) => {
  const mainImageStyle = getMainImageStyle();
  const {
    credit,
    caption
  } = image;
  const coverStyles = getCoverStyles(defaultBlocksWidths);
  return /*#__PURE__*/createElement(ErrorBoundary, null, /*#__PURE__*/createElement("div", {
    className: css(coverStyles.wrapper)
  }, getMainImage(image, credit, caption, mainImageStyle), media && getMedia(media, siteName, templateName)));
};

const componentConfigurationKey$l = 'articleParagraph';

const getStyle$i = theme => {
  const {
    linkColor,
    textColor
  } = theme;
  return element => {
    switch (element) {
      case 'a':
        return StyleSheet.create({
          style: {
            color: linkColor,
            textDecoration: 'none'
          }
        }).style;

      case 'ol':
      case 'ul':
        return StyleSheet.create({
          style: {
            listStylePosition: 'inside',
            padding: 0,
            color: textColor
          }
        }).style;

      case 'p':
      case 'h2':
      case 'h3':
        return StyleSheet.create({
          style: {
            color: textColor
          }
        }).style;

      default:
        return null;
    }
  };
};

const applyCssClasses = (html, theme) => {
  const style = getStyle$i(theme);
  const typographyStyleP = getStyleBySize(theme.fontSizesStyles, getFontSizesBySemanticTag('p'));
  const typographyStyleH2 = getStyleBySize(theme.fontSizesStyles, getFontSizesBySemanticTag('h2'));
  const typographyStyleH3 = getStyleBySize(theme.fontSizesStyles, getFontSizesBySemanticTag('h3'));
  return html.replace(/<p/g, `<p class="${css(typographyStyleP)}"`).replace(/<h2/g, `<p class="${css(typographyStyleH2)}"`).replace(/<h3/g, `<p class="${css(typographyStyleH3)}"`).replace(/<ol/g, `<ol class="${css(typographyStyleP, style('ol'))}"`).replace(/<ul/g, `<ul class="${css(typographyStyleP, style('ul'))}"`).replace(/<a/g, `<a class="${css(typographyStyleP, style('a'))}"`);
};

const extractWrapper = html => {
  const regex = /^<(\w+).*?>/;
  const match = html.match(regex);

  if (!match) {
    return ['p', html];
  }

  const indexOfLastElement = html.lastIndexOf('<');
  return [match[1], html.substr(0, indexOfLastElement).replace(match[0], '')];
};

const getBlockStyle$1 = defaultBlocksWidths => {
  return StyleSheet.create({
    style: { ...createBlockStyle(defaultBlocksWidths)
    }
  }).style;
};

const getWrapperAsElement = (wrapper, content, style, additionalStyle, dataId, fontSizesStyles, themeProps, defaultBlocksWidths) => {
  const typographyStyle = getStyleBySize(fontSizesStyles, getFontSizesBySemanticTag(wrapper));
  const marginStyle = getBlockStyle$1(defaultBlocksWidths);
  return /*#__PURE__*/createElement(wrapper, {
    dangerouslySetInnerHTML: {
      __html: content
    },
    className: css(typographyStyle, style, additionalStyle.style, marginStyle),
    'data-id': dataId
  });
};

const Paragraph = props => {
  const {
    html,
    additionalStyle,
    dataId,
    defaultBlocksWidths
  } = props;
  const themeProps = useTheme(componentConfigurationKey$l);
  const {
    fontSizesStyles
  } = themeProps;
  const [wrapper, content] = extractWrapper(html);
  const wrapperStyle = getStyle$i(themeProps)(wrapper);
  return /*#__PURE__*/createElement(ErrorBoundary, null, getWrapperAsElement(wrapper, applyCssClasses(content, themeProps), wrapperStyle, additionalStyle, dataId, fontSizesStyles, themeProps, defaultBlocksWidths));
};

const componentConfigurationKey$m = 'playbuzz';
const PlaybuzzScript = "(function(d,s,id){var js,fjs=d.getElementsByTagName(s)[0];if(d.getElementById(id))return;js=d.createElement(s);js.id=id;js.src='https://embed.playbuzz.com/sdk.js';fjs.parentNode.insertBefore(js,fjs);}(document,'script','playbuzz-sdk'));";

const getBlockStyle$2 = (defaultBlocksWidths, themeProps) => {
  return StyleSheet.create({
    style: { ...createBlockStyle(defaultBlocksWidths),
      backgroundColor: themeProps.backgroundColor
    }
  }).style;
};

const PlaybuzzComponent = props => {
  const {
    html,
    additionalStyle,
    dataId,
    defaultBlocksWidths
  } = props;
  const themeProps = useTheme(componentConfigurationKey$m);
  const style = getBlockStyle$2(defaultBlocksWidths, themeProps);
  useEffect(() => {
    // @ts-ignore TS2339
    if (typeof window.Playbuzz !== 'undefined') {
      // @ts-ignore TS2339
      window.Playbuzz.render();
    }
  }, []);
  return /*#__PURE__*/createElement(ErrorBoundary, null, /*#__PURE__*/createElement(Helmet, null, /*#__PURE__*/createElement("script", null, PlaybuzzScript)), /*#__PURE__*/createElement("figure", {
    "data-id": dataId,
    dangerouslySetInnerHTML: {
      __html: html
    },
    className: css(additionalStyle.style, style)
  }));
};
const Playbuzz = props => /*#__PURE__*/createElement(ErrorBoundary, null, /*#__PURE__*/createElement(PlaybuzzComponent, props));

const componentConfigurationKey$n = 'streamable';

const createStyle$d = (style, defaultBlocksWidths) => {
  return StyleSheet.create({
    style: { ...style,
      ...createBlockStyle(defaultBlocksWidths)
    }
  });
};

const Streamable = props => {
  const themeProps = useTheme(componentConfigurationKey$n);
  const {
    backgroundColor
  } = themeProps;
  const {
    style,
    defaultBlocksWidths
  } = props;
  const additionalStyle = createStyle$d(style, defaultBlocksWidths);
  return /*#__PURE__*/createElement(ErrorBoundary, null, /*#__PURE__*/createElement(Iframe, _extends({}, backgroundColor, props, {
    additionalStyle: additionalStyle
  })));
};

const getBlockStyle$3 = defaultBlocksWidths => {
  return StyleSheet.create({
    style: { ...createBlockStyle(defaultBlocksWidths),
      display: 'flex',
      justifyContent: 'center'
    }
  }).style;
};

const TwitterScript = `window.twttr = (function(d, s, id) {
  var js, fjs = d.getElementsByTagName(s)[0],
    t = window.twttr || {};
  if (d.getElementById(id)) return t;
  js = d.createElement(s);
  js.id = id;
  js.src = "https://platform.twitter.com/widgets.js";
  fjs.parentNode.insertBefore(js, fjs);

  t._e = [];
  t.ready = function(f) {
    t._e.push(f);
  };

  return t;
}(document, "script", "twitter-wjs"));`;
const TwitterComponent = props => {
  const {
    html,
    additionalStyle,
    dataId,
    defaultBlocksWidths
  } = props;
  const twitterStyle = getBlockStyle$3(defaultBlocksWidths);
  useEffect(() => {
    // @ts-ignore TS2339
    if (typeof window.twttr !== 'undefined' && typeof window.twttr.widgets !== 'undefined') {
      // @ts-ignore TS2339
      window.twttr.widgets.load();
    }
  }, []);
  return /*#__PURE__*/createElement(ErrorBoundary, null, /*#__PURE__*/createElement(Helmet, null, /*#__PURE__*/createElement("script", null, TwitterScript)), /*#__PURE__*/createElement("figure", {
    "data-id": dataId,
    dangerouslySetInnerHTML: {
      __html: html
    },
    className: css(twitterStyle, additionalStyle.style)
  }));
};
const Twitter = props => /*#__PURE__*/createElement(ErrorBoundary, null, /*#__PURE__*/createElement(TwitterComponent, props));

const getStyle$j = (width, height, defaultBlocksWidths) => {
  return StyleSheet.create({
    wrapper: { ...createBlockStyle(defaultBlocksWidths)
    },
    figure: {
      position: 'relative',
      overflow: 'hidden',
      margin: 0,
      boxSizing: 'border-box',
      flex: '1 1 100%',
      minWidth: '0%',
      paddingTop: `calc(100% / (${width / height}))`
    },
    image: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%'
    }
  });
};

const GiphyComponent = props => {
  const {
    additionalStyle,
    url,
    width,
    height,
    description,
    dataId,
    defaultBlocksWidths
  } = props;
  const style = getStyle$j(width, height, defaultBlocksWidths);
  return /*#__PURE__*/createElement("div", {
    "data-id": dataId,
    className: css(additionalStyle.style, style.wrapper)
  }, /*#__PURE__*/createElement("figure", {
    className: css(style.figure)
  }, /*#__PURE__*/createElement("img", {
    src: url,
    className: css(style.image),
    alt: description
  })));
};
const Giphy = props => /*#__PURE__*/createElement(ErrorBoundary, null, /*#__PURE__*/createElement(GiphyComponent, props));

const getBlockStyle$4 = defaultBlocksWidths => {
  return StyleSheet.create({
    style: { ...createBlockStyle(defaultBlocksWidths)
    }
  }).style;
};

const InstagramComponent = props => {
  const {
    html,
    additionalStyle,
    dataId,
    defaultBlocksWidths
  } = props;
  const instagramStyle = getBlockStyle$4(defaultBlocksWidths);
  useEffect(() => {
    // @ts-ignore TS2339
    if (typeof window.instgrm !== 'undefined') {
      // @ts-ignore TS2339
      window.instgrm.Embeds.process();
    }
  }, []);
  return /*#__PURE__*/createElement(ErrorBoundary, null, /*#__PURE__*/createElement(Helmet, null, /*#__PURE__*/createElement("script", {
    async: true,
    src: "https://www.instagram.com/embed.js"
  })), /*#__PURE__*/createElement("figure", {
    "data-id": dataId,
    dangerouslySetInnerHTML: {
      __html: html
    },
    className: css(instagramStyle, additionalStyle.style)
  }));
};
const Instagram = props => /*#__PURE__*/createElement(ErrorBoundary, null, /*#__PURE__*/createElement(InstagramComponent, props));

const createStyle$e = defaultBlocksWidths => {
  return StyleSheet.create({
    style: { ...createBlockStyle(defaultBlocksWidths),
      display: 'flex',
      justifyContent: 'center'
    }
  });
};

const removeDataWidthFromHtml = html => html.replace(/data-width="\d+"/, 'data-width=""');
const facebookVersion = 'v3.3';
const FacebookComponent = props => {
  const {
    html,
    additionalStyle,
    dataId,
    defaultBlocksWidths
  } = props;
  const facebookStyle = createStyle$e(defaultBlocksWidths);
  useEffect(() => {
    // @ts-ignore TS2339
    if (typeof window.FB !== 'undefined') {
      // @ts-ignore TS2339
      // eslint-disable-next-line no-undef
      FB.init({
        xfbml: true,
        version: facebookVersion
      });
    }
  }, []);
  const htmlWithFluidWidth = removeDataWidthFromHtml(html);
  return /*#__PURE__*/createElement(ErrorBoundary, null, /*#__PURE__*/createElement(Helmet, null, /*#__PURE__*/createElement("script", {
    async: true,
    defer: true,
    src: `https://connect.facebook.net/en_US/sdk.js#xfbml=1&version=${facebookVersion}`
  })), /*#__PURE__*/createElement("figure", {
    "data-id": dataId,
    dangerouslySetInnerHTML: {
      __html: htmlWithFluidWidth
    },
    className: css(facebookStyle.style, additionalStyle.style)
  }));
};
const Facebook = props => /*#__PURE__*/createElement(ErrorBoundary, null, /*#__PURE__*/createElement(FacebookComponent, props));

const componentConfigurationKey$o = 'listItemTitle';

const getStyle$k = (theme, blockStyle) => {
  const {
    numberColor,
    titleColor
  } = theme;
  return StyleSheet.create({
    numberStyle: {
      color: numberColor
    },
    titleStyle: {
      color: titleColor,
      ...blockStyle
    }
  });
};

const ListItemTitle = props => {
  const {
    listItemNumber,
    text,
    additionalStyle,
    dataId,
    defaultBlocksWidths
  } = props;
  const themeProps = useTheme(componentConfigurationKey$o);
  const {
    fontSizesStyles
  } = themeProps;
  const blockStyle = createBlockStyle(defaultBlocksWidths);
  const {
    numberStyle,
    titleStyle
  } = getStyle$k(themeProps, blockStyle);
  const contentStyle = getStyleBySize(fontSizesStyles, {
    fontSizeSmall: TYPOGRAPHY_SIZE.LARGE,
    fontSizeMedium: TYPOGRAPHY_SIZE.LARGE,
    fontSizeLarge: TYPOGRAPHY_SIZE.LARGE
  });
  return /*#__PURE__*/createElement(ErrorBoundary, null, /*#__PURE__*/createElement(H2, {
    fontSizeLarge: TYPOGRAPHY_SIZE.LARGE,
    fontSizeMedium: TYPOGRAPHY_SIZE.LARGE,
    fontSizeSmall: TYPOGRAPHY_SIZE.LARGE,
    "data-id": dataId,
    styles: semanticCSS(additionalStyle.style, titleStyle)
  }, /*#__PURE__*/createElement(Span, {
    styles: semanticCSS(numberStyle, contentStyle)
  }, `${listItemNumber}. `), text));
};

const emitInjectArticleBlockByIndexEvent = dataId => {
  setEventEmitter(injectArticleBlockByIndexEvent, callback => callback(dataId));
};

const getBlockDataId = block => {
  return block.getAttribute('data-id');
};

const sendViewabilityEvent = element => {
  const id = getBlockDataId(element);
  setEventEmitter(viewabilityArticleBlockEvent, callback => callback({
    id
  }));
};

const getStyle$l = defaultBlocksWidths => {
  return StyleSheet.create({
    style: { ...createBlockStyle(defaultBlocksWidths)
    }
  }).style;
};

const InjectedBlock = props => {
  const {
    html,
    additionalStyle,
    dataId,
    script,
    defaultBlocksWidths
  } = props;
  const style = getStyle$l(defaultBlocksWidths);
  useEffect(() => {
    emitInjectArticleBlockByIndexEvent(dataId);
  }, [dataId]);
  const ref = useViewabilityHandler(sendViewabilityEvent, 0.5);
  return /*#__PURE__*/createElement(ViewabilityProvider, null, /*#__PURE__*/createElement(ErrorBoundary, null, script ? /*#__PURE__*/createElement(Helmet, null, /*#__PURE__*/createElement("script", null, script)) : null, /*#__PURE__*/createElement("figure", {
    ref: ref,
    "data-id": dataId,
    dangerouslySetInnerHTML: {
      __html: html
    },
    className: css(additionalStyle.style, style)
  })));
};

const componentConfigurationKey$p = 'iframeEmbed';

const createIframeEmbedWrapperStyle = (themeProps, height, defaultBlocksWidths) => {
  const {
    backgroundColor
  } = themeProps;
  return StyleSheet.create({
    style: { ...createBlockStyle(defaultBlocksWidths),
      backgroundColor,
      paddingTop: `${height}px`,
      position: 'relative'
    }
  });
};

const createIframeEmbedStyle = () => StyleSheet.create({
  style: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%'
  }
});

const parseIframeHtml$1 = html => {
  const iframeStyle = createIframeEmbedStyle();
  return html.replace(/<iframe/, `<iframe class="${css(iframeStyle.style)}"`);
};

const IframeEmbed = props => {
  const {
    html,
    dataId,
    height,
    additionalStyle,
    defaultBlocksWidths
  } = props;
  const themeProps = useTheme(componentConfigurationKey$p);
  const iframeWrapperStyle = createIframeEmbedWrapperStyle(themeProps, height, defaultBlocksWidths);
  return /*#__PURE__*/createElement(ErrorBoundary, null, /*#__PURE__*/createElement("figure", {
    "data-id": dataId,
    className: css(iframeWrapperStyle.style, additionalStyle.style),
    dangerouslySetInnerHTML: {
      __html: parseIframeHtml$1(html)
    }
  }));
};

const componentConfigurationKey$q = 'gfycat';

const createIframeWrapperStyle$1 = (themeProps, defaultBlocksWidths) => {
  const {
    backgroundColor
  } = themeProps;
  return StyleSheet.create({
    figure: {
      backgroundColor
    },
    wrapper: { ...createBlockStyle(defaultBlocksWidths)
    }
  });
};

const Gfycat = props => {
  const {
    html,
    additionalStyle,
    dataId,
    defaultBlocksWidths
  } = props;
  const themeProps = useTheme(componentConfigurationKey$q);
  const style = createIframeWrapperStyle$1(themeProps, defaultBlocksWidths);
  return /*#__PURE__*/createElement(ErrorBoundary, null, /*#__PURE__*/createElement("div", {
    className: css(additionalStyle.style, style.wrapper)
  }, /*#__PURE__*/createElement("figure", {
    "data-id": dataId,
    className: css(style.figure),
    dangerouslySetInnerHTML: {
      __html: html
    }
  })));
};

const getStyle$m = defaultBlocksWidths => {
  return StyleSheet.create({
    style: { ...createBlockStyle(defaultBlocksWidths)
    }
  }).style;
};

const Apester = props => {
  const {
    html,
    additionalStyle,
    dataId,
    defaultBlocksWidths
  } = props;
  const style = getStyle$m(defaultBlocksWidths);
  return /*#__PURE__*/createElement(ErrorBoundary, null, /*#__PURE__*/createElement(Helmet, null, /*#__PURE__*/createElement("script", {
    async: true,
    defer: true,
    src: "https://static.apester.com/js/sdk/latest/apester-sdk.js"
  })), /*#__PURE__*/createElement("div", {
    className: css(additionalStyle.style, style)
  }, /*#__PURE__*/createElement("figure", {
    "data-id": dataId,
    dangerouslySetInnerHTML: {
      __html: html
    }
  })));
};

const getStyle$n = defaultBlocksWidths => {
  return StyleSheet.create({
    style: { ...createBlockStyle(defaultBlocksWidths)
    }
  }).style;
};

const Ceros = props => {
  const {
    html,
    additionalStyle,
    dataId,
    defaultBlocksWidths
  } = props;
  const style = getStyle$n(defaultBlocksWidths);
  return /*#__PURE__*/createElement(ErrorBoundary, null, /*#__PURE__*/createElement(Helmet, null, /*#__PURE__*/createElement("script", {
    async: true,
    defer: true,
    src: "https://static.apester.com/js/sdk/latest/apester-sdk.js"
  })), /*#__PURE__*/createElement("div", {
    className: css(additionalStyle.style, style)
  }, /*#__PURE__*/createElement("figure", {
    "data-id": dataId,
    dangerouslySetInnerHTML: {
      __html: html
    }
  })));
};

const getStyle$o = defaultBlocksWidths => {
  return StyleSheet.create({
    style: { ...createBlockStyle(defaultBlocksWidths)
    }
  });
};

const ArticleAd = props => {
  const {
    defaultBlocksWidths
  } = props;
  const additionalStyle = getStyle$o(defaultBlocksWidths);
  return /*#__PURE__*/createElement(ErrorBoundary, null, /*#__PURE__*/createElement(Ad, _extends({}, props, {
    additionalStyle: additionalStyle
  })));
};

const createStyle$f = (style, defaultBlocksWidths) => {
  return StyleSheet.create({
    style: { ...style,
      ...createBlockStyle(defaultBlocksWidths)
    }
  });
};

const ArticleIframe = props => {
  const {
    style,
    defaultBlocksWidths
  } = props;
  const additionalStyle = createStyle$f(style, defaultBlocksWidths);
  return /*#__PURE__*/createElement(ErrorBoundary, null, /*#__PURE__*/createElement(Iframe, _extends({}, props, {
    additionalStyle: additionalStyle
  })));
};

const componentConfigurationKey$r = 'articleJw';

const createStyle$g = (defaultBlocksWidths, style, backgroundColor) => {
  return StyleSheet.create({
    style: { ...style,
      ...createBlockStyle(defaultBlocksWidths),
      backgroundColor
    }
  });
};

const ArticleJw = props => {
  const {
    style,
    defaultBlocksWidths
  } = props;
  const {
    backgroundColor
  } = useTheme(componentConfigurationKey$r);
  const additionalStyle = createStyle$g(defaultBlocksWidths, style, backgroundColor);
  return /*#__PURE__*/createElement(ErrorBoundary, null, /*#__PURE__*/createElement(Jw, _extends({}, props, {
    additionalStyle: additionalStyle
  })));
};

const createStyle$h = (defaultBlocksWidths, style) => {
  return StyleSheet.create({
    style: { ...style,
      ...createBlockStyle(defaultBlocksWidths)
    }
  });
};

const ArticleMMPlayer = props => {
  const {
    style,
    defaultBlocksWidths
  } = props;
  const additionalStyle = createStyle$h(defaultBlocksWidths, style);
  return /*#__PURE__*/createElement(ErrorBoundary, null, /*#__PURE__*/createElement(MMPlayer, _extends({}, props, {
    additionalStyle: additionalStyle
  })));
};

const FULL_BLEED_IMAGE_SIZE_TYPE = 'fullBleed';

const createFullBleedStyle = style => {
  return StyleSheet.create({
    style: { ...style
    }
  });
};

const createStyle$i = (style, defaultBlocksWidths, overrideBlockWidths, blocksPaddingOverride) => {
  const blockStyle = overrideBlockWidths ? createBlockStyle(overrideBlockWidths, blocksPaddingOverride) : createBlockStyle(defaultBlocksWidths, blocksPaddingOverride);
  return StyleSheet.create({
    style: { ...style,
      ...blockStyle
    }
  });
};

const ArticleImage = props => {
  const {
    style,
    image,
    dataId,
    defaultBlocksWidths,
    overrideBlockWidths,
    sizeType,
    blocksPaddingOverride
  } = props;
  const {
    credit,
    caption
  } = image;
  const figcaption = getImageCaption(credit, caption);
  const largeScreenImageWidth = sizeType === FULL_BLEED_IMAGE_SIZE_TYPE ? 1440 : 720;
  const imageWidths = createImageWidths(540, 720, largeScreenImageWidth);
  const additionalStyle = sizeType === FULL_BLEED_IMAGE_SIZE_TYPE ? createFullBleedStyle(style) : createStyle$i(style, defaultBlocksWidths, overrideBlockWidths, blocksPaddingOverride);
  return figcaption ? /*#__PURE__*/createElement(ErrorBoundary, null, /*#__PURE__*/createElement(ImageWithFigcaption, _extends({}, image, {
    figcaption: figcaption,
    lazyLoad: true,
    additionalStyle: additionalStyle,
    dataId: dataId,
    imageWidths: imageWidths
  }))) : /*#__PURE__*/createElement(ErrorBoundary, null, /*#__PURE__*/createElement(Image, _extends({}, image, {
    lazyLoad: true,
    additionalStyle: additionalStyle,
    dataId: dataId,
    imageWidths: imageWidths
  })));
};

const componentConfigurationKey$s = 'articleContent';

const createBlockStyle$1 = (baseUnit, modifier) => {
  return {
    marginTop: `${baseUnit * modifier}px`
  };
};

const createBlockStyleSheet = (baseUnit, modifier) => StyleSheet.create({
  style: createBlockStyle$1(baseUnit, modifier)
});

const createBlocksComponents = (body, templateName, siteName, blocksWithMultipleOptions, overrideBlockWidths, defaultBlocksWidths, themeProps, blocksPaddingOverride) => {
  const {
    baseUnit,
    marginBetweenBlocksFactorAfterTitle,
    marginBetweenBlocksFactor
  } = themeProps;
  let isPreviousElementTitle = false;
  return body.map(block => {
    const {
      type
    } = block;
    const blockStyleSheet = isPreviousElementTitle ? createBlockStyleSheet(baseUnit, marginBetweenBlocksFactorAfterTitle) : createBlockStyleSheet(baseUnit, marginBetweenBlocksFactor);
    const blockStyle = isPreviousElementTitle ? createBlockStyle$1(baseUnit, marginBetweenBlocksFactorAfterTitle) : createBlockStyle$1(baseUnit, marginBetweenBlocksFactor);
    isPreviousElementTitle = false;

    switch (type) {
      case 'ad':
        {
          const {
            dataId,
            adId
          } = block;
          return /*#__PURE__*/createElement(ArticleAd, {
            key: dataId,
            additionalStyle: blockStyleSheet,
            id: adId,
            defaultBlocksWidths: defaultBlocksWidths
          });
        }

      case 'image':
        {
          const {
            image,
            dataId,
            sizeType
          } = block;
          const ImageComponent = blocksWithMultipleOptions.image || ArticleImage;
          const overrideBlockWidth = overrideBlockWidths && overrideBlockWidths.image;
          return /*#__PURE__*/createElement(ImageComponent, {
            key: dataId,
            image: image,
            style: blockStyle,
            dataId: dataId,
            defaultBlocksWidths: defaultBlocksWidths,
            overrideBlockWidths: overrideBlockWidth,
            sizeType: sizeType,
            blocksPaddingOverride: blocksPaddingOverride
          });
        }

      case 'title':
        {
          isPreviousElementTitle = true;
          const {
            listItemNumber,
            text,
            dataId
          } = block;
          return /*#__PURE__*/createElement(ListItemTitle, {
            key: dataId,
            listItemNumber: listItemNumber,
            text: text,
            additionalStyle: blockStyleSheet,
            dataId: dataId,
            defaultBlocksWidths: defaultBlocksWidths
          });
        }

      case 'inline-text':
        {
          const {
            html,
            dataId
          } = block;
          return /*#__PURE__*/createElement(Paragraph, {
            key: dataId,
            html: html,
            additionalStyle: blockStyleSheet,
            dataId: dataId,
            defaultBlocksWidths: defaultBlocksWidths
          });
        }

      case 'quote':
        {
          const {
            text,
            cite,
            dataId
          } = block;
          const Quote = blocksWithMultipleOptions.quote;
          return /*#__PURE__*/createElement(Quote, {
            key: dataId,
            text: text,
            cite: cite,
            additionalStyle: blockStyleSheet,
            dataId: dataId,
            defaultBlocksWidths: defaultBlocksWidths
          });
        }

      case 'youtube':
        {
          const {
            html,
            dataId
          } = block;
          return /*#__PURE__*/createElement(ArticleIframe, {
            key: dataId,
            html: html,
            style: blockStyle,
            dataId: dataId,
            defaultBlocksWidths: defaultBlocksWidths
          });
        }

      case 'twitter':
        {
          const {
            html,
            dataId
          } = block;
          return /*#__PURE__*/createElement(Twitter, {
            key: dataId,
            html: html,
            additionalStyle: blockStyleSheet,
            dataId: dataId,
            defaultBlocksWidths: defaultBlocksWidths
          });
        }

      case 'instagram':
        {
          const {
            html,
            dataId
          } = block;
          return /*#__PURE__*/createElement(Instagram, {
            key: dataId,
            html: html,
            additionalStyle: blockStyleSheet,
            dataId: dataId,
            defaultBlocksWidths: defaultBlocksWidths
          });
        }

      case 'facebook':
        {
          const {
            html,
            dataId
          } = block;
          return /*#__PURE__*/createElement(Facebook, {
            key: dataId,
            html: html,
            additionalStyle: blockStyleSheet,
            dataId: dataId,
            defaultBlocksWidths: defaultBlocksWidths
          });
        }

      case 'jw':
        {
          const {
            html,
            videoId,
            dataId
          } = block;
          return /*#__PURE__*/createElement(ArticleJw, {
            key: dataId,
            html: html,
            videoId: videoId,
            templateName: templateName,
            siteName: siteName,
            style: blockStyle,
            dataId: dataId,
            defaultBlocksWidths: defaultBlocksWidths
          });
        }

      case 'mmPlayer':
        {
          const {
            html,
            dataId
          } = block;
          return /*#__PURE__*/createElement(ArticleMMPlayer, {
            key: dataId,
            html: html,
            style: blockStyle,
            dataId: dataId,
            defaultBlocksWidths: defaultBlocksWidths
          });
        }

      case 'giphy':
        {
          const {
            dataId
          } = block;
          return /*#__PURE__*/createElement(Giphy, _extends({
            key: dataId
          }, block, {
            additionalStyle: blockStyleSheet,
            dataId: dataId,
            defaultBlocksWidths: defaultBlocksWidths
          }));
        }

      case 'playbuzz':
        {
          const {
            html,
            dataId
          } = block;
          return /*#__PURE__*/createElement(Playbuzz, {
            key: dataId,
            html: html,
            additionalStyle: blockStyleSheet,
            dataId: dataId,
            defaultBlocksWidths: defaultBlocksWidths
          });
        }

      case 'dailyMotion':
        {
          const {
            html,
            dataId
          } = block;
          return /*#__PURE__*/createElement(ArticleIframe, {
            key: dataId,
            html: html,
            style: blockStyle,
            dataId: dataId,
            defaultBlocksWidths: defaultBlocksWidths
          });
        }

      case 'divider':
        {
          const {
            dataId
          } = block;
          const Divider = blocksWithMultipleOptions.divider;
          return /*#__PURE__*/createElement(Divider, {
            key: dataId,
            additionalStyle: blockStyleSheet,
            dataId: dataId,
            defaultBlocksWidths: defaultBlocksWidths
          });
        }

      case 'streamable':
        {
          const {
            html,
            dataId
          } = block;
          return /*#__PURE__*/createElement(Streamable, {
            key: dataId,
            html: html,
            style: blockStyle,
            dataId: dataId,
            defaultBlocksWidths: defaultBlocksWidths
          });
        }

      case 'injectedBlock':
        {
          const {
            html,
            dataId,
            script
          } = block;
          return /*#__PURE__*/createElement(InjectedBlock, {
            key: dataId,
            html: html,
            additionalStyle: blockStyleSheet,
            dataId: dataId,
            script: script,
            defaultBlocksWidths: defaultBlocksWidths
          });
        }

      case 'soundcloud':
        {
          const {
            html,
            dataId
          } = block;
          return /*#__PURE__*/createElement(ArticleIframe, {
            key: dataId,
            html: html,
            style: blockStyle,
            dataId: dataId,
            defaultBlocksWidths: defaultBlocksWidths
          });
        }

      case 'vimeo':
        {
          const {
            html,
            dataId
          } = block;
          return /*#__PURE__*/createElement(ArticleIframe, {
            key: dataId,
            html: html,
            style: blockStyle,
            dataId: dataId,
            defaultBlocksWidths: defaultBlocksWidths
          });
        }

      case 'iframeEmbed':
        {
          const {
            html,
            dataId,
            height
          } = block;
          return /*#__PURE__*/createElement(IframeEmbed, {
            key: dataId,
            height: height,
            html: html,
            additionalStyle: blockStyleSheet,
            dataId: dataId,
            defaultBlocksWidths: defaultBlocksWidths
          });
        }

      case 'gfycat':
        {
          const {
            html,
            dataId
          } = block;
          return /*#__PURE__*/createElement(Gfycat, {
            key: dataId,
            html: html,
            additionalStyle: blockStyleSheet,
            dataId: dataId,
            defaultBlocksWidths: defaultBlocksWidths
          });
        }

      case 'apester':
        {
          const {
            html,
            dataId
          } = block;
          return /*#__PURE__*/createElement(Apester, {
            key: dataId,
            html: html,
            additionalStyle: blockStyleSheet,
            dataId: dataId,
            defaultBlocksWidths: defaultBlocksWidths
          });
        }

      case 'ceros':
        {
          const {
            html,
            dataId
          } = block;
          return /*#__PURE__*/createElement(Ceros, {
            key: dataId,
            html: html,
            additionalStyle: blockStyleSheet,
            dataId: dataId,
            defaultBlocksWidths: defaultBlocksWidths
          });
        }

      default:
        return null;
    }
  });
};

const ArticleContentComponent = props => {
  const {
    body,
    templateName,
    siteName,
    blocksWithMultipleOptions,
    overrideBlocksWidths,
    defaultBlocksWidths,
    blocksPaddingOverride
  } = props;
  const themeProps = useTheme(componentConfigurationKey$s);
  return body ? /*#__PURE__*/createElement(ViewabilityProvider, null, createBlocksComponents(body, templateName, siteName, blocksWithMultipleOptions, overrideBlocksWidths, defaultBlocksWidths, themeProps, blocksPaddingOverride)) : null;
};
const ArticleContent = props => {
  const {
    children
  } = props;
  return /*#__PURE__*/createElement(ErrorBoundary, null, children, /*#__PURE__*/createElement(ArticleContentComponent, props));
};

const getStyles$4 = (factor, baseUnit) => StyleSheet.create({
  wrapper: {
    minHeight: `${factor * baseUnit}px`,
    position: 'relative'
  }
});

const SafeArticleContentWrapper = ({
  children,
  minHeightFactor
}) => {
  const {
    baseUnit
  } = useTheme();
  const styles = getStyles$4(minHeightFactor, baseUnit);
  return /*#__PURE__*/createElement("div", {
    className: css(styles.wrapper)
  }, children);
};

const ampImageWidths = createImageWidths(540, 720, 720);
const ampAspectRatio = {
  x: 16,
  y: 9
};
const preloadImage = (host, path, aspectRatio = ampAspectRatio) => {
  return [/*#__PURE__*/createElement("link", {
    key: "1",
    rel: "preload",
    href: createImageSrc(host, path, ampImageWidths.small, aspectRatio, 1),
    media: `(max-width: ${SMALL_SCREEN_SIZE}px)`,
    as: "image"
  }), /*#__PURE__*/createElement("link", {
    key: "2",
    rel: "preload",
    href: createImageSrc(host, path, ampImageWidths.medium, aspectRatio, 1),
    media: `(max-width: ${MEDIUM_SCREEN_SIZE}px)`,
    as: "image"
  }), /*#__PURE__*/createElement("link", {
    key: "3",
    rel: "preload",
    href: createImageSrc(host, path, ampImageWidths.large, aspectRatio, 1),
    media: `(min-width: ${LARGE_SCREEN_SIZE}px)`,
    as: "image"
  })];
};
const normalizeDimensions = (aspectRatio = {
  x: 16,
  y: 9
}) => {
  return [aspectRatio.x / aspectRatio.y, 1];
};

const componentConfigThemeKey$p = 'imageWithFigcaptionWrapper';
const getStyle$p = (baseUnit, figcaptionTextColor) => StyleSheet.create({
  figcaption: {
    color: figcaptionTextColor,
    marginTop: `${baseUnit * 0.25}px`,
    marginLeft: `${baseUnit * 0.25}px`
  }
});
const getAMPImageProps = image => {
  const {
    description,
    host,
    path,
    aspectRatio = ampAspectRatio,
    imageWidths = ampImageWidths
  } = image;
  const {
    srcSetMediumScreen,
    srcSetLargeScreen,
    fallbackSrc,
    srcSetSmallScreen
  } = getImageSrcAndSrcSet(createImageAspectRatios(aspectRatio, aspectRatio, aspectRatio), imageWidths, host, path);
  const [width, height] = normalizeDimensions(aspectRatio);
  return {
    src: fallbackSrc,
    alt: description,
    width,
    height,
    srcSetMediumScreen,
    srcSetLargeScreen,
    srcSetSmallScreen
  };
};

const getAMPImage = imageProps => {
  const {
    width,
    height,
    src,
    alt,
    srcSetLargeScreen,
    srcSetMediumScreen,
    srcSetSmallScreen
  } = imageProps;
  return /*#__PURE__*/createElement(Fragment, null, /*#__PURE__*/createElement("amp-img", {
    src: src,
    srcSet: srcSetSmallScreen,
    alt: alt,
    layout: "responsive",
    width: width,
    height: height,
    media: `(max-width: ${SMALL_SCREEN_SIZE}px)`
  }), /*#__PURE__*/createElement("amp-img", {
    src: src,
    srcSet: srcSetMediumScreen,
    alt: alt,
    layout: "responsive",
    width: width,
    height: height,
    media: `(min-width: ${SMALL_SCREEN_SIZE + 1}px) and (max-width: ${MEDIUM_SCREEN_SIZE}px)`
  }), /*#__PURE__*/createElement("amp-img", {
    src: src,
    srcSet: srcSetLargeScreen,
    alt: alt,
    layout: "responsive",
    width: width,
    height: height,
    media: `(min-width: ${LARGE_SCREEN_SIZE}px)`
  }));
};

const getImageCaption$1 = (credit, caption) => {
  return [caption, credit].filter(value => {
    return typeof value === 'string' && value.trim() !== '';
  }).join(' | ');
};

const AMPImage = props => {
  const {
    baseUnit,
    figcaptionTextColor,
    fontSizesStyles
  } = useTheme(componentConfigThemeKey$p);
  const {
    additionalStyle,
    preload,
    credit,
    caption,
    host,
    path,
    aspectRatio
  } = props;
  const imageProps = getAMPImageProps(props);
  const figCaption = getImageCaption$1(credit, caption);
  const imageStyle = additionalStyle ? css(additionalStyle.style) : '';
  const figCaptionStyle = getStyle$p(baseUnit, figcaptionTextColor);
  const figcaptionText = getStyleBySize(fontSizesStyles, {
    fontSizeSmall: TYPOGRAPHY_SIZE.NORMAL,
    fontSizeMedium: TYPOGRAPHY_SIZE.NORMAL,
    fontSizeLarge: TYPOGRAPHY_SIZE.NORMAL
  });
  return /*#__PURE__*/createElement("div", {
    className: imageStyle
  }, preload && preloadImage(host, path, aspectRatio), getAMPImage(imageProps), figCaption && /*#__PURE__*/createElement("figcaption", {
    className: css(figCaptionStyle.figcaption, figcaptionText)
  }, figCaption));
};

const AMPJWPlayer = props => {
  const {
    playerId,
    videoId,
    additionalStyle
  } = props;
  return /*#__PURE__*/createElement(Fragment, null, /*#__PURE__*/createElement(Helmet, null, /*#__PURE__*/createElement("script", {
    async: undefined,
    "custom-element": "amp-jwplayer",
    src: "https://cdn.ampproject.org/v0/amp-jwplayer-0.1.js"
  })), /*#__PURE__*/createElement("figure", {
    className: css(additionalStyle.style)
  }, /*#__PURE__*/createElement("amp-jwplayer", {
    width: "16",
    height: "9",
    layout: "responsive",
    "data-media-id": videoId,
    "data-player-id": playerId
  })));
};

const AMPYoutube = props => {
  const {
    mediaId,
    additionalStyle
  } = props;
  return /*#__PURE__*/createElement(Fragment, null, /*#__PURE__*/createElement(Helmet, null, /*#__PURE__*/createElement("script", {
    async: undefined,
    "custom-element": "amp-youtube",
    src: "https://cdn.ampproject.org/v0/amp-youtube-0.1.js"
  })), /*#__PURE__*/createElement("figure", {
    className: css(additionalStyle.style)
  }, /*#__PURE__*/createElement("amp-youtube", {
    "data-videoid": mediaId,
    layout: "responsive",
    width: "480",
    height: "270"
  })));
};

const AMPMMPlayer = props => {
  const {
    playerId,
    playlistId,
    additionalStyle
  } = props;
  return playerId && playlistId ? /*#__PURE__*/createElement(Fragment, null, /*#__PURE__*/createElement(Helmet, null, /*#__PURE__*/createElement("script", {
    async: undefined,
    "custom-element": "amp-minute-media-player",
    src: "https://cdn.ampproject.org/v0/amp-minute-media-player-0.1.js"
  })), /*#__PURE__*/createElement("figure", {
    className: css(additionalStyle.style)
  }, /*#__PURE__*/createElement("amp-minute-media-player", {
    width: "16",
    height: "9",
    layout: "responsive",
    "data-content-type": "specific",
    "data-content-id": `${playlistId}#${playerId}`
  }))) : null;
};

const getStyles$5 = () => StyleSheet.create({
  wrapper: {
    position: 'relative'
  }
});

const getMainImageStyle$1 = baseUnit => {
  return StyleSheet.create({
    style: {
      marginTop: `${baseUnit}px`,
      marginBottom: `${baseUnit * 0.25}px`
    }
  });
};

const getBlockStyle$5 = () => {
  return StyleSheet.create({
    style: {
      position: 'absolute',
      top: '0',
      left: '0',
      width: '100%'
    }
  });
};

const getMedia$1 = media => {
  const {
    type
  } = media;
  const blockStyle = getBlockStyle$5();

  switch (type) {
    case 'jw':
      {
        const {
          videoId,
          playerId
        } = media; // eslint-disable-next-line @typescript-eslint/no-non-null-assertion

        return /*#__PURE__*/createElement(AMPJWPlayer, {
          videoId: videoId,
          playerId: playerId,
          additionalStyle: blockStyle
        });
      }

    case 'youtube':
      {
        const {
          mediaId
        } = media; // eslint-disable-next-line @typescript-eslint/no-non-null-assertion

        return /*#__PURE__*/createElement(AMPYoutube, {
          mediaId: mediaId,
          additionalStyle: blockStyle
        });
      }

    case 'mmPlayer':
      {
        const {
          mediaId,
          playerId
        } = media; // eslint-disable-next-line @typescript-eslint/no-non-null-assertion

        return /*#__PURE__*/createElement(AMPMMPlayer, {
          playlistId: mediaId,
          playerId: playerId,
          additionalStyle: blockStyle
        });
      }

    default:
      return null;
  }
};

const AMPCover = props => {
  const {
    baseUnit
  } = useTheme();
  const {
    media
  } = props;
  const styles = getStyles$5();
  const additionalStyle = getMainImageStyle$1(baseUnit);
  return /*#__PURE__*/createElement("div", {
    className: css(styles.wrapper)
  }, /*#__PURE__*/createElement(AMPImage, _extends({
    preload: true
  }, props, {
    additionalStyle: additionalStyle
  })), media && getMedia$1(media));
};

const logoHeight = 24;

const getLogoStyle$2 = () => {
  return StyleSheet.create({
    base: {
      height: `${logoHeight}px`,
      alignItems: 'center'
    },
    largeSvg: {
      [MEDIA_BREAKPOINTS.large]: {
        display: 'inline-flex'
      },
      [MEDIA_BREAKPOINTS.medium]: {
        display: 'none'
      },
      [MEDIA_BREAKPOINTS.small]: {
        display: 'none'
      }
    },
    smallSvg: {
      [MEDIA_BREAKPOINTS.large]: {
        display: 'none'
      },
      [MEDIA_BREAKPOINTS.medium]: {
        display: 'inline-flex'
      },
      [MEDIA_BREAKPOINTS.small]: {
        display: 'inline-flex'
      }
    },
    image: {
      height: '100%'
    }
  });
};

const getSvgWidthAndHeight = viewBoxValue => {
  const viewBowDimensions = viewBoxValue.split(' ');
  return {
    width: Number(viewBowDimensions[2]),
    height: Number(viewBowDimensions[3])
  };
};
const AMPLogo = props => {
  const {
    href,
    alt,
    svgLarge,
    svgSmall,
    svgLargeViewBox,
    svgSmallViewBox
  } = props;
  const style = getLogoStyle$2();
  const largeSvgWidthAndHeight = getSvgWidthAndHeight(svgLargeViewBox);
  const smallSvgWidthAndHeight = getSvgWidthAndHeight(svgSmallViewBox);
  return /*#__PURE__*/React__default.createElement(React__default.Fragment, null, /*#__PURE__*/React__default.createElement(Link, {
    className: css(style.largeSvg, style.base),
    href: href
  }, /*#__PURE__*/React__default.createElement("amp-img", {
    layout: "fixed",
    class: css(style.image),
    alt: alt,
    src: `data:image/svg+xml,${encodeURIComponent(svgLarge)}`,
    height: `${logoHeight}`,
    width: `${logoHeight * largeSvgWidthAndHeight.width / largeSvgWidthAndHeight.height}`
  })), /*#__PURE__*/React__default.createElement(Link, {
    className: css(style.smallSvg, style.base),
    href: href
  }, /*#__PURE__*/React__default.createElement("amp-img", {
    layout: "fixed",
    class: css(style.image),
    alt: alt,
    src: `data:image/svg+xml,${encodeURIComponent(svgSmall)}`,
    height: `${logoHeight}`,
    width: `${logoHeight * smallSvgWidthAndHeight.width / smallSvgWidthAndHeight.height}`
  })));
};

const componentConfigThemeKey$q = 'ampPrimaryLinks';
const additionalStyle$1 = {
  [MEDIA_BREAKPOINTS.large]: {
    display: 'block'
  },
  [MEDIA_BREAKPOINTS.medium]: {
    display: 'none'
  },
  [MEDIA_BREAKPOINTS.small]: {
    display: 'none'
  }
};
const radiusBorder$1 = 4;

const createStyle$j = (isActive = false, activeBorderBottom) => {
  return StyleSheet.create({
    link: {
      height: '100%',
      width: '100%',
      display: 'inline-flex',
      alignItems: 'center',
      position: 'relative',
      ':before': isActive ? {
        height: `${radiusBorder$1}px`,
        bottom: 0,
        left: 0,
        position: 'absolute',
        right: 0,
        content: "''",
        backgroundColor: activeBorderBottom
      } : {}
    }
  });
};

const createLink$1 = (idleColor, activeBorderBottom, hoverStateColor, activeStateColor, pagePath, fontSizesStyles, linkFontSizeLarge, linkFontSizeMedium, linkFontSizeSmall) => (navigationLink, index) => {
  const {
    href,
    text
  } = navigationLink.link;
  const isActiveLink = href === pagePath;
  const linkStyle = generateLinkStyleFromTheme({
    idleColor,
    activeStateColor,
    hoverStateColor
  }, additionalStyle$1);
  const style = createStyle$j(isActiveLink, activeBorderBottom);
  const fontStyle = getStyleBySize(fontSizesStyles, {
    fontSizeSmall: linkFontSizeSmall,
    fontSizeMedium: linkFontSizeMedium,
    fontSizeLarge: linkFontSizeLarge
  });
  return /*#__PURE__*/createElement(TextLinkComponent, {
    key: index,
    href: href,
    linkStyle: linkStyle
  }, /*#__PURE__*/createElement(Span, {
    styles: semanticCSS(fontStyle, style.link)
  }, text));
};

const AMPPrimaryLinks = props => {
  const {
    idleColor,
    fontSizesStyles,
    activeBorderBottom,
    hoverStateColor,
    activeStateColor,
    linkFontSizeLarge,
    linkFontSizeMedium,
    linkFontSizeSmall
  } = useTheme(componentConfigThemeKey$q);
  const {
    links,
    pagePath
  } = props;
  return /*#__PURE__*/createElement(ErrorBoundary, null, links.map(createLink$1(idleColor, activeBorderBottom, hoverStateColor, activeStateColor, pagePath, fontSizesStyles, linkFontSizeLarge, linkFontSizeMedium, linkFontSizeSmall)));
};

const componentConfigThemeKey$r = 'editions';

const getStyle$q = color => {
  return StyleSheet.create({
    editionsWrapper: {
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      [MEDIA_BREAKPOINTS.medium]: {
        display: 'none'
      },
      [MEDIA_BREAKPOINTS.small]: {
        display: 'none'
      }
    },
    editionsTitleWrapper: {
      width: '144px',
      color,
      textDecoration: 'none',
      backgroundColor: 'transparent',
      border: 'none',
      outline: 'none',
      ':hover': {
        cursor: 'pointer'
      },
      [MEDIA_BREAKPOINTS.large]: {
        height: `${primaryHeaderHeights.large}px`
      },
      [MEDIA_BREAKPOINTS.medium]: {
        height: `${primaryHeaderHeights.medium}px`
      },
      [MEDIA_BREAKPOINTS.small]: {
        height: `${primaryHeaderHeights.small}px`
      }
    },
    editionsTitle: {
      height: '100%',
      alignItems: 'center',
      display: 'flex',
      justifyContent: 'space-between'
    }
  });
};

const AMPEditions = props => {
  const {
    color
  } = useTheme(componentConfigThemeKey$r);
  const {
    editions,
    editionEndpoint
  } = props;
  const activeEdition = getActiveEdition(editions, editionEndpoint);
  const style = getStyle$q(color);
  return /*#__PURE__*/createElement(ErrorBoundary, null, /*#__PURE__*/createElement(Helmet, null, /*#__PURE__*/createElement("script", {
    async: undefined,
    "custom-element": "amp-accordion",
    src: "https://cdn.ampproject.org/v0/amp-accordion-0.1.js"
  })), /*#__PURE__*/createElement("div", {
    className: css(style.editionsWrapper)
  }, /*#__PURE__*/createElement("amp-accordion", {
    id: "accordion-editions",
    "disable-session-states": undefined
  }, /*#__PURE__*/createElement("section", null, /*#__PURE__*/createElement(H4, {
    styles: semanticCSS(style.editionsTitleWrapper)
  }, /*#__PURE__*/createElement("div", {
    className: css(style.editionsTitle)
  }, /*#__PURE__*/createElement("div", null, activeEdition.link.text), /*#__PURE__*/createElement(ChevronIcon, {
    direction: Directions.DOWN,
    color: color
  }))), /*#__PURE__*/createElement(DropDownMenu, {
    menuLinks: editions,
    configThemeKey: "editionsMenu",
    activeEndpoint: editionEndpoint
  })))));
};

const componentConfigThemeKey$s = 'menuIcon';

const getMenuIconStyle$1 = color => StyleSheet.create({
  wrapper: {
    width: '16px',
    height: '16px',
    position: 'relative',
    cursor: 'pointer',
    [MEDIA_BREAKPOINTS.large]: {
      display: 'none'
    },
    [MEDIA_BREAKPOINTS.medium]: {
      display: 'inline-flex'
    },
    [MEDIA_BREAKPOINTS.small]: {
      display: 'inline-flex'
    },
    ':focus': {
      outline: 'none'
    }
  },
  span: {
    display: 'block',
    position: 'absolute',
    backgroundColor: color,
    height: '2px',
    width: '100%',
    opacity: 1,
    left: 0
  },
  firstSpanClose: {
    top: 0
  },
  secondSpanClose: {
    top: '7px'
  },
  thirdSpanClose: {
    top: '14px'
  }
});

const AMPMenuIcon = () => {
  const {
    color
  } = useTheme(componentConfigThemeKey$s);
  const menuIcon = getMenuIconStyle$1(color);
  return /*#__PURE__*/createElement(ErrorBoundary, null, /*#__PURE__*/createElement("div", {
    tabIndex: "0",
    on: "tap:sidebar.open",
    role: "button",
    className: css(menuIcon.wrapper)
  }, /*#__PURE__*/createElement(Span, {
    styles: semanticCSS(menuIcon.span, menuIcon.firstSpanClose)
  }), /*#__PURE__*/createElement(Span, {
    styles: semanticCSS(menuIcon.span, menuIcon.secondSpanClose)
  }), /*#__PURE__*/createElement(Span, {
    styles: semanticCSS(menuIcon.span, menuIcon.thirdSpanClose)
  })));
};

const componentConfigThemeKey$t = 'primaryHeader';

const getPrimaryHeaderStyle = (backgroundColor, baseUnit) => StyleSheet.create({
  wrapper: {
    backgroundColor,
    position: 'relative',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    zIndex: 100,
    [MEDIA_BREAKPOINTS.large]: {
      height: `${primaryHeaderHeights.large}px`
    },
    [MEDIA_BREAKPOINTS.medium]: {
      height: `${primaryHeaderHeights.medium}px`
    },
    [MEDIA_BREAKPOINTS.small]: {
      height: `${primaryHeaderHeights.small}px`
    }
  },
  itemsBasicContainer: {
    display: 'grid',
    gridAutoFlow: 'column',
    height: '100%',
    width: 'fit-content',
    alignItems: 'center',
    gridColumnGap: `${baseUnit}px`
  },
  itemsRightContainer: {
    alignItems: 'baseline',
    [MEDIA_BREAKPOINTS.large]: {
      paddingRight: `${baseUnit}px`
    },
    [MEDIA_BREAKPOINTS.medium]: {
      paddingRight: `${baseUnit / 2}px`
    },
    [MEDIA_BREAKPOINTS.small]: {
      paddingRight: `${baseUnit / 2}px`
    }
  },
  itemsLeftContainer: {
    paddingLeft: `${baseUnit}px`
  },
  ctaWrapper: {
    [MEDIA_BREAKPOINTS.large]: {
      height: `${primaryHeaderHeights.large}px`
    },
    [MEDIA_BREAKPOINTS.medium]: {
      height: `${primaryHeaderHeights.medium}px`
    },
    [MEDIA_BREAKPOINTS.small]: {
      height: `${primaryHeaderHeights.small}px`
    },
    display: 'flex',
    alignItems: 'center'
  }
});

const AMPPrimaryHeader = props => {
  const {
    boxShadowTypeNormal,
    backgroundColor,
    baseUnit
  } = useTheme(componentConfigThemeKey$t);
  const {
    links,
    logo,
    cta,
    shouldShowClosedMenuIcon,
    editions,
    pagePath,
    editionEndpoint
  } = props;
  const style = getPrimaryHeaderStyle(backgroundColor, baseUnit);
  const boxShadowStyle = createStyleForBoxShadow({
    boxShadowTypeNormal
  });
  return /*#__PURE__*/createElement(ErrorBoundary, null, /*#__PURE__*/createElement("div", {
    className: css(style.wrapper, boxShadowStyle.style)
  }, /*#__PURE__*/createElement("div", {
    className: css(style.itemsBasicContainer, style.itemsLeftContainer)
  }, shouldShowClosedMenuIcon ? /*#__PURE__*/createElement(AMPMenuIcon, null) : null, /*#__PURE__*/createElement(AMPLogo, logo), links ? /*#__PURE__*/createElement(AMPPrimaryLinks, {
    links: links,
    pagePath: pagePath
  }) : null), /*#__PURE__*/createElement("div", {
    className: css(style.itemsBasicContainer, style.itemsRightContainer)
  }, cta ? /*#__PURE__*/createElement("div", {
    className: css(style.ctaWrapper)
  }, /*#__PURE__*/createElement(CtaButton, cta)) : null, editions ? /*#__PURE__*/createElement(AMPEditions, {
    editions: editions,
    editionEndpoint: editionEndpoint
  }) : null)));
};

/* eslint-disable react/no-unknown-property */
const componentConfigThemeKey$u = 'menuLink';

const getAmpSideBarLinkStyle = baseUnit => StyleSheet.create({
  wrapper: {
    [MEDIA_BREAKPOINTS.medium]: {
      margin: `${baseUnit}px`
    },
    [MEDIA_BREAKPOINTS.small]: {
      margin: `${baseUnit}px`
    }
  },
  title: {
    backgroundColor: 'inherit',
    border: 'inherit',
    paddingRight: 0,
    ':focus': {
      outline: 'none'
    }
  },
  titleAndChevronIconWrapper: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  }
});

const createAmpSideBarSubLink = (linkData, index) => {
  const {
    link
  } = linkData;
  return /*#__PURE__*/createElement(MenuSubLink, {
    text: link.text,
    href: link.href,
    key: index
  });
};

const AMPSideBarLink = props => {
  const {
    idleColor,
    baseUnit
  } = useTheme(componentConfigThemeKey$u);
  const {
    link,
    childrenLinks
  } = props;
  const style = getAmpSideBarLinkStyle(baseUnit);
  const linkStyle = generateLinkStyleFromTheme({
    idleColor
  });
  return /*#__PURE__*/createElement("section", {
    className: css(style.wrapper)
  }, /*#__PURE__*/createElement(H4, {
    styles: semanticCSS(style.title)
  }, /*#__PURE__*/createElement("div", {
    className: css(style.titleAndChevronIconWrapper)
  }, /*#__PURE__*/createElement(TextLinkComponent, {
    href: link.href,
    linkStyle: linkStyle
  }, link.text), childrenLinks && childrenLinks.length > 0 ? /*#__PURE__*/createElement(ChevronIcon, {
    direction: Directions.DOWN,
    color: idleColor
  }) : null)), /*#__PURE__*/createElement("ul", null, childrenLinks && childrenLinks.map(createAmpSideBarSubLink)));
};

const createStyle$k = (dividerColor, baseUnit) => StyleSheet.create({
  divider: {
    backgroundColor: dividerColor,
    height: '1px',
    [MEDIA_BREAKPOINTS.medium]: {
      margin: `0 ${baseUnit}px`
    },
    [MEDIA_BREAKPOINTS.small]: {
      margin: `0 ${baseUnit}px`
    }
  }
});

const AMPMenuEditions = props => {
  const {
    editions,
    dividerColor,
    baseUnit,
    editionEndpoint
  } = props;
  const activeEdition = getActiveEdition(editions, editionEndpoint);
  const {
    text
  } = activeEdition.link;
  const notActivatedEditions = editions.filter(edition => edition.link.text !== text).map(navigationLinkToMenuLinkDataProps);
  const style = createStyle$k(dividerColor, baseUnit);
  return /*#__PURE__*/createElement(Fragment, null, /*#__PURE__*/createElement("div", {
    className: css(style.divider)
  }), /*#__PURE__*/createElement("amp-accordion", {
    id: "editions-accordion",
    "disable-session-states": undefined
  }, /*#__PURE__*/createElement(AMPSideBarLink, {
    link: activeEdition.link,
    childrenLinks: notActivatedEditions
  })));
};

/* eslint-disable react/no-unknown-property */

const componentMenuLinksConfigThemeKey = 'menuLinks';
const componentMenuIconConfigThemeKey = 'ampCloseMenuIcon';

const getAmpSideBarStyle = backgroundColor => StyleSheet.create({
  wrapper: {
    backgroundColor,
    position: 'fixed',
    width: '100%',
    left: 0,
    overflowY: 'auto',
    flexDirection: 'column',
    [MEDIA_BREAKPOINTS.large]: {
      display: 'none'
    },
    [MEDIA_BREAKPOINTS.medium]: {
      display: 'inline-flex'
    },
    [MEDIA_BREAKPOINTS.small]: {
      display: 'inline-flex'
    }
  }
});

const getMenuIconStyle$2 = (baseUnit, menuIconColor) => StyleSheet.create({
  wrapper: {
    width: '16px',
    height: '16px',
    minHeight: '16px',
    position: 'relative',
    transition: '.5s ease-in-out',
    cursor: 'pointer',
    [MEDIA_BREAKPOINTS.medium]: {
      margin: `${baseUnit}px`,
      marginBottom: 0
    },
    [MEDIA_BREAKPOINTS.small]: {
      margin: `${baseUnit}px`,
      marginBottom: 0
    },
    ':focus': {
      outline: 'none'
    }
  },
  span: {
    display: 'block',
    position: 'absolute',
    height: '2px',
    width: '100%',
    backgroundColor: menuIconColor,
    opacity: 1,
    left: 0,
    transition: '.25s ease-in-out',
    transformOrigin: 'right center'
  },
  firstSpanOpen: {
    transform: 'rotate(-45deg)',
    width: '130%',
    left: '-6px',
    top: 0
  },
  secondSpanOpen: {
    top: '7px',
    transform: 'scaleX(0.1)',
    opacity: 0
  },
  thirdSpanOpen: {
    transform: 'rotate(45deg)',
    top: '14px',
    width: '130%',
    left: '-6px'
  }
});

const createAMPSideBarLink = (menuLink, index) => {
  const {
    link,
    childrenLinks
  } = menuLink;
  return /*#__PURE__*/createElement(AMPSideBarLink, {
    link: link,
    childrenLinks: childrenLinks,
    key: index
  });
};

const AMPSideBar = props => {
  const {
    backgroundColor,
    dividerColor,
    baseUnit
  } = useTheme(componentMenuLinksConfigThemeKey);
  const {
    color
  } = useTheme(componentMenuIconConfigThemeKey);
  const {
    menu,
    editions,
    editionEndpoint
  } = props;
  const menuIconStyle = getMenuIconStyle$2(baseUnit, color);
  const ampSideBarStyle = getAmpSideBarStyle(backgroundColor);
  return (
    /*#__PURE__*/
    // @ts-ignore TS2339: Property 'amp-sidebar' does not exist on type 'JSX.IntrinsicElements'.
    createElement(ErrorBoundary, null, /*#__PURE__*/createElement(Helmet, null, /*#__PURE__*/createElement("script", {
      async: undefined,
      "custom-element": "amp-sidebar",
      src: "https://cdn.ampproject.org/v0/amp-sidebar-0.1.js"
    }), /*#__PURE__*/createElement("script", {
      async: undefined,
      "custom-element": "amp-accordion",
      src: "https://cdn.ampproject.org/v0/amp-accordion-0.1.js"
    })), /*#__PURE__*/createElement("amp-sidebar", {
      id: "sidebar",
      layout: "nodisplay",
      side: "left",
      class: css(ampSideBarStyle.wrapper)
    }, /*#__PURE__*/createElement("div", {
      tabIndex: "0",
      on: "tap:sidebar.close,accordion.collapse,editions-accordion.collapse",
      role: "button",
      className: css(menuIconStyle.wrapper)
    }, /*#__PURE__*/createElement(Span, {
      styles: semanticCSS(menuIconStyle.span, menuIconStyle.firstSpanOpen)
    }), /*#__PURE__*/createElement(Span, {
      styles: semanticCSS(menuIconStyle.span, menuIconStyle.secondSpanOpen)
    }), /*#__PURE__*/createElement(Span, {
      styles: semanticCSS(menuIconStyle.span, menuIconStyle.thirdSpanOpen)
    })), /*#__PURE__*/createElement("amp-accordion", {
      id: "accordion",
      "disable-session-states": undefined
    }, menu.map(createAMPSideBarLink)), editions ? /*#__PURE__*/createElement(AMPMenuEditions, {
      editionEndpoint: editionEndpoint,
      editions: editions,
      baseUnit: baseUnit,
      dividerColor: dividerColor
    }) : null))
  );
};

const componentConfigThemeKey$v = 'moreLinks';
const getStyle$r = titleColor => {
  return StyleSheet.create({
    wrapper: {
      height: 'inherit',
      [MEDIA_BREAKPOINTS.large]: {
        display: 'block'
      },
      [MEDIA_BREAKPOINTS.small]: {
        display: 'none'
      },
      [MEDIA_BREAKPOINTS.medium]: {
        display: 'none'
      }
    },
    titleWrapper: {
      width: 'fit-content',
      height: `${secondaryHeaderHeight}px`,
      color: titleColor,
      textDecoration: 'none',
      backgroundColor: 'transparent',
      border: 'none',
      outline: 'none',
      ':hover': {
        cursor: 'pointer'
      }
    },
    title: {
      height: '100%',
      alignItems: 'center',
      display: 'flex',
      justifyContent: 'space-between'
    },
    text: {
      marginRight: '9px'
    }
  });
};
const AMPMoreLinks = props => {
  const {
    titleColor,
    fontSizesStyles,
    chevronColor
  } = useTheme(componentConfigThemeKey$v);
  const {
    links,
    moreLinksText,
    pagePath
  } = props;
  const titleStyle = getStyleBySize(fontSizesStyles, {
    fontSizeSmall: TYPOGRAPHY_SIZE.NORMAL,
    fontSizeMedium: TYPOGRAPHY_SIZE.NORMAL,
    fontSizeLarge: TYPOGRAPHY_SIZE.NORMAL
  });
  const style = getStyle$r(titleColor);
  return /*#__PURE__*/React__default.createElement(React__default.Fragment, null, /*#__PURE__*/React__default.createElement(Helmet, null, /*#__PURE__*/React__default.createElement("script", {
    async: undefined,
    "custom-element": "amp-accordion",
    src: "https://cdn.ampproject.org/v0/amp-accordion-0.1.js"
  })), /*#__PURE__*/React__default.createElement("div", {
    className: css(style.wrapper)
  }, /*#__PURE__*/React__default.createElement("amp-accordion", {
    id: "accordion-more-links",
    "disable-session-states": undefined
  }, /*#__PURE__*/React__default.createElement("section", null, /*#__PURE__*/React__default.createElement(H4, {
    styles: semanticCSS(style.titleWrapper, titleStyle)
  }, /*#__PURE__*/React__default.createElement("div", {
    className: css(style.title)
  }, /*#__PURE__*/React__default.createElement("div", {
    className: css(style.text)
  }, moreLinksText), /*#__PURE__*/React__default.createElement(ChevronIcon, {
    color: chevronColor,
    direction: Directions.DOWN
  }))), /*#__PURE__*/React__default.createElement(DropDownMenu, {
    menuLinks: links,
    configThemeKey: "moreLinks",
    activeEndpoint: pagePath
  })))));
};

const componentConfigThemeKey$w = 'ampSecondaryLink';

const getTextStyle$1 = (baseUnit, isActiveLink, lastLink) => {
  return StyleSheet.create({
    link: {
      width: 'max-content',
      [MEDIA_BREAKPOINTS.medium]: {
        paddingRight: lastLink ? `${baseUnit * 2}px` : 0
      },
      [MEDIA_BREAKPOINTS.small]: {
        paddingRight: lastLink ? `${baseUnit * 2}px` : 0
      }
    }
  });
};

const AMPSecondaryLink = props => {
  const {
    link,
    pagePath,
    linksLength,
    index
  } = props;
  const {
    activeStateColor,
    idleColor,
    hoverStateColor,
    baseUnit,
    fontSizesStyles,
    linkFontSizeLarge,
    linkFontSizeMedium,
    linkFontSizeSmall
  } = useTheme(componentConfigThemeKey$w);
  const {
    href,
    text
  } = link.link;
  const isActiveLink = href === pagePath;
  const linkStyleBasedOnScreen = linksLength > maxVisibleLinksOnLargeAndXLarge && index >= maxVisibleLinksOnLargeAndXLarge - 1 ? {
    [MEDIA_BREAKPOINTS.large]: {
      display: 'none'
    },
    [MEDIA_BREAKPOINTS.small]: {
      display: 'inline-flex'
    },
    [MEDIA_BREAKPOINTS.medium]: {
      display: 'inline-flex'
    }
  } : {};
  const linkStyle = generateLinkStyleFromTheme({
    idleColor: isActiveLink ? activeStateColor : idleColor,
    activeStateColor,
    hoverStateColor: isActiveLink ? activeStateColor : hoverStateColor
  }, linkStyleBasedOnScreen);
  const style = getTextStyle$1(baseUnit, isActiveLink, isLastLink(index, linksLength));
  const fontStyle = getStyleBySize(fontSizesStyles, {
    fontSizeSmall: linkFontSizeSmall,
    fontSizeMedium: linkFontSizeMedium,
    fontSizeLarge: linkFontSizeLarge
  });
  return /*#__PURE__*/createElement(TextLinkComponent, {
    href: href,
    linkStyle: linkStyle
  }, /*#__PURE__*/createElement(Span, {
    styles: semanticCSS(fontStyle, style.link)
  }, text));
};

const getContainerStyle$2 = baseUnit => {
  return StyleSheet.create({
    container: {
      display: 'grid',
      gridAutoFlow: 'column',
      alignItems: 'center',
      height: '100%',
      gridColumnGap: `${baseUnit}px`,
      [MEDIA_BREAKPOINTS.medium]: {
        overflowX: 'auto'
      },
      [MEDIA_BREAKPOINTS.small]: {
        overflowX: 'auto'
      }
    }
  });
};
const AMPSecondaryLinks = props => {
  const {
    moreLinksText,
    links,
    pagePath
  } = props;
  const {
    baseUnit
  } = useTheme();
  const moreLinks = links.length > maxVisibleLinksOnLargeAndXLarge ? links.slice(maxVisibleLinksOnLargeAndXLarge - 1) : [];
  const style = getContainerStyle$2(baseUnit);
  return /*#__PURE__*/React__default.createElement(React__default.Fragment, null, /*#__PURE__*/React__default.createElement("div", {
    className: css(style.container)
  }, links.map((link, index) => /*#__PURE__*/React__default.createElement(AMPSecondaryLink, {
    link: link,
    index: index,
    key: index,
    linksLength: links.length,
    pagePath: pagePath
  }))), moreLinks.length > 0 ? /*#__PURE__*/React__default.createElement(AMPMoreLinks, {
    links: moreLinks,
    moreLinksText: moreLinksText,
    pagePath: pagePath
  }) : null);
};

const componentConfigThemeKey$x = 'secondaryHeader';

const createStyle$l = (baseUnit, backgroundColor) => {
  return StyleSheet.create({
    wrapper: {
      backgroundColor,
      position: 'relative',
      height: `${secondaryHeaderHeight}px`,
      display: 'grid',
      gridAutoFlow: 'column',
      width: '100%',
      alignItems: 'center',
      justifyContent: 'flex-start',
      zIndex: 50,
      gridColumnGap: `${baseUnit}px`,
      paddingLeft: `${baseUnit}px`
    }
  });
};

const AMPSecondaryHeader = props => {
  const {
    baseUnit,
    backgroundColor,
    boxShadowTypeNormal
  } = useTheme(componentConfigThemeKey$x);
  const {
    moreLinksText,
    links,
    logo,
    pagePath
  } = props;
  const style = createStyle$l(baseUnit, backgroundColor);
  const boxShadowStyle = createStyleForBoxShadow({
    boxShadowTypeNormal
  });
  return /*#__PURE__*/createElement(ErrorBoundary, null, /*#__PURE__*/createElement("div", {
    className: css(style.wrapper, boxShadowStyle.style)
  }, logo ? /*#__PURE__*/createElement(AMPLogo, logo) : null, links ? /*#__PURE__*/createElement(AMPSecondaryLinks, {
    links: links,
    moreLinksText: moreLinksText,
    pagePath: pagePath
  }) : null));
};

const getHeaderStyle = () => StyleSheet.create({
  wrapper: {
    position: 'fixed',
    width: '100%',
    zIndex: 1500,
    top: 0
  }
});

const AMPHeader = props => {
  const {
    primaryHeader,
    shouldShowClosedMenuIcon,
    secondaryHeader,
    editionEndpoint,
    pagePath
  } = props;
  const {
    links,
    logo,
    cta,
    editions
  } = primaryHeader;
  const style = getHeaderStyle();
  return /*#__PURE__*/createElement("header", {
    className: css(style.wrapper)
  }, /*#__PURE__*/createElement(AMPPrimaryHeader, {
    links: links,
    logo: logo,
    cta: cta,
    shouldShowClosedMenuIcon: shouldShowClosedMenuIcon,
    editions: editions,
    pagePath: pagePath,
    editionEndpoint: editionEndpoint
  }), secondaryHeader ? /*#__PURE__*/createElement(AMPSecondaryHeader, _extends({}, secondaryHeader, {
    pagePath: pagePath
  })) : null);
};
const AMPHeaderAndSideBar = props => {
  const {
    secondaryHeader,
    primaryHeader,
    editionEndpoint,
    pagePath
  } = props;
  const {
    editions,
    menu
  } = primaryHeader;
  return /*#__PURE__*/createElement(Fragment, null, /*#__PURE__*/createElement(AMPHeader, {
    primaryHeader: primaryHeader,
    secondaryHeader: secondaryHeader,
    shouldShowClosedMenuIcon: !!menu,
    editionEndpoint: editionEndpoint,
    pagePath: pagePath
  }), menu ? /*#__PURE__*/createElement(AMPSideBar, {
    menu: menu.links,
    editions: editions,
    editionEndpoint: editionEndpoint
  }) : null);
};

const AMPComscore = props => {
  const {
    comscoreId
  } = props;
  const scriptJson = {
    vars: {
      c2: comscoreId
    },
    extraUrlParams: {
      comscorekw: 'amp'
    }
  };
  return /*#__PURE__*/createElement(Fragment, null, /*#__PURE__*/createElement(Helmet, null, /*#__PURE__*/createElement("script", {
    async: undefined,
    "custom-element": "amp-analytics",
    src: "https://cdn.ampproject.org/v0/amp-analytics-0.1.js"
  })), /*#__PURE__*/createElement("amp-analytics", {
    type: "comscore"
  }, /*#__PURE__*/createElement("script", {
    type: "application/json",
    dangerouslySetInnerHTML: {
      __html: JSON.stringify(scriptJson)
    }
  })));
};

const createPageViewParams = ({
  property,
  articleAuthor,
  articleId,
  articlePublicationTime,
  articleTemplate,
  articleTitle,
  contentTags,
  distributionChannels,
  mainMedia,
  language,
  markupFormat,
  pageIdentifier,
  commercialTags,
  articleContentCount,
  articleWordCount
}) => ({
  property,
  articleAuthor,
  articleId,
  articlePublicationTime,
  articleTemplate,
  articleTitle,
  contentTags,
  distributionChannels,
  mainMedia,
  language,
  markupFormat,
  pageIdentifier,
  commercialTags,
  articleWordCount,
  articleContentCount
});

const AMPConfigAnalytics = props => {
  const {
    analyticsConfigURL
  } = props;
  const scriptJson = {
    vars: createPageViewParams(props)
  };
  return /*#__PURE__*/createElement(Fragment, null, /*#__PURE__*/createElement("amp-analytics", {
    id: "analytics",
    config: analyticsConfigURL,
    "data-credentials": "include"
  }, /*#__PURE__*/createElement("script", {
    type: "application/json",
    dangerouslySetInnerHTML: {
      __html: JSON.stringify(scriptJson)
    }
  })));
};

const AMPAnalytics = props => {
  const {
    comscoreId,
    analyticsConfigURL,
    property,
    owner,
    articleId,
    articlePublicationTime,
    articleTemplate,
    articleTitle,
    contentTags,
    distributionChannels,
    mainMedia,
    language,
    markupFormat = 'web',
    pageIdentifier,
    commercialTags,
    articleContentCount,
    articleWordCount
  } = props;
  const {
    name: ownerName
  } = owner;
  return /*#__PURE__*/createElement(ErrorBoundary, null, /*#__PURE__*/createElement(Helmet, null, /*#__PURE__*/createElement("script", {
    async: undefined,
    "custom-element": "amp-analytics",
    src: "https://cdn.ampproject.org/v0/amp-analytics-0.1.js"
  })), /*#__PURE__*/createElement(AMPConfigAnalytics, {
    analyticsConfigURL: analyticsConfigURL,
    property: property,
    articleAuthor: ownerName,
    articleId: articleId,
    articlePublicationTime: articlePublicationTime,
    articleTemplate: articleTemplate,
    articleTitle: articleTitle,
    contentTags: contentTags,
    distributionChannels: distributionChannels,
    mainMedia: mainMedia,
    language: language,
    markupFormat: markupFormat,
    pageIdentifier: pageIdentifier,
    commercialTags: commercialTags,
    articleWordCount: articleWordCount,
    articleContentCount: articleContentCount
  }), /*#__PURE__*/createElement(AMPComscore, {
    comscoreId: comscoreId
  }));
};

const getStyle$s = (baseUnit, type, src) => {
  const style = {
    backgroundSize: '32px',
    borderRadius: '100%',
    marginRight: `${baseUnit * 0.5}px`,
    ...(type === 'reddit' && {
      backgroundColor: '#ff4500',
      backgroundImage: `url("${src}")`
    })
  };
  return StyleSheet.create({
    style
  });
};

const preconfiguredProviders = ['twitter', 'facebook', 'email', 'linkedin', 'pinterest', 'tumblr', 'whatsapp'];

const isNotPreconfiguredProvider = type => preconfiguredProviders.indexOf(type) >= 0;

const AMPSocialButton = props => {
  const {
    type,
    shareURL,
    src,
    baseUnit
  } = props;
  const socialShareProps = {
    type,
    ...(isNotPreconfiguredProvider && {
      'data-share-endpoint': shareURL
    })
  };
  const styles = getStyle$s(baseUnit, type, src); // @ts-ignore TS2339

  return /*#__PURE__*/createElement("amp-social-share", _extends({}, socialShareProps, {
    class: css(styles.style),
    width: "42",
    height: "42"
  }));
};

const getStyle$t = baseUnit => StyleSheet.create({
  wrapper: {
    margin: `${baseUnit}px`
  }
});

const getSocialButtons = (shareConfig, baseUnit) => {
  return shareConfig.map((data, index) => /*#__PURE__*/createElement(AMPSocialButton, _extends({
    key: index
  }, data, {
    baseUnit: baseUnit
  })));
};

const AMPSocialContainer = props => {
  const {
    baseUnit
  } = useTheme();
  const {
    shareConfig
  } = props;
  const styles = getStyle$t(baseUnit);
  return /*#__PURE__*/createElement(ErrorBoundary, null, /*#__PURE__*/createElement(Helmet, null, /*#__PURE__*/createElement("script", {
    async: undefined,
    "custom-element": "amp-social-share",
    src: "https://cdn.ampproject.org/v0/amp-social-share-0.1.js"
  })), /*#__PURE__*/createElement("div", {
    className: css(styles.wrapper)
  }, getSocialButtons(shareConfig, baseUnit)));
};

const componentConfigurationKey$t = 'sponsor';
const logoHeight$1 = 40;

const createSponsorStyle = (baseUnit, textColor) => StyleSheet.create({
  text: {
    color: textColor,
    marginBottom: `${0.5 * baseUnit}px`
  },
  wrapper: {
    textAlign: 'center',
    margin: `${1 * baseUnit}px 0px`
  }
});

const AMPSponsor = props => {
  const {
    text,
    logo,
    brandName,
    href,
    logoViewBox
  } = props;
  const {
    textColor,
    baseUnit
  } = useTheme(componentConfigurationKey$t);
  const style = createSponsorStyle(baseUnit, textColor);
  const svgWidthAndHeight = logoViewBox ? getSvgWidthAndHeight(logoViewBox) : null;
  return /*#__PURE__*/createElement(ErrorBoundary, null, /*#__PURE__*/createElement("div", {
    className: css(style.wrapper)
  }, /*#__PURE__*/createElement(H5, {
    styles: semanticCSS(style.text)
  }, text), /*#__PURE__*/createElement(Link, {
    href: href,
    rel: "nofollow"
  }, svgWidthAndHeight ? /*#__PURE__*/createElement("amp-img", {
    className: css(style.image),
    src: `data:image/svg+xml,${encodeURIComponent(logo)}`,
    alt: brandName,
    layout: "fixed",
    height: `${logoHeight$1}`,
    width: `${logoHeight$1 * svgWidthAndHeight.width / svgWidthAndHeight.height}`
  }) : null)));
};

const getStyle$u = baseUnit => StyleSheet.create({
  wrapper: {
    [MEDIA_BREAKPOINTS.small]: {
      margin: `0 ${baseUnit}px`
    }
  }
});
const AMPRecommendationsAdvertisement = props => {
  const {
    revcontentWidgetID
  } = props;
  const {
    baseUnit
  } = useTheme();
  const style = getStyle$u(baseUnit);
  return /*#__PURE__*/createElement(Fragment, null, /*#__PURE__*/createElement(Helmet, null, /*#__PURE__*/createElement("script", {
    async: undefined,
    "custom-element": "amp-ad",
    src: "https://cdn.ampproject.org/v0/amp-ad-0.1.js"
  })), /*#__PURE__*/createElement("amp-ad", {
    width: "100",
    height: "100",
    type: "revcontent",
    layout: "responsive",
    heights: "(min-width:1582px) 282%, (min-width:1116px) 288%,(min-width:782px) 297%, (min-width:561px) 309%,(min-width:460px) 328%, (min-width:395px) 350%,375%",
    "data-revcontent": undefined,
    "data-id": revcontentWidgetID,
    class: css(style.wrapper)
  }));
};

const AMPTwitter = props => {
  const {
    mediaId,
    additionalStyle
  } = props;
  return /*#__PURE__*/createElement(Fragment, null, /*#__PURE__*/createElement(Helmet, null, /*#__PURE__*/createElement("script", {
    async: undefined,
    "custom-element": "amp-twitter",
    src: "https://cdn.ampproject.org/v0/amp-twitter-0.1.js"
  })), /*#__PURE__*/createElement("figure", {
    className: css(additionalStyle.style)
  }, /*#__PURE__*/createElement("amp-twitter", {
    width: "1",
    height: "1",
    layout: "responsive",
    "data-tweetid": mediaId
  })));
};

const mediaIdValidator = mediaId => {
  return typeof mediaId === 'string' && mediaId.trim() !== '';
};

const AMPMediaValidator = props => {
  const {
    mediaId,
    children
  } = props;
  const isMediaIdValid = mediaIdValidator(mediaId);
  return /*#__PURE__*/createElement(Fragment, null, isMediaIdValid && children);
};

const AMPInstagram = props => {
  const {
    mediaId,
    additionalStyle
  } = props;
  return /*#__PURE__*/createElement(Fragment, null, /*#__PURE__*/createElement(Helmet, null, /*#__PURE__*/createElement("script", {
    async: undefined,
    "custom-element": "amp-instagram",
    src: "https://cdn.ampproject.org/v0/amp-instagram-0.1.js"
  })), /*#__PURE__*/createElement("figure", {
    className: css(additionalStyle.style)
  }, /*#__PURE__*/createElement("amp-instagram", {
    "data-shortcode": mediaId,
    width: "1",
    height: "1",
    layout: "responsive"
  })));
};

const AMPFacebook = props => {
  const {
    mediaId,
    additionalStyle
  } = props;
  return /*#__PURE__*/createElement(Fragment, null, /*#__PURE__*/createElement(Helmet, null, /*#__PURE__*/createElement("script", {
    async: undefined,
    "custom-element": "amp-facebook",
    src: "https://cdn.ampproject.org/v0/amp-facebook-0.1.js"
  })), /*#__PURE__*/createElement("figure", {
    className: css(additionalStyle.style)
  }, /*#__PURE__*/createElement("amp-facebook", {
    width: "552",
    height: "303",
    layout: "responsive",
    "data-href": mediaId
  })));
};

const AMPPlaybuzz = props => {
  const {
    mediaId,
    additionalStyle
  } = props;
  return /*#__PURE__*/createElement(Fragment, null, /*#__PURE__*/createElement(Helmet, null, /*#__PURE__*/createElement("script", {
    async: undefined,
    "custom-element": "amp-playbuzz",
    src: "https://cdn.ampproject.org/v0/amp-playbuzz-0.1.js"
  })), /*#__PURE__*/createElement("figure", {
    className: css(additionalStyle.style)
  }, /*#__PURE__*/createElement("amp-playbuzz", {
    width: "552",
    height: "303",
    layout: "responsive",
    src: mediaId
  })));
};

const AMPDailymotion = props => {
  const {
    mediaId,
    additionalStyle
  } = props;
  return /*#__PURE__*/createElement(Fragment, null, /*#__PURE__*/createElement(Helmet, null, /*#__PURE__*/createElement("script", {
    async: undefined,
    "custom-element": "amp-dailymotion",
    src: "https://cdn.ampproject.org/v0/amp-dailymotion-0.1.js"
  })), /*#__PURE__*/createElement("figure", {
    className: css(additionalStyle.style)
  }, /*#__PURE__*/createElement("amp-dailymotion", {
    width: "552",
    height: "303",
    layout: "responsive",
    "data-videoid": mediaId
  })));
};

const createStyle$m = () => StyleSheet.create({
  ad: {
    display: 'flex',
    justifyContent: 'center'
  }
});

const AMPAd = props => {
  const {
    adUnit,
    additionalStyle,
    height,
    width
  } = props;
  const style = createStyle$m();
  return /*#__PURE__*/createElement(Fragment, null, /*#__PURE__*/createElement(Helmet, null, /*#__PURE__*/createElement("script", {
    async: undefined,
    "custom-element": "amp-ad",
    src: "https://cdn.ampproject.org/v0/amp-ad-0.1.js"
  })), /*#__PURE__*/createElement("div", {
    className: css(additionalStyle.style, style.ad)
  }, /*#__PURE__*/createElement("amp-ad", {
    width: width,
    height: height,
    type: "doubleclick",
    "data-slot": adUnit
  })));
};

const AMPSoundclound = props => {
  const {
    mediaId,
    additionalStyle
  } = props;
  return /*#__PURE__*/createElement(Fragment, null, /*#__PURE__*/createElement(Helmet, null, /*#__PURE__*/createElement("script", {
    async: undefined,
    "custom-element": "amp-soundcloud",
    src: "https://cdn.ampproject.org/v0/amp-soundcloud-0.1.js"
  })), /*#__PURE__*/createElement("figure", {
    className: css(additionalStyle.style)
  }, /*#__PURE__*/createElement("amp-soundcloud", {
    width: "16",
    height: "9",
    layout: "responsive",
    "data-trackid": mediaId,
    "data-visual": "true"
  })));
};

const AMPIframe = props => {
  const {
    src,
    additionalStyle,
    height
  } = props;
  return /*#__PURE__*/createElement(Fragment, null, /*#__PURE__*/createElement(Helmet, null, /*#__PURE__*/createElement("script", {
    async: undefined,
    "custom-element": "amp-iframe",
    src: "https://cdn.ampproject.org/v0/amp-iframe-0.1.js"
  })), /*#__PURE__*/createElement("figure", {
    className: css(additionalStyle.style)
  }, /*#__PURE__*/createElement("amp-iframe", {
    height: height,
    sandbox: "allow-scripts allow-same-origin",
    layout: "fixed-height",
    frameborder: "0",
    src: src
  })));
};

const AMPVimeo = props => {
  const {
    mediaId,
    additionalStyle
  } = props;
  return /*#__PURE__*/createElement(Fragment, null, /*#__PURE__*/createElement(Helmet, null, /*#__PURE__*/createElement("script", {
    async: undefined,
    "custom-element": "amp-vimeo",
    src: "https://cdn.ampproject.org/v0/amp-vimeo-0.1.js"
  })), /*#__PURE__*/createElement("figure", {
    className: css(additionalStyle.style)
  }, /*#__PURE__*/createElement("amp-vimeo", {
    width: "16",
    height: "9",
    layout: "responsive",
    "data-videoid": mediaId
  })));
};

const AMPGfycat = props => {
  const {
    additionalStyle,
    mediaId
  } = props;
  return /*#__PURE__*/createElement(Fragment, null, /*#__PURE__*/createElement(Helmet, null, /*#__PURE__*/createElement("script", {
    async: undefined,
    "custom-element": "amp-gfycat",
    src: "https://cdn.ampproject.org/v0/amp-gfycat-0.1.js"
  })), /*#__PURE__*/createElement("figure", {
    className: css(additionalStyle.style)
  }, /*#__PURE__*/createElement("amp-gfycat", {
    width: "16",
    height: "9",
    layout: "responsive",
    "data-gfyid": mediaId
  })));
};

const AMPApester = props => {
  const {
    mediaId,
    additionalStyle
  } = props;
  return /*#__PURE__*/createElement(Fragment, null, /*#__PURE__*/createElement(Helmet, null, /*#__PURE__*/createElement("script", {
    async: undefined,
    "custom-element": "amp-apester-media",
    src: "https://cdn.ampproject.org/v0/amp-apester-media-0.1.js"
  })), /*#__PURE__*/createElement("figure", {
    className: css(additionalStyle.style)
  }, /*#__PURE__*/createElement("amp-apester-media", {
    width: "16",
    height: "9",
    layout: "responsive",
    "data-apester-media-id": mediaId
  })));
};

const createBlockStyle$2 = (baseUnit, modifier) => StyleSheet.create({
  style: {
    marginTop: `${baseUnit * modifier}px`,
    marginLeft: `${baseUnit}px`,
    marginRight: `${baseUnit}px`
  }
});

const createBlocksComponents$1 = (body, baseUnit, blocksWithMultipleOptions, defaultBlocksWidths) => {
  let isPreviousElementTitle = false;
  return body.map(block => {
    const {
      type
    } = block;
    const blockStyle = isPreviousElementTitle ? createBlockStyle$2(baseUnit, 0.5) : createBlockStyle$2(baseUnit, 1);
    isPreviousElementTitle = false;

    switch (type) {
      case 'ad':
        {
          const {
            adWidth,
            adHeight,
            dataId,
            adUnitPath
          } = block;
          return /*#__PURE__*/createElement(AMPAd, {
            key: dataId,
            adUnit: adUnitPath,
            width: adWidth,
            height: adHeight,
            additionalStyle: blockStyle
          });
        }

      case 'image':
        {
          const {
            image,
            dataId
          } = block;
          return /*#__PURE__*/createElement(AMPImage, _extends({
            key: dataId
          }, image, {
            additionalStyle: blockStyle
          }));
        }

      case 'title':
        {
          isPreviousElementTitle = true;
          const {
            listItemNumber,
            text,
            dataId
          } = block;
          return /*#__PURE__*/createElement(ListItemTitle, {
            key: dataId,
            listItemNumber: listItemNumber,
            text: text,
            additionalStyle: blockStyle,
            dataId: dataId,
            defaultBlocksWidths: defaultBlocksWidths
          });
        }

      case 'inline-text':
        {
          const {
            html,
            dataId
          } = block;
          return /*#__PURE__*/createElement(Paragraph, {
            key: dataId,
            html: html,
            additionalStyle: blockStyle,
            dataId: dataId,
            defaultBlocksWidths: defaultBlocksWidths
          });
        }

      case 'quote':
        {
          const {
            text,
            cite,
            dataId
          } = block;
          const Quote = blocksWithMultipleOptions.quote;
          return /*#__PURE__*/createElement(Quote, {
            key: dataId,
            text: text,
            cite: cite,
            additionalStyle: blockStyle,
            dataId: dataId,
            defaultBlocksWidths: defaultBlocksWidths
          });
        }

      case 'youtube':
        {
          const {
            dataId,
            mediaId
          } = block; // eslint-disable-next-line @typescript-eslint/no-non-null-assertion

          return /*#__PURE__*/createElement(AMPMediaValidator, {
            key: dataId,
            mediaId: mediaId
          }, /*#__PURE__*/createElement(AMPYoutube, {
            mediaId: mediaId,
            additionalStyle: blockStyle
          }));
        }

      case 'twitter':
        {
          const {
            mediaId,
            dataId
          } = block; // eslint-disable-next-line @typescript-eslint/no-non-null-assertion

          return /*#__PURE__*/createElement(AMPMediaValidator, {
            key: dataId,
            mediaId: mediaId
          }, /*#__PURE__*/createElement(AMPTwitter, {
            mediaId: mediaId,
            additionalStyle: blockStyle
          }));
        }

      case 'instagram':
        {
          const {
            mediaId,
            dataId
          } = block; // eslint-disable-next-line @typescript-eslint/no-non-null-assertion

          return /*#__PURE__*/createElement(AMPMediaValidator, {
            key: dataId,
            mediaId: mediaId
          }, /*#__PURE__*/createElement(AMPInstagram, {
            mediaId: mediaId,
            additionalStyle: blockStyle
          }));
        }

      case 'facebook':
        {
          const {
            mediaId,
            dataId
          } = block; // eslint-disable-next-line @typescript-eslint/no-non-null-assertion

          return /*#__PURE__*/createElement(AMPMediaValidator, {
            key: dataId,
            mediaId: mediaId
          }, /*#__PURE__*/createElement(AMPFacebook, {
            mediaId: mediaId,
            additionalStyle: blockStyle
          }));
        }

      case 'jw':
        {
          const {
            playerId,
            videoId,
            dataId
          } = block; // eslint-disable-next-line @typescript-eslint/no-non-null-assertion

          return /*#__PURE__*/createElement(AMPJWPlayer, {
            key: dataId,
            videoId: videoId,
            playerId: playerId,
            additionalStyle: blockStyle
          });
        }

      case 'giphy':
        {
          const {
            dataId
          } = block;
          const className = css(blockStyle.style); // @ts-ignore TS2339

          return /*#__PURE__*/createElement("amp-img", {
            key: dataId,
            alt: block.caption,
            src: block.url,
            layout: "responsive",
            width: block.width,
            height: block.height,
            class: className
          });
        }

      case 'playbuzz':
        {
          const {
            mediaId,
            dataId
          } = block; // eslint-disable-next-line @typescript-eslint/no-non-null-assertion

          return /*#__PURE__*/createElement(AMPMediaValidator, {
            key: dataId,
            mediaId: mediaId
          }, /*#__PURE__*/createElement(AMPPlaybuzz, {
            mediaId: mediaId,
            additionalStyle: blockStyle
          }));
        }

      case 'dailyMotion':
        {
          const {
            mediaId,
            dataId
          } = block; // eslint-disable-next-line @typescript-eslint/no-non-null-assertion

          return /*#__PURE__*/createElement(AMPMediaValidator, {
            key: dataId,
            mediaId: mediaId
          }, /*#__PURE__*/createElement(AMPDailymotion, {
            mediaId: mediaId,
            additionalStyle: blockStyle
          }));
        }

      case 'divider':
        {
          const {
            dataId
          } = block;
          const Divider = blocksWithMultipleOptions.divider;
          return /*#__PURE__*/createElement(Divider, {
            key: dataId,
            additionalStyle: blockStyle,
            dataId: dataId,
            defaultBlocksWidths: defaultBlocksWidths
          });
        }

      case 'soundcloud':
        {
          const {
            mediaId,
            dataId
          } = block; // eslint-disable-next-line @typescript-eslint/no-non-null-assertion

          return /*#__PURE__*/createElement(AMPMediaValidator, {
            key: dataId,
            mediaId: mediaId
          }, /*#__PURE__*/createElement(AMPSoundclound, {
            mediaId: mediaId,
            additionalStyle: blockStyle
          }));
        }

      case 'iframeEmbed':
        {
          const {
            src,
            dataId,
            height
          } = block;
          return /*#__PURE__*/createElement(AMPIframe, {
            key: dataId,
            height: height,
            src: src,
            additionalStyle: blockStyle
          });
        }

      case 'mmPlayer':
        {
          const {
            dataId,
            playerId,
            mediaId
          } = block;
          return /*#__PURE__*/createElement(AMPMMPlayer, {
            key: dataId,
            playlistId: mediaId,
            additionalStyle: blockStyle,
            playerId: playerId
          });
        }

      case 'vimeo':
        {
          const {
            dataId,
            mediaId
          } = block;
          return /*#__PURE__*/createElement(AMPMediaValidator, {
            key: dataId,
            mediaId: mediaId
          }, /*#__PURE__*/createElement(AMPVimeo, {
            key: dataId,
            mediaId: mediaId,
            additionalStyle: blockStyle
          }));
        }

      case 'gfycat':
        {
          const {
            dataId,
            mediaId
          } = block; // eslint-disable-next-line @typescript-eslint/no-non-null-assertion

          return /*#__PURE__*/createElement(AMPMediaValidator, {
            key: dataId,
            mediaId: mediaId
          }, /*#__PURE__*/createElement(AMPGfycat, {
            key: dataId,
            additionalStyle: blockStyle,
            mediaId: mediaId
          }));
        }

      case 'apester':
        {
          const {
            dataId,
            mediaId
          } = block; // eslint-disable-next-line @typescript-eslint/no-non-null-assertion

          return /*#__PURE__*/createElement(AMPMediaValidator, {
            key: dataId,
            mediaId: mediaId
          }, /*#__PURE__*/createElement(AMPApester, {
            key: dataId,
            additionalStyle: blockStyle,
            mediaId: mediaId
          }));
        }

      default:
        return null;
    }
  });
};

const createMMPlayer = (videoPlaylistId, videoPlayerId, baseUnit) => /*#__PURE__*/createElement(AMPMMPlayer, {
  key: "first-amp-mmPlayer",
  playlistId: videoPlaylistId,
  additionalStyle: createBlockStyle$2(baseUnit, 1),
  playerId: videoPlayerId
});

const AMPArticleContentComponent = props => {
  const {
    videoPlaylistId,
    videoPlayerId,
    body,
    blocksWithMultipleOptions,
    defaultBlocksWidths
  } = props;
  const {
    baseUnit
  } = useTheme();
  return /*#__PURE__*/createElement("div", null, videoPlaylistId && videoPlayerId ? createMMPlayer(videoPlaylistId, videoPlayerId, baseUnit) : null, body ? createBlocksComponents$1(body, baseUnit, blocksWithMultipleOptions, defaultBlocksWidths) : null);
};
const AMPArticleContent = props => /*#__PURE__*/createElement(ErrorBoundary, null, /*#__PURE__*/createElement(AMPArticleContentComponent, props));

const componentConfigurationKey$u = 'copyLinkButton';

const getStyles$6 = () => {
  return StyleSheet.create({
    copyLinkIcon: {
      position: 'absolute',
      left: '50%',
      top: '50%',
      transform: 'translate(-50%, -50%)',
      width: '16px',
      transition: '200ms ease-in-out'
    },
    copyLinkIconAction: {
      left: '-150%'
    },
    successIcon: {
      left: '150%'
    },
    successIconAction: {
      left: '50%'
    }
  });
};

const copyToClipboard = text => {
  const el = document.createElement('textarea');
  el.value = text;
  el.setAttribute('readonly', '');
  el.style.position = 'absolute';
  el.style.opacity = '0';
  document.body.appendChild(el);
  el.select();
  document.execCommand('copy');
  document.body.removeChild(el);
};

const CopyLinkButton = props => {
  const {
    canonical,
    buttonClass
  } = props;
  const {
    color
  } = useTheme(componentConfigurationKey$u);
  const styles = getStyles$6();
  let timeout;
  const [wasCopied, setWasCopied] = useState(false);

  const onClickCopy = () => {
    copyToClipboard(canonical);
    clearTimeout(timeout);
    setWasCopied(true);
    timeout = setTimeout(() => {
      setWasCopied(false);
    }, 1000);
  };

  const copyIconAction = wasCopied ? styles.copyLinkIconAction : styles.copyLinkIcon;
  const succcessIconAction = wasCopied ? styles.successIconAction : styles.successIcon;
  return /*#__PURE__*/createElement(ErrorBoundary, null, /*#__PURE__*/createElement("button", {
    type: "button",
    className: buttonClass,
    onClick: onClickCopy
  }, /*#__PURE__*/createElement("svg", {
    version: "1.1",
    xmlns: "http://www.w3.org/2000/svg",
    viewBox: "0 0 64 64",
    className: css(styles.copyLinkIcon, copyIconAction)
  }, /*#__PURE__*/createElement("g", {
    fill: color
  }, /*#__PURE__*/createElement("path", {
    d: "M4.3,49.8c-0.9,0-1.8-0.3-2.6-0.9C0.6,48.1,0,46.8,0,45.5L0,4.3C0,1.9,1.9,0,4.3,0l41.2,0c2.4,0,4.3,1.9,4.3,4.3l0,6.5 l-7.1,0l0-3.7l-35.6,0l0,35.5l3.8,0l0,7.1l-6.3,0C4.5,49.8,4.4,49.8,4.3,49.8z"
  }), /*#__PURE__*/createElement("path", {
    d: "M18.6,64c-2.4,0-4.3-1.9-4.3-4.3l0-41.1c0-2.4,1.9-4.3,4.3-4.3l41,0c2.4,0,4.3,1.9,4.3,4.3L64,59.7c0,2.4-1.9,4.3-4.3,4.3 L18.6,64z M21.3,21.3l0,35.5l35.5,0l-0.1-35.5L21.3,21.3z M56.8,18.5C56.8,18.5,56.8,18.5,56.8,18.5L56.8,18.5z"
  }))), /*#__PURE__*/createElement("svg", {
    version: "1.1",
    xmlns: "http://www.w3.org/2000/svg",
    viewBox: "0 0 64 64",
    className: css(styles.copyLinkIcon, succcessIconAction)
  }, /*#__PURE__*/createElement("g", {
    fill: color
  }, /*#__PURE__*/createElement("path", {
    d: "M59.5,10.8"
  }), /*#__PURE__*/createElement("path", {
    d: "M23.1,56L1,32.9c-1.4-1.4-1.3-3.7,0.1-5.1c1.4-1.4,3.7-1.4,5,0.1l17,17.8L57.9,9.1c1.4-1.4,3.6-1.5,5-0.1 c1.4,1.4,1.4,3.7,0.1,5.1L23.1,56z"
  }), /*#__PURE__*/createElement("path", {
    d: "M23.4,43.4"
  })))));
};

const componentConfigurationKey$v = 'articleShareButton';
const socialButtonAnimation = {
  '0%': {
    opacity: 0
  },
  '100%': {
    opacity: 1
  }
};

const getStyles$7 = (baseUnit, backgroundColor) => {
  return StyleSheet.create({
    shareMenu: {
      marginTop: `${baseUnit / 2}px`,
      [MEDIA_BREAKPOINTS.large]: {
        width: '48px'
      },
      [MEDIA_BREAKPOINTS.medium]: {
        width: '40px'
      },
      [MEDIA_BREAKPOINTS.small]: {
        width: '40px'
      }
    },
    socialButton: {
      backgroundColor,
      border: 'none',
      borderRadius: '100%',
      position: 'relative',
      display: 'block',
      marginLeft: 'auto',
      marginRight: 'auto',
      cursor: 'pointer',
      outline: 'none',
      overflow: 'hidden',
      opacity: 0,
      animationName: [socialButtonAnimation],
      animationDuration: '500ms',
      animationFillMode: 'forwards',
      [MEDIA_BREAKPOINTS.large]: {
        marginBottom: `${baseUnit / 4}px`,
        width: '40px',
        height: '40px'
      },
      [MEDIA_BREAKPOINTS.medium]: {
        marginBottom: `${baseUnit / 4}px`,
        width: '40px',
        height: '40px'
      },
      [MEDIA_BREAKPOINTS.small]: {
        marginBottom: `${baseUnit / 3}px`,
        width: '36px',
        height: '36px'
      }
    },
    socialIcon: {
      position: 'absolute',
      left: '50%',
      top: '50%',
      transform: 'translate(-50%, -50%)',
      [MEDIA_BREAKPOINTS.large]: {
        height: '20px'
      },
      [MEDIA_BREAKPOINTS.medium]: {
        height: '20px'
      },
      [MEDIA_BREAKPOINTS.small]: {
        height: '16px'
      }
    }
  });
};

const getAnimationDelayClass = index => {
  return StyleSheet.create({
    animationModifier: {
      animationDelay: `${index * 50}ms`
    }
  });
};

const getShareButtons = (buttons, styles) => buttons.map((button, index) => {
  const {
    shareURL,
    src,
    type
  } = button;
  const animationDurationClass = getAnimationDelayClass(index);
  return (
    /*#__PURE__*/
    // eslint-disable-next-line jsx-a11y/anchor-is-valid
    createElement(Link, {
      className: css(styles.socialButton, animationDurationClass.animationModifier),
      key: index,
      target: "_blank",
      rel: "noopener noreferrer",
      onClick: () => {
        window.open(shareURL, '_blank', 'noopener');
        return false;
      }
    }, /*#__PURE__*/createElement("img", {
      src: src,
      alt: type,
      className: css(styles.socialIcon)
    }))
  );
});

const ShareMenu = props => {
  const {
    shareConfig,
    canonical
  } = props;
  const {
    baseUnit,
    buttonBackgroundColor
  } = useTheme(componentConfigurationKey$v);
  const styles = getStyles$7(baseUnit, buttonBackgroundColor);
  const copyLinkAnimationClass = getAnimationDelayClass(shareConfig.length);
  return /*#__PURE__*/createElement(ErrorBoundary, null, /*#__PURE__*/createElement("div", {
    className: css(styles.shareMenu)
  }, getShareButtons(shareConfig, styles), /*#__PURE__*/createElement(CopyLinkButton, {
    canonical: canonical,
    buttonClass: css(styles.socialButton, copyLinkAnimationClass.animationModifier)
  })));
};

const componentConfigurationKey$w = 'articleShareButton';
const shareButtonSize = {
  xLarge: 48,
  large: 48,
  medium: 40,
  small: 40
};

const getStyles$8 = (baseUnit, backgroundColor, buttonHoverColor) => {
  return StyleSheet.create({
    wrapper: {
      position: 'absolute',
      right: 0,
      zIndex: 1000,
      marginRight: `${baseUnit}px`,
      [MEDIA_BREAKPOINTS.large]: {
        width: '40px',
        top: `calc((${shareButtonSize.large}px - 100%)/-2)`
      },
      [MEDIA_BREAKPOINTS.medium]: {
        width: '32px',
        top: `calc((${shareButtonSize.medium}px - 100%)/-2)`
      },
      [MEDIA_BREAKPOINTS.small]: {
        width: '32px',
        top: `calc((${shareButtonSize.small}px - 100%)/-2)`
      }
    },
    shareButton: {
      outline: 'none',
      border: 'none',
      background: 'transparent',
      borderRadius: '100%',
      '-webkit-tap-highlight-color': 'transparent',
      [MEDIA_BREAKPOINTS.large]: {
        width: `${shareButtonSize.large}px`,
        height: `${shareButtonSize.large}px`
      },
      [MEDIA_BREAKPOINTS.medium]: {
        width: `${shareButtonSize.medium}px`,
        height: `${shareButtonSize.medium}px`
      },
      [MEDIA_BREAKPOINTS.small]: {
        width: `${shareButtonSize.small}px`,
        height: `${shareButtonSize.small}px`
      },
      '@media (hover: hover)': {
        ':hover': {
          backgroundColor: buttonHoverColor
        }
      }
    },
    shareButtonOpen: {
      backgroundColor: buttonHoverColor
    },
    shareIcon: {
      margin: 'auto',
      display: 'block',
      cursor: 'pointer',
      [MEDIA_BREAKPOINTS.large]: {
        width: '32px'
      },
      [MEDIA_BREAKPOINTS.medium]: {
        width: '24px'
      },
      [MEDIA_BREAKPOINTS.small]: {
        width: '24px'
      }
    }
  });
};

const ShareButton = props => {
  const {
    shareConfig,
    canonical
  } = props;
  const {
    baseUnit,
    buttonBackgroundColor,
    buttonHoverColor
  } = useTheme(componentConfigurationKey$w);
  const styles = getStyles$8(baseUnit, buttonBackgroundColor, buttonHoverColor);
  const [isOpen, setIsOpen] = useState(false);
  const shareButtonClass = isOpen ? css(styles.shareButton, styles.shareButtonOpen) : css(styles.shareButton);
  return /*#__PURE__*/createElement(ErrorBoundary, null, /*#__PURE__*/createElement("div", {
    className: css(styles.wrapper)
  }, /*#__PURE__*/createElement("button", {
    type: "button",
    className: shareButtonClass,
    "aria-label": "share-button"
  }, /*#__PURE__*/createElement("svg", {
    viewBox: "0 0 16 16",
    version: "1.1",
    xmlns: "http://www.w3.org/2000/svg",
    className: css(styles.shareIcon),
    onClick: () => setIsOpen(!isOpen)
  }, /*#__PURE__*/createElement("g", {
    stroke: "none",
    strokeWidth: "1",
    fill: "none",
    fillRule: "evenodd"
  }, /*#__PURE__*/createElement("path", {
    d: "M9.58105,13.3911 L9.58105,9.7861 C9.58105,9.7861 4.17905,9.4641 5.00000001e-05,14.0001 C5.00000001e-05,14.0001 1.13705,5.6716 9.57955,4.6051 L9.57955,1.0001 L16.00005,7.1946 L9.58105,13.3911 Z",
    fill: buttonBackgroundColor,
    fillRule: "nonzero"
  })))), isOpen ? /*#__PURE__*/createElement(ShareMenu, {
    shareConfig: shareConfig,
    canonical: canonical
  }) : null));
};

const ShareConfigLinks = props => {
  const {
    shareConfig,
    linkStyle
  } = props;
  return /*#__PURE__*/createElement(Fragment, null, shareConfig.map((button, index) => {
    const {
      shareURL,
      src,
      type
    } = button;
    return (
      /*#__PURE__*/
      // eslint-disable-next-line jsx-a11y/anchor-is-valid
      createElement(Link, {
        key: index,
        target: "_blank",
        rel: "noopener noreferrer",
        onClick: () => {
          window.open(shareURL, '_blank', 'noopener');
          return false;
        },
        className: css(linkStyle)
      }, /*#__PURE__*/createElement("img", {
        src: src,
        alt: type,
        height: "16"
      }))
    );
  }));
};

const componentConfigurationKey$x = 'verticalShareMenu';

const getStyles$9 = (backgroundColor, baseUnit, smallScreenOnly = false) => {
  return StyleSheet.create({
    sticky: {
      [MEDIA_BREAKPOINTS.large]: {
        position: 'sticky',
        top: `${baseUnit * 4}px`,
        display: smallScreenOnly ? 'none' : 'block',
        marginBottom: 0,
        paddingLeft: `${baseUnit}px`,
        marginLeft: 'unset'
      },
      [MEDIA_BREAKPOINTS.medium]: {
        position: 'sticky',
        top: `${baseUnit * 5}px`,
        display: smallScreenOnly ? 'none' : 'block',
        marginBottom: 0,
        paddingLeft: 0,
        marginLeft: `-${baseUnit}px`
      },
      [MEDIA_BREAKPOINTS.small]: {
        position: 'relative',
        top: 'unset',
        display: 'block',
        marginBottom: `${baseUnit}px`,
        paddingLeft: 0,
        marginLeft: 'unset'
      }
    },
    wrapperBase: {
      display: 'flex',
      marginTop: `${baseUnit}px`
    },
    wrapperDefault: {
      [MEDIA_BREAKPOINTS.large]: {
        flexDirection: 'column',
        position: 'absolute',
        transform: 'unset'
      },
      [MEDIA_BREAKPOINTS.medium]: {
        flexDirection: 'column',
        position: 'absolute',
        transform: 'translateX(50px)'
      },
      [MEDIA_BREAKPOINTS.small]: {
        flexDirection: 'row',
        justifyContent: 'center',
        position: 'relative',
        transform: 'unset'
      }
    },
    link: {
      position: 'relative',
      display: 'grid',
      height: '40px',
      width: '40px',
      borderRadius: '100%',
      backgroundColor,
      justifyContent: 'center',
      alignItems: 'center',
      overflow: 'hidden',
      ':not(:first-child)': {
        [MEDIA_BREAKPOINTS.large]: {
          marginTop: '10px',
          marginLeft: 0
        },
        [MEDIA_BREAKPOINTS.medium]: {
          marginTop: '10px',
          marginLeft: 0
        },
        [MEDIA_BREAKPOINTS.small]: {
          marginTop: 0,
          marginLeft: '10px'
        }
      }
    }
  });
};

const SwitchableShareMenu = props => {
  const {
    shareConfig,
    canonical,
    smallScreenOnly
  } = props;
  const {
    backgroundColor,
    baseUnit
  } = useTheme(componentConfigurationKey$x);
  const styles = getStyles$9(backgroundColor, baseUnit, smallScreenOnly);
  return /*#__PURE__*/createElement(ErrorBoundary, null, /*#__PURE__*/createElement("div", {
    className: css(styles.sticky)
  }, /*#__PURE__*/createElement("div", {
    className: css(styles.wrapperBase, styles.wrapperDefault)
  }, /*#__PURE__*/createElement(ShareConfigLinks, {
    shareConfig: shareConfig,
    linkStyle: styles.link
  }), /*#__PURE__*/createElement(CopyLinkButton, {
    canonical: canonical,
    buttonClass: css(styles.link)
  }))));
};

const componentConfigurationKey$y = 'recommendationCardPlaceHolder';
const getRecommendationCardPlaceHolderStyle = (baseUnit, backgroundColor) => StyleSheet.create({
  cardPlaceHolderWrapper: {
    width: '100%',
    marginTop: `${baseUnit * 0.5}px`
  },
  cardPlaceHolder: {
    paddingTop: 'calc(100% / (16 / 9))',
    backgroundColor
  }
});
const RecommendationCardPlaceHolder = () => {
  const {
    baseUnit,
    backgroundColor
  } = useTheme(componentConfigurationKey$y);
  const style = getRecommendationCardPlaceHolderStyle(baseUnit, backgroundColor);
  return /*#__PURE__*/React__default.createElement(ErrorBoundary, null, /*#__PURE__*/React__default.createElement("div", {
    className: css(style.cardPlaceHolderWrapper)
  }, /*#__PURE__*/React__default.createElement("div", {
    className: css(style.cardPlaceHolder)
  })));
};

const useRecommendationScrollTrigger = onScrolledIntoView => {
  const wrapper = useRef(null);
  useEffect(() => {
    if (!wrapper.current || typeof IntersectionObserver === 'undefined') return;
    const options = {
      rootMargin: '0px 0px 100px 0px',
      threshold: 0.1
    };
    const obsrever = new IntersectionObserver(e => {
      if (e[0].isIntersecting) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        obsrever.unobserve(wrapper.current);
        onScrolledIntoView();
      }
    }, options); // eslint-disable-next-line @typescript-eslint/no-non-null-assertion

    obsrever.observe(wrapper.current);
  }, [onScrolledIntoView]);
  return wrapper;
};
const getSectionStyle$1 = baseUnit => StyleSheet.create({
  section: {
    margin: `0 ${baseUnit}px`,
    [MEDIA_BREAKPOINTS.large]: {
      margin: `0 ${baseUnit}px 0 ${baseUnit + 80}px`
    }
  }
});

const createArrayWithPlaceHoldersNumberLength = numberOfPlaceHolders => new Array(numberOfPlaceHolders).fill(0);

const createPlaceholders = numberOfPlaceHolders => {
  return createArrayWithPlaceHoldersNumberLength(numberOfPlaceHolders).map((item, index) => /*#__PURE__*/createElement(RecommendationCardPlaceHolder, {
    key: index
  }));
};

const componentConfigurationKey$z = 'postRecommendationsHeader';

const getStyle$v = (color, baseUnit) => StyleSheet.create({
  square: {
    width: `${baseUnit}px`,
    height: `${baseUnit}px`,
    marginRight: `${baseUnit * 0.5}px`,
    backgroundColor: color
  },
  container: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    color,
    marginTop: `${baseUnit * 2}px`
  }
});

const PostRecommendationsHeader = ({
  header
}) => {
  const {
    color,
    baseUnit
  } = useTheme(componentConfigurationKey$z);
  const style = getStyle$v(color, baseUnit);
  return /*#__PURE__*/createElement(ErrorBoundary, null, /*#__PURE__*/createElement("div", {
    className: css(style.container)
  }, /*#__PURE__*/createElement("div", {
    className: css(style.square)
  }), /*#__PURE__*/createElement(H2, null, header)));
};

const SixSixPostRecommendations = props => {
  const {
    sectionName,
    sectionCards,
    onScrolledIntoView
  } = props;
  const {
    baseUnit
  } = useTheme();
  const memoizedOnScrolledIntoView = useCallback(onScrolledIntoView, []);
  const wrapper = useRecommendationScrollTrigger(memoizedOnScrolledIntoView);
  const sectionStyle = getSectionStyle$1(baseUnit);
  return /*#__PURE__*/createElement(ErrorBoundary, null, /*#__PURE__*/createElement("section", {
    ref: wrapper,
    className: css(sectionStyle.section)
  }, /*#__PURE__*/createElement(EventProvider, {
    category: "Recommendations"
  }, /*#__PURE__*/createElement(PostRecommendationsHeader, {
    header: sectionName
  }), sectionCards && sectionCards.length !== 0 ? /*#__PURE__*/createElement(SixSixArticleList, {
    cardsProps: sectionCards
  }) : createPlaceholders(12))));
};

const getStyle$w = baseUnit => StyleSheet.create({
  card: {
    marginTop: `${baseUnit * 0.5}px`
  }
});

const imageWidths$4 = createImageWidths(540, 720, 720);
const OnTopCardsPostRecommendations = props => {
  const {
    sectionName,
    sectionCards,
    onScrolledIntoView
  } = props;
  const {
    baseUnit
  } = useTheme();
  const memoizedOnScrolledIntoView = useCallback(onScrolledIntoView, []);
  const wrapper = useRecommendationScrollTrigger(memoizedOnScrolledIntoView);
  const style = getStyle$w(baseUnit);
  const sectionStyle = getSectionStyle$1(baseUnit);
  return /*#__PURE__*/createElement(ErrorBoundary, null, /*#__PURE__*/createElement("section", {
    ref: wrapper,
    className: css(sectionStyle.section)
  }, /*#__PURE__*/createElement(EventProvider, {
    category: "Recommendations"
  }, /*#__PURE__*/createElement(PostRecommendationsHeader, {
    header: sectionName
  }), sectionCards && sectionCards.length !== 0 ? sectionCards.map((card, index) => /*#__PURE__*/createElement("div", {
    className: css(style.card),
    key: index
  }, /*#__PURE__*/createElement(OnTopCard, _extends({}, card, {
    imageWidths: imageWidths$4
  })))) : createPlaceholders(4))));
};

var fontfaceobserver_standalone = createCommonjsModule(function (module) {
/* Font Face Observer v2.1.0 - © Bram Stein. License: BSD-3-Clause */
(function () {
  function l(a, b) {
    document.addEventListener ? a.addEventListener("scroll", b, !1) : a.attachEvent("scroll", b);
  }

  function m(a) {
    document.body ? a() : document.addEventListener ? document.addEventListener("DOMContentLoaded", function c() {
      document.removeEventListener("DOMContentLoaded", c);
      a();
    }) : document.attachEvent("onreadystatechange", function k() {
      if ("interactive" == document.readyState || "complete" == document.readyState) document.detachEvent("onreadystatechange", k), a();
    });
  }

  function t(a) {
    this.a = document.createElement("div");
    this.a.setAttribute("aria-hidden", "true");
    this.a.appendChild(document.createTextNode(a));
    this.b = document.createElement("span");
    this.c = document.createElement("span");
    this.h = document.createElement("span");
    this.f = document.createElement("span");
    this.g = -1;
    this.b.style.cssText = "max-width:none;display:inline-block;position:absolute;height:100%;width:100%;overflow:scroll;font-size:16px;";
    this.c.style.cssText = "max-width:none;display:inline-block;position:absolute;height:100%;width:100%;overflow:scroll;font-size:16px;";
    this.f.style.cssText = "max-width:none;display:inline-block;position:absolute;height:100%;width:100%;overflow:scroll;font-size:16px;";
    this.h.style.cssText = "display:inline-block;width:200%;height:200%;font-size:16px;max-width:none;";
    this.b.appendChild(this.h);
    this.c.appendChild(this.f);
    this.a.appendChild(this.b);
    this.a.appendChild(this.c);
  }

  function u(a, b) {
    a.a.style.cssText = "max-width:none;min-width:20px;min-height:20px;display:inline-block;overflow:hidden;position:absolute;width:auto;margin:0;padding:0;top:-999px;white-space:nowrap;font-synthesis:none;font:" + b + ";";
  }

  function z(a) {
    var b = a.a.offsetWidth,
        c = b + 100;
    a.f.style.width = c + "px";
    a.c.scrollLeft = c;
    a.b.scrollLeft = a.b.scrollWidth + 100;
    return a.g !== b ? (a.g = b, !0) : !1;
  }

  function A(a, b) {
    function c() {
      var a = k;
      z(a) && a.a.parentNode && b(a.g);
    }

    var k = a;
    l(a.b, c);
    l(a.c, c);
    z(a);
  }

  function B(a, b) {
    var c = b || {};
    this.family = a;
    this.style = c.style || "normal";
    this.weight = c.weight || "normal";
    this.stretch = c.stretch || "normal";
  }

  var C = null,
      D = null,
      E = null,
      F = null;

  function G() {
    if (null === D) if (J() && /Apple/.test(window.navigator.vendor)) {
      var a = /AppleWebKit\/([0-9]+)(?:\.([0-9]+))(?:\.([0-9]+))/.exec(window.navigator.userAgent);
      D = !!a && 603 > parseInt(a[1], 10);
    } else D = !1;
    return D;
  }

  function J() {
    null === F && (F = !!document.fonts);
    return F;
  }

  function K() {
    if (null === E) {
      var a = document.createElement("div");

      try {
        a.style.font = "condensed 100px sans-serif";
      } catch (b) {}

      E = "" !== a.style.font;
    }

    return E;
  }

  function L(a, b) {
    return [a.style, a.weight, K() ? a.stretch : "", "100px", b].join(" ");
  }

  B.prototype.load = function (a, b) {
    var c = this,
        k = a || "BESbswy",
        r = 0,
        n = b || 3E3,
        H = new Date().getTime();
    return new Promise(function (a, b) {
      if (J() && !G()) {
        var M = new Promise(function (a, b) {
          function e() {
            new Date().getTime() - H >= n ? b(Error("" + n + "ms timeout exceeded")) : document.fonts.load(L(c, '"' + c.family + '"'), k).then(function (c) {
              1 <= c.length ? a() : setTimeout(e, 25);
            }, b);
          }

          e();
        }),
            N = new Promise(function (a, c) {
          r = setTimeout(function () {
            c(Error("" + n + "ms timeout exceeded"));
          }, n);
        });
        Promise.race([N, M]).then(function () {
          clearTimeout(r);
          a(c);
        }, b);
      } else m(function () {
        function v() {
          var b;
          if (b = -1 != f && -1 != g || -1 != f && -1 != h || -1 != g && -1 != h) (b = f != g && f != h && g != h) || (null === C && (b = /AppleWebKit\/([0-9]+)(?:\.([0-9]+))/.exec(window.navigator.userAgent), C = !!b && (536 > parseInt(b[1], 10) || 536 === parseInt(b[1], 10) && 11 >= parseInt(b[2], 10))), b = C && (f == w && g == w && h == w || f == x && g == x && h == x || f == y && g == y && h == y)), b = !b;
          b && (d.parentNode && d.parentNode.removeChild(d), clearTimeout(r), a(c));
        }

        function I() {
          if (new Date().getTime() - H >= n) d.parentNode && d.parentNode.removeChild(d), b(Error("" + n + "ms timeout exceeded"));else {
            var a = document.hidden;
            if (!0 === a || void 0 === a) f = e.a.offsetWidth, g = p.a.offsetWidth, h = q.a.offsetWidth, v();
            r = setTimeout(I, 50);
          }
        }

        var e = new t(k),
            p = new t(k),
            q = new t(k),
            f = -1,
            g = -1,
            h = -1,
            w = -1,
            x = -1,
            y = -1,
            d = document.createElement("div");
        d.dir = "ltr";
        u(e, L(c, "sans-serif"));
        u(p, L(c, "serif"));
        u(q, L(c, "monospace"));
        d.appendChild(e.a);
        d.appendChild(p.a);
        d.appendChild(q.a);
        document.body.appendChild(d);
        w = e.a.offsetWidth;
        x = p.a.offsetWidth;
        y = q.a.offsetWidth;
        I();
        A(e, function (a) {
          f = a;
          v();
        });
        u(e, L(c, '"' + c.family + '",sans-serif'));
        A(p, function (a) {
          g = a;
          v();
        });
        u(p, L(c, '"' + c.family + '",serif'));
        A(q, function (a) {
          h = a;
          v();
        });
        u(q, L(c, '"' + c.family + '",monospace'));
      });
    });
  };

   module.exports = B ;
})();
});

const getFontWeights = font => {
  switch (font.type) {
    case fontFaceTypeName:
      return [font.weight];

    case stylesheetUrlTypeName:
      return font.weights;

    default:
      return [];
  }
};

const loadWebFonts = fonts => {
  const fontObservers = [];
  fonts.forEach(font => {
    const {
      family
    } = font;
    const weights = getFontWeights(font);
    weights.forEach(weight => {
      const fontVariantObserver = new fontfaceobserver_standalone(family, {
        weight
      });
      fontObservers.push(fontVariantObserver.load());
    });
  });
  return Promise.all(fontObservers);
};

const createFontFaceSRC = fonts => {
  return fonts.reduce((acc, currentFont, index) => {
    if (index === 0) {
      return `url("${currentFont.url}") format("${currentFont.type}")`;
    }

    return `${acc}, url("${currentFont.url}") format("${currentFont.type}")`;
  }, '');
};

const FontFace = props => {
  const {
    fontConfiguration
  } = props;
  const {
    family,
    weight,
    fontURLs
  } = fontConfiguration;
  const customFont = {
    fontFamily: family,
    fontWeight: weight,
    src: createFontFaceSRC(fontURLs),
    fontDisplay: 'swap'
  };
  const customFontStyle = StyleSheet.create({
    style: {
      fontFamily: [customFont]
    }
  });
  css(customFontStyle.style);
  return null;
};

const FontLink = ({
  fontConfiguration
}) => {
  const [rel, setRel] = useState('preload');
  const stylesheetByProvider = fontConfiguration.stylesheetUrl;
  useEffect(() => {
    setRel('stylesheet');
  }, []);
  return /*#__PURE__*/createElement(Helmet, null, /*#__PURE__*/createElement("link", {
    rel: rel,
    href: stylesheetByProvider,
    as: "style"
  }));
};

const createFontFamilyLinkOrFontFace = (fontConfiguration, index) => {
  switch (fontConfiguration.type) {
    case stylesheetUrlTypeName:
      return /*#__PURE__*/createElement(FontLink, {
        fontConfiguration: fontConfiguration,
        key: index
      });

    case fontFaceTypeName:
      {
        return /*#__PURE__*/createElement(FontFace, {
          fontConfiguration: fontConfiguration,
          key: index
        });
      }

    default:
      return null;
  }
};

const createDefaultFontFamiliesMap = typographySizesFontFamiliesMap => {
  return Object.keys(typographySizesFontFamiliesMap).reduce((sizesObj, current) => {
    return { ...sizesObj,
      [current]: typographySizesFontFamiliesMap[current].defaultFontFamily
    };
  }, {});
};

const createFontFamiliesMap = typographySizesFontFamiliesMap => {
  return Object.keys(typographySizesFontFamiliesMap).reduce((sizesObj, current) => {
    return { ...sizesObj,
      [current]: typographySizesFontFamiliesMap[current].fontFamily
    };
  }, {});
};

const WebFonts = props => {
  const {
    fontsConfiguration,
    typographySizesFontFamiliesMap,
    children
  } = props;
  const [webFontsContext, setWebFontsContext] = useState(createDefaultFontFamiliesMap(typographySizesFontFamiliesMap));
  useEffect(() => {
    loadWebFonts(fontsConfiguration).then(() => setWebFontsContext(createFontFamiliesMap(typographySizesFontFamiliesMap)));
  }, [fontsConfiguration, typographySizesFontFamiliesMap]);
  return /*#__PURE__*/createElement(WebFontsContext.Provider, {
    value: webFontsContext
  }, fontsConfiguration.map(createFontFamilyLinkOrFontFace), children);
};

const FontAmpLink = ({
  fontConfiguration
}) => {
  const errorReporting = useContext(ErrorReporting);
  const stylesheetByProvider = fontConfiguration.stylesheetUrl;

  try {
    return /*#__PURE__*/createElement(Helmet, null, /*#__PURE__*/createElement("link", {
      rel: "stylesheet",
      href: stylesheetByProvider
    }));
  } catch (e) {
    errorReporting.reportException(e);
    return null;
  }
};

const createFontFamilyLinkOrFontFace$1 = (fontConfiguration, index) => {
  switch (fontConfiguration.type) {
    case stylesheetUrlTypeName:
      return /*#__PURE__*/createElement(FontAmpLink, {
        key: index,
        fontConfiguration: fontConfiguration
      });

    case fontFaceTypeName:
      {
        return /*#__PURE__*/createElement(FontFace, {
          key: index,
          fontConfiguration: fontConfiguration
        });
      }

    default:
      return null;
  }
};

const createFontFamiliesMap$1 = typographySizesFontFamiliesMap => {
  return Object.keys(typographySizesFontFamiliesMap).reduce((sizesObj, current) => {
    return { ...sizesObj,
      [current]: typographySizesFontFamiliesMap[current].fontFamily
    };
  }, {});
};

const AmpWebFonts = props => {
  const {
    children,
    fontsConfiguration,
    typographySizesFontFamiliesMap
  } = props;
  return /*#__PURE__*/createElement(WebFontsContext.Provider, {
    value: createFontFamiliesMap$1(typographySizesFontFamiliesMap)
  }, fontsConfiguration.map(createFontFamilyLinkOrFontFace$1), children);
};

const componentConfigurationKey$A = 'sponsor';

const createSponsorStyle$1 = (baseUnit, textColor) => StyleSheet.create({
  logo: {
    height: '40px'
  },
  text: {
    color: textColor,
    marginBottom: `${0.5 * baseUnit}px`
  },
  wrapper: {
    textAlign: 'center',
    margin: `${1 * baseUnit}px 0px`
  }
});

const Sponsor = props => {
  const {
    text,
    logo,
    brandName,
    href
  } = props;
  const {
    textColor,
    baseUnit
  } = useTheme(componentConfigurationKey$A);
  const style = createSponsorStyle$1(baseUnit, textColor);
  return /*#__PURE__*/createElement(ErrorBoundary, null, /*#__PURE__*/createElement("div", {
    className: css(style.wrapper)
  }, /*#__PURE__*/createElement(H5, {
    styles: semanticCSS(style.text)
  }, text), /*#__PURE__*/createElement(Link, {
    href: href,
    rel: "nofollow"
  }, logo ? /*#__PURE__*/createElement("img", {
    className: css(style.logo),
    alt: brandName,
    src: `data:image/svg+xml,${encodeURIComponent(logo)}`
  }) : null)));
};

const componentConfigThemeKey$y = 'authorImage';

const getStyle$x = baseUnit => StyleSheet.create({
  wrapper: {
    position: 'relative',
    overflow: 'hidden',
    borderRadius: '100%',
    marginLeft: 'auto',
    marginRight: 'auto',
    boxSizing: 'border-box',
    border: '5px solid white',
    marginTop: `${baseUnit * 2}px`,
    [MEDIA_BREAKPOINTS.large]: {
      width: '160px',
      height: '160px'
    },
    [MEDIA_BREAKPOINTS.medium]: {
      width: '120px',
      height: '120px'
    },
    [MEDIA_BREAKPOINTS.small]: {
      width: '120px',
      height: '120px'
    }
  }
});

const imageWidths$5 = createImageWidths(180, 180, 180);
const AuthorImage = props => {
  const {
    image
  } = props;
  const {
    baseUnit,
    boxShadowTypeNormal
  } = useTheme(componentConfigThemeKey$y);
  const style = getStyle$x(baseUnit);
  const aspectRatio = {
    x: 1,
    y: 1
  };
  const boxShadowStyle = createStyleForBoxShadow({
    boxShadowTypeNormal
  });
  return /*#__PURE__*/createElement(ErrorBoundary, null, /*#__PURE__*/createElement("div", {
    className: css(boxShadowStyle.style, style.wrapper)
  }, /*#__PURE__*/createElement(Image, _extends({}, image, {
    aspectRatio: aspectRatio,
    imageWidths: imageWidths$5
  }))));
};

const numOnTop = 4;
const numHorizontal = 8;
const numVertical = 4;

const getStyles$a = baseUnit => StyleSheet.create({
  wrapper: {
    maxWidth: '640px',
    margin: 'auto',
    display: 'grid',
    gridTemplateColumns: '1fr',
    marginTop: `${baseUnit}px`,
    gridRowGap: `${baseUnit}px`
  }
});

const imageWidthsOnTopCard = createImageWidths(540, 1080, 1080);
const imageWidthsHorizontalCard = createImageWidths(360, 360, 360);
const imageWidthsVerticalCard = createImageWidths(540, 1080, 1080);

const arrangeCards = articles => {
  const structure = [numOnTop, numHorizontal, numVertical];
  let section = 0;
  let counter = 0;
  return articles.reduce((acc, article) => {
    let Component;

    switch (section) {
      case 0:
        Component = /*#__PURE__*/createElement(OnTopCard, _extends({
          key: article.id
        }, article, {
          imageWidths: imageWidthsOnTopCard
        }));
        break;

      case 1:
        Component = /*#__PURE__*/createElement(HorizontalCard, _extends({
          key: article.id
        }, article, {
          imageWidths: imageWidthsHorizontalCard
        }));
        break;

      case 2:
        Component = /*#__PURE__*/createElement(VerticalCard, _extends({
          key: article.id
        }, article, {
          imageWidths: imageWidthsVerticalCard
        }));
        break;

      default:
        Component = null;
        break;
    }

    counter += 1;

    if (counter >= structure[section]) {
      counter = 0;
      section = section === structure.length - 1 ? 0 : section + 1;
    }

    return [...acc, Component];
  }, []);
};

const AuthorPageFeed = props => {
  const {
    baseUnit
  } = useTheme();
  const {
    articles
  } = props;
  const styles = getStyles$a(baseUnit);
  return /*#__PURE__*/createElement(ErrorBoundary, null, articles && articles.length > 0 && /*#__PURE__*/createElement("div", {
    className: css(styles.wrapper)
  }, arrangeCards(articles)));
};

const componentConfigThemeKey$z = 'authorTopGradient';

const getOverlayHeight = (baseUnit, imageHeight, menuHeight) => {
  return baseUnit * 2.5 + imageHeight / 2 + menuHeight;
};

const getStyles$b = (baseUnit, color, headerHeights) => StyleSheet.create({
  wrapper: {
    position: 'absolute',
    background: `linear-gradient(0deg, rgba(0,0,0,0) 0%, ${hex2rgba(color, 1)} 100%)`,
    top: 0,
    left: 0,
    right: 0,
    [MEDIA_BREAKPOINTS.large]: {
      height: `${getOverlayHeight(baseUnit, 160, headerHeights.large)}px`
    },
    [MEDIA_BREAKPOINTS.medium]: {
      height: `${getOverlayHeight(baseUnit, 120, headerHeights.medium)}px`
    },
    [MEDIA_BREAKPOINTS.small]: {
      height: `${getOverlayHeight(baseUnit, 120, headerHeights.small)}px`
    }
  }
});

const AuthorTopGradient = props => {
  const {
    isSecondaryHeaderEnabled
  } = props;
  const {
    baseUnit,
    color
  } = useTheme(componentConfigThemeKey$z);
  const headerHeights = getHeaderHeights(isSecondaryHeaderEnabled);
  const styles = getStyles$b(baseUnit, color, headerHeights);
  return /*#__PURE__*/createElement(ErrorBoundary, null, /*#__PURE__*/createElement("div", {
    className: css(styles.wrapper)
  }));
};

const componentConfigThemeKey$A = 'authorPageSocial';

const getStyle$y = (baseUnit, backgroundColor) => StyleSheet.create({
  wrapper: {
    display: 'flex',
    justifyContent: 'center'
  },
  socialButton: {
    display: 'flex',
    width: '32px',
    height: '32px',
    borderRadius: '100%',
    overflow: 'hidden',
    backgroundColor,
    justifyContent: 'center',
    alignItems: 'center',
    margin: `0 ${baseUnit / 4}px`
  }
});

const createSocialButtonStyle = themeProps => {
  const {
    socialButtonsNormalColor,
    socialButtonsHoverColor
  } = themeProps;
  return StyleSheet.create({
    style: {
      height: '16px',
      fill: socialButtonsNormalColor,
      ':hover': {
        fill: socialButtonsHoverColor
      }
    }
  }).style;
};

const OldHorizontalSocialButtonsWrapper = props => {
  const {
    buttons
  } = props;
  const {
    baseUnit,
    backgroundColor
  } = useTheme(componentConfigThemeKey$A);
  const style = getStyle$y(baseUnit, backgroundColor);
  return /*#__PURE__*/createElement(ErrorBoundary, null, /*#__PURE__*/createElement("div", {
    className: css(style.wrapper)
  }, buttons.map((button, index) => /*#__PURE__*/createElement(Link, {
    className: css(style.socialButton),
    key: index,
    href: button.link
  }, /*#__PURE__*/createElement("img", {
    src: button.src,
    alt: button.alt
  })))));
};
const HorizontalSocialButtonsWrapper = props => {
  const {
    buttons
  } = props;
  const themeProps = useTheme(componentConfigThemeKey$A);
  const {
    baseUnit,
    backgroundColor
  } = themeProps;
  const style = getStyle$y(baseUnit, backgroundColor);
  const socialButtonStyle = createSocialButtonStyle(themeProps);
  return /*#__PURE__*/createElement(ErrorBoundary, null, /*#__PURE__*/createElement("div", {
    className: css(style.wrapper)
  }, buttons.map((link, index) => {
    const svg = link.src.replace(/<svg/g, `<svg class="${css(socialButtonStyle)}"`);
    return /*#__PURE__*/createElement(Link, {
      href: link.href,
      key: index,
      rel: "noopener",
      target: "_blank",
      dangerouslySetInnerHTML: {
        __html: svg
      },
      alt: link.alt,
      className: css(style.socialButton)
    });
  })));
};

const componentConfigThemeKey$B = 'authorPage';

const getStyles$c = ({
  baseUnit,
  titleColor,
  metaDataColor,
  color
}) => StyleSheet.create({
  title: {
    color: titleColor,
    textAlign: 'center',
    maxWidth: '640px',
    margin: `${baseUnit / 2}px auto`
  },
  metaData: {
    textAlign: 'center',
    color: metaDataColor,
    maxWidth: '640px',
    margin: 'auto',
    marginTop: `${baseUnit}px`,
    marginBottom: `${baseUnit / 2}px`
  },
  description: {
    color,
    maxWidth: '640px',
    margin: 'auto',
    textAlign: 'center'
  }
});

const getMetaData = ({
  joinDateText,
  joinDate
}) => {
  const base = `${joinDateText} ${joinDate}`;
  return base;
};

const Author$1 = props => {
  const {
    image,
    name,
    socialButtons,
    bio,
    articles,
    isSecondaryHeaderEnabled,
    paddingFactors,
    oldSocialButtons
  } = props;
  const theme = useTheme(componentConfigThemeKey$B);
  const styles = getStyles$c(theme);
  const paddingStyle = getLayoutComponentPadding(paddingFactors);
  const metaData = getMetaData(props);

  const getHorizontalSocialButtonsWrapperComponent = () => {
    if (socialButtons) {
      return /*#__PURE__*/createElement(HorizontalSocialButtonsWrapper, {
        buttons: socialButtons
      });
    }

    if (oldSocialButtons) {
      return /*#__PURE__*/createElement(OldHorizontalSocialButtonsWrapper, {
        buttons: oldSocialButtons
      });
    }

    return null;
  };

  return /*#__PURE__*/createElement(ErrorBoundary, null, /*#__PURE__*/createElement("div", {
    className: css(paddingStyle)
  }, /*#__PURE__*/createElement(ErrorBoundary, null, /*#__PURE__*/createElement(AuthorTopGradient, {
    isSecondaryHeaderEnabled: isSecondaryHeaderEnabled
  })), /*#__PURE__*/createElement(ErrorBoundary, null, /*#__PURE__*/createElement(AuthorImage, {
    image: image
  })), /*#__PURE__*/createElement(H1, {
    styles: semanticCSS(styles.title)
  }, name), /*#__PURE__*/createElement(ErrorBoundary, null, getHorizontalSocialButtonsWrapperComponent()), /*#__PURE__*/createElement(H4, {
    styles: semanticCSS(styles.metaData)
  }, metaData), bio ? /*#__PURE__*/createElement(P, {
    styles: semanticCSS(styles.description)
  }, bio) : null, /*#__PURE__*/createElement(ErrorBoundary, null, /*#__PURE__*/createElement(AuthorPageFeed, {
    articles: articles
  }))));
};

const componentConfigurationKey$B = 'HoverAuthorsWithRoundImage';
const largeScreenImageSize = 80;
const smallScreenImageSize = 50;

const getStyles$d = baseUnit => {
  return StyleSheet.create({
    wrapper: {
      display: 'grid',
      [MEDIA_BREAKPOINTS.large]: {
        width: '160px',
        position: 'absolute',
        left: `${baseUnit}px`,
        gridRowGap: `${baseUnit * 2}px`,
        marginTop: 'unset'
      },
      [MEDIA_BREAKPOINTS.medium]: {
        width: 'unset',
        position: 'relative',
        left: 'unset',
        transform: 'unset',
        gridTemplateColumns: '1fr 1fr',
        justifyItems: 'center',
        gridRowGap: `${baseUnit}px`,
        maxWidth: '360px',
        margin: 'auto',
        marginTop: `${baseUnit}px`
      },
      [MEDIA_BREAKPOINTS.small]: {
        width: 'unset',
        position: 'relative',
        left: 'unset',
        transform: 'unset',
        gridTemplateColumns: '1fr 1fr',
        justifyItems: 'center',
        gridRowGap: `${baseUnit}px`,
        maxWidth: '260px',
        margin: 'auto',
        marginTop: `${baseUnit}px`
      }
    }
  });
};

const getImageAdditionalStyle = () => {
  return StyleSheet.create({
    style: {
      borderRadius: '100%',
      display: 'block',
      overflow: 'hidden',
      [MEDIA_BREAKPOINTS.large]: {
        width: `${largeScreenImageSize}px`
      },
      [MEDIA_BREAKPOINTS.medium]: {
        width: `${smallScreenImageSize}px`
      },
      [MEDIA_BREAKPOINTS.small]: {
        width: `${smallScreenImageSize}px`
      }
    }
  });
};

const getImageStyle = () => {
  return StyleSheet.create({
    oddLink: {
      [MEDIA_BREAKPOINTS.medium]: {
        gridColumn: '1 / 3'
      },
      [MEDIA_BREAKPOINTS.small]: {
        gridColumn: '1 / 3'
      }
    },
    link: {
      display: 'grid',
      justifyItems: 'center',
      [MEDIA_BREAKPOINTS.large]: {
        gridTemplateRows: `${largeScreenImageSize}px auto`
      },
      [MEDIA_BREAKPOINTS.medium]: {
        gridTemplateRows: `${smallScreenImageSize}px auto`
      },
      [MEDIA_BREAKPOINTS.small]: {
        gridTemplateRows: `${smallScreenImageSize}px auto`
      }
    }
  });
};

const getTextsStyle = (baseUnit, authorColor, dateColor, bylineColor, separatorColor) => StyleSheet.create({
  authorText: {
    textAlign: 'center',
    color: authorColor,
    [MEDIA_BREAKPOINTS.large]: {
      marginTop: `${baseUnit / 2}px`
    },
    [MEDIA_BREAKPOINTS.medium]: {
      marginTop: `${baseUnit / 2}px`
    },
    [MEDIA_BREAKPOINTS.small]: {
      marginTop: `${baseUnit / 4}px`
    }
  },
  date: {
    textAlign: 'center',
    color: dateColor
  },
  byline: {
    textAlign: 'center',
    color: bylineColor
  },
  separator: {
    height: '1px',
    width: '100%',
    backgroundColor: separatorColor,
    margin: `${baseUnit / 4}px auto ${baseUnit / 4}px`
  }
});

const getAuthorTexts = (name, byline, date, showDate, themeProps) => {
  const {
    baseUnit,
    authorColor,
    dateColor,
    bylineColor,
    separatorColor,
    authorFontSizeLarge,
    authorFontSizeSmall,
    authorFontSizeMedium,
    dateFontSizeSmall,
    dateFontSizeMedium,
    dateFontSizeLarge,
    bylineFontSizeLarge,
    bylineFontSizeMedium,
    bylineFontSizeSmall
  } = themeProps;
  const hasByline = typeof byline === 'string' && byline.trim().length > 0;
  const hasSeparator = hasByline && showDate;
  const styles = getTextsStyle(baseUnit, authorColor, dateColor, bylineColor, separatorColor);
  return /*#__PURE__*/createElement("div", null, /*#__PURE__*/createElement(P, {
    styles: semanticCSS(styles.authorText),
    fontSizeLarge: authorFontSizeLarge,
    fontSizeMedium: authorFontSizeMedium,
    fontSizeSmall: authorFontSizeSmall
  }, name), hasByline && /*#__PURE__*/createElement(P, {
    styles: semanticCSS(styles.byline),
    fontSizeLarge: bylineFontSizeLarge,
    fontSizeMedium: bylineFontSizeMedium,
    fontSizeSmall: bylineFontSizeSmall
  }, byline), hasSeparator && /*#__PURE__*/createElement("div", {
    className: css(styles.separator)
  }), showDate && /*#__PURE__*/createElement(P, {
    fontSizeLarge: dateFontSizeLarge,
    fontSizeMedium: dateFontSizeMedium,
    fontSizeSmall: dateFontSizeSmall,
    styles: semanticCSS(styles.date)
  }, date));
};

const getAuthors = (authors, date, themeProps) => {
  const isOddList = authors.length % 2 !== 0;
  const styles = getImageStyle();
  const additionalStyle = getImageAdditionalStyle();
  const imageWidths = createImageWidths(50, 50, 80);

  const getLinkClass = index => {
    return isOddList && index === 0 ? css(styles.link, styles.oddLink) : css(styles.link);
  };

  return authors.map((author, index) => {
    const isFirstAuthor = index === 0;
    const showTimestampForAuthor = themeProps.showTimestamp && isFirstAuthor;
    return /*#__PURE__*/createElement(Link, {
      key: index,
      href: author.link,
      className: getLinkClass(index)
    }, author.image && /*#__PURE__*/createElement(Image, _extends({}, author.image, {
      imageWidths: imageWidths,
      additionalStyle: additionalStyle,
      aspectRatio: {
        x: 1,
        y: 1
      }
    })), getAuthorTexts(author.name, author.byline, date, showTimestampForAuthor, themeProps));
  });
};

const HoverAuthorsWithRoundImage = props => {
  const {
    authors,
    dateFormatted,
    timeAndDateFormatted
  } = props;
  const themeProps = useTheme(componentConfigurationKey$B);
  const {
    baseUnit,
    showTime
  } = themeProps;
  const styles = getStyles$d(baseUnit);
  const updatedAtFormatted = showTime ? timeAndDateFormatted : dateFormatted;
  return /*#__PURE__*/createElement("div", {
    className: css(styles.wrapper)
  }, authors && getAuthors(authors, updatedAtFormatted, themeProps));
};

const componentConfigurationKey$C = 'list';

const createStyles$6 = (backgroundColor, borderWidth, borderRadius, borderColor) => StyleSheet.create({
  listWrapper: { ...LIST_WRAPPER_STYLE,
    backgroundColor,
    border: `${borderWidth}px solid ${borderColor}`,
    borderRadius: `${borderRadius}px`,
    overflow: 'hidden'
  },
  list: { ...LIST_STYLE,
    [MEDIA_BREAKPOINTS.small]: {
      position: 'relative'
    }
  }
});

const List = ({
  listProps
}) => {
  const {
    backgroundColor,
    borderWidth,
    borderRadius,
    borderColor,
    boxShadowTypeNormal,
    boxShadowTypeHover,
    boxShadowTypeActive
  } = useTheme(componentConfigurationKey$C);
  const styles = createStyles$6(backgroundColor, borderWidth, borderRadius, borderColor);
  const boxShadowStyle = createStyleForBoxShadow({
    boxShadowTypeNormal,
    boxShadowTypeHover,
    boxShadowTypeActive
  });
  return /*#__PURE__*/createElement("div", {
    className: css(boxShadowStyle.style, styles.listWrapper)
  }, /*#__PURE__*/createElement(DecorationLine, null), /*#__PURE__*/createElement("div", {
    className: css(styles.list)
  }, listProps.map((listItemProps, index) => /*#__PURE__*/createElement(ListItem, _extends({}, listItemProps, {
    key: index
  })))));
};

const createStyles$7 = baseUnit => StyleSheet.create({
  container: {
    display: 'grid',
    gridTemplateColumns: '2fr 1fr',
    gridGap: `${baseUnit / 2}px`,
    [MEDIA_BREAKPOINTS.large]: {
      gridGap: `${baseUnit / 2}px`
    },
    [MEDIA_BREAKPOINTS.medium]: {
      gridTemplateColumns: '1fr 1fr',
      gridGap: `${baseUnit / 2}px`
    },
    [MEDIA_BREAKPOINTS.small]: {
      gridTemplateColumns: '1fr',
      gridTemplateRows: 'auto auto',
      gridGap: `${baseUnit / 2}px`
    }
  }
});

const imageWidths$6 = createImageWidths(360, 720, 720);
const HighlightsArticleList = ({
  cardsProps,
  paddingFactors
}) => {
  const {
    baseUnit
  } = useTheme();
  const styles = createStyles$7(baseUnit);
  const paddingStyle = getLayoutComponentPadding(paddingFactors);
  const [onTopCardProps, ...listProps] = cardsProps || [];
  return /*#__PURE__*/createElement(ErrorBoundary, null, cardsProps && cardsProps.length > 0 && /*#__PURE__*/createElement("div", {
    className: css(paddingStyle, styles.container)
  }, /*#__PURE__*/createElement(OnTopCard, _extends({}, onTopCardProps, {
    imageWidths: imageWidths$6
  })), /*#__PURE__*/createElement(List, {
    listProps: listProps
  })));
};

const componentConfigThemeKey$C = 'scrollableArticleList';

const createStyles$8 = baseUnit => StyleSheet.create({
  listWrapper: {
    display: 'grid',
    maxWidth: `${LARGE_SCREEN_SIZE}px`
  },
  scrollContainer: {
    '-webkit-overflow-scrolling': 'touch',
    display: 'flex',
    justifyContent: 'space-between',
    overflowX: 'scroll',
    overflowY: 'visible',
    [MEDIA_BREAKPOINTS.large]: {
      overflow: 'visible',
      justifyContent: 'center',
      gridTemplateColumns: '1fr 1fr 1fr 1fr',
      gridColumnGap: `${baseUnit / 2}px`,
      display: 'grid'
    },
    [MEDIA_BREAKPOINTS.medium]: {
      paddingBottom: `${baseUnit}px`
    },
    [MEDIA_BREAKPOINTS.small]: {
      paddingBottom: `${baseUnit}px`
    },
    '::-webkit-scrollbar': {
      display: 'none'
    }
  },
  scrollItem: {
    flex: '0 0 auto',
    flexBasis: `${300 - baseUnit / 2}px`,
    [MEDIA_BREAKPOINTS.medium]: {
      margin: `0 ${baseUnit / 4}px`,
      ':first-child': {
        marginLeft: 0
      },
      ':last-child': {
        marginRight: 0
      }
    },
    [MEDIA_BREAKPOINTS.small]: {
      margin: `0 ${baseUnit / 4}px`,
      ':first-child': {
        marginLeft: 0
      },
      ':last-child': {
        marginRight: 0
      }
    },
    position: 'relative',
    boxSizing: 'border-box',
    '@media (max-width: 330px)': {
      flexBasis: 'min(85%, 255px)'
    }
  }
});

const imageWidths$7 = createImageWidths(360, 360, 360);
const ScrollableArticleList = ({
  cardsProps,
  paddingFactors
}) => {
  const {
    baseUnit,
    showDateInLargeScreen,
    showDateInMediumScreen,
    showDateInSmallScreen
  } = useTheme(componentConfigThemeKey$C);
  const styles = createStyles$8(baseUnit);
  const paddingStyle = getLayoutComponentPadding(paddingFactors);
  const themeData = {
    showDateInLargeScreen,
    showDateInMediumScreen,
    showDateInSmallScreen
  };
  const themeContext = useThemeOverride(themeData, 'verticalCard');

  const createCard = props => {
    const {
      id
    } = props;
    return /*#__PURE__*/createElement("div", {
      className: css(styles.scrollItem),
      key: id
    }, /*#__PURE__*/createElement(VerticalCard, _extends({}, props, {
      imageWidths: imageWidths$7
    })));
  };

  return /*#__PURE__*/createElement(ErrorBoundary, null, cardsProps.length > 0 && /*#__PURE__*/createElement("div", {
    className: css(paddingStyle, styles.listWrapper)
  }, /*#__PURE__*/createElement("div", {
    className: css(styles.scrollContainer)
  }, /*#__PURE__*/createElement(ThemeContext.Provider, {
    value: themeContext
  }, cardsProps.map(createCard)))));
};

const createStyle$n = baseUnit => StyleSheet.create({
  container: {
    display: 'grid',
    gridColumnGap: `${baseUnit / 2}px`,
    gridTemplateRows: '1fr',
    gridTemplateColumns: '1fr 1fr',
    [MEDIA_BREAKPOINTS.small]: {
      gridColumnGap: '0',
      gridRowGap: `${baseUnit / 2}px`,
      gridTemplateRows: 'auto',
      gridTemplateColumns: '1fr'
    }
  },
  horizontalCardsWrapper: {
    position: 'relative'
  },
  horizontalCardsContainer: {
    maxHeight: '100%',
    height: '100%',
    display: 'grid',
    gridTemplateColumns: '1fr',
    gridTemplateRows: '1fr 1fr 1fr',
    gridRowGap: `${baseUnit / 2}px`
  },
  horizontalCardWrapper: {
    minWidth: '0',
    minHeight: '0',
    boxSizing: 'border-box',
    padding: '0.1px',
    position: 'relative',
    ':last-child': {
      marginBottom: '0'
    }
  }
});

const imageWidthsHorizontalCard$1 = createImageWidths(180, 180, 180);
const imageWidthsVerticalCard$1 = createImageWidths(360, 720, 540);
const OneThreeArticleList = ({
  cardsProps = [],
  paddingFactors
}) => {
  const [highlight, ...moreCards] = cardsProps;
  const {
    baseUnit
  } = useTheme();
  const style = createStyle$n(baseUnit);
  const paddingStyle = getLayoutComponentPadding(paddingFactors);
  const themeContextHorizontalCards = useThemeOverride({
    aspectRatioLarge: {
      x: 4,
      y: 3
    },
    aspectRatioMedium: {
      x: 4,
      y: 3
    },
    aspectRatioSmall: {
      x: 1,
      y: 1
    }
  }, componentConfigThemeKey$7);

  const createCard = (cardProps, i) => /*#__PURE__*/createElement("div", {
    className: css(style.horizontalCardWrapper),
    key: i
  }, /*#__PURE__*/createElement(ThemeContext.Provider, {
    value: themeContextHorizontalCards
  }, /*#__PURE__*/createElement(HorizontalCard, _extends({}, cardProps, {
    imageWidths: imageWidthsHorizontalCard$1
  }))));

  return /*#__PURE__*/createElement(ErrorBoundary, null, cardsProps.length > 0 && /*#__PURE__*/createElement("div", {
    className: css(paddingStyle, style.container)
  }, /*#__PURE__*/createElement(VerticalCard, _extends({}, highlight, {
    imageWidths: imageWidthsVerticalCard$1
  })), /*#__PURE__*/createElement("div", {
    className: css(style.horizontalCardsWrapper)
  }, /*#__PURE__*/createElement("div", {
    className: css(style.horizontalCardsContainer)
  }, moreCards.map(createCard)))));
};

const componentConfigThemeKey$D = 'oneTwoOnTopFullWidth';

const getStyles$e = theme => {
  const {
    largeHorizontalGap,
    largeVerticalGap,
    mediumHorizontalGap,
    mediumVerticalGap,
    smallHorizontalGap
  } = theme;
  return StyleSheet.create({
    container: {
      [MEDIA_BREAKPOINTS.large]: {
        display: 'grid',
        gridTemplateColumns: '2fr 1fr',
        gridColumnGap: `${largeVerticalGap * baseUnit}px`
      },
      [MEDIA_BREAKPOINTS.medium]: {
        display: 'grid',
        gridTemplateRows: '1fr auto',
        gridRowGap: `${mediumHorizontalGap * baseUnit}px`
      },
      [MEDIA_BREAKPOINTS.small]: {
        display: 'block'
      }
    },
    lastCardsContainer: {
      [MEDIA_BREAKPOINTS.medium]: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gridColumnGap: `${mediumVerticalGap * baseUnit}px`
      },
      [MEDIA_BREAKPOINTS.small]: {
        display: 'block'
      }
    },
    secondAndThirdCards: {
      [MEDIA_BREAKPOINTS.large]: {
        height: '50%',
        boxSizing: 'border-box'
      },
      [MEDIA_BREAKPOINTS.small]: {
        marginTop: `${smallHorizontalGap * baseUnit}px`
      }
    },
    secondChild: {
      [MEDIA_BREAKPOINTS.large]: {
        paddingBottom: `${largeHorizontalGap * baseUnit / 2}px`
      }
    },
    thirdChild: {
      [MEDIA_BREAKPOINTS.large]: {
        paddingTop: `${largeHorizontalGap * baseUnit / 2}px`
      }
    }
  });
};

const secondAndThirdImageHeightCalculationMethods = createImageHeightCalculationMethods('ASPECT_RATIO', 'ASPECT_RATIO', '100%_HEIGHT');

const addImageHeightCalculationMethodsProp = cardProps => ({ ...cardProps,
  imageHeightCalculationMethods: secondAndThirdImageHeightCalculationMethods
});

const imageWidthsMainCard = createImageWidths(360, 720, 720);
const imageWidthsMinorCards = createImageWidths(360, 360, 360);
const OneTwoOnTopFullWidth = props => {
  const {
    paddingFactors
  } = props;
  const themeProps = useTheme(componentConfigThemeKey$D);
  const {
    mainCardTitleFontSizeLarge,
    mainCardTitleFontSizeMedium,
    mainCardTitleFontSizeSmall,
    secondaryCardsTitleFontSizeLarge,
    secondaryCardsTitleFontSizeMedium,
    secondaryCardsTitleFontSizeSmall,
    secondaryCardsMetadataFontSizeLarge,
    secondaryCardsMetadataFontSizeMedium,
    secondaryCardsMetadataFontSizeSmall,
    insideCardSmallHorizontalGap,
    insideCardMediumHorizontalGap,
    insideCardLargeHorizontalGap,
    aspectRatioSmall,
    titleSemanticTag
  } = themeProps;
  const {
    authorColor
  } = themeProps;
  const styles = getStyles$e(themeProps);
  const paddingStyle = getLayoutComponentPadding(paddingFactors);
  const {
    cards
  } = props;
  const topCard = cards && cards[0];
  const secondCard = cards && addImageHeightCalculationMethodsProp(cards[1]);
  const thirdCard = cards && addImageHeightCalculationMethodsProp(cards[2]);
  const themeContextFirstCard = useThemeOverride({
    titleFontSizeLarge: mainCardTitleFontSizeLarge,
    titleFontSizeMedium: mainCardTitleFontSizeMedium,
    titleFontSizeSmall: mainCardTitleFontSizeSmall,
    horizontalGapSmall: insideCardSmallHorizontalGap,
    horizontalGapMedium: insideCardMediumHorizontalGap,
    horizontalGapLarge: insideCardLargeHorizontalGap,
    titleSemanticTag,
    aspectRatioSmall,
    authorColor
  }, componentConfigThemeKey$3);
  const themeContextSecondAndThirdCards = useThemeOverride({
    titleFontSizeLarge: secondaryCardsTitleFontSizeLarge,
    titleFontSizeMedium: secondaryCardsTitleFontSizeMedium,
    titleFontSizeSmall: secondaryCardsTitleFontSizeSmall,
    metadataFontSizeLarge: secondaryCardsMetadataFontSizeLarge,
    metadataFontSizeMedium: secondaryCardsMetadataFontSizeMedium,
    metadataFontSizeSmall: secondaryCardsMetadataFontSizeSmall,
    horizontalGapSmall: insideCardSmallHorizontalGap,
    horizontalGapMedium: insideCardMediumHorizontalGap,
    horizontalGapLarge: insideCardLargeHorizontalGap,
    authorColor
  }, componentConfigThemeKey$3);
  return /*#__PURE__*/createElement(ErrorBoundary, null, cards && cards.length > 0 && /*#__PURE__*/createElement("div", {
    className: css(paddingStyle, styles.container)
  }, /*#__PURE__*/createElement(ThemeContext.Provider, {
    value: themeContextFirstCard
  }, topCard && /*#__PURE__*/createElement(OnTopCard, _extends({}, topCard, {
    imageWidths: imageWidthsMainCard
  }))), /*#__PURE__*/createElement("div", {
    className: css(styles.lastCardsContainer)
  }, /*#__PURE__*/createElement(ThemeContext.Provider, {
    value: themeContextSecondAndThirdCards
  }, /*#__PURE__*/createElement("div", {
    className: css(styles.secondAndThirdCards, styles.secondChild)
  }, secondCard && /*#__PURE__*/createElement(OnTopCard, _extends({}, secondCard, {
    imageWidths: imageWidthsMinorCards
  }))), /*#__PURE__*/createElement("div", {
    className: css(styles.secondAndThirdCards, styles.thirdChild)
  }, thirdCard && /*#__PURE__*/createElement(OnTopCard, _extends({}, thirdCard, {
    imageWidths: imageWidthsMinorCards
  })))))));
};

const componentConfigThemeKey$E = 'twoOneTwoArticleList';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const createStyle$o = ({
  largeHorizontalGap,
  largeVerticalGap,
  mediumHorizontalGap,
  mediumVerticalGap,
  smallHorizontalGap,
  minorCardsRatioLarge,
  minorCardsRatioMedium
}) => StyleSheet.create({
  wrapper: {
    display: 'grid',
    [MEDIA_BREAKPOINTS.large]: {
      gridTemplateColumns: '1fr 2fr 1fr',
      gridTemplateRows: `${minorCardsRatioLarge.x}fr ${minorCardsRatioLarge.y}fr`,
      gridColumnGap: `${largeVerticalGap * baseUnit}px`,
      gridRowGap: `${largeHorizontalGap * baseUnit}px`
    },
    [MEDIA_BREAKPOINTS.medium]: {
      gridTemplateColumns: `${minorCardsRatioMedium.x}fr ${minorCardsRatioMedium.y}fr`,
      gridTemplateRows: 'auto auto auto',
      gridColumnGap: `${mediumVerticalGap * baseUnit}px`,
      gridRowGap: `${mediumHorizontalGap * baseUnit}px`
    },
    [MEDIA_BREAKPOINTS.small]: {
      gridTemplateColumns: '1fr',
      gridRowGap: `${smallHorizontalGap * baseUnit}px`
    }
  },
  secondCard: {
    [MEDIA_BREAKPOINTS.large]: {
      gridRowStart: '2',
      gridRowEnd: '3',
      gridColumnStart: '1'
    },
    [MEDIA_BREAKPOINTS.small]: {
      gridRowStart: 'unset',
      gridRowEnd: 'unset',
      gridColumnStart: 'unset'
    }
  },
  mainCard: {
    [MEDIA_BREAKPOINTS.large]: {
      gridRowStart: '1',
      gridRowEnd: '3',
      gridColumnStart: '2'
    },
    [MEDIA_BREAKPOINTS.medium]: {
      gridRowStart: '2',
      gridRowEnd: '3',
      gridColumnStart: '1',
      gridColumnEnd: '3'
    },
    [MEDIA_BREAKPOINTS.small]: {
      order: '-1',
      gridRowStart: 'unset',
      gridRowEnd: 'unset',
      gridColumnStart: 'unset',
      gridColumnEnd: 'unset'
    }
  },
  fourthCard: {
    [MEDIA_BREAKPOINTS.medium]: {
      gridRowStart: '3',
      gridRowEnd: '4',
      gridColumnStart: '1',
      gridColumnEnd: '2'
    },
    [MEDIA_BREAKPOINTS.small]: {
      gridRowStart: 'unset',
      gridRowEnd: 'unset',
      gridColumnStart: 'unset',
      gridColumnEnd: 'unset'
    }
  },
  fifthCard: {
    [MEDIA_BREAKPOINTS.large]: {
      gridColumnStart: '3'
    },
    [MEDIA_BREAKPOINTS.medium]: {
      gridRowStart: '3',
      gridRowEnd: '4',
      gridColumnStart: '2',
      gridColumnEnd: '3'
    },
    [MEDIA_BREAKPOINTS.small]: {
      gridRowStart: 'unset',
      gridRowEnd: 'unset',
      gridColumnStart: 'unset',
      gridColumnEnd: 'unset'
    }
  }
});

const addImageHeightCalculationMethodsProp$1 = (cardProps, imageHeightCalculationMethods) => ({ ...cardProps,
  imageHeightCalculationMethods
});

const imageWidthsMainCard$1 = createImageWidths(360, 720, 540);
const imageWidthsMinorCard = createImageWidths(360, 360, 360);
const TwoOneTwoArticleList = props => {
  const {
    cards,
    paddingFactors
  } = props;
  const themeProps = useTheme(componentConfigThemeKey$E);
  const firstCard = addImageHeightCalculationMethodsProp$1(cards[1], createImageHeightCalculationMethods('ASPECT_RATIO', 'ASPECT_RATIO', '100%_HEIGHT'));
  const secondCard = addImageHeightCalculationMethodsProp$1(cards[2], createImageHeightCalculationMethods('ASPECT_RATIO', '100%_HEIGHT', '100%_HEIGHT'));
  const mainCard = addImageHeightCalculationMethodsProp$1(cards[0], createImageHeightCalculationMethods('ASPECT_RATIO', 'ASPECT_RATIO', 'ASPECT_RATIO'));
  const fourthCard = addImageHeightCalculationMethodsProp$1(cards[3], createImageHeightCalculationMethods('ASPECT_RATIO', 'ASPECT_RATIO', '100%_HEIGHT'));
  const fifthCard = addImageHeightCalculationMethodsProp$1(cards[4], createImageHeightCalculationMethods('ASPECT_RATIO', '100%_HEIGHT', '100%_HEIGHT'));
  const style = createStyle$o(themeProps);
  const paddingStyle = getLayoutComponentPadding(paddingFactors);
  const themeContextMainCard = useThemeOverride({
    titleFontSizeMedium: 'large',
    titleFontSizeLarge: 'large',
    titleFontSizeSmall: 'bigger',
    horizontalGapSmall: 0.25,
    horizontalGapMedium: 0.5,
    horizontalGapLarge: 0.5,
    aspectRatioSmall: {
      x: 4,
      y: 3
    }
  }, componentConfigThemeKey$3);
  const themeContextMinorCards = useThemeOverride({
    titleFontSizeMedium: 'bigger',
    titleFontSizeLarge: 'bigger',
    titleFontSizeSmall: 'bigger',
    horizontalGapSmall: 0.25,
    horizontalGapMedium: 0.25,
    horizontalGapLarge: 0.25
  }, componentConfigThemeKey$3);
  return /*#__PURE__*/createElement(ErrorBoundary, null, cards.length > 0 && /*#__PURE__*/createElement("div", {
    className: css(paddingStyle, style.wrapper)
  }, /*#__PURE__*/createElement(ThemeContext.Provider, {
    value: themeContextMinorCards
  }, /*#__PURE__*/createElement(OnTopCard, _extends({}, firstCard, {
    imageWidths: imageWidthsMinorCard
  })), /*#__PURE__*/createElement("div", {
    className: css(style.secondCard)
  }, /*#__PURE__*/createElement(OnTopCard, _extends({}, secondCard, {
    imageWidths: imageWidthsMinorCard
  })))), /*#__PURE__*/createElement(ThemeContext.Provider, {
    value: themeContextMainCard
  }, /*#__PURE__*/createElement("div", {
    className: css(style.mainCard)
  }, /*#__PURE__*/createElement(OnTopCard, _extends({}, mainCard, {
    imageWidths: imageWidthsMainCard$1
  })))), /*#__PURE__*/createElement(ThemeContext.Provider, {
    value: themeContextMinorCards
  }, /*#__PURE__*/createElement("div", {
    className: css(style.fourthCard)
  }, /*#__PURE__*/createElement(OnTopCard, _extends({}, fourthCard, {
    imageWidths: imageWidthsMinorCard
  }))), /*#__PURE__*/createElement("div", {
    className: css(style.fifthCard)
  }, /*#__PURE__*/createElement(OnTopCard, _extends({}, fifthCard, {
    imageWidths: imageWidthsMinorCard
  }))))));
};

const componentConfigThemeKey$F = 'oneThreeOTC';
const imageWidthsFirstCard = createImageWidths(360, 720, 1080);
const imageWidthsSecondAndThirdCards = createImageWidths(360, 360, 360);
const imageWidthsFourthCard = createImageWidths(360, 720, 360);

const getStyle$z = theme => {
  const {
    largeHorizontalGap,
    largeVerticalGap,
    mediumHorizontalGap,
    mediumVerticalGap,
    smallHorizontalGap,
    minorCardsSmallHorizontalCard
  } = theme;
  const firstCardArea = 'first-card';
  const adArea = 'ad';
  const minorCardsArea = 'minor-cards';
  return StyleSheet.create({
    wrapper: {
      [MEDIA_BREAKPOINTS.large]: {
        display: 'grid',
        gridTemplateColumns: '1fr',
        gridTemplateRows: 'auto',
        gridColumnGap: `${largeVerticalGap * baseUnit}px`,
        gridRowGap: `${largeHorizontalGap * baseUnit}px`,
        gridTemplateAreas: `'${firstCardArea}' '${adArea}' '${minorCardsArea}'`
      },
      [MEDIA_BREAKPOINTS.medium]: {
        display: 'grid',
        gridTemplateColumns: '1fr',
        gridTemplateRows: 'auto',
        gridColumnGap: `${mediumVerticalGap * baseUnit}px`,
        gridRowGap: `${mediumHorizontalGap * baseUnit}px`,
        gridTemplateAreas: `'${firstCardArea}' '${adArea}' '${minorCardsArea}'`
      },
      [MEDIA_BREAKPOINTS.small]: {
        display: 'grid',
        gridTemplateColumns: '1fr',
        gridTemplateRows: 'auto',
        gridRowGap: `${smallHorizontalGap * baseUnit}px`,
        gridTemplateAreas: `'${firstCardArea}' '${adArea}' '${minorCardsArea}'`
      }
    },
    minorCardsWrapper: {
      [MEDIA_BREAKPOINTS.large]: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr',
        gridTemplateRows: 'auto',
        gridColumnGap: `${largeVerticalGap * baseUnit}px`
      },
      [MEDIA_BREAKPOINTS.medium]: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr',
        gridTemplateRows: 'auto',
        gridColumnGap: `${mediumVerticalGap * baseUnit}px`
      },
      [MEDIA_BREAKPOINTS.small]: {
        display: 'grid',
        gridTemplateColumns: '1fr',
        gridTemplateRows: 'auto',
        gridRowGap: `${minorCardsSmallHorizontalCard * baseUnit}px`
      }
    },
    firstCard: {
      gridArea: firstCardArea
    },
    ad: {
      gridArea: adArea
    },
    minorCards: {
      gridArea: minorCardsArea
    }
  });
};

const OneThreeOTC = props => {
  const {
    cards,
    children
  } = props;
  const themeProps = useTheme(componentConfigThemeKey$F);
  const style = getStyle$z(themeProps);
  const themeContextFirstCard = useThemeOverride({
    titleFontSizeLarge: 'gigantic',
    titleFontSizeMedium: 'large',
    titleFontSizeSmall: 'gigantic',
    metadataFontSizeLarge: 'big',
    metadataFontSizeMedium: 'big',
    metadataFontSizeSmall: 'big',
    aspectRatioLarge: {
      x: 2,
      y: 1
    },
    aspectRatioSmall: {
      x: 4,
      y: 5
    },
    titleAlignment: 'CENTER',
    metadataAlignment: 'CENTER',
    verticalGapLarge: 1,
    verticalGapMedium: 1,
    verticalGapSmall: 1,
    horizontalGapLarge: 2,
    horizontalGapMedium: 0.75,
    horizontalGapSmall: 0.75
  }, componentConfigThemeKey$3);
  const themeContextMinorCards = useThemeOverride({
    titleFontSizeLarge: 'gigantic',
    titleFontSizeMedium: 'large',
    titleFontSizeSmall: 'gigantic',
    metadataFontSizeLarge: 'big',
    metadataFontSizeMedium: 'big',
    metadataFontSizeSmall: 'big',
    aspectRatioLarge: {
      x: 4,
      y: 5
    },
    aspectRatioMedium: {
      x: 4,
      y: 5
    },
    aspectRatioSmall: {
      x: 4,
      y: 5
    },
    titleAlignment: 'CENTER',
    metadataAlignment: 'CENTER',
    verticalGapLarge: 1,
    verticalGapMedium: 1,
    verticalGapSmall: 1,
    horizontalGapLarge: 2,
    horizontalGapMedium: 0.75,
    horizontalGapSmall: 0.75
  }, componentConfigThemeKey$3);
  return /*#__PURE__*/createElement(ErrorBoundary, null, cards.length > 0 && /*#__PURE__*/createElement("div", {
    className: css(style.wrapper)
  }, /*#__PURE__*/createElement(ThemeContext.Provider, {
    value: themeContextFirstCard
  }, /*#__PURE__*/createElement("div", {
    className: css(style.firstCard)
  }, /*#__PURE__*/createElement(OnTopCard, _extends({}, cards[0], {
    imageWidths: imageWidthsFirstCard
  })))), /*#__PURE__*/createElement("div", {
    className: css(style.ad)
  }, children), /*#__PURE__*/createElement("div", {
    className: css(style.minorCards, style.minorCardsWrapper)
  }, /*#__PURE__*/createElement(ThemeContext.Provider, {
    value: themeContextMinorCards
  }, /*#__PURE__*/createElement(OnTopCard, _extends({}, cards[1], {
    imageWidths: imageWidthsSecondAndThirdCards
  })), /*#__PURE__*/createElement(OnTopCard, _extends({}, cards[2], {
    imageWidths: imageWidthsSecondAndThirdCards
  })), /*#__PURE__*/createElement(OnTopCard, _extends({}, cards[3], {
    imageWidths: imageWidthsFourthCard
  }))))));
};

const componentConfigThemeKey$G = 'oneOTCThreeHC';
const imageWidthsOnTopCard$1 = createImageWidths(360, 720, 540);
const imageWidthsHorizontalCard$2 = createImageWidths(180, 180, 180);

const getStyles$f = baseUnit => {
  return StyleSheet.create({
    section: {
      [MEDIA_BREAKPOINTS.large]: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gridColumnGap: `${0.5 * baseUnit}px`
      }
    },
    horizontalCards: {
      display: 'grid',
      gridTemplateRows: '1fr 1fr 1fr',
      gridRowGap: `${0.5 * baseUnit}px`,
      [MEDIA_BREAKPOINTS.large]: {
        marginTop: 0
      },
      [MEDIA_BREAKPOINTS.medium]: {
        marginTop: `${0.5 * baseUnit}px`
      },
      [MEDIA_BREAKPOINTS.small]: {
        marginTop: `${0.5 * baseUnit}px`,
        paddingLeft: `${0.5 * baseUnit}px`,
        paddingRight: `${0.5 * baseUnit}px`
      }
    }
  });
};

const OneOnTopThreeHorizontalArticleList = props => {
  const {
    cards,
    paddingFactors = {
      large: 0.5,
      medium: 0.5,
      small: 0
    }
  } = props;
  const {
    baseUnit
  } = useTheme();
  const styles = getStyles$f(baseUnit);
  const paddingStyle = getLayoutComponentPadding(paddingFactors);
  const onTopCardHeightCalculation = createImageHeightCalculationMethods('ASPECT_RATIO', 'ASPECT_RATIO', '100%_HEIGHT');
  const themeProps = useTheme(componentConfigThemeKey$G);
  const {
    titleFontSizeLarge,
    titleFontSizeMedium,
    titleFontSizeSmall,
    OTCTitleFontSizeSmall,
    metadataFontSizeLarge,
    metadataFontSizeMedium,
    metadataFontSizeSmall,
    authorColor,
    showDescriptionInMediumScreen,
    insideCardSmallHorizontalGap,
    aspectRatioSmallForHC,
    aspectRatioLargeForHC
  } = themeProps;
  const themeContextHC = useThemeOverride({
    titleFontSizeLarge,
    titleFontSizeMedium,
    titleFontSizeSmall,
    metadataFontSizeLarge,
    metadataFontSizeMedium,
    metadataFontSizeSmall,
    showDescriptionInMediumScreen,
    aspectRatioSmall: aspectRatioSmallForHC,
    aspectRatioLarge: aspectRatioLargeForHC
  }, componentConfigThemeKey$7);
  const themeContextOTC = useThemeOverride({
    titleFontSizeSmall: OTCTitleFontSizeSmall,
    horizontalGapSmall: insideCardSmallHorizontalGap,
    authorColor
  }, componentConfigThemeKey$3);
  return /*#__PURE__*/createElement(ErrorBoundary, null, cards.length > 0 && /*#__PURE__*/createElement("div", {
    className: css(styles.section, paddingStyle)
  }, /*#__PURE__*/createElement(ThemeContext.Provider, {
    value: themeContextOTC
  }, /*#__PURE__*/createElement(OnTopCard, _extends({}, cards[0], {
    imageWidths: imageWidthsOnTopCard$1,
    imageHeightCalculationMethods: onTopCardHeightCalculation,
    lazyLoad: true
  }))), /*#__PURE__*/createElement("div", {
    className: css(styles.horizontalCards)
  }, /*#__PURE__*/createElement(ThemeContext.Provider, {
    value: themeContextHC
  }, /*#__PURE__*/createElement(HorizontalCard, _extends({}, cards[1], {
    imageWidths: imageWidthsHorizontalCard$2
  })), /*#__PURE__*/createElement(HorizontalCard, _extends({}, cards[2], {
    imageWidths: imageWidthsHorizontalCard$2
  })), /*#__PURE__*/createElement(HorizontalCard, _extends({}, cards[3], {
    imageWidths: imageWidthsHorizontalCard$2
  }))))));
};

const componentConfigThemeKey$H = 'section';

const getMaxWidth = (theme, themeConfigurationPropKey) => {
  if (typeof themeConfigurationPropKey === 'string' && themeConfigurationPropKey in theme) {
    return theme[themeConfigurationPropKey];
  }

  return LARGE_SCREEN_SIZE;
};

const Section = props => {
  const {
    children,
    themeConfigurationPropKey
  } = props;
  const theme = useTheme(componentConfigThemeKey$H);
  const sectionStyle = getSectionStyle(getMaxWidth(theme, themeConfigurationPropKey));
  const additionalStyle = getBackgroundColorStyle(theme.backgroundColor);
  return /*#__PURE__*/createElement(ErrorBoundary, null, /*#__PURE__*/createElement("section", {
    className: css(sectionStyle, additionalStyle)
  }, children));
};

const SectionWithTitle = props => {
  const {
    maxWidth,
    children,
    additionalStyle
  } = props;
  const sectionStyle = getSectionStyle(maxWidth);
  return /*#__PURE__*/createElement(ErrorBoundary, null, additionalStyle ? /*#__PURE__*/createElement("section", {
    className: css(sectionStyle, additionalStyle.style)
  }, children) : /*#__PURE__*/createElement("section", {
    className: css(sectionStyle)
  }, children));
};

const componentConfigThemeKey$I = 'sixHCForTwoThirds';
const imageWidths$8 = createImageWidths(180, 360, 360);
const SixHCForTwoThirds = props => {
  const {
    cards
  } = props;
  const themeProps = useTheme(componentConfigThemeKey$I);
  const themeContextHC = useThemeOverride(themeProps, componentConfigThemeKey$7);
  return /*#__PURE__*/createElement(ErrorBoundary, null, cards.length > 0 && /*#__PURE__*/createElement(ThemeContext.Provider, {
    value: themeContextHC
  }, /*#__PURE__*/createElement(SixHC, {
    cards: cards,
    paddingFactors: {
      large: 0,
      medium: 0,
      small: 0
    },
    imageWidths: imageWidths$8
  })));
};

const componentConfigThemeKey$J = 'fourVCForOneThird';

const createStyle$p = themeProps => {
  const {
    largeHorizontalGap,
    mediumHorizontalGap,
    mediumVerticalGap,
    smallHorizontalGap
  } = themeProps;
  return StyleSheet.create({
    wrapper: {
      display: 'grid',
      [MEDIA_BREAKPOINTS.large]: {
        gridTemplateColumns: '1fr',
        gridTemplateRows: 'auto',
        gridRowGap: `${largeHorizontalGap * baseUnit}px`
      },
      [MEDIA_BREAKPOINTS.medium]: {
        gridTemplateColumns: '1fr 1fr',
        gridTemplateRows: 'auto',
        gridRowGap: `${mediumHorizontalGap * baseUnit}px`,
        gridColumnGap: `${mediumVerticalGap * baseUnit}px`
      },
      [MEDIA_BREAKPOINTS.small]: {
        gridTemplateColumns: '1fr',
        gridTemplateRows: 'auto',
        gridRowGap: `${smallHorizontalGap * baseUnit}px`
      }
    }
  });
};

const imageWidths$9 = createImageWidths(360, 360, 360);
const FourVCForOneThird = props => {
  const {
    cards
  } = props;
  const themeProps = useTheme(componentConfigThemeKey$J);
  const style = createStyle$p(themeProps);
  const themeContextVC = useThemeOverride({
    titleFontSizeLarge: 'bigger',
    titleFontSizeMedium: 'bigger',
    titleFontSizeSmall: 'bigger',
    titleSemanticTag: 'h4',
    metadataSemanticTag: 'h5',
    metadataFontSizeLarge: 'big',
    metadataFontSizeMedium: 'big',
    metadataFontSizeSmall: 'big',
    showAuthorInLargeScreen: false,
    showDateInLargeScreen: false
  }, componentConfigThemeKey$5);
  return /*#__PURE__*/createElement(ErrorBoundary, null, cards.length > 0 && /*#__PURE__*/createElement("div", {
    className: css(style.wrapper)
  }, /*#__PURE__*/createElement(ThemeContext.Provider, {
    value: themeContextVC
  }, /*#__PURE__*/createElement(VerticalCard, _extends({}, cards[0], {
    imageWidths: imageWidths$9
  })), /*#__PURE__*/createElement(VerticalCard, _extends({}, cards[1], {
    imageWidths: imageWidths$9
  })), /*#__PURE__*/createElement(VerticalCard, _extends({}, cards[2], {
    imageWidths: imageWidths$9
  })), /*#__PURE__*/createElement(VerticalCard, _extends({}, cards[3], {
    imageWidths: imageWidths$9
  })))));
};

const createStyle$q = () => StyleSheet.create({
  wrapper: {
    display: 'grid',
    boxSizing: 'border-box',
    [MEDIA_BREAKPOINTS.large]: {
      gridTemplateColumns: '2fr 1fr',
      gridColumnGap: `${baseUnit}px`
    },
    [MEDIA_BREAKPOINTS.medium]: {
      gridTemplateColumns: '1fr',
      gridRowGap: `${baseUnit * 1.5}px`
    },
    [MEDIA_BREAKPOINTS.small]: {
      gridTemplateColumns: '1fr',
      gridRowGap: `${baseUnit * 1.5}px`
    }
  }
});

const TwoThirdsOneThirdWrapper = props => {
  const {
    children,
    maxWidth,
    paddingFactors
  } = props;
  const style = createStyle$q();
  const sectionStyle = getSectionStyle(maxWidth);
  const paddingStyle = getLayoutComponentPadding(paddingFactors);
  return /*#__PURE__*/createElement("div", {
    className: css(style.wrapper, sectionStyle, paddingStyle)
  }, children);
};

const componentConfigThemeKey$K = 'oneTwoTwoOTC';

const createStyle$r = themeProps => {
  const {
    largeHorizontalGap,
    largeVerticalGap,
    mediumHorizontalGap,
    mediumVerticalGap,
    smallHorizontalGap,
    baseUnit
  } = themeProps;
  const firstCardArea = 'first_card_area';
  const secondCardArea = 'second_card_area';
  const thirdCardArea = 'third_card_area';
  const fourthCardArea = 'fourth_card_area';
  const fifthCardArea = 'fifth_card_area';
  return StyleSheet.create({
    wrapper: {
      display: 'grid',
      [MEDIA_BREAKPOINTS.large]: {
        gridTemplateColumns: '2fr 1fr 1fr',
        gridTemplateRows: '1fr 1fr',
        gridColumnGap: `${largeVerticalGap * baseUnit}px`,
        gridRowGap: `${largeHorizontalGap * baseUnit}px`,
        gridTemplateAreas: `'${firstCardArea} ${secondCardArea} ${thirdCardArea}' '${firstCardArea} ${fourthCardArea} ${fifthCardArea}'`
      },
      [MEDIA_BREAKPOINTS.medium]: {
        gridTemplateColumns: '1fr 1fr',
        gridTemplateRows: 'auto auto auto',
        gridColumnGap: `${mediumVerticalGap * baseUnit}px`,
        gridRowGap: `${mediumHorizontalGap * baseUnit}px`,
        gridTemplateAreas: `'${firstCardArea} ${firstCardArea}' '${secondCardArea} ${thirdCardArea}' '${fourthCardArea} ${fifthCardArea}'`
      },
      [MEDIA_BREAKPOINTS.small]: {
        gridTemplateColumns: '1fr',
        gridRowGap: `${smallHorizontalGap * baseUnit}px`,
        gridTemplateAreas: `'${firstCardArea}' '${secondCardArea}' '${thirdCardArea}' '${fourthCardArea}' '${fifthCardArea}'`
      }
    },
    firstCard: {
      gridArea: firstCardArea
    },
    secondCard: {
      gridArea: secondCardArea
    },
    thirdCard: {
      gridArea: thirdCardArea
    },
    fourthCard: {
      gridArea: fourthCardArea
    },
    fifthCard: {
      gridArea: fifthCardArea
    }
  });
};

const imageWidthsForMainCard = createImageWidths(360, 720, 540);
const imageWidthsForMinorCard = createImageWidths(360, 360, 360);
const minorCardsImageHeightCalculationMethods = createImageHeightCalculationMethods('ASPECT_RATIO', 'ASPECT_RATIO', '100%_HEIGHT');
const OneTwoTwoOTC = props => {
  const {
    cards,
    paddingFactors
  } = props;
  const paddingStyle = getLayoutComponentPadding(paddingFactors);
  const themeProps = useTheme(componentConfigThemeKey$K);
  const styles = createStyle$r(themeProps);
  const themeContextFirstCard = useThemeOverride({
    titleFontSizeSmall: 'large',
    titleFontSizeMedium: 'large',
    titleFontSizeLarge: 'large',
    metadataFontSizeSmall: 'big',
    metadataFontSizeMedium: 'bigger',
    metadataFontSizeLarge: 'bigger',
    horizontalGapSmall: 0.25,
    horizontalGapMedium: 0.5,
    horizontalGapLarge: 0.5,
    aspectRatioSmall: {
      x: 4,
      y: 3
    }
  }, componentConfigThemeKey$3);
  const themeContextMinorCard = useThemeOverride({
    titleFontSizeSmall: 'large',
    titleFontSizeMedium: 'big',
    titleFontSizeLarge: 'big',
    metadataFontSizeSmall: 'big',
    metadataFontSizeMedium: 'big',
    metadataFontSizeLarge: 'big',
    horizontalGapSmall: 0.25,
    horizontalGapMedium: 0.25,
    horizontalGapLarge: 0.25
  }, componentConfigThemeKey$3);
  return /*#__PURE__*/createElement(ErrorBoundary, null, cards.length > 0 && /*#__PURE__*/createElement("div", {
    className: css(paddingStyle, styles.wrapper)
  }, /*#__PURE__*/createElement(ThemeContext.Provider, {
    value: themeContextFirstCard
  }, /*#__PURE__*/createElement("div", {
    className: css(styles.firstCard)
  }, /*#__PURE__*/createElement(OnTopCard, _extends({}, cards[0], {
    imageWidths: imageWidthsForMainCard
  })))), /*#__PURE__*/createElement(ThemeContext.Provider, {
    value: themeContextMinorCard
  }, /*#__PURE__*/createElement("div", {
    className: css(styles.secondCard)
  }, /*#__PURE__*/createElement(OnTopCard, _extends({}, cards[1], {
    imageWidths: imageWidthsForMinorCard,
    imageHeightCalculationMethods: minorCardsImageHeightCalculationMethods
  }))), /*#__PURE__*/createElement("div", {
    className: css(styles.thirdCard)
  }, /*#__PURE__*/createElement(OnTopCard, _extends({}, cards[2], {
    imageWidths: imageWidthsForMinorCard,
    imageHeightCalculationMethods: minorCardsImageHeightCalculationMethods
  }))), /*#__PURE__*/createElement("div", {
    className: css(styles.fourthCard)
  }, /*#__PURE__*/createElement(OnTopCard, _extends({}, cards[3], {
    imageWidths: imageWidthsForMinorCard,
    imageHeightCalculationMethods: minorCardsImageHeightCalculationMethods
  }))), /*#__PURE__*/createElement("div", {
    className: css(styles.fifthCard)
  }, /*#__PURE__*/createElement(OnTopCard, _extends({}, cards[4], {
    imageWidths: imageWidthsForMinorCard,
    imageHeightCalculationMethods: minorCardsImageHeightCalculationMethods
  }))))));
};

const componentConfigThemeKey$L = 'announcementCard';
const numberOfLinesForTitle = 1;
const numberOfLinesForSubHeadlineLargeAndMedium = 1;
const numberOfLinesForSubHeadlineSmall = 2;

const createStyle$s = ({
  titleColor,
  subHeadlineColor,
  wrapperGapLarge,
  wrapperGapMedium,
  wrapperGapSmall,
  horizontalGapLarge,
  horizontalGapMedium,
  horizontalGapSmall
}, maxWidth = LARGE_SCREEN_SIZE) => StyleSheet.create({
  wrapper: {
    display: 'grid',
    overflow: 'hidden',
    background: 'transparent',
    justifyItems: 'center',
    maxWidth: `${maxWidth}px`,
    margin: 'auto'
  },
  link: {
    position: 'relative',
    textDecoration: 'none',
    display: 'block',
    gridTemplateRows: 'max-content auto',
    maxWidth: `${maxWidth}px`,
    width: '-webkit-fill-available',
    paddingLeft: '10px',
    paddingRight: '10px',
    [MEDIA_BREAKPOINTS.large]: {
      height: '80px'
    },
    [MEDIA_BREAKPOINTS.medium]: {
      height: '80px'
    },
    [MEDIA_BREAKPOINTS.small]: {
      height: '80px'
    }
  },
  innerWrapper: {
    overflow: 'hidden',
    display: 'grid',
    background: 'transparent',
    position: 'relative',
    [MEDIA_BREAKPOINTS.large]: {
      padding: `${wrapperGapLarge * baseUnit}px`
    },
    [MEDIA_BREAKPOINTS.medium]: {
      padding: `${wrapperGapMedium * baseUnit}px`
    },
    [MEDIA_BREAKPOINTS.small]: {
      padding: `${wrapperGapSmall * baseUnit}px`
    }
  },
  title: {
    color: titleColor,
    height: 'fit-content',
    ...ellipsify(numberOfLinesForTitle).style,
    [MEDIA_BREAKPOINTS.large]: {
      paddingBottom: `${horizontalGapLarge * baseUnit}px`
    },
    [MEDIA_BREAKPOINTS.medium]: {
      paddingBottom: `${horizontalGapMedium * baseUnit}px`
    },
    [MEDIA_BREAKPOINTS.small]: {
      paddingBottom: `${horizontalGapSmall * baseUnit}px`
    }
  },
  seoDescription: {
    color: subHeadlineColor,
    height: 'fit-content',
    [MEDIA_BREAKPOINTS.large]: { ...ellipsify(numberOfLinesForSubHeadlineLargeAndMedium).style
    },
    [MEDIA_BREAKPOINTS.medium]: { ...ellipsify(numberOfLinesForSubHeadlineLargeAndMedium).style
    },
    [MEDIA_BREAKPOINTS.small]: { ...ellipsify(numberOfLinesForSubHeadlineSmall).style
    }
  }
});

const AnnouncementCard = props => {
  const {
    title,
    seoDescription,
    path,
    image,
    lazyLoad = false,
    maxWidth
  } = props;
  const themeProps = useTheme(componentConfigThemeKey$L);
  const {
    titleFontSizeLarge,
    titleFontSizeMedium,
    titleFontSizeSmall,
    titleSemanticTag,
    subHeadlineFontSizeLarge,
    subHeadlineFontSizeMedium,
    subHeadlineFontSizeSmall,
    subHeadlineSemanticTag,
    boxShadowTypeActive,
    boxShadowTypeHover,
    boxShadowTypeNormal,
    borderColorTypeActive,
    borderColorTypeHover,
    borderColorTypeNormal,
    borderWidthTypeActive,
    borderWidthTypeHover,
    borderWidthTypeNormal,
    aspectRatioLarge,
    aspectRatioMedium,
    aspectRatioSmall
  } = themeProps;
  const borderBoxShadow = {
    borderColorTypeActive,
    borderColorTypeHover,
    borderColorTypeNormal,
    borderWidthTypeActive,
    borderWidthTypeHover,
    borderWidthTypeNormal
  };
  const boxShadowStyle = createStyleForBoxShadow({
    boxShadowTypeNormal,
    boxShadowTypeHover,
    boxShadowTypeActive,
    borderBoxShadow
  });
  const style = createStyle$s(themeProps, maxWidth);
  const imageWidths = createImageWidths(360, 720, 1080);

  const createImageAdditionalStyle = () => StyleSheet.create({
    style: {
      position: 'absolute',
      top: '0',
      bottom: '0',
      margin: '0',
      [MEDIA_BREAKPOINTS.large]: {
        left: '10px',
        right: '10px'
      },
      [MEDIA_BREAKPOINTS.medium]: {
        left: '10px',
        right: '10px'
      },
      [MEDIA_BREAKPOINTS.small]: {
        left: '10px',
        right: '10px'
      }
    }
  });

  return /*#__PURE__*/createElement("div", {
    className: css(style.wrapper, boxShadowStyle.style)
  }, /*#__PURE__*/createElement(Link, {
    className: css(style.link),
    href: path
  }, /*#__PURE__*/createElement(Image, _extends({}, image, {
    lazyLoad: lazyLoad,
    imageAspectRatios: createImageAspectRatios(aspectRatioSmall, aspectRatioMedium, aspectRatioLarge),
    imageWidths: imageWidths,
    additionalStyle: createImageAdditionalStyle()
  })), /*#__PURE__*/createElement("div", {
    className: css(style.innerWrapper)
  }, /*#__PURE__*/createElement(DynamicSemanticTag, {
    semanticTag: titleSemanticTag,
    fontSizeLarge: titleFontSizeLarge,
    fontSizeMedium: titleFontSizeMedium,
    fontSizeSmall: titleFontSizeSmall,
    styles: semanticCSS(style.title)
  }, title), /*#__PURE__*/createElement(DynamicSemanticTag, {
    semanticTag: subHeadlineSemanticTag,
    fontSizeLarge: subHeadlineFontSizeLarge,
    fontSizeMedium: subHeadlineFontSizeMedium,
    fontSizeSmall: subHeadlineFontSizeSmall,
    styles: semanticCSS(style.seoDescription)
  }, seoDescription))));
};

const componentConfigThemeKey$M = 'fourVCOneOTC';

const createStyle$t = themeProps => {
  const {
    largeHorizontalGap,
    largeVerticalGap,
    mediumHorizontalGap,
    mediumVerticalGap,
    smallHorizontalGap,
    baseUnit
  } = themeProps;
  const mainCardArea = 'main_card_area';
  const firstCardArea = 'first_card_area';
  const secondCardArea = 'second_card_area';
  const thirdCardArea = 'third_card_area';
  const fourthCardArea = 'fourth_card_area';
  return StyleSheet.create({
    wrapper: {
      display: 'grid',
      [MEDIA_BREAKPOINTS.large]: {
        gridTemplateColumns: '1fr 1fr 2fr',
        gridTemplateRows: '1fr 1fr',
        gridColumnGap: `${largeVerticalGap * baseUnit}px`,
        gridRowGap: `${largeHorizontalGap * baseUnit}px`,
        gridTemplateAreas: `'${firstCardArea} ${secondCardArea} ${mainCardArea}' '${thirdCardArea} ${fourthCardArea} ${mainCardArea}'`
      },
      [MEDIA_BREAKPOINTS.medium]: {
        gridTemplateColumns: '1fr 1fr',
        gridTemplateRows: 'auto auto auto',
        gridColumnGap: `${mediumVerticalGap * baseUnit}px`,
        gridRowGap: `${mediumHorizontalGap * baseUnit}px`,
        gridTemplateAreas: `'${mainCardArea} ${mainCardArea}' '${firstCardArea} ${secondCardArea}' '${thirdCardArea} ${fourthCardArea}'`
      },
      [MEDIA_BREAKPOINTS.small]: {
        gridTemplateColumns: '1fr',
        gridRowGap: `${smallHorizontalGap * baseUnit}px`,
        gridTemplateAreas: `'${mainCardArea}' '${firstCardArea}' '${secondCardArea}' '${thirdCardArea}' '${fourthCardArea}'`
      }
    },
    mainCard: {
      gridArea: mainCardArea
    },
    firstCard: {
      gridArea: firstCardArea
    },
    secondCard: {
      gridArea: secondCardArea
    },
    thirdCard: {
      gridArea: thirdCardArea
    },
    fourthCard: {
      gridArea: fourthCardArea
    },
    minorCard: {
      [MEDIA_BREAKPOINTS.small]: {
        paddingLeft: `${0.5 * baseUnit}px`,
        paddingRight: `${0.5 * baseUnit}px`
      }
    }
  });
};

const imageWidthsForMainCard$1 = createImageWidths(360, 720, 540);
const imageWidthsForMinorCard$1 = createImageWidths(360, 360, 360);
const onTopCardImageHeightCalculationMethods = createImageHeightCalculationMethods('ASPECT_RATIO', 'ASPECT_RATIO', '100%_HEIGHT');
const FourVCOneOTC = props => {
  const {
    cards,
    paddingFactors = {
      large: 0.5,
      medium: 0.5,
      small: 0
    }
  } = props;
  const paddingStyle = getLayoutComponentPadding(paddingFactors);
  const themeProps = useTheme(componentConfigThemeKey$M);
  const styles = createStyle$t(themeProps);
  const {
    authorColor,
    primaryTitleFontSizeSmall,
    primaryTitleFontSizeMedium,
    primaryTitleFontSizeLarge,
    primaryMetadataFontSizeSmall,
    primaryMetadataFontSizeMedium,
    primaryMetadataFontSizeLarge,
    primaryHorizontalGapSmall,
    primaryHorizontalGapMedium,
    primaryHorizontalGapLarge,
    aspectRatioSmall,
    secondaryTitleFontSizeSmall,
    secondaryTitleFontSizeMedium,
    secondaryTitleFontSizeLarge,
    secondaryMetadataFontSizeSmall,
    secondaryMetadataFontSizeMedium,
    secondaryMetadataFontSizeLarge,
    secondaryHorizontalGapSmall,
    secondaryHorizontalGapMedium,
    secondaryHorizontalGapLarge
  } = themeProps;
  const themeContextFirstCard = useThemeOverride({
    titleFontSizeLarge: primaryTitleFontSizeLarge,
    titleFontSizeMedium: primaryTitleFontSizeMedium,
    titleFontSizeSmall: primaryTitleFontSizeSmall,
    metadataFontSizeLarge: primaryMetadataFontSizeLarge,
    metadataFontSizeMedium: primaryMetadataFontSizeMedium,
    metadataFontSizeSmall: primaryMetadataFontSizeSmall,
    horizontalGapLarge: primaryHorizontalGapLarge,
    horizontalGapMedium: primaryHorizontalGapMedium,
    horizontalGapSmall: primaryHorizontalGapSmall,
    aspectRatioSmall,
    aspectRatioLarge: {
      x: 3,
      y: 4
    },
    authorColor
  }, componentConfigThemeKey$3);
  const themeContextMinorCard = useThemeOverride({
    titleFontSizeLarge: secondaryTitleFontSizeLarge,
    titleFontSizeMedium: secondaryTitleFontSizeMedium,
    titleFontSizeSmall: secondaryTitleFontSizeSmall,
    metadataFontSizeLarge: secondaryMetadataFontSizeLarge,
    metadataFontSizeMedium: secondaryMetadataFontSizeMedium,
    metadataFontSizeSmall: secondaryMetadataFontSizeSmall,
    horizontalGapLarge: secondaryHorizontalGapLarge,
    horizontalGapMedium: secondaryHorizontalGapMedium,
    horizontalGapSmall: secondaryHorizontalGapSmall
  }, componentConfigThemeKey$5);
  return /*#__PURE__*/createElement(ErrorBoundary, null, cards.length > 0 && /*#__PURE__*/createElement("div", {
    className: css(paddingStyle, styles.wrapper)
  }, /*#__PURE__*/createElement(ThemeContext.Provider, {
    value: themeContextMinorCard
  }, /*#__PURE__*/createElement("div", {
    className: css(styles.firstCard, styles.minorCard)
  }, /*#__PURE__*/createElement(VerticalCard, _extends({}, cards[1], {
    imageWidths: imageWidthsForMinorCard$1
  }))), /*#__PURE__*/createElement("div", {
    className: css(styles.secondCard, styles.minorCard)
  }, /*#__PURE__*/createElement(VerticalCard, _extends({}, cards[2], {
    imageWidths: imageWidthsForMinorCard$1
  }))), /*#__PURE__*/createElement("div", {
    className: css(styles.thirdCard, styles.minorCard)
  }, /*#__PURE__*/createElement(VerticalCard, _extends({}, cards[3], {
    imageWidths: imageWidthsForMinorCard$1
  }))), /*#__PURE__*/createElement("div", {
    className: css(styles.fourthCard, styles.minorCard)
  }, /*#__PURE__*/createElement(VerticalCard, _extends({}, cards[4], {
    imageWidths: imageWidthsForMinorCard$1
  })))), /*#__PURE__*/createElement(ThemeContext.Provider, {
    value: themeContextFirstCard
  }, /*#__PURE__*/createElement("div", {
    className: css(styles.mainCard)
  }, /*#__PURE__*/createElement(OnTopCard, _extends({}, cards[0], {
    imageWidths: imageWidthsForMainCard$1,
    imageHeightCalculationMethods: onTopCardImageHeightCalculationMethods,
    lazyLoad: true
  }))))));
};

const componentConfigThemeKey$N = 'oneOTCFourVC';

const createStyle$u = themeProps => {
  const {
    largeHorizontalGap,
    largeVerticalGap,
    mediumHorizontalGap,
    mediumVerticalGap,
    smallHorizontalGap,
    baseUnit
  } = themeProps;
  const firstCardArea = 'first_card_area';
  const secondCardArea = 'second_card_area';
  const thirdCardArea = 'third_card_area';
  const fourthCardArea = 'fourth_card_area';
  const fifthCardArea = 'fifth_card_area';
  return StyleSheet.create({
    wrapper: {
      display: 'grid',
      [MEDIA_BREAKPOINTS.large]: {
        gridTemplateColumns: '2fr 1fr 1fr',
        gridTemplateRows: '1fr 1fr',
        gridColumnGap: `${largeVerticalGap * baseUnit}px`,
        gridRowGap: `${largeHorizontalGap * baseUnit}px`,
        gridTemplateAreas: `'${firstCardArea} ${secondCardArea} ${thirdCardArea}' '${firstCardArea} ${fourthCardArea} ${fifthCardArea}'`
      },
      [MEDIA_BREAKPOINTS.medium]: {
        gridTemplateColumns: '1fr 1fr',
        gridTemplateRows: 'auto auto auto',
        gridColumnGap: `${mediumVerticalGap * baseUnit}px`,
        gridRowGap: `${mediumHorizontalGap * baseUnit}px`,
        gridTemplateAreas: `'${firstCardArea} ${firstCardArea}' '${secondCardArea} ${thirdCardArea}' '${fourthCardArea} ${fifthCardArea}'`
      },
      [MEDIA_BREAKPOINTS.small]: {
        gridTemplateColumns: '1fr',
        gridRowGap: `${smallHorizontalGap * baseUnit}px`,
        gridTemplateAreas: `'${firstCardArea}' '${secondCardArea}' '${thirdCardArea}' '${fourthCardArea}' '${fifthCardArea}'`
      }
    },
    firstCard: {
      gridArea: firstCardArea
    },
    secondCard: {
      gridArea: secondCardArea
    },
    thirdCard: {
      gridArea: thirdCardArea
    },
    fourthCard: {
      gridArea: fourthCardArea
    },
    fifthCard: {
      gridArea: fifthCardArea
    },
    minorCard: {
      [MEDIA_BREAKPOINTS.small]: {
        paddingLeft: `${0.5 * baseUnit}px`,
        paddingRight: `${0.5 * baseUnit}px`
      }
    }
  });
};

const imageWidthsForMainCard$2 = createImageWidths(360, 720, 540);
const imageWidthsForMinorCard$2 = createImageWidths(360, 360, 360);
const onTopCardImageHeightCalculationMethods$1 = createImageHeightCalculationMethods('ASPECT_RATIO', 'ASPECT_RATIO', '100%_HEIGHT');
const OneOTCFourVC = props => {
  const {
    cards,
    paddingFactors = {
      large: 0.5,
      medium: 0.5,
      small: 0
    }
  } = props;
  const paddingStyle = getLayoutComponentPadding(paddingFactors);
  const themeProps = useTheme(componentConfigThemeKey$N);
  const styles = createStyle$u(themeProps);
  const {
    authorColor
  } = themeProps;
  const themeContextFirstCard = useThemeOverride({
    titleFontSizeLarge: 'large',
    titleFontSizeMedium: 'large',
    titleFontSizeSmall: 'bigger',
    metadataFontSizeLarge: 'bigger',
    metadataFontSizeMedium: 'bigger',
    metadataFontSizeSmall: 'big',
    horizontalGapSmall: 0.5,
    horizontalGapMedium: 0.5,
    horizontalGapLarge: 0.5,
    aspectRatioSmall: {
      x: 4,
      y: 3
    },
    aspectRatioLarge: {
      x: 3,
      y: 4
    },
    authorColor
  }, componentConfigThemeKey$3);
  const themeContextMinorCard = useThemeOverride({
    titleFontSizeLarge: 'bigger',
    titleFontSizeMedium: 'bigger',
    titleFontSizeSmall: 'bigger',
    metadataFontSizeLarge: 'big',
    metadataFontSizeMedium: 'big',
    metadataFontSizeSmall: 'big',
    horizontalGapSmall: 0.5,
    horizontalGapMedium: 0.5,
    horizontalGapLarge: 0.5
  }, componentConfigThemeKey$5);
  return /*#__PURE__*/createElement(ErrorBoundary, null, cards.length > 0 && /*#__PURE__*/createElement("div", {
    className: css(paddingStyle, styles.wrapper)
  }, /*#__PURE__*/createElement(ThemeContext.Provider, {
    value: themeContextFirstCard
  }, /*#__PURE__*/createElement("div", {
    className: css(styles.firstCard)
  }, /*#__PURE__*/createElement(OnTopCard, _extends({}, cards[0], {
    imageWidths: imageWidthsForMainCard$2,
    imageHeightCalculationMethods: onTopCardImageHeightCalculationMethods$1,
    lazyLoad: true
  })))), /*#__PURE__*/createElement(ThemeContext.Provider, {
    value: themeContextMinorCard
  }, /*#__PURE__*/createElement("div", {
    className: css(styles.secondCard, styles.minorCard)
  }, /*#__PURE__*/createElement(VerticalCard, _extends({}, cards[1], {
    imageWidths: imageWidthsForMinorCard$2
  }))), /*#__PURE__*/createElement("div", {
    className: css(styles.thirdCard, styles.minorCard)
  }, /*#__PURE__*/createElement(VerticalCard, _extends({}, cards[2], {
    imageWidths: imageWidthsForMinorCard$2
  }))), /*#__PURE__*/createElement("div", {
    className: css(styles.fourthCard, styles.minorCard)
  }, /*#__PURE__*/createElement(VerticalCard, _extends({}, cards[3], {
    imageWidths: imageWidthsForMinorCard$2
  }))), /*#__PURE__*/createElement("div", {
    className: css(styles.fifthCard, styles.minorCard)
  }, /*#__PURE__*/createElement(VerticalCard, _extends({}, cards[4], {
    imageWidths: imageWidthsForMinorCard$2
  }))))));
};

const componentConfigThemeKey$O = 'nineHC';
const maximumNumberOfCards$2 = 9;

const getStyle$A = themeProps => {
  const {
    largeHorizontalGap,
    mediumHorizontalGap,
    smallHorizontalGap
  } = themeProps;
  return StyleSheet.create({
    wrapper: {
      display: 'grid',
      gridTemplateColumns: '1fr',
      gridTemplateRows: 'auto',
      height: 'fit-content',
      [MEDIA_BREAKPOINTS.large]: {
        gridRowGap: `${baseUnit * largeHorizontalGap}px`
      },
      [MEDIA_BREAKPOINTS.medium]: {
        gridRowGap: `${baseUnit * mediumHorizontalGap}px`
      },
      [MEDIA_BREAKPOINTS.small]: {
        gridRowGap: `${baseUnit * smallHorizontalGap}px`
      }
    }
  });
};

const defaultImageWidths$1 = createImageWidths(180, 180, 360);
const NineHC = props => {
  const {
    cards,
    paddingFactors,
    imageWidths = defaultImageWidths$1
  } = props;
  const themeProps = useTheme(componentConfigThemeKey$O);
  const style = getStyle$A(themeProps);
  const paddingStyle = getLayoutComponentPadding(paddingFactors);
  return /*#__PURE__*/createElement(ErrorBoundary, null, cards.length > 0 && /*#__PURE__*/createElement("div", {
    className: css(paddingStyle, style.wrapper)
  }, cards.map(card => /*#__PURE__*/createElement(HorizontalCard, _extends({
    key: card.id
  }, card, {
    imageWidths: imageWidths
  }))).slice(0, maximumNumberOfCards$2)));
};

const componentConfigThemeKey$P = 'nineHCForOneThird';
const imageWidths$a = createImageWidths(180, 180, 180);
const NineHCForOneThird = props => {
  const {
    cards
  } = props;
  const themeProps = useTheme(componentConfigThemeKey$P);
  const themeContextHC = useThemeOverride(themeProps, componentConfigThemeKey$7);
  return /*#__PURE__*/createElement(ErrorBoundary, null, cards.length > 0 && /*#__PURE__*/createElement(ThemeContext.Provider, {
    value: themeContextHC
  }, /*#__PURE__*/createElement(NineHC, {
    cards: cards,
    paddingFactors: {
      large: 0,
      medium: 0,
      small: 0
    },
    imageWidths: imageWidths$a
  })));
};

const componentConfigThemeKey$Q = 'threeHCOneOTC';
const imageWidthsOnTopCard$2 = createImageWidths(360, 720, 540);
const imageWidthsHorizontalCard$3 = createImageWidths(180, 180, 180);

const getStyles$g = themeProps => {
  const {
    largeHorizontalGap,
    largeVerticalGap,
    mediumHorizontalGap,
    smallHorizontalGap
  } = themeProps;
  const mainCardArea = 'main_card_area';
  const firstCardArea = 'first_card_area';
  const secondCardArea = 'second_card_area';
  const thirdCardArea = 'third_card_area';
  return StyleSheet.create({
    section: {
      display: 'grid',
      [MEDIA_BREAKPOINTS.large]: {
        gridTemplateColumns: '1fr 1fr',
        gridTemplateRows: '1fr 1fr 1fr',
        gridRowGap: `${largeHorizontalGap * baseUnit}px`,
        gridColumnGap: `${largeVerticalGap * baseUnit}px`,
        gridTemplateAreas: `'${firstCardArea} ${mainCardArea}' '${secondCardArea} ${mainCardArea}' '${thirdCardArea} ${mainCardArea}'`
      },
      [MEDIA_BREAKPOINTS.medium]: {
        gridTemplateRows: 'auto auto auto auto',
        gridRowGap: `${mediumHorizontalGap * baseUnit}px`,
        gridTemplateAreas: `'${mainCardArea}' '${firstCardArea}' '${secondCardArea}' '${thirdCardArea}'`
      },
      [MEDIA_BREAKPOINTS.small]: {
        gridTemplateRows: 'auto auto auto auto',
        gridRowGap: `${smallHorizontalGap * baseUnit}px`,
        gridTemplateAreas: `'${mainCardArea}' '${firstCardArea}' '${secondCardArea}' '${thirdCardArea}'`
      }
    },
    minorCard: {
      [MEDIA_BREAKPOINTS.small]: {
        paddingLeft: `${0.5 * baseUnit}px`,
        paddingRight: `${0.5 * baseUnit}px`
      }
    },
    mainCard: {
      gridArea: mainCardArea
    },
    firstCard: {
      gridArea: firstCardArea
    },
    secondCard: {
      gridArea: secondCardArea
    },
    thirdCard: {
      gridArea: thirdCardArea
    }
  });
};

const ThreeHCOneOTC = props => {
  const {
    cards,
    paddingFactors = {
      large: 0.5,
      medium: 0.5,
      small: 0
    }
  } = props;
  const themeProps = useTheme(componentConfigThemeKey$Q);
  const styles = getStyles$g(themeProps);
  const paddingStyle = getLayoutComponentPadding(paddingFactors);
  const onTopCardHeightCalculation = createImageHeightCalculationMethods('ASPECT_RATIO', 'ASPECT_RATIO', '100%_HEIGHT');
  const {
    titleFontSizeSmall,
    titleFontSizeMedium,
    titleFontSizeLarge,
    aspectRatioLarge,
    aspectRatioSmall,
    showDescriptionInMediumScreen,
    metadataFontSizeLarge,
    metadataFontSizeMedium,
    metadataFontSizeSmall,
    authorColor,
    insideCardSmallHorizontalGap
  } = themeProps;
  const themeContextHorizontalCard = useThemeOverride({
    titleFontSizeSmall,
    titleFontSizeMedium,
    titleFontSizeLarge,
    aspectRatioLarge,
    aspectRatioSmall,
    showDescriptionInMediumScreen,
    metadataFontSizeLarge,
    metadataFontSizeMedium,
    metadataFontSizeSmall
  }, componentConfigThemeKey$7);
  const themeContextOnTopCard = useThemeOverride({
    horizontalGapSmall: insideCardSmallHorizontalGap,
    titleFontSizeSmall,
    authorColor
  }, componentConfigThemeKey$3);
  return /*#__PURE__*/createElement(ErrorBoundary, null, cards.length > 0 && /*#__PURE__*/createElement("div", {
    className: css(styles.section, paddingStyle)
  }, /*#__PURE__*/createElement(ThemeContext.Provider, {
    value: themeContextHorizontalCard
  }, /*#__PURE__*/createElement("div", {
    className: css(styles.firstCard, styles.minorCard)
  }, /*#__PURE__*/createElement(HorizontalCard, _extends({}, cards[1], {
    imageWidths: imageWidthsHorizontalCard$3
  }))), /*#__PURE__*/createElement("div", {
    className: css(styles.secondCard, styles.minorCard)
  }, /*#__PURE__*/createElement(HorizontalCard, _extends({}, cards[2], {
    imageWidths: imageWidthsHorizontalCard$3
  }))), /*#__PURE__*/createElement("div", {
    className: css(styles.thirdCard, styles.minorCard)
  }, /*#__PURE__*/createElement(HorizontalCard, _extends({}, cards[3], {
    imageWidths: imageWidthsHorizontalCard$3
  })))), /*#__PURE__*/createElement("div", {
    className: css(styles.mainCard)
  }, /*#__PURE__*/createElement(ThemeContext.Provider, {
    value: themeContextOnTopCard
  }, /*#__PURE__*/createElement(OnTopCard, _extends({}, cards[0], {
    imageWidths: imageWidthsOnTopCard$2,
    imageHeightCalculationMethods: onTopCardHeightCalculation,
    lazyLoad: true
  }))))));
};

let Directions$1;

(function (Directions) {
  Directions["UP"] = "180deg";
  Directions["DOWN"] = "0deg";
  Directions["LEFT"] = "90deg";
  Directions["RIGHT"] = "270deg";
})(Directions$1 || (Directions$1 = {}));

const getStyle$B = (direction, baseUnit) => StyleSheet.create({
  caret: {
    width: '10px',
    transform: `rotate(${direction})`,
    transition: 'transform 400ms',
    marginLeft: `${baseUnit * 0.25}px`
  }
});

const getFillColor = (color, hoverColor = '', isHovering = false) => {
  return isHovering ? hoverColor : color;
};

const TriangleIcon = props => {
  const {
    direction = Directions$1.DOWN,
    color = '#FFF',
    hoverColor,
    isHovering
  } = props;
  const {
    baseUnit
  } = useTheme();
  const fillColor = getFillColor(color, hoverColor, isHovering);
  return /*#__PURE__*/React__default.createElement("svg", {
    className: css(getStyle$B(direction, baseUnit).caret),
    width: "64",
    height: "10px",
    viewBox: "0 0 64 64",
    version: "1.1"
  }, /*#__PURE__*/React__default.createElement("g", {
    stroke: "none",
    strokeWidth: "1",
    fill: "none",
    fillRule: "evenodd"
  }, /*#__PURE__*/React__default.createElement("polygon", {
    fill: fillColor,
    points: "0 16 32 48 64 16"
  })));
};

const getLinkColorsStyle = (activeStateColor, hoverStateColor, idleColor, currentLinkColor, isActive) => {
  return StyleSheet.create({
    link: {
      color: isActive ? currentLinkColor : idleColor,
      textDecoration: 'none',
      '@media (hover: hover)': {
        ':hover': {
          color: hoverStateColor
        }
      },
      ':active': {
        color: activeStateColor
      }
    }
  });
};

const getStyles$h = isInvisibleLink => StyleSheet.create({
  span: {
    visibility: isInvisibleLink ? 'hidden' : 'visible',
    whiteSpace: 'nowrap',
    position: 'relative',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center'
  },
  menu: {
    position: 'absolute'
  }
});

const LinkWithSubMenu = props => {
  const {
    title,
    hoverStateColor,
    idleColor,
    index,
    linkDropdownIconColor,
    linkFontSizeLarge,
    linkFontSizeMedium,
    linkFontSizeSmall,
    isInvisibleLink = false,
    isOpen = false,
    currentLinkColor,
    activeStateColor,
    registerLinkWithSubMenu,
    toggleMenu
  } = props;
  const [isHovering, setIsHovering] = useState(false);
  const {
    fontSizesStyles
  } = useTheme();
  const styles = getStyles$h(isInvisibleLink);
  const linkRef = useRef(null);
  const fontStyle = getStyleBySize(fontSizesStyles, {
    fontSizeSmall: linkFontSizeSmall,
    fontSizeMedium: linkFontSizeMedium,
    fontSizeLarge: linkFontSizeLarge
  });
  const linkColorsStyle = getLinkColorsStyle(activeStateColor, hoverStateColor, idleColor, currentLinkColor, false);
  useEffect(() => {
    if (typeof registerLinkWithSubMenu === 'function') {
      registerLinkWithSubMenu(linkRef, index);
    }
  }, [registerLinkWithSubMenu, index]);

  const onHover = () => {
    setIsHovering(true);
  };

  const onLeave = () => {
    setIsHovering(false);
  };

  const onClick = () => {
    if (linkRef.current !== null && typeof toggleMenu === 'function') {
      toggleMenu(linkRef.current, index);
    }
  };

  const triangleDirection = isOpen ? Directions.UP : Directions.DOWN;
  return /*#__PURE__*/createElement(Fragment, null, /*#__PURE__*/createElement(Span, {
    styles: semanticCSS(fontStyle, styles.span, linkColorsStyle.link),
    onMouseEnter: onHover,
    onMouseLeave: onLeave,
    onClick: onClick,
    reference: linkRef
  }, title, /*#__PURE__*/createElement(TriangleIcon, {
    hoverColor: hoverStateColor,
    color: linkDropdownIconColor,
    isHovering: isHovering,
    direction: triangleDirection
  })));
};

const getVisibilityStyle = isInvisibleLink => StyleSheet.create({
  spanWrapper: {
    visibility: isInvisibleLink ? 'hidden' : 'visible'
  },
  span: {
    display: 'flex',
    alignItems: 'center'
  }
});

const RegularHeaderLink = props => {
  const {
    href,
    text,
    activeStateColor,
    hoverStateColor,
    idleColor,
    isActive,
    linkFontSizeLarge,
    linkFontSizeMedium,
    linkFontSizeSmall,
    isInvisibleLink = false,
    currentLinkColor
  } = props;
  const {
    fontSizesStyles
  } = useTheme();
  const linkColorsStyle = getLinkColorsStyle(activeStateColor, hoverStateColor, idleColor, currentLinkColor, isActive);
  const fontStyle = getStyleBySize(fontSizesStyles, {
    fontSizeSmall: linkFontSizeSmall,
    fontSizeMedium: linkFontSizeMedium,
    fontSizeLarge: linkFontSizeLarge
  });
  const visibilityStyle = getVisibilityStyle(isInvisibleLink);
  return /*#__PURE__*/createElement(Link, {
    href: href,
    className: css(visibilityStyle.spanWrapper)
  }, /*#__PURE__*/createElement(Span, {
    className: css(visibilityStyle.span, fontStyle, linkColorsStyle.link)
  }, text));
};

const getStyles$i = (secondRowBackgroundColor, baseUnit, secondRowHeightInMediumScreen, secondRowHeightInSmallScreen, boxShadow) => {
  return StyleSheet.create({
    scrollableNav: {
      boxShadow,
      backgroundColor: secondRowBackgroundColor,
      overflowX: 'auto',
      alignItems: 'center',
      zIndex: 50,
      [MEDIA_BREAKPOINTS.large]: {
        display: 'none'
      },
      [MEDIA_BREAKPOINTS.medium]: {
        height: `${secondRowHeightInMediumScreen * baseUnit}px`,
        display: 'flex'
      },
      [MEDIA_BREAKPOINTS.small]: {
        height: `${secondRowHeightInSmallScreen * baseUnit}px`,
        display: 'flex'
      }
    },
    scrollableUl: {
      display: 'flex',
      padding: 0
    }
  });
};

const getLinksStyle = (baseUnit, verticalGapInLargeScreen, verticalGapInMediumScreen, verticalGapInSmallScreen) => StyleSheet.create({
  li: {
    display: 'inline-block',
    whiteSpace: 'nowrap',
    [MEDIA_BREAKPOINTS.large]: {
      marginLeft: `${baseUnit * verticalGapInLargeScreen}px`
    },
    [MEDIA_BREAKPOINTS.medium]: {
      marginLeft: `${baseUnit * verticalGapInMediumScreen}px`
    },
    [MEDIA_BREAKPOINTS.small]: {
      marginLeft: `${baseUnit * verticalGapInSmallScreen}px`
    },
    ':last-child': {
      [MEDIA_BREAKPOINTS.medium]: {
        marginRight: `${baseUnit * verticalGapInMediumScreen}px`
      },
      [MEDIA_BREAKPOINTS.small]: {
        marginRight: `${baseUnit * verticalGapInSmallScreen}px`
      }
    }
  }
});

const getLinks = (links, activeStateColor, hoverStateColor, idleColor, currentLinkColor, pagePath, baseUnit, linkFontSizeLarge, linkFontSizeMedium, linkFontSizeSmall, verticalGapInLargeScreen, verticalGapInMediumScreen, verticalGapInSmallScreen, linkDropdownIconColor, onMenuToggle, openLinkIndex) => {
  return links.map((navigationLink, index) => {
    const isActive = pagePath === navigationLink.link.href;
    const style = getLinksStyle(baseUnit, verticalGapInLargeScreen, verticalGapInMediumScreen, verticalGapInSmallScreen);
    const hasSubMenu = (navigationLink.children || []).length > 0;
    const fontStyles = {
      linkFontSizeLarge,
      linkFontSizeMedium,
      linkFontSizeSmall
    };
    return /*#__PURE__*/createElement("li", {
      key: index,
      className: css(style.li)
    }, hasSubMenu ? /*#__PURE__*/createElement(LinkWithSubMenu, _extends({
      currentLinkColor: currentLinkColor,
      activeStateColor: activeStateColor,
      title: navigationLink.link.text,
      hoverStateColor: hoverStateColor,
      toggleMenu: onMenuToggle,
      idleColor: openLinkIndex === index ? hoverStateColor : idleColor,
      index: index,
      linkDropdownIconColor: linkDropdownIconColor
    }, fontStyles, {
      isOpen: openLinkIndex === index
    })) : /*#__PURE__*/createElement(RegularHeaderLink, _extends({
      currentLinkColor: currentLinkColor,
      activeStateColor: activeStateColor,
      hoverStateColor: hoverStateColor,
      idleColor: idleColor,
      isActive: isActive,
      href: navigationLink.link.href,
      text: navigationLink.link.text
    }, fontStyles)));
  });
};

const createHeaderSecondRowStyleWithAnimation = animationDuration => {
  return StyleSheet.create({
    foldingUp: {
      [MEDIA_BREAKPOINTS.medium]: {
        transition: `${animationDuration}ms`,
        transform: 'translateY(-100%)'
      },
      [MEDIA_BREAKPOINTS.small]: {
        transition: `${animationDuration}ms`,
        transform: 'translateY(-100%)'
      }
    },
    foldingDown: {
      [MEDIA_BREAKPOINTS.medium]: {
        transition: `${animationDuration}ms`,
        transform: 'translateY(0)',
        transitionDelay: `${animationDuration}ms`
      },
      [MEDIA_BREAKPOINTS.small]: {
        transition: `${animationDuration}ms`,
        transform: 'translateY(0)',
        transitionDelay: `${animationDuration}ms`
      }
    }
  });
};

const getHeaderSecondRowStyleWithAnimation = (menuFoldState, animationDuration) => {
  const style = createHeaderSecondRowStyleWithAnimation(animationDuration);
  return menuFoldState === AnimationDirection.UP ? style.foldingUp : style.foldingDown;
};

const ScrollableNav = props => {
  const {
    menuFoldState,
    animationDuration,
    links,
    linkColorTypeActive,
    linkColorTypeHover,
    linkColorTypeNormal,
    currentLinkColor,
    pagePath,
    baseUnit,
    linkFontSizeLarge,
    linkFontSizeMedium,
    linkFontSizeSmall,
    verticalGapInLargeScreen,
    verticalGapInMediumScreen,
    verticalGapInSmallScreen,
    secondRowBackgroundColor,
    secondRowHeightInMediumScreen,
    secondRowHeightInSmallScreen,
    linkDropdownIconColor,
    onMenuToggle,
    openLinkIndex,
    boxShadow
  } = props;
  const styles = getStyles$i(secondRowBackgroundColor, baseUnit, secondRowHeightInMediumScreen, secondRowHeightInSmallScreen, boxShadow);
  return /*#__PURE__*/createElement("nav", {
    className: css(getHeaderSecondRowStyleWithAnimation(menuFoldState, animationDuration), styles.scrollableNav)
  }, /*#__PURE__*/createElement("ul", {
    className: css(styles.scrollableUl)
  }, getLinks(links || [], linkColorTypeActive, linkColorTypeHover, linkColorTypeNormal, currentLinkColor, pagePath, baseUnit, linkFontSizeLarge, linkFontSizeMedium, linkFontSizeSmall, verticalGapInLargeScreen, verticalGapInMediumScreen, verticalGapInSmallScreen, linkDropdownIconColor, onMenuToggle, openLinkIndex)));
};

const getStyles$j = (baseUnit, firstRowHeightInMediumScreen, firstRowHeightInSmallScreen, secondRowHeightInMediumScreen, secondRowHeightInSmallScreen, backgroundColor) => {
  return StyleSheet.create({
    menuWrapper: {
      zIndex: 1000,
      position: 'fixed',
      backgroundColor,
      left: 0,
      right: 0,
      bottom: 0,
      padding: `${baseUnit}px`,
      [MEDIA_BREAKPOINTS.large]: {
        display: 'none'
      },
      [MEDIA_BREAKPOINTS.medium]: {
        top: `${(firstRowHeightInMediumScreen + secondRowHeightInMediumScreen) * baseUnit}px`
      },
      [MEDIA_BREAKPOINTS.small]: {
        top: `${(firstRowHeightInSmallScreen + secondRowHeightInSmallScreen) * baseUnit}px`,
        gridTemplateColumns: 'repeat(2, 1fr)'
      }
    },
    ul: {
      listStyle: 'none',
      display: 'grid',
      gridRowGap: `${baseUnit}px`,
      gridColumnGap: `${baseUnit}px`,
      [MEDIA_BREAKPOINTS.medium]: {
        gridTemplateColumns: 'repeat(4, 1fr)'
      },
      [MEDIA_BREAKPOINTS.small]: {
        gridTemplateColumns: 'repeat(2, 1fr)'
      }
    }
  });
};

const ScrollableNavDropdown = props => {
  const {
    firstRowHeightInMediumScreen,
    firstRowHeightInSmallScreen,
    secondRowHeightInMediumScreen,
    secondRowHeightInSmallScreen,
    linkColorTypeActive,
    linkColorTypeHover,
    linkColorTypeNormal,
    currentLinkColor,
    backgroundColor,
    links,
    pagePath,
    linkFontSize
  } = props;
  const {
    baseUnit
  } = useTheme();
  const styles = getStyles$j(baseUnit, firstRowHeightInMediumScreen, firstRowHeightInSmallScreen, secondRowHeightInMediumScreen, secondRowHeightInSmallScreen, backgroundColor);
  const fontStyles = {
    linkFontSizeLarge: linkFontSize,
    linkFontSizeMedium: linkFontSize,
    linkFontSizeSmall: linkFontSize
  };
  return /*#__PURE__*/createElement("div", {
    className: css(styles.menuWrapper)
  }, /*#__PURE__*/createElement("ul", {
    className: css(styles.ul)
  }, links.map((navigationLink, index) => {
    const isActive = pagePath === navigationLink.link.href;
    return /*#__PURE__*/createElement("li", {
      key: index
    }, /*#__PURE__*/createElement(RegularHeaderLink, _extends({
      currentLinkColor: currentLinkColor,
      activeStateColor: linkColorTypeActive,
      hoverStateColor: linkColorTypeHover,
      idleColor: linkColorTypeNormal,
      isActive: isActive,
      href: navigationLink.link.href,
      text: navigationLink.link.text
    }, fontStyles)));
  })));
};

const componentConfigThemeKey$R = 'menuLinks';

const getMenuLinksStyle$1 = (backgroundColor, baseUnit, headerHeights) => StyleSheet.create({
  style: {
    padding: 0,
    margin: 0,
    backgroundColor,
    position: 'fixed',
    width: '100%',
    zIndex: 2,
    left: 0,
    overflowY: 'auto',
    [MEDIA_BREAKPOINTS.medium]: {
      top: `${headerHeights.medium * baseUnit}px`,
      height: `calc(100vh - ${headerHeights.medium * baseUnit}px)`
    },
    [MEDIA_BREAKPOINTS.small]: {
      top: `${headerHeights.small * baseUnit}px`,
      height: `calc(100vh - ${headerHeights.small * baseUnit}px)`
    }
  },
  enter: {
    opacity: 0,
    transform: 'translate(-100%)'
  },
  enterActive: {
    opacity: 1,
    transform: 'translate(0)',
    transition: `transform ${menuEnterDuration}ms, opacity ${menuEnterDuration}ms`,
    transitionTimingFunction: 'ease-out'
  },
  exit: {
    opacity: 1,
    transform: 'translate(0)'
  },
  exitActive: {
    opacity: 0,
    transform: 'translate(-100%)',
    transition: `transform ${menuExitDuration}ms ease-in , opacity ${menuExitDuration}ms ease-in`,
    transitionDelay: `${menuExitDelay}ms`
  }
});

const getMenuLinkStyle$2 = () => StyleSheet.create({
  enter: {
    opacity: 0.5,
    transform: 'translate(-100%)'
  },
  enterActive: {
    opacity: 1,
    transform: 'translate(0)',
    transition: `transform ${linkEnterDuration}ms ease-out, opacity ${linkEnterDuration}ms ease-in`,
    transitionDelay: `${linkEnterDelay}ms`
  },
  exit: {
    transform: 'translate(0)',
    opacity: 1
  },
  exitActive: {
    opacity: 0.5,
    transform: 'translate(-100%)',
    transition: `transform ${linkExitDuration}ms ease-in, opacity ${linkExitDuration}ms ease-in`,
    transitionDelay: `${linkExitDelay}ms`
  }
});

const MenuLinkAnimationWrapper$1 = props => {
  const {
    children,
    shouldAppear
  } = props;
  const style = getMenuLinkStyle$2();
  return /*#__PURE__*/createElement(CSSTransition, {
    unmountOnExit: true,
    mountOnEnter: true,
    in: shouldAppear,
    timeout: {
      enter: linkEnterTimeOut,
      exit: linkExitTimeOut
    },
    classNames: {
      enter: css(style.enter),
      enterActive: css(style.enterActive),
      exit: css(style.exit),
      exitActive: css(style.exitActive)
    }
  }, children);
};

const createMenuLink$1 = (onClick, currentOpenLink, shouldAppear, startIndex = 0) => (menuLink, index) => {
  return /*#__PURE__*/createElement(MenuLinkAnimationWrapper$1, {
    shouldAppear: shouldAppear,
    key: index
  }, state => /*#__PURE__*/createElement(MenuLink, {
    key: index,
    link: menuLink.link,
    childrenLinks: menuLink.childrenLinks,
    index: startIndex + index,
    onClick: onClick,
    isLinkOpen: currentOpenLink !== -1 && currentOpenLink === startIndex + index,
    startChevronIconAnimation: state === 'entering' || state === 'entered'
  }));
};

const createMenuButton$1 = shouldAppear => ({
  text,
  onClick
}, key) => /*#__PURE__*/createElement(MenuLinkAnimationWrapper$1, {
  shouldAppear: shouldAppear,
  key: key
}, /*#__PURE__*/createElement(MenuButton, {
  text: text,
  onClick: onClick
}));
const MenuLinks$1 = props => {
  const {
    menuLinks,
    isMenuOpen,
    onClick,
    currentOpenLink,
    buttons = [],
    children
  } = props;
  const {
    backgroundColor,
    baseUnit
  } = useTheme(componentConfigThemeKey$R);
  const headerHeights = useFirstRowHeaderHeights();
  const style = getMenuLinksStyle$1(backgroundColor, baseUnit, headerHeights);
  return /*#__PURE__*/createElement(ErrorBoundary, null, /*#__PURE__*/createElement(CSSTransition, {
    in: isMenuOpen,
    unmountOnExit: true,
    mountOnEnter: true,
    timeout: {
      enter: menuEnterTimeOut,
      exit: menuExitTimeOut
    },
    classNames: {
      enter: css(style.enter),
      enterActive: css(style.enterActive),
      exit: css(style.exit),
      exitActive: css(style.exitActive)
    }
  }, state => /*#__PURE__*/createElement("ul", {
    className: css(style.style)
  }, menuLinks.map(createMenuLink$1(onClick, currentOpenLink, state === 'entering' || state === 'entered')), buttons.map(createMenuButton$1(state === 'entering' || state === 'entered')), children)));
};

const componentConfigurationKey$D = 'hamburgerMenuSocialLinks';

const getStyles$k = (titleColor, baseUnit, socialLinksColor, socialLinksHoverColor) => StyleSheet.create({
  wrapper: {
    padding: `${baseUnit / 2}px ${baseUnit}px`
  },
  title: {
    color: titleColor,
    marginBottom: `${baseUnit}px`
  },
  ul: {
    listStyle: 'none',
    display: 'inline-grid',
    gridAutoFlow: 'column',
    gridGap: `${baseUnit}px`
  },
  svg: {
    height: '16px',
    fill: socialLinksColor,
    ':hover': {
      fill: socialLinksHoverColor
    }
  }
});

const MenuSocialLinks = props => {
  const {
    socialSection,
    oldSocialSection
  } = props;
  const {
    titleColor,
    baseUnit,
    socialLinksColor,
    socialLinksHoverColor,
    titleFontSizeLarge,
    titleFontSizeMedium,
    titleFontSizeSmall
  } = useTheme(componentConfigurationKey$D);
  const styles = getStyles$k(titleColor, baseUnit, socialLinksColor, socialLinksHoverColor);
  return socialSection ? /*#__PURE__*/createElement("div", {
    className: css(styles.wrapper)
  }, /*#__PURE__*/createElement(H4, {
    styles: semanticCSS(styles.title),
    fontSizeLarge: titleFontSizeLarge,
    fontSizeMedium: titleFontSizeMedium,
    fontSizeSmall: titleFontSizeSmall
  }, socialSection.title), /*#__PURE__*/createElement("ul", {
    className: css(styles.ul)
  }, socialSection.imageLinks.map((link, index) => {
    const svg = link.src.replace(/<svg/g, `<svg class="${css(styles.svg)}"`);
    return /*#__PURE__*/createElement("li", {
      key: index
    }, /*#__PURE__*/createElement(Link, {
      href: link.href,
      rel: "noopener",
      dangerouslySetInnerHTML: {
        __html: svg
      },
      alt: link.alt
    }));
  }))) : /*#__PURE__*/createElement("div", {
    className: css(styles.wrapper)
  }, /*#__PURE__*/createElement(H4, {
    styles: semanticCSS(styles.title)
  }, oldSocialSection.title), /*#__PURE__*/createElement("ul", {
    className: css(styles.ul)
  }, oldSocialSection.imageLinks.map((link, index) => /*#__PURE__*/createElement("li", {
    key: index
  }, /*#__PURE__*/createElement(Link, {
    href: link.href
  }, /*#__PURE__*/createElement("img", {
    alt: link.alt,
    src: link.src,
    height: "16"
  }))))));
};

const componentConfigThemeKey$S = 'dropDownHamburgerMenu';

const getStyles$l = (dividerColor, baseUnit) => StyleSheet.create({
  divider: {
    backgroundColor: dividerColor,
    height: '1px',
    [MEDIA_BREAKPOINTS.medium]: {
      margin: `${baseUnit}px`
    },
    [MEDIA_BREAKPOINTS.small]: {
      margin: `${baseUnit}px`
    }
  }
});

const Menu$1 = props => {
  const {
    links,
    buttons,
    additionalStyle,
    socialSection,
    oldSocialSection
  } = props;
  const shouldShowSocialSection = socialSection && socialSection.imageLinks.length > 0;
  const shouldShowSocial = shouldShowSocialSection || oldSocialSection && oldSocialSection.imageLinks.length > 0;
  const {
    baseUnit,
    dividerColor
  } = useTheme(componentConfigThemeKey$S);
  const [state, setState] = useState({
    isMenuOpen: false,
    currentOpenLink: -1
  });
  const {
    isMenuOpen,
    currentOpenLink
  } = state;

  const clickOnMenuIcon = () => {
    setState({
      isMenuOpen: !isMenuOpen,
      currentOpenLink: isMenuOpen ? currentOpenLink : -1
    });
  };

  const clickOnLinkDropDown = index => {
    setState({
      isMenuOpen,
      currentOpenLink: currentOpenLink === index ? -1 : index
    });
  };

  const style = getStyles$l(dividerColor, baseUnit);
  return /*#__PURE__*/React__default.createElement("div", {
    className: css(additionalStyle && additionalStyle.style)
  }, /*#__PURE__*/React__default.createElement(MenuIcon, {
    isMenuOpen: isMenuOpen,
    onClick: clickOnMenuIcon
  }), /*#__PURE__*/React__default.createElement(MenuLinks$1, {
    menuLinks: links,
    buttons: buttons,
    isMenuOpen: isMenuOpen,
    onClick: clickOnLinkDropDown,
    currentOpenLink: currentOpenLink
  }, shouldShowSocial && /*#__PURE__*/React__default.createElement(React__default.Fragment, null, /*#__PURE__*/React__default.createElement("div", {
    className: css(style.divider)
  }), /*#__PURE__*/React__default.createElement(MenuSocialLinks, {
    socialSection: socialSection,
    oldSocialSection: oldSocialSection
  }))));
};

const getInjectedVideoLogoStyle$1 = (reduceHeightAnimationDuration, menuAnimation, baseUnit, firstRowHeightInMediumScreen, firstRowHeightInSmallScreen, largeLogoHeightsAndWidths) => {
  return StyleSheet.create({
    wrapper: {
      [MEDIA_BREAKPOINTS.large]: {
        height: menuAnimation === AnimationDirection.UP ? `${largeLogoHeightsAndWidths.reducedHeight}px` : `${largeLogoHeightsAndWidths.height}px`,
        minWidth: menuAnimation === AnimationDirection.UP ? `${largeLogoHeightsAndWidths.reducedWidth}px` : `${largeLogoHeightsAndWidths.width}px`
      },
      [MEDIA_BREAKPOINTS.medium]: {
        height: `${baseUnit * firstRowHeightInMediumScreen}px`,
        width: 'auto'
      },
      [MEDIA_BREAKPOINTS.small]: {
        height: `${baseUnit * firstRowHeightInSmallScreen}px`,
        width: 'auto'
      },
      transition: `${reduceHeightAnimationDuration}ms`
    },
    video: {
      height: '100%',
      alignItems: 'center',
      display: 'inline-flex'
    },
    source: {
      height: '100%'
    }
  });
};

const VideoLogo$1 = props => {
  const {
    baseUnit
  } = useTheme();
  const {
    videoPath,
    href,
    reduceHeightAnimationDuration = 0,
    menuAnimation = AnimationDirection.NONE,
    firstRowHeightInLargeScreen,
    firstRowHeightInMediumScreen,
    firstRowHeightInSmallScreen
  } = props;
  React__default.useEffect(() => {
    setEventEmitter(injectVideoLogoSuccessfully, callback => callback());
  }, [videoPath]);
  const [ref, largeLogoHeightsAndWidths] = useAnimationLogoHeightsAndWidths(firstRowHeightInLargeScreen);
  const style = getInjectedVideoLogoStyle$1(reduceHeightAnimationDuration, menuAnimation, baseUnit, firstRowHeightInMediumScreen, firstRowHeightInSmallScreen, largeLogoHeightsAndWidths);
  return /*#__PURE__*/React__default.createElement(Link, {
    className: css(style.wrapper),
    href: href
  }, /*#__PURE__*/React__default.createElement("video", {
    ref: ref,
    className: css(style.video),
    loop: true,
    autoPlay: true,
    muted: "true",
    playsinline: "true"
  }, /*#__PURE__*/React__default.createElement("source", {
    className: css(style.source),
    src: videoPath,
    type: "video/mp4"
  })));
};

const getStyles$m = (colorTypeHover, colorTypeNormal, colorTypeActive) => {
  return StyleSheet.create({
    button: {
      color: colorTypeNormal,
      border: 'none',
      background: 'transparent',
      alignItems: 'center',
      cursor: 'pointer',
      ':hover': {
        color: colorTypeHover
      },
      ':active': {
        color: colorTypeActive
      }
    }
  });
};

const MoreButton$1 = props => {
  const {
    fontSizesStyles
  } = useTheme();
  const {
    moreButtonText,
    additionalStyle,
    refObject,
    fontSizeLarge,
    fontSizeMedium,
    fontSizeSmall,
    colorTypeHover,
    colorTypeNormal,
    dropdownIconColor,
    colorTypeActive,
    registerLinkWithSubMenu,
    index,
    isMenuOpen = false
  } = props;
  const styles = getStyles$m(colorTypeHover, colorTypeNormal, colorTypeActive);
  const fontStyle = getStyleBySize(fontSizesStyles, {
    fontSizeSmall,
    fontSizeMedium,
    fontSizeLarge
  });
  const triangleDirection = isMenuOpen ? Directions$1.UP : Directions$1.DOWN;
  useEffect(() => {
    if (typeof registerLinkWithSubMenu === 'function') {
      registerLinkWithSubMenu(refObject, index, true);
    }
  }, [registerLinkWithSubMenu, index, refObject]);
  return /*#__PURE__*/createElement("button", {
    ref: refObject,
    type: "button",
    className: css(styles.button, additionalStyle && additionalStyle.style, fontStyle)
  }, moreButtonText, /*#__PURE__*/createElement(TriangleIcon, {
    color: dropdownIconColor,
    direction: triangleDirection
  }));
};

const getLinkBackgroundOffset = subMenuState => {
  if (subMenuState.element && subMenuState.index >= 0) {
    const offset = subMenuState.element.getBoundingClientRect().left;
    return offset;
  }

  return 0;
};

const getStyles$n = (navHeightLarge, baseUnit, linkBackgroundColor, rowHeight, subMenuState) => {
  const backgroundWidth = subMenuState.element ? subMenuState.element.getBoundingClientRect().width : 0;
  const elementOffset = getLinkBackgroundOffset(subMenuState);
  return StyleSheet.create({
    fixedNav: {
      height: `${navHeightLarge}px`,
      [MEDIA_BREAKPOINTS.large]: {
        display: 'block'
      },
      [MEDIA_BREAKPOINTS.medium]: {
        display: 'none'
      },
      [MEDIA_BREAKPOINTS.small]: {
        display: 'none'
      }
    },
    navHiddenLayer: {
      overflow: 'hidden',
      height: `${navHeightLarge}px`
    },
    menuContainer: {
      position: 'relative'
    },
    fixedUl: {
      display: 'inline',
      marginLeft: `${baseUnit}px`,
      padding: 0
    },
    menuWrapper: {
      position: 'relative'
    },
    linkBackground: {
      position: 'absolute',
      display: elementOffset === 0 ? 'none' : 'block',
      backgroundColor: linkBackgroundColor,
      height: `${rowHeight * baseUnit}px`,
      top: 0,
      left: `${elementOffset - baseUnit / 2}px`,
      width: `${backgroundWidth + baseUnit}px`,
      [MEDIA_BREAKPOINTS.medium]: {
        display: 'none'
      },
      [MEDIA_BREAKPOINTS.small]: {
        display: 'none'
      }
    }
  });
};

const getLinksStyle$1 = baseUnit => StyleSheet.create({
  li: {
    display: 'inline-block',
    position: 'relative',
    whiteSpace: 'nowrap',
    ':not(:first-child)': {
      marginLeft: `${baseUnit}px`
    },
    ':last-child': {
      [MEDIA_BREAKPOINTS.medium]: {
        marginRight: `${baseUnit}px`
      },
      [MEDIA_BREAKPOINTS.small]: {
        marginRight: `${baseUnit}px`
      }
    },
    ':first-child': {
      [MEDIA_BREAKPOINTS.medium]: {
        marginLeft: `${baseUnit}px`
      },
      [MEDIA_BREAKPOINTS.small]: {
        marginLeft: `${baseUnit}px`
      }
    }
  }
});

const showMoreButtonStyle = (buttonOffset, shouldShowMoreButton) => {
  return StyleSheet.create({
    style: {
      [MEDIA_BREAKPOINTS.large]: {
        display: shouldShowMoreButton ? 'flex' : 'none',
        transform: `translateX(-${buttonOffset}px)`
      },
      [MEDIA_BREAKPOINTS.medium]: {
        display: 'none'
      },
      [MEDIA_BREAKPOINTS.small]: {
        display: 'none'
      }
    }
  });
};

const getMoreButton = (ref, text, style, fontSizeSmall, fontSizeMedium, fontSizeLarge, hoverColor, normalColor, activeColor, iconColor, registerLinkWithSubMenu) => (isMenuOpen, index) => {
  return /*#__PURE__*/createElement(MoreButton$1, {
    refObject: ref,
    moreButtonText: text,
    additionalStyle: style,
    fontSizeSmall: fontSizeSmall,
    fontSizeMedium: fontSizeMedium,
    fontSizeLarge: fontSizeLarge,
    colorTypeHover: hoverColor,
    colorTypeNormal: normalColor,
    colorTypeActive: activeColor,
    dropdownIconColor: iconColor,
    registerLinkWithSubMenu: registerLinkWithSubMenu,
    index: index,
    isMenuOpen: isMenuOpen
  });
};

const getLink = (hasSubMenu, currentLinkColor, activeStateColor, hoverStateColor, idleColor, linkDropdownIconColor, isActive, href, title, openLinkIndex, index, indexOfFirstInvisibleLink, linkFontSizeSmall, linkFontSizeMedium, linkFontSizeLarge, registerLinkWithSubMenu) => {
  const fontStyles = {
    linkFontSizeSmall,
    linkFontSizeMedium,
    linkFontSizeLarge
  };
  return hasSubMenu ? /*#__PURE__*/createElement(LinkWithSubMenu, _extends({
    currentLinkColor: currentLinkColor,
    activeStateColor: activeStateColor,
    title: title,
    hoverStateColor: hoverStateColor,
    idleColor: openLinkIndex === index ? hoverStateColor : idleColor,
    index: index,
    linkDropdownIconColor: linkDropdownIconColor
  }, fontStyles, {
    isInvisibleLink: indexOfFirstInvisibleLink < index || indexOfFirstInvisibleLink === index,
    isOpen: index === openLinkIndex,
    registerLinkWithSubMenu: registerLinkWithSubMenu
  })) : /*#__PURE__*/createElement(RegularHeaderLink, _extends({
    currentLinkColor: currentLinkColor,
    activeStateColor: activeStateColor,
    hoverStateColor: hoverStateColor,
    idleColor: idleColor,
    isActive: isActive,
    href: href,
    text: title
  }, fontStyles, {
    isInvisibleLink: indexOfFirstInvisibleLink < index || indexOfFirstInvisibleLink === index
  }));
};

const getLinkList = (props, baseUnit, buttonRef, moreButtonCreateMethod) => {
  const {
    links,
    pagePath,
    currentLinkColor,
    activeStateColor,
    hoverStateColor,
    idleColor,
    linkDropdownIconColor,
    linkFontSizeSmall,
    linkFontSizeMedium,
    linkFontSizeLarge,
    indexOfFirstInvisibleLink,
    subMenuState,
    registerLinkWithSubMenu
  } = props;
  if (!links) return [];
  const openLinkIndex = subMenuState.index;
  const isMoreLinksOpen = subMenuState.element === buttonRef.current;
  return links.map((link, index) => {
    const isActive = pagePath === link.link.href;
    const style = getLinksStyle$1(baseUnit);
    const hasSubMenu = (link.children || []).length > 0;
    const showMenu = index === indexOfFirstInvisibleLink;
    return /*#__PURE__*/createElement(Fragment, {
      key: index
    }, showMenu && /*#__PURE__*/createElement("li", {
      className: css(style.li)
    }, moreButtonCreateMethod(isMoreLinksOpen, index)), /*#__PURE__*/createElement("li", {
      className: css(style.li)
    }, getLink(hasSubMenu, currentLinkColor, activeStateColor, hoverStateColor, idleColor, linkDropdownIconColor, isActive, link.link.href, link.link.text, openLinkIndex, index, indexOfFirstInvisibleLink, linkFontSizeSmall, linkFontSizeMedium, linkFontSizeLarge, registerLinkWithSubMenu)));
  });
};

const FixedNav = props => {
  const {
    baseUnit
  } = useTheme();
  const {
    navHeightLarge,
    ulRef,
    rowHeight,
    subMenuState,
    linkBackgroundColor,
    linkFontSizeSmall,
    linkFontSizeMedium,
    linkFontSizeLarge,
    hoverStateColor,
    idleColor,
    activeStateColor,
    linkDropdownIconColor,
    moreButtonText,
    registerLinkWithSubMenu
  } = props;
  const buttonRef = useRef(null);
  const buttonStyle = showMoreButtonStyle(0, true);
  const moreButtonMethod = getMoreButton(buttonRef, moreButtonText, buttonStyle, linkFontSizeSmall, linkFontSizeMedium, linkFontSizeLarge, hoverStateColor, idleColor, activeStateColor, linkDropdownIconColor, registerLinkWithSubMenu);
  const styles = getStyles$n(navHeightLarge, baseUnit, linkBackgroundColor, rowHeight, subMenuState);
  return /*#__PURE__*/createElement(ErrorBoundary, null, /*#__PURE__*/createElement("div", {
    className: css(styles.linkBackground)
  }), /*#__PURE__*/createElement("nav", {
    className: css(styles.fixedNav)
  }, /*#__PURE__*/createElement("div", {
    className: css(styles.navHiddenLayer)
  }, /*#__PURE__*/createElement("ul", {
    className: css(styles.fixedUl),
    ref: ulRef
  }, getLinkList(props, baseUnit, buttonRef, moreButtonMethod)))));
};

const componentConfigThemeKey$T = 'socialLinks';

const getStyles$o = (numSocialLinks, verticalGapInLargeScreen, verticalGapInMediumScreen, reduceHeightAnimationDuration, menuAnimation, socialLinksColor, socialLinksHoverColor) => StyleSheet.create({
  wrapper: {
    gridTemplateColumns: `repeat(${numSocialLinks}, 1fr)`,
    alignItems: 'center',
    textAlign: 'center',
    [MEDIA_BREAKPOINTS.large]: {
      display: 'grid',
      paddingLeft: `${baseUnit * verticalGapInLargeScreen}px`,
      gridColumnGap: `${baseUnit * verticalGapInLargeScreen}px`
    },
    [MEDIA_BREAKPOINTS.medium]: {
      display: 'grid',
      paddingLeft: `${baseUnit * verticalGapInMediumScreen}px`,
      gridColumnGap: `${baseUnit * verticalGapInMediumScreen}px`
    },
    [MEDIA_BREAKPOINTS.small]: {
      display: 'none'
    }
  },
  img: {
    height: menuAnimation === AnimationDirection.UP ? `${baseUnit * 0.5}px` : `${baseUnit * 0.75}px`,
    transition: `${reduceHeightAnimationDuration}ms`
  },
  svg: {
    height: menuAnimation === AnimationDirection.UP ? `${baseUnit * 0.5}px` : `${baseUnit * 0.75}px`,
    transition: `${reduceHeightAnimationDuration}ms`,
    fill: socialLinksColor,
    ':hover': {
      fill: socialLinksHoverColor
    }
  }
});

const SocialLinks = props => {
  const {
    socialLinks,
    oldSocialSection,
    verticalGapInLargeScreen,
    verticalGapInMediumScreen,
    reduceHeightAnimationDuration,
    menuAnimation
  } = props;
  const {
    socialLinksColor,
    socialLinksHoverColor
  } = useTheme(componentConfigThemeKey$T); // eslint-disable-next-line no-nested-ternary

  const socialLinksLength = socialLinks ? socialLinks.length : oldSocialSection ? oldSocialSection.imageLinks.length : 0;
  const styles = getStyles$o(socialLinksLength, verticalGapInLargeScreen, verticalGapInMediumScreen, reduceHeightAnimationDuration, menuAnimation, socialLinksColor, socialLinksHoverColor);
  return socialLinks ? /*#__PURE__*/createElement("div", {
    className: css(styles.wrapper)
  }, socialLinks.map((link, index) => {
    const svg = link.src.replace(/<svg/g, `<svg class="${css(styles.svg)}"`);
    return /*#__PURE__*/createElement(Link, {
      href: link.href,
      key: index,
      rel: "noopener",
      target: "_blank",
      dangerouslySetInnerHTML: {
        __html: svg
      },
      alt: link.alt
    });
  })) : /*#__PURE__*/createElement("div", {
    className: css(styles.wrapper)
  }, oldSocialSection && oldSocialSection.imageLinks.map((link, index) => /*#__PURE__*/createElement(Link, {
    href: link.href,
    key: index,
    rel: "noopener",
    target: "_blank"
  }, /*#__PURE__*/createElement("img", {
    className: css(styles.img),
    src: link.src,
    alt: link.alt
  }))));
};

const createStyle$v = (editionsIconHoverColor, editionsIconNormalColor, baseUnit, menuAnimation) => {
  return StyleSheet.create({
    colors: {
      fill: editionsIconNormalColor,
      ':hover': {
        fill: editionsIconHoverColor
      }
    },
    svg: {
      height: menuAnimation === AnimationDirection.UP ? `${baseUnit * 0.5}px` : `${baseUnit * 0.75}px`,
      width: 'auto'
    }
  });
};

const EditionsIcon = props => {
  const {
    src,
    alt,
    editionsIconHoverColor,
    editionsIconNormalColor,
    menuAnimation
  } = props;
  const {
    baseUnit
  } = useTheme();
  const style = createStyle$v(editionsIconHoverColor, editionsIconNormalColor, baseUnit, menuAnimation);
  const svg = src.replace(/<svg/g, `<svg class="${css(style.svg, style.colors)}"`);
  return /*#__PURE__*/createElement("div", {
    "aria-label": alt,
    dangerouslySetInnerHTML: {
      __html: svg
    }
  });
};

const createStyles$9 = ({
  verticalGapInSmallScreen,
  verticalGapInMediumScreen,
  verticalGapInLargeScreen,
  colorTypeActive,
  colorTypeNormal,
  colorTypeHover,
  editionsSeparatorColor,
  editionsMenuBackgroundColor
}, baseUnit, editionsMenuToggle) => StyleSheet.create({
  wrapper: {
    position: 'relative',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    [MEDIA_BREAKPOINTS.large]: {
      marginLeft: `${verticalGapInLargeScreen * baseUnit}px`
    },
    [MEDIA_BREAKPOINTS.medium]: {
      marginLeft: `${verticalGapInMediumScreen * baseUnit}px`
    },
    [MEDIA_BREAKPOINTS.small]: {
      marginLeft: `${verticalGapInSmallScreen * baseUnit}px`
    }
  },
  separator: {
    backgroundColor: editionsSeparatorColor,
    height: '50%',
    width: '1px',
    [MEDIA_BREAKPOINTS.large]: {
      marginRight: `${verticalGapInLargeScreen * baseUnit}px`
    },
    [MEDIA_BREAKPOINTS.medium]: {
      marginRight: `${verticalGapInMediumScreen * baseUnit}px`
    },
    [MEDIA_BREAKPOINTS.small]: {
      display: 'none'
    }
  },
  button: {
    display: 'flex',
    color: colorTypeNormal,
    alignItems: 'center',
    cursor: 'pointer',
    ':hover': {
      color: colorTypeHover
    },
    ':active': {
      color: colorTypeActive
    }
  },
  link: {
    color: colorTypeNormal,
    ':hover': {
      color: colorTypeHover
    },
    ':active': {
      color: colorTypeActive
    }
  },
  ul: {
    backgroundColor: editionsMenuBackgroundColor,
    position: 'absolute',
    top: '100%',
    boxSizing: 'border-box',
    listStyle: 'none',
    padding: 0,
    margin: 0,
    display: editionsMenuToggle ? 'flex' : 'none',
    flexDirection: 'column',
    zIndex: 100,
    [MEDIA_BREAKPOINTS.large]: {
      width: `calc(${verticalGapInLargeScreen * baseUnit}px + 100%)`
    },
    [MEDIA_BREAKPOINTS.medium]: {
      width: `calc(${verticalGapInMediumScreen * baseUnit}px + 100%)`
    },
    [MEDIA_BREAKPOINTS.small]: {
      width: `calc(${verticalGapInSmallScreen * baseUnit}px + 100%)`
    }
  },
  li: {
    textAlign: 'center',
    marginBottom: `${baseUnit}px`,
    ':first-child': {
      marginTop: `${baseUnit}px`
    }
  }
});

const Editions$1 = props => {
  const {
    baseUnit,
    fontSizesStyles
  } = useTheme();
  const {
    editions,
    editionEndpoint,
    fontSizeSmall,
    fontSizeMedium,
    fontSizeLarge,
    dropdownIconColor,
    colorTypeHover,
    editionsIcon,
    editionsIconNormalColor,
    editionsIconHoverColor,
    menuAnimation
  } = props;
  const [editionsMenuToggle, setEditionsMenuToggle] = useState(false);
  const styles = createStyles$9(props, baseUnit, editionsMenuToggle);
  const activeEdition = getActiveEdition(editions, editionEndpoint);
  const fontStyle = getStyleBySize(fontSizesStyles, {
    fontSizeSmall,
    fontSizeMedium,
    fontSizeLarge
  });

  const scrollHandler = () => {
    if (editionsMenuToggle) {
      setEditionsMenuToggle(false);
    }
  };

  useEffect(() => {
    window.addEventListener('scroll', scrollHandler);
    return () => window.removeEventListener('scroll', scrollHandler);
  });

  const createEditionLink = (edition, index) => /*#__PURE__*/createElement("li", {
    className: css(styles.li),
    key: index
  }, /*#__PURE__*/createElement(Link, {
    href: edition.link.href,
    className: css(styles.link, fontStyle)
  }, edition.link.text));

  const EditionsMenu = () => /*#__PURE__*/createElement("ul", {
    className: css(styles.ul)
  }, editions.map(createEditionLink));

  return /*#__PURE__*/createElement("div", {
    className: css(styles.wrapper)
  }, /*#__PURE__*/createElement("div", {
    className: css(styles.separator)
  }), /*#__PURE__*/createElement("button", {
    type: "button",
    className: css(styles.button, fontStyle),
    onClick: () => setEditionsMenuToggle(!editionsMenuToggle)
  }, editionsIcon ? /*#__PURE__*/createElement(EditionsIcon, _extends({}, editionsIcon, {
    editionsIconNormalColor: editionsIconNormalColor,
    editionsIconHoverColor: editionsIconHoverColor,
    menuAnimation: menuAnimation
  })) : activeEdition.link.text, /*#__PURE__*/createElement(TriangleIcon, {
    direction: editionsMenuToggle ? Directions$1.UP : Directions$1.DOWN,
    color: dropdownIconColor,
    hoverColor: colorTypeHover
  })), /*#__PURE__*/createElement(EditionsMenu, null));
};

const subMenuReducerInitialState = {
  links: null,
  element: null,
  index: -1,
  linkFontSize: null,
  backgroundColor: ''
};
const subMenuReducer = (state, action) => {
  if (action.data.element === state.element) {
    return {
      links: null,
      element: null,
      index: -1,
      linkFontSize: null,
      backgroundColor: ''
    };
  }

  return action.data;
};
const useSubMenu = () => {
  return React__default.useReducer(subMenuReducer, subMenuReducerInitialState);
};

const MAX_NUM_ROWS = 7;

const getStyles$p = (backgroundColor, baseUnit, offset, numMenuRows, minWidth, shouldOffsetLeft, animationDuration, menuFoldState) => {
  return StyleSheet.create({
    ul: {
      position: 'absolute',
      left: shouldOffsetLeft ? 0 : `${offset - baseUnit / 2}px`,
      transition: `top ${animationDuration}ms ease 0s`,
      top: menuFoldState === AnimationDirection.UP ? `${baseUnit * 2 * reducedHeaderFirstRowHeightFactors.large}px` : `${baseUnit * 2}px`,
      backgroundColor,
      listStyle: 'none',
      padding: `${baseUnit / 2}px`,
      display: 'grid',
      gridRowGap: `${baseUnit / 2}px`,
      gridColumnGap: `${baseUnit * 4}px`,
      gridAutoFlow: 'column',
      gridTemplateRows: `repeat(${numMenuRows}, 1fr)`,
      minWidth: `${minWidth}px`,
      transform: shouldOffsetLeft ? 'translateX(-100%)' : ''
    },
    li: {
      padding: 0,
      position: 'relative'
    },
    link: {
      whiteSpace: 'nowrap',
      cursor: 'pointer'
    }
  });
};

const getOpenListItemStyle = (isSubMenuOpen, activeMenuBackgroundColor, baseUnit) => {
  if (!isSubMenuOpen) return null;
  return StyleSheet.create({
    li: {
      position: 'relative',
      ':before': {
        content: isSubMenuOpen ? "''" : '',
        backgroundColor: activeMenuBackgroundColor,
        position: 'absolute',
        top: `-${baseUnit / 2}px`,
        left: `-${baseUnit / 2}px`,
        bottom: `-${baseUnit / 2}px`,
        right: `-${baseUnit / 2}px`,
        zIndex: 0
      }
    }
  }).li;
};

const getMenuOffset = parentElement => {
  const offset = parentElement.getBoundingClientRect().left;
  return offset;
};

const getMenuMinWidth = parentElement => {
  return parentElement.getBoundingClientRect().width;
};

const getWrapperStyle$2 = (element, baseUnit) => {
  return StyleSheet.create({
    subMenuWrapper: {
      position: 'absolute',
      left: `-${baseUnit / 2}px`,
      top: `-${baseUnit * 2.5}px`
    }
  });
};

const SubMenu = props => {
  const {
    links,
    parentElement,
    activeStateColor,
    hoverStateColor,
    idleColor,
    currentLinkColor,
    linkFontSize,
    backgroundColor,
    pagePath,
    shouldOffsetLeft,
    animationDuration,
    menuFoldState,
    openLinkIndex,
    innerLinkFontSize,
    innerLinksBackgroundColor,
    linkDropdownIconColor
  } = props;
  const {
    baseUnit,
    fontSizesStyles
  } = useTheme();
  const [subMenuState, dispatch] = useSubMenu();
  const offset = getMenuOffset(parentElement);
  const numMenuRows = Math.min(links.length, MAX_NUM_ROWS);
  const minWidth = getMenuMinWidth(parentElement);
  const styles = getStyles$p(backgroundColor, baseUnit, offset, numMenuRows, minWidth, shouldOffsetLeft, animationDuration, menuFoldState);
  const fontStyle = getStyleBySize(fontSizesStyles, {
    fontSizeSmall: linkFontSize,
    fontSizeMedium: linkFontSize,
    fontSizeLarge: linkFontSize
  });
  const isMenuOpen = subMenuState.links && subMenuState.links.length > 0;
  const ulRef = useRef(null);

  const onHoverLink = (element, index) => {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const menuLinks = links[index].children;
    dispatch({
      data: {
        links: menuLinks,
        element,
        index,
        linkFontSize: innerLinkFontSize || linkFontSize,
        backgroundColor: innerLinksBackgroundColor || backgroundColor
      }
    });
  };

  const onLeaveLink = () => {
    dispatch({
      data: {
        links: null,
        element: null,
        index: -1,
        linkFontSize: innerLinkFontSize || linkFontSize,
        backgroundColor: innerLinksBackgroundColor || backgroundColor
      }
    });
  };

  return /*#__PURE__*/createElement(Fragment, null, /*#__PURE__*/createElement("ul", {
    className: css(styles.ul),
    ref: ulRef
  }, links.map((navigationLink, index) => {
    const isLinkActive = pagePath === navigationLink.link.href;
    const isLinkWithSubMenu = navigationLink.children !== null && navigationLink.children.length > 0;
    return /*#__PURE__*/createElement("li", {
      key: index,
      className: css(getOpenListItemStyle(subMenuState.index === index, innerLinksBackgroundColor || backgroundColor, baseUnit)),
      onMouseEnter: e => isLinkWithSubMenu && onHoverLink(e.target, index),
      onMouseLeave: onLeaveLink
    }, isLinkWithSubMenu ? /*#__PURE__*/createElement(Fragment, null, isMenuOpen && subMenuState.index === index &&
    /*#__PURE__*/
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    createElement("div", {
      className: css(getWrapperStyle$2(subMenuState.element, baseUnit).subMenuWrapper)
    }, /*#__PURE__*/createElement(SubMenu, {
      activeStateColor: activeStateColor,
      hoverStateColor: hoverStateColor,
      idleColor: idleColor,
      backgroundColor: innerLinksBackgroundColor || backgroundColor,
      linkFontSize: innerLinkFontSize || linkFontSize,
      pagePath: pagePath // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      ,
      links: subMenuState.links // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      ,
      parentElement: subMenuState.element,
      menuFoldState: menuFoldState,
      animationDuration: animationDuration,
      shouldOffsetLeft: true,
      currentLinkColor: currentLinkColor,
      openLinkIndex: subMenuState.index,
      linkDropdownIconColor: linkDropdownIconColor
    })), /*#__PURE__*/createElement(LinkWithSubMenu, {
      title: navigationLink.link.text,
      hoverStateColor: hoverStateColor,
      idleColor: openLinkIndex === index ? hoverStateColor : idleColor,
      index: index,
      linkDropdownIconColor: linkDropdownIconColor,
      linkFontSizeLarge: linkFontSize,
      linkFontSizeMedium: linkFontSize,
      linkFontSizeSmall: linkFontSize,
      isOpen: subMenuState.index === index,
      activeStateColor: activeStateColor,
      currentLinkColor: currentLinkColor
    })) : /*#__PURE__*/createElement(Link, {
      href: navigationLink.link.href,
      className: css(styles.link)
    }, /*#__PURE__*/createElement(Span, {
      className: css(fontStyle, getLinkColorsStyle(activeStateColor, hoverStateColor, idleColor, currentLinkColor, isLinkActive).link)
    }, navigationLink.link.text)));
  })));
};

const getLinkType = type => {
  return type === 'button' ? 'button' : 'link';
};

const initialState = [];

const reducer = (state, action) => {
  if (action) {
    switch (action.type) {
      case 'addLink':
        return [...state, action.data];

      case 'addButton':
        {
          const buttonIndex = state.findIndex(link => link.ref.current === action.data.ref.current);

          if (buttonIndex < 0) {
            return [...state, action.data];
          }

          if (state[buttonIndex].index !== action.data.index) {
            return [...state.slice(0, buttonIndex), action.data, ...state.slice(buttonIndex + 1)];
          }

          return state;
        }

      default:
        return state;
    }
  }

  return state;
};

const useLinksWithSubMenuHover = (onHoverLink, ulRef, firstRowHeightInLargeScreen, baseUnit) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const registerLinkWithSubMenu = (ref, index, isButton = false) => {
    if (ref.current === null) return;
    const data = {
      ref,
      index,
      type: isButton ? getLinkType('button') : getLinkType('link')
    };

    if (!isButton) {
      if (!state.find(element => element.ref.current === ref.current)) {
        dispatch({
          data,
          type: 'addLink'
        });
      }
    } else {
      dispatch({
        data,
        type: 'addButton'
      });
    }
  };

  const getHoverPlaceholders = () => {
    return state.filter(trigger => {
      const {
        ref
      } = trigger;
      if (!ref.current || !ulRef.current) return false;
      const visibleArea = ulRef.current.getBoundingClientRect().top + 5;
      if (trigger.type !== 'button' && ref.current.getBoundingClientRect().top > visibleArea) return false;
      return true;
    }).map((trigger, index) => {
      const {
        ref
      } = trigger; // eslint-disable-next-line @typescript-eslint/no-non-null-assertion

      const rect = ref.current.getBoundingClientRect();
      const {
        style
      } = StyleSheet.create({
        style: {
          position: 'absolute',
          width: `${rect.width + baseUnit}px`,
          height: `${firstRowHeightInLargeScreen * baseUnit}px`,
          top: `-${firstRowHeightInLargeScreen * baseUnit / 2}px`,
          left: `${rect.left - baseUnit / 2}px`,
          cursor: 'pointer'
        }
      }); // eslint-disable-next-line @typescript-eslint/no-non-null-assertion

      const linkActualIndex = state.find(element => element.ref === ref).index; // eslint-disable-next-line @typescript-eslint/no-non-null-assertion

      return /*#__PURE__*/createElement("div", {
        className: css(style),
        key: index,
        onMouseEnter: () => onHoverLink(ref.current, linkActualIndex, trigger.type)
      });
    });
  };

  return [registerLinkWithSubMenu, getHoverPlaceholders];
};

const getStyles$q = (themeProps, menuFoldState, animationDuration) => {
  const {
    firstRowBackgroundColor,
    baseUnit,
    firstRowHeightInLargeScreen,
    firstRowHeightInMediumScreen,
    firstRowHeightInSmallScreen,
    verticalGapInLargeScreen,
    verticalGapInMediumScreen,
    verticalGapInSmallScreen,
    fontSizesStyles,
    linkFontSizeLarge,
    firstRowBoxShadow
  } = themeProps;
  const largeScreenBaseUnitFactored = menuFoldState === AnimationDirection.UP ? firstRowHeightInLargeScreen * reducedHeaderFirstRowHeightFactors.large * baseUnit + baseUnit : firstRowHeightInLargeScreen * baseUnit;
  return StyleSheet.create({
    headerFirstRow: {
      boxShadow: firstRowBoxShadow,
      backgroundColor: firstRowBackgroundColor,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      zIndex: 100,
      position: 'relative',
      transition: `height ${animationDuration}ms ease 0s`,
      [MEDIA_BREAKPOINTS.large]: {
        height: menuFoldState === AnimationDirection.UP ? `${firstRowHeightInLargeScreen * reducedHeaderFirstRowHeightFactors.large * baseUnit}px` : `${firstRowHeightInLargeScreen * baseUnit}px`,
        padding: `0 max(${baseUnit * verticalGapInLargeScreen}px, calc((100% - ${HUGE_SCREEN_SIZE}px) / 2))`
      },
      [MEDIA_BREAKPOINTS.medium]: {
        height: `${firstRowHeightInMediumScreen * baseUnit}px`,
        padding: `0 ${baseUnit * verticalGapInMediumScreen}px`,
        justifyContent: 'space-between'
      },
      [MEDIA_BREAKPOINTS.small]: {
        height: `${firstRowHeightInSmallScreen * baseUnit}px`,
        padding: `0 ${baseUnit * verticalGapInSmallScreen}px`,
        justifyContent: 'space-between'
      }
    },
    headerFirstRowItemsContainer: {
      display: 'flex',
      alignItems: 'center',
      height: '100%'
    },
    fixedNav: {
      [MEDIA_BREAKPOINTS.large]: {
        height: `${getLineHeightBySize(fontSizesStyles, linkFontSizeLarge, 'large')}px`,
        overflow: 'hidden',
        display: 'block'
      },
      [MEDIA_BREAKPOINTS.medium]: {
        display: 'none'
      },
      [MEDIA_BREAKPOINTS.small]: {
        display: 'none'
      }
    },
    subMenuWrapper: {
      position: 'absolute',
      left: 0,
      top: `${largeScreenBaseUnitFactored - baseUnit * 2}px`,
      [MEDIA_BREAKPOINTS.medium]: {
        display: 'none'
      },
      [MEDIA_BREAKPOINTS.small]: {
        display: 'none'
      }
    }
  });
};

const createMenuStyle = (baseUnit, verticalGapInMediumScreen, verticalGapInSmallScreen) => {
  return StyleSheet.create({
    style: {
      [MEDIA_BREAKPOINTS.large]: {
        display: 'none'
      },
      [MEDIA_BREAKPOINTS.medium]: {
        display: 'inline-flex',
        marginRight: `${baseUnit * verticalGapInMediumScreen}px`
      },
      [MEDIA_BREAKPOINTS.small]: {
        display: 'inline-flex',
        marginRight: `${baseUnit * verticalGapInSmallScreen}px`
      }
    }
  });
};

const getMoreMenuLinks = (invisibleLinks, links) => {
  const numLinks = invisibleLinks.length;
  return links.slice(links.length - numLinks);
};

const isSubMenuOpen = subMenuState => {
  return subMenuState.element !== null;
};

const getInvisibleLinks = parent => {
  if (!parent) return [];
  const safetyBuffer = 5;
  const baseline = parent.getBoundingClientRect().top + safetyBuffer;
  return Array.from(parent.childNodes).filter(child => {
    return child.getBoundingClientRect().top > baseline;
  });
};

const RowWithFixedNav = props => {
  const {
    videoLogo,
    logo,
    links,
    moreButtonText,
    socialSection,
    oldSocialSection,
    pagePath,
    menu,
    editions,
    editionEndpoint,
    editionsIcon
  } = props;
  const themeProps = useTheme(componentConfigThemeKey$b);
  const {
    menuFoldState,
    animationDuration
  } = React__default.useContext(HeaderAnimationContext);
  const {
    firstRowLinkColorTypeActive,
    firstRowLinkColorTypeNormal,
    firstRowLinkColorTypeHover,
    firstRowLinkCurrentColor,
    baseUnit,
    linkFontSizeLarge,
    linkFontSizeMedium,
    linkFontSizeSmall,
    logoHeightInLargeScreen,
    logoHeightInMediumScreen,
    logoHeightInSmallScreen,
    verticalGapInLargeScreen,
    verticalGapInMediumScreen,
    verticalGapInSmallScreen,
    linkDropdownIconColor,
    firstRowHeightInLargeScreen,
    firstRowHeightInMediumScreen,
    firstRowHeightInSmallScreen,
    subMenuBackgroundColor,
    editionsSeparatorColor,
    editionsMenuBackgroundColor,
    subMenuLinksFontSize,
    editionsIconHoverColor,
    editionsIconNormalColor,
    fontSizesStyles,
    firstRowBackgroundColor
  } = themeProps;
  const navHeightLarge = getLineHeightBySize(fontSizesStyles, linkFontSizeLarge, 'large');
  const styles = getStyles$q(themeProps, menuFoldState, animationDuration);
  const menuStyle = createMenuStyle(baseUnit, verticalGapInMediumScreen, verticalGapInSmallScreen);
  const ulRef = React__default.useRef(null);
  const [subMenuState, dispatchMenuState] = useSubMenu();
  const [invisibleLinks, setInvisibleLinks] = React__default.useState([]);
  const webFontsContext = React__default.useContext(WebFontsContext);

  const closeSubMenu = () => {
    dispatchMenuState({
      data: {
        element: null,
        links: null,
        index: -1,
        linkFontSize: null,
        backgroundColor: ''
      }
    });
  };

  React__default.useEffect(() => {
    if (ulRef.current !== null) {
      if (menuFoldState === AnimationDirection.NONE) {
        const newInvisibleLinksList = getInvisibleLinks(ulRef.current);

        if (newInvisibleLinksList.length !== invisibleLinks.length) {
          setInvisibleLinks(newInvisibleLinksList);
        }
      }
    }
  }, [ulRef, webFontsContext, baseUnit, verticalGapInLargeScreen, menuFoldState, invisibleLinks]);
  React__default.useEffect(() => {
    window.addEventListener('scroll', closeSubMenu);
    return () => window.removeEventListener('scroll', closeSubMenu);
  });
  const shouldEditions = editions && editions.length > 0;
  const factoredFirstRowHeight = menuFoldState === AnimationDirection.UP ? firstRowHeightInLargeScreen * reducedHeaderFirstRowHeightFactors.large : firstRowHeightInLargeScreen;
  const shouldShowSocialSection = socialSection && socialSection.length > 0;
  const shouldShowSocial = shouldShowSocialSection || oldSocialSection && oldSocialSection.imageLinks.length > 0;

  const toggleSubMenu = (element, navigationLinks, index, linkFontSize, backgroundColor) => {
    if (navigationLinks) {
      dispatchMenuState({
        data: {
          element,
          links: navigationLinks,
          index,
          linkFontSize,
          backgroundColor
        }
      });
    }
  };

  const onToggleFixedNavMenu = (element, index, type) => {
    if (type === 'button') {
      const moreButtonLinks = getMoreMenuLinks(invisibleLinks, links);
      toggleSubMenu(element, moreButtonLinks, -1, linkFontSizeLarge, firstRowBackgroundColor);
    } else {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const fixedNavLinks = links[index].children;
      toggleSubMenu(element, fixedNavLinks, index, subMenuLinksFontSize, subMenuBackgroundColor);
    }
  };

  const [registerLinkWithSubMenu, getHoverPlaceholders] = useLinksWithSubMenuHover(onToggleFixedNavMenu, ulRef, factoredFirstRowHeight, baseUnit);
  const indexOfFirstInvisibleLink = links.length - invisibleLinks.length;
  return /*#__PURE__*/React__default.createElement("div", {
    className: css(styles.headerFirstRow)
  }, /*#__PURE__*/React__default.createElement("div", {
    className: css(styles.headerFirstRowItemsContainer)
  }, menu ? /*#__PURE__*/React__default.createElement(Menu$1, {
    links: menu.links,
    buttons: menu.buttons,
    additionalStyle: menuStyle,
    oldSocialSection: menu.oldSocialSection,
    socialSection: menu.socialSection
  }) : null, videoLogo ? /*#__PURE__*/React__default.createElement(VideoLogo$1, _extends({}, videoLogo, {
    href: logo.href,
    firstRowHeightInSmallScreen: firstRowHeightInSmallScreen,
    firstRowHeightInMediumScreen: firstRowHeightInMediumScreen,
    firstRowHeightInLargeScreen: firstRowHeightInLargeScreen,
    menuAnimation: menuFoldState,
    reduceHeightAnimationDuration: animationDuration
  })) : /*#__PURE__*/React__default.createElement(Logo, _extends({}, logo, {
    logoHeightInLargeScreen: logoHeightInLargeScreen,
    logoHeightInMediumScreen: logoHeightInMediumScreen,
    logoHeightInSmallScreen: logoHeightInSmallScreen,
    menuAnimation: menuFoldState,
    reduceHeightAnimationDuration: animationDuration
  })), /*#__PURE__*/React__default.createElement(FixedNav, {
    rowHeight: firstRowHeightInLargeScreen,
    navHeightLarge: navHeightLarge,
    ulRef: ulRef,
    links: links,
    pagePath: pagePath,
    activeStateColor: firstRowLinkColorTypeActive,
    hoverStateColor: firstRowLinkColorTypeHover,
    idleColor: firstRowLinkColorTypeNormal,
    linkFontSizeLarge: linkFontSizeLarge,
    linkFontSizeMedium: linkFontSizeMedium,
    linkFontSizeSmall: linkFontSizeSmall,
    currentLinkColor: firstRowLinkCurrentColor,
    linkDropdownIconColor: linkDropdownIconColor,
    subMenuState: subMenuState,
    indexOfFirstInvisibleLink: indexOfFirstInvisibleLink,
    linkBackgroundColor: subMenuState.backgroundColor,
    moreButtonText: moreButtonText,
    registerLinkWithSubMenu: registerLinkWithSubMenu
  })), /*#__PURE__*/React__default.createElement("div", {
    className: css(styles.headerFirstRowItemsContainer)
  }, shouldShowSocial && /*#__PURE__*/React__default.createElement(SocialLinks, {
    socialLinks: socialSection,
    oldSocialSection: oldSocialSection,
    verticalGapInLargeScreen: verticalGapInLargeScreen,
    verticalGapInMediumScreen: verticalGapInMediumScreen,
    menuAnimation: menuFoldState,
    reduceHeightAnimationDuration: animationDuration
  }), shouldEditions && /*#__PURE__*/React__default.createElement(Editions$1 // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  , {
    editions: editions,
    editionEndpoint: editionEndpoint,
    verticalGapInLargeScreen: verticalGapInLargeScreen,
    verticalGapInMediumScreen: verticalGapInMediumScreen,
    verticalGapInSmallScreen: verticalGapInSmallScreen,
    fontSizeSmall: linkFontSizeSmall,
    fontSizeMedium: linkFontSizeMedium,
    fontSizeLarge: linkFontSizeLarge,
    colorTypeHover: firstRowLinkColorTypeHover,
    colorTypeNormal: firstRowLinkColorTypeNormal,
    colorTypeActive: firstRowLinkColorTypeActive,
    dropdownIconColor: linkDropdownIconColor,
    editionsSeparatorColor: editionsSeparatorColor,
    editionsMenuBackgroundColor: editionsMenuBackgroundColor,
    editionsIcon: editionsIcon,
    editionsIconHoverColor: editionsIconHoverColor,
    editionsIconNormalColor: editionsIconNormalColor,
    menuAnimation: menuFoldState
  })), /*#__PURE__*/React__default.createElement("div", {
    className: css(styles.subMenuWrapper),
    onMouseLeave: closeSubMenu
  }, getHoverPlaceholders(), isSubMenuOpen(subMenuState) && /*#__PURE__*/React__default.createElement(SubMenu, {
    menuFoldState: menuFoldState,
    animationDuration: animationDuration // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    ,
    links: subMenuState.links // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    ,
    parentElement: subMenuState.element,
    activeStateColor: firstRowLinkColorTypeActive,
    hoverStateColor: firstRowLinkColorTypeHover,
    idleColor: firstRowLinkColorTypeNormal,
    currentLinkColor: firstRowLinkCurrentColor,
    pagePath: pagePath // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    ,
    linkFontSize: subMenuState.linkFontSize,
    innerLinkFontSize: subMenuLinksFontSize,
    backgroundColor: subMenuState.backgroundColor,
    innerLinksBackgroundColor: subMenuBackgroundColor,
    shouldOffsetLeft: false,
    openLinkIndex: subMenuState.index,
    linkDropdownIconColor: linkDropdownIconColor
  })));
};

const getHeaderStyles = () => {
  return StyleSheet.create({
    header: {
      position: 'fixed',
      width: '100%',
      zIndex: 1500,
      top: 0
    }
  });
};

const createHeaderStyleWithAnimation = animationDuration => {
  return StyleSheet.create({
    foldingUp: {
      [MEDIA_BREAKPOINTS.medium]: {
        transition: `${animationDuration}ms`,
        transform: 'translateY(-100%)',
        transitionDelay: `${animationDuration}ms`
      },
      [MEDIA_BREAKPOINTS.small]: {
        transition: `${animationDuration}ms`,
        transform: 'translateY(-100%)',
        transitionDelay: `${animationDuration}ms`
      }
    },
    foldingDown: {
      [MEDIA_BREAKPOINTS.medium]: {
        transform: 'translateY(0)'
      },
      [MEDIA_BREAKPOINTS.small]: {
        transform: 'translateY(0)'
      }
    }
  });
};

const getInvisibleLinks$1 = parent => {
  if (!parent) return [];
  const safetyBuffer = 5;
  const baseline = parent.getBoundingClientRect().top + safetyBuffer;
  return Array.from(parent.childNodes).filter(child => {
    return child.getBoundingClientRect().top > baseline;
  });
};

const getHeaderStyleWithAnimation = (menuFoldState, animationDuration) => {
  const style = createHeaderStyleWithAnimation(animationDuration);
  return menuFoldState === AnimationDirection.UP ? style.foldingUp : style.foldingDown;
};

const getChildrenByIndex = (links, index) => {
  if (!links || !links[index] || !links[index].children) return [];
  return links[index].children || [];
};

const HeaderWithSubMenusComponent = props => {
  const {
    links,
    pagePath
  } = props;
  const {
    menuFoldState,
    animationDuration
  } = useContext(HeaderAnimationContext);
  const themeProps = useTheme(componentConfigThemeKey$b);
  const {
    secondRowLinkColorTypeActive,
    secondRowLinkColorTypeHover,
    secondRowLinkColorTypeNormal,
    secondRowLinkCurrentColor,
    baseUnit,
    linkFontSizeLarge,
    linkFontSizeMedium,
    linkFontSizeSmall,
    verticalGapInLargeScreen,
    verticalGapInMediumScreen,
    verticalGapInSmallScreen,
    linkDropdownIconColor,
    firstRowHeightInMediumScreen,
    firstRowHeightInSmallScreen,
    subMenuBackgroundColor,
    secondRowBackgroundColor,
    secondRowHeightInMediumScreen,
    secondRowHeightInSmallScreen,
    subMenuLinksFontSize,
    secondRowBoxShadow
  } = themeProps;
  const ulRef = useRef(null);
  const [invisibleLinks, setInvisibleLinks] = useState([]);
  const [scrollableMenuIndex, setScrollableMenuIndex] = useState(-1);
  const headerStyles = getHeaderStyles();
  const webFontsContext = useContext(WebFontsContext);
  useEffect(() => {
    if (ulRef.current !== null) {
      if (menuFoldState === AnimationDirection.NONE) {
        const newInvisibleLinksList = getInvisibleLinks$1(ulRef.current);

        if (newInvisibleLinksList.length !== invisibleLinks.length) {
          setInvisibleLinks(newInvisibleLinksList);
        }
      }
    }
  }, [ulRef, webFontsContext, baseUnit, verticalGapInLargeScreen, menuFoldState, invisibleLinks]);

  const closeSubMenu = () => {
    setScrollableMenuIndex(-1);
  };

  useEffect(() => {
    window.addEventListener('scroll', closeSubMenu);
    return () => window.removeEventListener('scroll', closeSubMenu);
  });

  const onScrollableNavMenuToggle = (element, index) => {
    if (!links) return;

    if (index !== scrollableMenuIndex) {
      setScrollableMenuIndex(index);
    } else {
      setScrollableMenuIndex(-1);
    }
  };

  return /*#__PURE__*/createElement(Fragment, null, /*#__PURE__*/createElement("header", {
    className: css(getHeaderStyleWithAnimation(menuFoldState, animationDuration), headerStyles.header)
  }, /*#__PURE__*/createElement(RowWithFixedNav, props), /*#__PURE__*/createElement(ScrollableNav, {
    menuFoldState: menuFoldState,
    animationDuration: animationDuration,
    links: links,
    linkColorTypeActive: secondRowLinkColorTypeActive,
    linkColorTypeHover: secondRowLinkColorTypeHover,
    linkColorTypeNormal: secondRowLinkColorTypeNormal,
    currentLinkColor: secondRowLinkCurrentColor,
    pagePath: pagePath,
    baseUnit: baseUnit,
    linkFontSizeLarge: linkFontSizeLarge,
    linkFontSizeMedium: linkFontSizeMedium,
    linkFontSizeSmall: linkFontSizeSmall,
    verticalGapInLargeScreen: verticalGapInLargeScreen,
    verticalGapInMediumScreen: verticalGapInMediumScreen,
    verticalGapInSmallScreen: verticalGapInSmallScreen,
    secondRowBackgroundColor: secondRowBackgroundColor,
    secondRowHeightInMediumScreen: secondRowHeightInMediumScreen,
    secondRowHeightInSmallScreen: secondRowHeightInSmallScreen,
    linkDropdownIconColor: linkDropdownIconColor,
    openLinkIndex: scrollableMenuIndex,
    onMenuToggle: onScrollableNavMenuToggle,
    boxShadow: secondRowBoxShadow
  })), scrollableMenuIndex >= 0 && /*#__PURE__*/createElement(ScrollableNavDropdown, {
    firstRowHeightInMediumScreen: firstRowHeightInMediumScreen,
    firstRowHeightInSmallScreen: firstRowHeightInSmallScreen,
    secondRowHeightInMediumScreen: secondRowHeightInMediumScreen,
    secondRowHeightInSmallScreen: secondRowHeightInSmallScreen,
    backgroundColor: subMenuBackgroundColor,
    links: getChildrenByIndex(links, scrollableMenuIndex),
    pagePath: pagePath,
    linkColorTypeActive: secondRowLinkColorTypeActive,
    linkColorTypeHover: secondRowLinkColorTypeHover,
    linkColorTypeNormal: secondRowLinkColorTypeNormal,
    currentLinkColor: secondRowLinkCurrentColor,
    linkFontSize: subMenuLinksFontSize
  }));
};
const HeaderWithSubMenus = props => {
  const headerHeights = useHeaderHeights();
  return /*#__PURE__*/createElement(ErrorBoundary, null, /*#__PURE__*/createElement(HeaderAnimation, {
    headerHeights: headerHeights
  }, /*#__PURE__*/createElement(HeaderWithSubMenusComponent, props)));
};

const RecommendationsAdvertisement = props => {
  const {
    revcontentWidgetID
  } = props;

  const createStyle = baseUnit => {
    return StyleSheet.create({
      style: {
        paddingTop: `${baseUnit * 2}px`,
        paddingLeft: `${baseUnit}px`,
        paddingRight: `${baseUnit}px`,
        [MEDIA_BREAKPOINTS.large]: {
          paddingLeft: `${baseUnit + 80}px`
        }
      }
    });
  };

  const {
    baseUnit
  } = useTheme();
  const style = createStyle(baseUnit);
  return /*#__PURE__*/React__default.createElement(ErrorBoundary, null, /*#__PURE__*/React__default.createElement("div", {
    className: css(style.style)
  }, /*#__PURE__*/React__default.createElement("div", {
    id: "rc-widget-3d8993",
    "data-rc-widget": true,
    "data-widget-host": "habitat",
    "data-endpoint": "//trends.revcontent.com",
    "data-widget-id": revcontentWidgetID
  })));
};

const componentConfigThemeKey$U = 'primarySectionTitle';
const PrimarySectionTitle = props => {
  const {
    children
  } = props;
  const theme = useTheme(componentConfigThemeKey$U);
  return /*#__PURE__*/createElement(H1, theme, children);
};

const componentConfigThemeKey$V = 'secondarySectionTitle';
const SecondarySectionTitle = props => {
  const {
    children
  } = props;
  const theme = useTheme(componentConfigThemeKey$V);
  return /*#__PURE__*/createElement(ErrorBoundary, null, /*#__PURE__*/createElement(H2, theme, children));
};

const componentConfigThemeKey$W = 'fourVC';

const createStyle$w = themeProps => {
  const {
    largeHorizontalGap,
    largeVerticalGap,
    mediumHorizontalGap,
    mediumVerticalGap,
    smallHorizontalGap,
    baseUnit
  } = themeProps;
  return StyleSheet.create({
    wrapper: {
      display: 'grid',
      [MEDIA_BREAKPOINTS.large]: {
        gridTemplateColumns: '1fr 1fr',
        gridTemplateRows: 'auto',
        gridRowGap: `${largeHorizontalGap * baseUnit}px`,
        gridColumnGap: `${largeVerticalGap * baseUnit}px`
      },
      [MEDIA_BREAKPOINTS.medium]: {
        gridTemplateColumns: '1fr 1fr',
        gridTemplateRows: 'auto',
        gridRowGap: `${mediumHorizontalGap * baseUnit}px`,
        gridColumnGap: `${mediumVerticalGap * baseUnit}px`
      },
      [MEDIA_BREAKPOINTS.small]: {
        gridTemplateColumns: '1fr',
        gridTemplateRows: 'auto',
        gridRowGap: `${smallHorizontalGap * baseUnit}px`
      }
    }
  });
};

const imageWidths$b = createImageWidths(360, 360, 540);
const FourVC = props => {
  const {
    cards,
    paddingFactors
  } = props;
  const paddingStyle = getLayoutComponentPadding(paddingFactors);
  const themeProps = useTheme(componentConfigThemeKey$W);
  const style = createStyle$w(themeProps);
  const themeContextVC = useThemeOverride({
    titleFontSizeLarge: 'large',
    titleFontSizeMedium: 'bigger',
    titleFontSizeSmall: 'large',
    metadataFontSizeLarge: 'bigger',
    metadataFontSizeMedium: 'big',
    metadataFontSizeSmall: 'bigger'
  }, componentConfigThemeKey$5);
  return /*#__PURE__*/createElement(ErrorBoundary, null, cards.length > 0 && /*#__PURE__*/createElement("div", {
    className: css(paddingStyle, style.wrapper)
  }, /*#__PURE__*/createElement(ThemeContext.Provider, {
    value: themeContextVC
  }, /*#__PURE__*/createElement(VerticalCard, _extends({}, cards[0], {
    imageWidths: imageWidths$b
  })), /*#__PURE__*/createElement(VerticalCard, _extends({}, cards[1], {
    imageWidths: imageWidths$b
  })), /*#__PURE__*/createElement(VerticalCard, _extends({}, cards[2], {
    imageWidths: imageWidths$b
  })), /*#__PURE__*/createElement(VerticalCard, _extends({}, cards[3], {
    imageWidths: imageWidths$b
  })))));
};

const componentConfigThemeKey$X = 'fourHC';

const createStyle$x = themeProps => {
  const {
    largeHorizontalGap,
    mediumHorizontalGap,
    smallHorizontalGap
  } = themeProps;
  return StyleSheet.create({
    wrapper: {
      display: 'grid',
      [MEDIA_BREAKPOINTS.large]: {
        gridTemplateColumns: '1fr',
        gridTemplateRows: 'auto',
        gridRowGap: `${largeHorizontalGap * baseUnit}px`
      },
      [MEDIA_BREAKPOINTS.medium]: {
        gridTemplateColumns: '1fr',
        gridTemplateRows: 'auto',
        gridRowGap: `${mediumHorizontalGap * baseUnit}px`
      },
      [MEDIA_BREAKPOINTS.small]: {
        gridTemplateColumns: '1fr',
        gridTemplateRows: 'auto',
        gridRowGap: `${smallHorizontalGap * baseUnit}px`
      }
    }
  });
};

const imageWidths$c = createImageWidths(180, 360, 540);
const FourHC = props => {
  const {
    cards,
    paddingFactors
  } = props;
  const paddingStyle = getLayoutComponentPadding(paddingFactors);
  const themeProps = useTheme(componentConfigThemeKey$X);
  const style = createStyle$x(themeProps);
  const themeContextVC = useThemeOverride({
    titleFontSizeLarge: 'large',
    titleFontSizeMedium: 'bigger',
    titleFontSizeSmall: 'large',
    metadataFontSizeLarge: 'bigger',
    metadataFontSizeMedium: 'big',
    metadataFontSizeSmall: 'bigger',
    descriptionFontSizeLarge: 'normal',
    descriptionFontSizeMedium: 'normal',
    descriptionFontSizeSmall: 'normal',
    cardProportionsLarge: {
      x: 1,
      y: 1
    },
    cardProportionsMedium: {
      x: 1,
      y: 1
    }
  }, componentConfigThemeKey$7);
  return /*#__PURE__*/createElement(ErrorBoundary, null, cards.length > 0 && /*#__PURE__*/createElement("div", {
    className: css(paddingStyle, style.wrapper)
  }, /*#__PURE__*/createElement(ThemeContext.Provider, {
    value: themeContextVC
  }, /*#__PURE__*/createElement(HorizontalCard, _extends({}, cards[0], {
    imageWidths: imageWidths$c
  })), /*#__PURE__*/createElement(HorizontalCard, _extends({}, cards[1], {
    imageWidths: imageWidths$c
  })), /*#__PURE__*/createElement(HorizontalCard, _extends({}, cards[2], {
    imageWidths: imageWidths$c
  })), /*#__PURE__*/createElement(HorizontalCard, _extends({}, cards[3], {
    imageWidths: imageWidths$c
  })))));
};

const componentConfigThemeKey$Y = 'headlineCard';

const createStyle$y = ({
  baseUnit,
  horizontalGapLarge,
  horizontalGapMedium,
  horizontalGapSmall,
  cardTitleColorTypeNormal,
  cardTitleColorTypeHover,
  cardTitleColorTypeActive,
  authorColor,
  showAuthorInLargeScreen,
  showAuthorInMediumScreen,
  showAuthorInSmallScreen,
  showDateInLargeScreen,
  showDateInMediumScreen,
  showDateInSmallScreen,
  pipeColor,
  dateColor,
  numberOfLinesForTitle,
  numberOfLinesForAuthors
}) => StyleSheet.create({
  wrapper: {
    width: '100%',
    height: '100%',
    boxSizing: 'border-box',
    background: 'transparent'
  },
  innerWrapper: {
    overflow: 'hidden',
    width: '-webkit-fill-available',
    display: 'grid',
    gridTemplateColumns: '1fr',
    textDecoration: 'none',
    color: cardTitleColorTypeNormal,
    ':hover': {
      color: cardTitleColorTypeHover
    },
    ':active': {
      color: cardTitleColorTypeActive
    },
    [MEDIA_BREAKPOINTS.large]: {
      gridRowGap: `${baseUnit * horizontalGapLarge}px`
    },
    [MEDIA_BREAKPOINTS.medium]: {
      gridRowGap: `${baseUnit * horizontalGapMedium}px`
    },
    [MEDIA_BREAKPOINTS.small]: {
      gridRowGap: `${baseUnit * horizontalGapSmall}px`
    }
  },
  title: {
    height: 'fit-content',
    ...ellipsify(numberOfLinesForTitle).style
  },
  metadata: {
    gridTemplateColumns: 'auto auto max-content',
    width: 'fit-content',
    [MEDIA_BREAKPOINTS.large]: {
      display: showMetadataInLargeScreenSize(showAuthorInLargeScreen, showDateInLargeScreen) ? 'inline-grid' : 'none'
    },
    [MEDIA_BREAKPOINTS.medium]: {
      display: showMetadataInMediumScreenSize(showAuthorInMediumScreen, showDateInMediumScreen) ? 'inline-grid' : 'none'
    },
    [MEDIA_BREAKPOINTS.small]: {
      display: showMetadataInSmallScreenSize(showAuthorInSmallScreen, showDateInSmallScreen) ? 'inline-grid' : 'none'
    }
  },
  authors: {
    color: authorColor,
    height: 'fit-content',
    [MEDIA_BREAKPOINTS.large]: {
      marginRight: `${baseUnit * 0.25}px`,
      ...(showAuthorInLargeScreen ? ellipsify(numberOfLinesForAuthors).style : {
        display: 'none'
      })
    },
    [MEDIA_BREAKPOINTS.medium]: {
      marginRight: `${baseUnit * 0.25}px`,
      ...(showAuthorInMediumScreen ? ellipsify(numberOfLinesForAuthors).style : {
        display: 'none'
      })
    },
    [MEDIA_BREAKPOINTS.small]: {
      marginRight: `${baseUnit * 0.25}px`,
      ...(showAuthorInSmallScreen ? ellipsify(numberOfLinesForAuthors).style : {
        display: 'none'
      })
    }
  },
  pipe: {
    color: pipeColor,
    [MEDIA_BREAKPOINTS.large]: {
      display: showDateInLargeScreen && showAuthorInLargeScreen ? 'block' : 'none',
      marginRight: `${baseUnit * 0.25}px`
    },
    [MEDIA_BREAKPOINTS.medium]: {
      display: showDateInMediumScreen && showAuthorInMediumScreen ? 'block' : 'none',
      marginRight: `${baseUnit * 0.25}px`
    },
    [MEDIA_BREAKPOINTS.small]: {
      display: showDateInSmallScreen && showAuthorInSmallScreen ? 'block' : 'none',
      marginRight: `${baseUnit * 0.25}px`
    }
  },
  date: {
    color: dateColor,
    overflow: 'hidden',
    [MEDIA_BREAKPOINTS.large]: {
      display: showDateInLargeScreen ? 'inline-block' : 'none'
    },
    [MEDIA_BREAKPOINTS.medium]: {
      display: showDateInMediumScreen ? 'inline-block' : 'none'
    },
    [MEDIA_BREAKPOINTS.small]: {
      display: showDateInSmallScreen ? 'inline-block' : 'none'
    }
  }
});

const HeadlineCard = props => {
  const {
    title,
    authors,
    articleUrl,
    updatedAt
  } = props;
  const themeProps = useTheme(componentConfigThemeKey$Y);
  const {
    titleFontSizeLarge,
    titleFontSizeMedium,
    titleFontSizeSmall,
    titleSemanticTag,
    metadataSemanticTag,
    metadataFontSizeSmall,
    metadataFontSizeMedium,
    metadataFontSizeLarge,
    showDateInSmallScreen,
    showDateInMediumScreen,
    showDateInLargeScreen,
    showAuthorInSmallScreen,
    showAuthorInMediumScreen,
    showAuthorInLargeScreen
  } = themeProps;
  const style = createStyle$y(themeProps);
  const showAuthor = showAuthorInAnyScreenSize(authors, showAuthorInLargeScreen, showAuthorInMediumScreen, showAuthorInSmallScreen);
  const showDate = showDateInAnyScreenSize(showDateInLargeScreen, showDateInMediumScreen, showDateInSmallScreen);
  return /*#__PURE__*/createElement("article", {
    className: css(style.wrapper)
  }, /*#__PURE__*/createElement(Link, {
    className: css(style.innerWrapper),
    href: articleUrl
  }, /*#__PURE__*/createElement("header", null, /*#__PURE__*/createElement(DynamicSemanticTag, {
    semanticTag: titleSemanticTag,
    fontSizeLarge: titleFontSizeLarge,
    fontSizeMedium: titleFontSizeMedium,
    fontSizeSmall: titleFontSizeSmall,
    styles: semanticCSS(style.title)
  }, title)), showMetadataInAnyScreenSize(showAuthor, showDate) ? /*#__PURE__*/createElement("footer", null, /*#__PURE__*/createElement(DynamicSemanticTag, {
    semanticTag: metadataSemanticTag,
    fontSizeLarge: metadataFontSizeLarge,
    fontSizeMedium: metadataFontSizeMedium,
    fontSizeSmall: metadataFontSizeSmall,
    styles: semanticCSS(style.metadata)
  }, showAuthor ? /*#__PURE__*/createElement("div", {
    className: css(style.authors)
  }, listAuthors(authors)) : null, showPipeInAnyScreenSize(showAuthor, showDate) ? /*#__PURE__*/createElement("div", {
    className: css(style.pipe)
  }, "|") : null, showDate ? /*#__PURE__*/createElement("div", {
    className: css(style.date)
  }, updatedAt) : null)) : null));
};

const componentConfigThemeKey$Z = 'fourHeadlineCards';

const createStyle$z = themeProps => {
  const {
    dividerWidth,
    baseUnit,
    dividerGapInSmallScreen,
    dividerGapInMediumScreen,
    dividerGapInLargeScreen,
    diverColor,
    showDivider,
    backgroundColor,
    borderRadius,
    wrapperGapLarge,
    wrapperGapMedium,
    wrapperGapSmall
  } = themeProps;
  return StyleSheet.create({
    wrapper: {
      display: 'grid',
      gridTemplateRows: 'auto',
      backgroundColor,
      borderRadius: `${borderRadius}px`,
      [MEDIA_BREAKPOINTS.large]: {
        gridTemplateColumns: showDivider ? '1fr auto 1fr auto 1fr auto 1fr' : '1fr 1fr 1fr 1fr',
        gridColumnGap: `${dividerGapInLargeScreen * baseUnit}px`,
        padding: `${wrapperGapLarge * baseUnit}px`
      },
      [MEDIA_BREAKPOINTS.medium]: {
        gridTemplateColumns: showDivider ? '1fr auto 1fr' : '1fr 1fr',
        gridColumnGap: `${dividerGapInMediumScreen * baseUnit}px`,
        gridRowGap: `${dividerGapInMediumScreen * baseUnit}px`,
        padding: `${wrapperGapMedium * baseUnit}px`
      },
      [MEDIA_BREAKPOINTS.small]: {
        gridTemplateColumns: '1fr',
        gridRowGap: `${dividerGapInSmallScreen * baseUnit}px`,
        padding: `${wrapperGapSmall * baseUnit}px`
      }
    },
    dividerBetweenTwoCards: {
      backgroundColor: diverColor,
      [MEDIA_BREAKPOINTS.large]: {
        display: 'block',
        width: `${dividerWidth}px`,
        height: '100%'
      },
      [MEDIA_BREAKPOINTS.medium]: {
        display: 'none'
      },
      [MEDIA_BREAKPOINTS.small]: {
        display: 'block',
        width: '100%',
        height: `${dividerWidth}px`
      }
    },
    dividerBetweenFourCards: {
      [MEDIA_BREAKPOINTS.large]: {
        display: 'none'
      },
      [MEDIA_BREAKPOINTS.medium]: {
        backgroundColor: diverColor,
        display: 'block',
        width: `${dividerWidth}px`,
        height: '100%',
        margin: `0 ${dividerGapInMediumScreen * baseUnit}px`,
        gridRowEnd: '3',
        gridRowStart: '1',
        gridColumnStart: '2'
      },
      [MEDIA_BREAKPOINTS.small]: {
        display: 'none'
      }
    }
  });
};

const FourHeadlineCards = props => {
  const {
    cards
  } = props;
  const themeProps = useTheme(componentConfigThemeKey$Z);
  const {
    borderColorTypeActive,
    borderColorTypeHover,
    borderColorTypeNormal,
    boxShadowTypeActive,
    boxShadowTypeHover,
    boxShadowTypeNormal,
    borderWidthTypeActive,
    borderWidthTypeHover,
    borderWidthTypeNormal
  } = themeProps;
  const borderBoxShadow = {
    borderColorTypeActive,
    borderColorTypeHover,
    borderColorTypeNormal,
    borderWidthTypeActive,
    borderWidthTypeHover,
    borderWidthTypeNormal
  };
  const boxShadowStyle = createStyleForBoxShadow({
    boxShadowTypeNormal,
    boxShadowTypeHover,
    boxShadowTypeActive,
    borderBoxShadow
  });
  const {
    showDivider
  } = themeProps;
  const style = createStyle$z(themeProps);
  const themeContextHeadlineCard = useThemeOverride({
    horizontalGapMedium: 0
  }, componentConfigThemeKey$Y);
  return /*#__PURE__*/createElement(ErrorBoundary, null, cards.length > 0 && /*#__PURE__*/createElement("div", {
    className: css(style.wrapper, boxShadowStyle.style)
  }, /*#__PURE__*/createElement(ThemeContext.Provider, {
    value: themeContextHeadlineCard
  }, /*#__PURE__*/createElement(HeadlineCard, cards[0]), showDivider ? /*#__PURE__*/createElement("div", {
    className: css(style.dividerBetweenTwoCards)
  }) : null, showDivider ? /*#__PURE__*/createElement("div", {
    className: css(style.dividerBetweenFourCards)
  }) : null, /*#__PURE__*/createElement(HeadlineCard, cards[1]), showDivider ? /*#__PURE__*/createElement("div", {
    className: css(style.dividerBetweenTwoCards)
  }) : null, /*#__PURE__*/createElement(HeadlineCard, cards[2]), showDivider ? /*#__PURE__*/createElement("div", {
    className: css(style.dividerBetweenTwoCards)
  }) : null, /*#__PURE__*/createElement(HeadlineCard, cards[3]))));
};

const componentConfigThemeKey$_ = 'twoOTCFourHLC';

const createStyle$A = (largeVerticalGap, largeHorizontalGap, mediumVerticalGap, mediumHorizontalGap, smallHorizontalGap) => {
  const firstOnTopCardArea = 'first_on_top_card_area';
  const secondOnTopCardArea = 'second_on_top_card_area';
  return StyleSheet.create({
    wrapper: {
      [MEDIA_BREAKPOINTS.large]: {
        display: 'grid',
        gridTemplateRows: '1fr auto',
        gridRowGap: `${largeHorizontalGap * baseUnit}px`
      },
      [MEDIA_BREAKPOINTS.medium]: {
        display: 'grid',
        gridTemplateRows: '1fr auto',
        gridRowGap: `${mediumHorizontalGap * baseUnit}px`
      },
      [MEDIA_BREAKPOINTS.small]: {
        display: 'grid',
        gridTemplateRows: '1fr',
        gridRowGap: `${smallHorizontalGap * baseUnit}px`
      }
    },
    onTopCardsWrapper: {
      [MEDIA_BREAKPOINTS.large]: {
        display: 'inline-grid',
        gridTemplateColumns: '1fr 1fr',
        gridTemplateRows: '1fr',
        gridColumnGap: `${largeVerticalGap * baseUnit}px`,
        gridTemplateAreas: `'${firstOnTopCardArea} ${secondOnTopCardArea}'`
      },
      [MEDIA_BREAKPOINTS.medium]: {
        display: 'inline-grid',
        gridTemplateColumns: '1fr 1fr',
        gridTemplateRows: '1fr',
        gridColumnGap: `${mediumVerticalGap * baseUnit}px`,
        gridTemplateAreas: `'${firstOnTopCardArea} ${secondOnTopCardArea}'`
      },
      [MEDIA_BREAKPOINTS.small]: {
        display: 'grid',
        gridTemplateColumns: '1fr',
        gridRowGap: `${smallHorizontalGap * baseUnit}px`,
        gridTemplateAreas: `'${firstOnTopCardArea}' '${secondOnTopCardArea}'`
      }
    },
    firstOnTopCard: {
      gridArea: firstOnTopCardArea
    },
    secondOnTopCard: {
      gridArea: secondOnTopCardArea
    },
    minorCard: {
      [MEDIA_BREAKPOINTS.small]: {
        paddingLeft: `${0.5 * baseUnit}px`,
        paddingRight: `${0.5 * baseUnit}px`
      }
    }
  });
};

const imageWidthsForMainCard$3 = createImageWidths(360, 360, 540);
const TwoOTCFourHLC = props => {
  const {
    cards,
    paddingFactors
  } = props;
  const paddingStyle = getLayoutComponentPadding(paddingFactors);
  const themeProps = useTheme(componentConfigThemeKey$_);
  const headlineCards = {
    cards: [cards[2], cards[3], cards[4], cards[5]]
  };
  const {
    largeVerticalGap,
    largeHorizontalGap,
    mediumVerticalGap,
    mediumHorizontalGap,
    smallHorizontalGap
  } = themeProps;
  const styles = createStyle$A(largeVerticalGap, largeHorizontalGap, mediumVerticalGap, mediumHorizontalGap, smallHorizontalGap);
  const themeContextOnTopCards = useThemeOverride({
    aspectRatioSmall: {
      x: 4,
      y: 3
    },
    titleFontSizeLarge: 'large',
    titleFontSizeMedium: 'bigger',
    titleFontSizeSmall: 'large',
    metadataFontSizeMedium: 'big',
    metadataFontSizeSmall: 'bigger',
    horizontalGapSmall: 0.5
  }, componentConfigThemeKey$3);
  const themeContextHeadlineCards = useThemeOverride({
    horizontalGapLarge: 0.5,
    horizontalGapMedium: 0,
    horizontalGapSmall: 0.25,
    titleFontSizeSmall: 'large',
    metadataFontSizeSmall: 'bigger'
  }, componentConfigThemeKey$Y);
  return /*#__PURE__*/createElement(ErrorBoundary, null, cards.length > 0 && /*#__PURE__*/createElement("div", {
    className: css(paddingStyle, styles.wrapper)
  }, /*#__PURE__*/createElement(ThemeContext.Provider, {
    value: themeContextOnTopCards
  }, /*#__PURE__*/createElement("div", {
    className: css(styles.onTopCardsWrapper)
  }, /*#__PURE__*/createElement("div", {
    className: css(styles.firstOnTopCard)
  }, /*#__PURE__*/createElement(OnTopCard, _extends({}, cards[0], {
    imageWidths: imageWidthsForMainCard$3
  }))), /*#__PURE__*/createElement("div", {
    className: css(styles.secondOnTopCard)
  }, /*#__PURE__*/createElement(OnTopCard, _extends({}, cards[1], {
    imageWidths: imageWidthsForMainCard$3
  }))))), /*#__PURE__*/createElement(ThemeContext.Provider, {
    value: themeContextHeadlineCards
  }, /*#__PURE__*/createElement(FourHeadlineCards, headlineCards))));
};

const getStyles$r = baseUnit => {
  return StyleSheet.create({
    icon: {
      margin: 'auto',
      display: 'block',
      [MEDIA_BREAKPOINTS.large]: {
        width: '24px',
        height: '24px',
        flex: `0 0 ${(24 + baseUnit / 4) * 2}px`
      },
      [MEDIA_BREAKPOINTS.medium]: {
        width: '16px',
        height: '16px',
        flex: `0 0 ${(16 + baseUnit / 4) * 2}px`
      },
      [MEDIA_BREAKPOINTS.small]: {
        width: '16px',
        height: '16px',
        flex: `0 0 ${(16 + baseUnit / 4) * 2}px`
      }
    }
  });
};

const QuoteIcon = props => {
  const {
    iconColor,
    baseUnit
  } = props;
  const styles = getStyles$r(baseUnit);
  return /*#__PURE__*/createElement("svg", {
    className: css(styles.icon),
    viewBox: "0 0 16 16",
    version: "1.1",
    xmlns: "http://www.w3.org/2000/svg"
  }, /*#__PURE__*/createElement("g", {
    stroke: "none",
    strokeWidth: "1",
    fill: "none",
    fillRule: "evenodd"
  }, /*#__PURE__*/createElement("path", {
    d: "M9.0788,1 L16.00005,1 L16.00005,5.6195 C16.00005,8.41675 15.5258,10.498 14.5773,11.86525 C13.6283,13.2325 12.2058,14.27725 10.3088,15 L9.0498,12.313 C11.44405,11.52775 12.6413,9.90875 12.6413,7.458 L9.0788,7.458 L9.0788,1 Z M0.029,1 L6.95025,1 L6.95025,5.6195 C6.95025,8.41675 6.476,10.498 5.5275,11.86525 C4.5785,13.2325 3.156,14.27725 1.259,15 L-8.8817842e-15,12.313 C2.39425,11.52775 3.5915,9.90875 3.5915,7.458 L0.029,7.458 L0.029,1 Z",
    fill: iconColor,
    fillRule: "nonzero"
  })));
};

const componentConfigurationKey$E = 'decoratedQuote';

const createCiteStyle = fontSizesStyles => {
  const citeFontSize = fontSizesStyles[TYPOGRAPHY_SIZE.NORMAL];
  const {
    style: {
      fontFamily,
      fontWeight
    },
    breakpoints: {
      large,
      medium,
      small
    }
  } = citeFontSize;
  const fontFamilyConverted = fontFamily;
  const fontWeightConverted = fontWeight;
  return {
    fontFamily: fontFamilyConverted,
    fontWeight: fontWeightConverted,
    [MEDIA_BREAKPOINTS.large]: { ...large
    },
    [MEDIA_BREAKPOINTS.medium]: { ...medium
    },
    [MEDIA_BREAKPOINTS.small]: { ...small
    }
  };
};

const getStyles$s = (theme, defaultBlocksWidths) => {
  const {
    baseUnit,
    borderColor,
    textColor,
    citeColor,
    fontSizesStyles
  } = theme;
  const citeStyles = createCiteStyle(fontSizesStyles);
  return StyleSheet.create({
    wrapper: { ...createBlockStyle(defaultBlocksWidths),
      position: 'relative',
      borderBottom: `1px solid ${borderColor}`
    },
    text: {
      fontStyle: 'italic',
      color: textColor,
      marginTop: `${baseUnit * 0.5}px`
    },
    cite: {
      color: citeColor,
      fontStyle: 'normal',
      marginTop: `${baseUnit * 0.25}px`,
      ...citeStyles
    },
    quoteTop: {
      display: 'flex',
      flexDirection: 'row',
      width: '100%',
      alignItems: 'center',
      ':before': {
        content: '""',
        display: 'block',
        height: '1px',
        borderTop: `1px solid ${borderColor}`,
        flex: '2 0 0'
      },
      ':after': {
        content: '""',
        display: 'block',
        height: '1px',
        borderTop: `1px solid ${borderColor}`,
        flex: '2 0 0'
      }
    }
  });
};

const DecoratedQuote = props => {
  const {
    text,
    cite,
    additionalStyle,
    dataId,
    defaultBlocksWidths
  } = props;
  const themeProps = useTheme(componentConfigurationKey$E);
  const {
    iconColor,
    baseUnit
  } = themeProps;
  const styles = getStyles$s(themeProps, defaultBlocksWidths);
  return /*#__PURE__*/createElement(ErrorBoundary, null, /*#__PURE__*/createElement("blockquote", {
    "data-id": dataId,
    className: css(additionalStyle.style, styles.wrapper)
  }, /*#__PURE__*/createElement("div", {
    className: css(styles.quoteTop)
  }, /*#__PURE__*/createElement(QuoteIcon, {
    iconColor: iconColor,
    baseUnit: baseUnit
  })), /*#__PURE__*/createElement(P, {
    styles: semanticCSS(styles.text),
    fontSizeLarge: TYPOGRAPHY_SIZE.LARGE,
    fontSizeMedium: TYPOGRAPHY_SIZE.LARGE,
    fontSizeSmall: TYPOGRAPHY_SIZE.LARGE
  }, text), cite ? /*#__PURE__*/createElement("cite", {
    className: css(styles.cite)
  }, "-\xA0", cite) : null));
};

const componentConfigurationKey$F = 'basicQuote';

const createCiteStyle$1 = fontSizesStyles => {
  const citeFontSize = fontSizesStyles[TYPOGRAPHY_SIZE.BIG];
  const {
    style: {
      fontFamily,
      fontWeight
    },
    breakpoints: {
      large,
      medium,
      small
    }
  } = citeFontSize;
  const fontFamilyConverted = fontFamily;
  const fontWeightConverted = fontWeight;
  return {
    fontFamily: fontFamilyConverted,
    fontWeight: fontWeightConverted,
    [MEDIA_BREAKPOINTS.large]: { ...large
    },
    [MEDIA_BREAKPOINTS.medium]: { ...medium
    },
    [MEDIA_BREAKPOINTS.small]: { ...small
    }
  };
};

const getStyles$t = (theme, defaultBlocksWidths) => {
  const {
    baseUnit,
    textColor,
    citeColor,
    alignment,
    fontSizesStyles
  } = theme;
  const citeFontsSizes = createCiteStyle$1(fontSizesStyles);
  return StyleSheet.create({
    wrapper: {
      position: 'relative',
      marginTop: `${baseUnit}px`,
      ...createBlockStyle(defaultBlocksWidths)
    },
    text: {
      fontStyle: 'italic',
      textAlign: alignment === ALIGNMENT_OPTIONS.CENTER ? 'center' : 'left',
      color: textColor,
      margin: `${baseUnit * 0.5}px 0px`
    },
    cite: {
      textAlign: alignment === ALIGNMENT_OPTIONS.CENTER ? 'center' : 'left',
      color: citeColor,
      marginTop: `${baseUnit * 0.5}px`,
      display: 'block',
      width: 'auto',
      ...citeFontsSizes
    }
  });
};

const BasicQuote = props => {
  const {
    text,
    additionalStyle,
    dataId,
    defaultBlocksWidths,
    cite
  } = props;
  const themeProps = useTheme(componentConfigurationKey$F);
  const styles = getStyles$t(themeProps, defaultBlocksWidths);
  const {
    fontSizeLarge,
    fontSizeMedium,
    fontSizeSmall,
    showCite,
    showApostrophes
  } = themeProps;
  return /*#__PURE__*/createElement(ErrorBoundary, null, /*#__PURE__*/createElement("blockquote", {
    "data-id": dataId,
    className: css(styles.wrapper, additionalStyle.style)
  }, /*#__PURE__*/createElement(P, {
    styles: semanticCSS(styles.text),
    fontSizeLarge: fontSizeLarge,
    fontSizeMedium: fontSizeMedium,
    fontSizeSmall: fontSizeSmall
  }, showApostrophes ? `"${text}"` : text), showCite && cite ? /*#__PURE__*/createElement("cite", {
    className: css(styles.cite)
  }, "-\xA0", cite) : null));
};

const componentConfigurationKey$G = 'divider';

const createStyle$B = (themeProps, defaultBlocksWidths) => {
  const {
    color
  } = themeProps;
  return StyleSheet.create({
    divider: {
      backgroundColor: color,
      width: 'auto',
      height: '1px',
      ...createBlockStyle(defaultBlocksWidths)
    }
  });
};

const Divider = props => {
  const themeProps = useTheme(componentConfigurationKey$G);
  const {
    additionalStyle,
    dataId,
    defaultBlocksWidths
  } = props;
  const style = createStyle$B(themeProps, defaultBlocksWidths);
  return /*#__PURE__*/createElement(ErrorBoundary, null, /*#__PURE__*/createElement("div", {
    "data-id": dataId,
    className: css(style.divider, additionalStyle.style)
  }));
};

const componentConfigurationKey$H = 'dividerWithQuoteIcon';

const getStyles$u = (baseUnit, lineColor, defaultBlocksWidths) => {
  return StyleSheet.create({
    wrapper: { ...createBlockStyle(defaultBlocksWidths),
      display: 'flex',
      alignItems: 'center'
    },
    line: {
      height: '1px',
      width: '100%',
      backgroundColor: lineColor
    },
    quote: {
      margin: `0 ${baseUnit}px`,
      width: `${baseUnit * 1.5}px`
    }
  });
};
/* eslint-disable */


const getSvg = color => `<?xml version="1.0" encoding="utf-8"?>
<svg version="1.1" id="Layer_1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px"
	 viewBox="0 0 64 64" style="enable-background:new 0 0 64 64;" xml:space="preserve">
<path fill="${color}" d="M58.8,27c0-14.8-12-27-26.7-27C17.8,0,5.2,10.7,5.2,26.8c0,10.2,8.6,24.9,27,24.9c0,3.1-3.2,7.2-13.5,8.8l0.4,3.4
	C40.3,64,58.8,46.6,58.8,27z"/>
</svg>`;
/* eslint-enable */


const DividerWithQuoteIcon = props => {
  const {
    baseUnit,
    lineColor,
    logoColor
  } = useTheme(componentConfigurationKey$H);
  const {
    defaultBlocksWidths,
    additionalStyle
  } = props;
  const styles = getStyles$u(baseUnit, lineColor, defaultBlocksWidths);
  const svg = getSvg(logoColor);
  const imgSrc = `data:image/svg+xml,${encodeURIComponent(svg)}`;
  return /*#__PURE__*/createElement("div", {
    className: css(styles.wrapper, additionalStyle.style)
  }, /*#__PURE__*/createElement("div", {
    className: css(styles.line)
  }), /*#__PURE__*/createElement("img", {
    className: css(styles.quote),
    src: imgSrc,
    alt: ""
  }), /*#__PURE__*/createElement("div", {
    className: css(styles.line)
  }));
};

const componentConfigThemeKey$$ = 'threeVC';

const createStyle$C = themeProps => {
  const {
    largeVerticalGap,
    mediumVerticalGap,
    smallHorizontalGap,
    baseUnit
  } = themeProps;
  return StyleSheet.create({
    wrapper: {
      [MEDIA_BREAKPOINTS.large]: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr',
        gridTemplateRows: 'auto',
        gridColumnGap: `${largeVerticalGap * baseUnit}px`
      },
      [MEDIA_BREAKPOINTS.medium]: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr',
        gridTemplateRows: 'auto',
        gridColumnGap: `${mediumVerticalGap * baseUnit}px`
      },
      [MEDIA_BREAKPOINTS.small]: {
        display: 'grid',
        gridTemplateColumns: 'auto',
        gridTemplateRows: 'min-content',
        gridRowGap: `${smallHorizontalGap * baseUnit}px`
      }
    }
  });
};

const imagesWidths = createImageWidths(360, 360, 360);
const ThreeVC = props => {
  const {
    cards,
    paddingFactors
  } = props;
  const paddingStyle = getLayoutComponentPadding(paddingFactors);
  const themeProps = useTheme(componentConfigThemeKey$$);
  const style = createStyle$C(themeProps);
  const themeContextVC = useThemeOverride({
    titleFontSizeLarge: 'large',
    titleFontSizeMedium: 'large',
    titleFontSizeSmall: 'large',
    metadataFontSizeLarge: 'big',
    metadataFontSizeMedium: 'big',
    metadataFontSizeSmall: 'big',
    verticalGapLarge: 1,
    verticalGapMedium: 1,
    verticalGapSmall: 1,
    horizontalGapLarge: 0.75,
    horizontalGapMedium: 0.75,
    horizontalGapSmall: 0.75
  }, componentConfigThemeKey$5);
  return /*#__PURE__*/createElement(ErrorBoundary, null, cards.length > 0 && /*#__PURE__*/createElement("div", {
    className: css(paddingStyle, style.wrapper)
  }, /*#__PURE__*/createElement(ThemeContext.Provider, {
    value: themeContextVC
  }, /*#__PURE__*/createElement(VerticalCard, _extends({}, cards[0], {
    imageWidths: imagesWidths
  })), /*#__PURE__*/createElement(VerticalCard, _extends({}, cards[1], {
    imageWidths: imagesWidths
  })), /*#__PURE__*/createElement(VerticalCard, _extends({}, cards[2], {
    imageWidths: imagesWidths
  })))));
};

const componentConfigThemeKey$10 = 'threeHC';

const createStyle$D = themeProps => {
  const {
    largeHorizontalGap,
    mediumHorizontalGap,
    smallHorizontalGap
  } = themeProps;
  return StyleSheet.create({
    wrapper: {
      display: 'grid',
      [MEDIA_BREAKPOINTS.large]: {
        gridTemplateColumns: '1fr',
        gridTemplateRows: 'auto',
        gridRowGap: `${largeHorizontalGap * baseUnit}px`
      },
      [MEDIA_BREAKPOINTS.medium]: {
        gridTemplateColumns: '1fr',
        gridTemplateRows: '1fr 1fr 1fr',
        gridRowGap: `${mediumHorizontalGap * baseUnit}px`
      },
      [MEDIA_BREAKPOINTS.small]: {
        gridTemplateColumns: '1fr',
        gridTemplateRows: 'auto',
        gridRowGap: `${smallHorizontalGap * baseUnit}px`
      }
    }
  });
};

const imageWidths$d = createImageWidths(180, 360, 540);
const ThreeHC = props => {
  const {
    cards,
    paddingFactors
  } = props;
  const paddingStyle = getLayoutComponentPadding(paddingFactors);
  const themeProps = useTheme(componentConfigThemeKey$10);
  const {
    descriptionFontSizeLarge,
    descriptionFontSizeMedium,
    descriptionFontSizeSmall
  } = themeProps;
  const style = createStyle$D(themeProps);
  const themeContextVC = useThemeOverride({
    descriptionFontSizeLarge,
    descriptionFontSizeMedium,
    descriptionFontSizeSmall,
    titleFontSizeLarge: 'large',
    titleFontSizeMedium: 'large',
    titleFontSizeSmall: 'large',
    metadataFontSizeLarge: 'big',
    metadataFontSizeMedium: 'big',
    metadataFontSizeSmall: 'big',
    cardProportionsLarge: {
      x: 2,
      y: 1
    },
    cardProportionsMedium: {
      x: 1,
      y: 1
    },
    shouldReverseImageAlignment: true,
    verticalGapLarge: 1,
    verticalGapMedium: 1,
    verticalGapSmall: 1,
    horizontalGapLarge: 0.75,
    horizontalGapMedium: 0.75,
    horizontalGapSmall: 0.75
  }, componentConfigThemeKey$7);
  return /*#__PURE__*/createElement(ErrorBoundary, null, cards.length > 0 && /*#__PURE__*/createElement("div", {
    className: css(paddingStyle, style.wrapper)
  }, /*#__PURE__*/createElement(ThemeContext.Provider, {
    value: themeContextVC
  }, /*#__PURE__*/createElement(HorizontalCard, _extends({}, cards[0], {
    imageWidths: imageWidths$d
  })), /*#__PURE__*/createElement(HorizontalCard, _extends({}, cards[1], {
    imageWidths: imageWidths$d
  })), /*#__PURE__*/createElement(HorizontalCard, _extends({}, cards[2], {
    imageWidths: imageWidths$d
  })))));
};

const componentConfigThemeKey$11 = 'oneHCThreeVC';

const createStyle$E = themeProps => {
  const {
    largeHorizontalGap,
    mediumHorizontalGap,
    smallHorizontalGap,
    baseUnit
  } = themeProps;
  return StyleSheet.create({
    wrapper: {
      display: 'grid',
      [MEDIA_BREAKPOINTS.large]: {
        gridRowGap: `${largeHorizontalGap * baseUnit}px`
      },
      [MEDIA_BREAKPOINTS.medium]: {
        gridRowGap: `${mediumHorizontalGap * baseUnit}px`
      },
      [MEDIA_BREAKPOINTS.small]: {
        gridRowGap: `${smallHorizontalGap * baseUnit}px`
      }
    }
  });
};

const imageWidthsHC = createImageWidths(360, 540, 720);
const OneHCThreeVC = props => {
  const {
    cards,
    paddingFactors
  } = props;
  const paddingStyle = getLayoutComponentPadding(paddingFactors);
  const themeProps = useTheme(componentConfigThemeKey$11);
  const style = createStyle$E(themeProps);
  const [firstCard, ...rest] = cards;
  const themeContextHC = useThemeOverride({
    titleFontSizeLarge: 'large',
    titleFontSizeMedium: 'large',
    titleFontSizeSmall: 'large',
    metadataFontSizeLarge: 'big',
    metadataFontSizeMedium: 'big',
    metadataFontSizeSmall: 'big',
    shouldReverseImageAlignment: true,
    cardProportionsLarge: {
      x: 2,
      y: 1
    },
    cardProportionsMedium: {
      x: 2,
      y: 1
    },
    verticalGapLarge: 1,
    verticalGapMedium: 1,
    verticalGapSmall: 1,
    horizontalGapLarge: 0.75,
    horizontalGapMedium: 0.75,
    horizontalGapSmall: 0.75
  }, componentConfigThemeKey$7);
  return /*#__PURE__*/createElement(ErrorBoundary, null, cards.length > 0 && /*#__PURE__*/createElement("div", {
    className: css(paddingStyle, style.wrapper)
  }, /*#__PURE__*/createElement(ThemeContext.Provider, {
    value: themeContextHC
  }, /*#__PURE__*/createElement(HorizontalCard, _extends({}, firstCard, {
    imageWidths: imageWidthsHC
  }))), /*#__PURE__*/createElement(ThreeVC, {
    cards: rest,
    paddingFactors: {
      large: 0,
      medium: 0,
      small: 0
    }
  })));
};

const imageWidths$e = createImageWidths(540, 1080, 1080);
const FullWidthOTC = props => {
  const {
    cards
  } = props;
  const themeContext = useThemeOverride({
    titleFontSizeLarge: 'gigantic',
    titleFontSizeMedium: 'gigantic',
    titleFontSizeSmall: 'gigantic',
    metadataFontSizeLarge: 'big',
    metadataFontSizeMedium: 'big',
    metadataFontSizeSmall: 'big',
    titleAlignment: 'CENTER',
    metadataAlignment: 'CENTER',
    aspectRatioLarge: {
      x: 2,
      y: 1
    },
    aspectRatioMedium: {
      x: 2,
      y: 1
    },
    aspectRatioSmall: {
      x: 4,
      y: 3
    },
    verticalGapLarge: 1,
    verticalGapMedium: 1,
    verticalGapSmall: 1,
    horizontalGapLarge: 2,
    horizontalGapMedium: 0.75,
    horizontalGapSmall: 0.75
  }, componentConfigThemeKey$3);
  return /*#__PURE__*/createElement(ErrorBoundary, null, cards.length > 0 && /*#__PURE__*/createElement(ThemeContext.Provider, {
    value: themeContext
  }, /*#__PURE__*/createElement(OnTopCard, _extends({}, cards[0], {
    imageWidths: imageWidths$e,
    lazyLoad: true
  }))));
};

const AuthorsWithRoundImageClipMAsk = ({
  clipPathLarge,
  clipPathSmall
}) => /*#__PURE__*/createElement("svg", {
  width: "0",
  height: "0",
  style: {
    position: 'absolute'
  }
}, /*#__PURE__*/createElement("defs", null, /*#__PURE__*/createElement("clipPath", {
  id: clipPathLarge
}, /*#__PURE__*/createElement("path", {
  d: "M34.5,0C25,0,16.4,3.7,10,9.6c5.8,6.9,9.2,15.8,9.2,25.4S15.8,53.5,10,60.4c6.4,6,15,9.6,24.5,9.6C54.1,70,70,54.3,70,35 S54.1,0,34.5,0z"
})), /*#__PURE__*/createElement("clipPath", {
  id: clipPathSmall
}, /*#__PURE__*/createElement("path", {
  d: "M24.6,0C17.8,0,11.6,2.6,7,6.9c4.2,4.9,6.6,11.3,6.6,18.1S11.2,38.2,7,43.1c4.6,4.3,10.8,6.9,17.6,6.9 C38.6,50,50,38.8,50,25S38.6,0,24.6,0z"
}))));

const componentConfigurationKey$I = 'authorsWithRoundImage';
const CLIP_PATH_SMALL = 'clipPathSmall';
const CLIP_PATH_LARGE = 'clipPathLarge';

const getImageAdditionalStyle$1 = () => {
  return StyleSheet.create({
    style: {
      borderRadius: '100%',
      display: 'block',
      overflow: 'hidden',
      [MEDIA_BREAKPOINTS.large]: {
        width: '70px',
        ':not(:first-child)': {
          clipPath: `url(#${CLIP_PATH_LARGE})`,
          marginLeft: '-10px'
        }
      },
      [MEDIA_BREAKPOINTS.medium]: {
        width: '70px',
        ':not(:first-child)': {
          clipPath: `url(#${CLIP_PATH_LARGE})`,
          marginLeft: '-10px'
        }
      },
      [MEDIA_BREAKPOINTS.small]: {
        width: '50px',
        ':not(:first-child)': {
          clipPath: `url(#${CLIP_PATH_SMALL})`,
          marginLeft: '-8px'
        }
      }
    }
  });
};

const getImageStyle$1 = () => StyleSheet.create({
  wrapper: {
    whiteSpace: 'nowrap',
    display: 'flex'
  },
  image: {
    borderRadius: '100%',
    display: 'block',
    overflow: 'hidden',
    [MEDIA_BREAKPOINTS.large]: {
      width: '70px',
      ':not(:first-child)': {
        clipPath: `url(#${CLIP_PATH_LARGE})`,
        marginLeft: '-10px'
      }
    },
    [MEDIA_BREAKPOINTS.medium]: {
      width: '70px',
      ':not(:first-child)': {
        clipPath: `url(#${CLIP_PATH_LARGE})`,
        marginLeft: '-10px'
      }
    },
    [MEDIA_BREAKPOINTS.small]: {
      width: '50px',
      ':not(:first-child)': {
        clipPath: `url(#${CLIP_PATH_SMALL})`,
        marginLeft: '-8px'
      }
    }
  }
});

const getTextStyle$2 = (baseUnit, textColor, smallVerticalMarginFactor, mediumVerticalMarginFactor, largeVerticalMarginFactor, smallHorizontalMarginFactor, mediumHorizontalMarginFactor, largeHorizontalMarginFactor) => StyleSheet.create({
  wrapper: {
    display: 'flex',
    alignItems: 'center',
    [MEDIA_BREAKPOINTS.large]: {
      margin: `${baseUnit * largeVerticalMarginFactor}px ${baseUnit * largeHorizontalMarginFactor}px`
    },
    [MEDIA_BREAKPOINTS.medium]: {
      margin: `${baseUnit * mediumVerticalMarginFactor}px ${baseUnit * mediumHorizontalMarginFactor}px`
    },
    [MEDIA_BREAKPOINTS.small]: {
      margin: `${baseUnit * smallVerticalMarginFactor}px ${baseUnit * smallHorizontalMarginFactor}px`
    }
  },
  text: {
    [MEDIA_BREAKPOINTS.large]: {
      marginLeft: `${baseUnit}px`
    },
    [MEDIA_BREAKPOINTS.medium]: {
      marginLeft: `${baseUnit}px`
    },
    [MEDIA_BREAKPOINTS.small]: {
      marginLeft: `${baseUnit / 2}px`
    }
  },
  textWithColor: {
    color: textColor
  }
});

const getAuthorsImages = authors => {
  const imageWidths = createImageWidths(50, 50, 80);
  const additionalStyle = getImageAdditionalStyle$1();
  return authors && authors.filter(({
    image
  }) => typeof image === 'object').map((author, index) => {
    return author.image && /*#__PURE__*/createElement(Image, _extends({}, author.image, {
      imageWidths: imageWidths,
      key: index,
      additionalStyle: additionalStyle,
      aspectRatio: {
        x: 1,
        y: 1
      }
    }));
  });
};

const getAuthors$1 = (authors, style) => authors && authors.map((author, index) => {
  const prefix = index >= 1 ? ', ' : '';
  const authorText = `${prefix}${author.name}`;
  return typeof author.link === 'string' ? /*#__PURE__*/createElement(Link, {
    key: index,
    href: author.link
  }, /*#__PURE__*/createElement(Span, {
    styles: semanticCSS(style.textWithColor)
  }, authorText)) : /*#__PURE__*/createElement(Span, {
    key: index,
    styles: semanticCSS(style.textWithColor)
  }, authorText);
});

const AuthorsWithRoundImage = props => {
  const {
    authors,
    by,
    updatedAtDateFormatted,
    updatedAtTimeAndDateFormatted
  } = props;
  const {
    baseUnit,
    textColor,
    smallVerticalMarginFactor,
    mediumVerticalMarginFactor,
    largeVerticalMarginFactor,
    smallHorizontalMarginFactor,
    mediumHorizontalMarginFactor,
    largeHorizontalMarginFactor,
    showTime,
    showTimestamp
  } = useTheme(componentConfigurationKey$I);
  const imageStyle = getImageStyle$1();
  const textStyle = getTextStyle$2(baseUnit, textColor, smallVerticalMarginFactor, mediumVerticalMarginFactor, largeVerticalMarginFactor, smallHorizontalMarginFactor, mediumHorizontalMarginFactor, largeHorizontalMarginFactor);
  const updatedAtFormatted = showTime ? updatedAtTimeAndDateFormatted : updatedAtDateFormatted;
  return /*#__PURE__*/createElement(Fragment, null, /*#__PURE__*/createElement(AuthorsWithRoundImageClipMAsk, {
    clipPathLarge: CLIP_PATH_LARGE,
    clipPathSmall: CLIP_PATH_SMALL
  }), /*#__PURE__*/createElement("div", {
    className: css(textStyle.wrapper)
  }, /*#__PURE__*/createElement("div", {
    className: css(imageStyle.wrapper)
  }, getAuthorsImages(authors)), /*#__PURE__*/createElement("div", {
    className: css(textStyle.text)
  }, /*#__PURE__*/createElement("div", null, /*#__PURE__*/createElement(Span, {
    styles: semanticCSS(textStyle.textWithColor)
  }, `${by} `), getAuthors$1(authors, textStyle)), showTimestamp ? /*#__PURE__*/createElement("div", null, /*#__PURE__*/createElement(Span, {
    styles: semanticCSS(textStyle.textWithColor)
  }, updatedAtFormatted)) : null)));
};

const getStyles$v = color => StyleSheet.create({
  svg: {
    fill: color,
    [MEDIA_BREAKPOINTS.large]: {
      height: '15px'
    },
    [MEDIA_BREAKPOINTS.medium]: {
      height: '15px'
    },
    [MEDIA_BREAKPOINTS.small]: {
      height: '10px'
    }
  }
});

const CameraIcon = ({
  color
}) => {
  const styles = getStyles$v(color);
  return /*#__PURE__*/createElement("svg", {
    xmlns: "http://www.w3.org/2000/svg",
    viewBox: "0 0 32 26",
    className: css(styles.svg)
  }, /*#__PURE__*/createElement("g", {
    "data-name": "Layer 2"
  }, /*#__PURE__*/createElement("g", {
    "data-name": "Layer 1"
  }, /*#__PURE__*/createElement("circle", {
    cx: "16",
    cy: "15",
    r: "5"
  }), /*#__PURE__*/createElement("path", {
    d: "M29.09,4.33H23.27L20.36,0H11.64L8.73,4.33H2.91A2.9,2.9,0,0,0,0,7.22V23.11A2.9,2.9,0,0,0,2.91,26H29.09A2.9,2.9,0,0,0,32,23.11V7.22A2.9,2.9,0,0,0,29.09,4.33ZM16,22a7,7,0,1,1,7-7A7,7,0,0,1,16,22Z"
  }))));
};

const componentConfigurationKey$J = 'ImageWithCaptionAndIcon';

const getStyles$w = (textColor, borderColor, baseUnit, defaultBlocksWidths, captionPaddingFactor) => {
  return StyleSheet.create({
    wrapper: {
      display: 'flex',
      alignItems: 'center',
      borderBottom: `1px solid ${borderColor}`,
      marginBottom: `${baseUnit}px`,
      [MEDIA_BREAKPOINTS.large]: {
        paddingTop: `${baseUnit}px`,
        paddingBottom: `${baseUnit}px`,
        margin: 'auto',
        width: `min(${defaultBlocksWidths.largeWidth - 2 * captionPaddingFactor * baseUnit}px, 100%)`
      },
      [MEDIA_BREAKPOINTS.medium]: {
        paddingTop: `${baseUnit}px`,
        paddingBottom: `${baseUnit / 2}px`,
        margin: `0 ${baseUnit}px`
      },
      [MEDIA_BREAKPOINTS.small]: {
        paddingTop: `${baseUnit}px`,
        paddingBottom: `${baseUnit / 2}px`,
        margin: `0 ${baseUnit}px`
      }
    },
    text: {
      color: textColor,
      [MEDIA_BREAKPOINTS.large]: {
        marginLeft: `${baseUnit / 2}px`
      },
      [MEDIA_BREAKPOINTS.medium]: {
        marginLeft: `${baseUnit / 2}px`
      },
      [MEDIA_BREAKPOINTS.small]: {
        marginLeft: `${baseUnit / 4}px`
      }
    }
  });
};

const createFullBleedStyle$1 = style => {
  return StyleSheet.create({
    style: { ...style
    }
  }).style;
};

const getContainerStyle$3 = (style, defaultBlocksWidths, overrideBlockWidths, blocksPaddingOverride) => {
  const blockStyle = overrideBlockWidths ? createBlockStyle(overrideBlockWidths, blocksPaddingOverride) : createBlockStyle(defaultBlocksWidths, blocksPaddingOverride);
  return StyleSheet.create({
    style: { ...style,
      ...blockStyle
    }
  }).style;
};

const getCaption = (caption, credit) => {
  const hasCaption = typeof caption === 'string';
  const hasCredit = typeof credit === 'string';
  const separator = hasCaption && hasCredit ? ' / ' : '';
  return `${hasCaption ? caption : ''}${separator}${hasCredit ? credit : ''}`;
};

const ImageWithCaptionAndIcon = props => {
  const {
    image,
    lazyLoad,
    style,
    dataId,
    defaultBlocksWidths,
    overrideBlockWidths,
    sizeType,
    blocksPaddingOverride
  } = props;
  const {
    caption,
    credit
  } = image;
  const {
    iconColor,
    textColor,
    borderColor,
    fontSizeLarge,
    fontSizeMedium,
    fontSizeSmall,
    baseUnit,
    fontSizesStyles,
    captionPaddingFactor
  } = useTheme(componentConfigurationKey$J);
  const styles = getStyles$w(textColor, borderColor, baseUnit, defaultBlocksWidths, captionPaddingFactor);
  const figcaptionText = getStyleBySize(fontSizesStyles, {
    fontSizeSmall,
    fontSizeMedium,
    fontSizeLarge
  });
  const hasCaptionOrCredit = typeof caption === 'string' || typeof credit === 'string';
  const largeScreenImageWidth = sizeType === FULL_BLEED_IMAGE_SIZE_TYPE ? 1440 : 1080;
  const imageWidths = createImageWidths(360, 720, largeScreenImageWidth);
  const additionalStyle = sizeType === FULL_BLEED_IMAGE_SIZE_TYPE ? createFullBleedStyle$1(style) : getContainerStyle$3(style, defaultBlocksWidths, overrideBlockWidths, blocksPaddingOverride);
  return /*#__PURE__*/createElement("div", {
    className: css(additionalStyle)
  }, /*#__PURE__*/createElement(Image, _extends({
    imageWidths: imageWidths
  }, image, {
    lazyLoad: lazyLoad,
    dataId: dataId
  })), hasCaptionOrCredit && /*#__PURE__*/createElement("div", {
    className: css(styles.wrapper)
  }, /*#__PURE__*/createElement(CameraIcon, {
    color: iconColor
  }), /*#__PURE__*/createElement("figcaption", {
    className: css(figcaptionText, styles.text)
  }, getCaption(caption, credit))));
};

const componentConfigThemeKey$12 = 'threeVCExpandable';

const createStyle$F = themeProps => {
  const {
    largeVerticalGap,
    mediumVerticalGap,
    smallHorizontalGap,
    largeHorizontalGap,
    mediumHorizontalGap,
    baseUnit
  } = themeProps;
  return StyleSheet.create({
    wrapper: {
      [MEDIA_BREAKPOINTS.large]: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr',
        gridTemplateRows: 'auto',
        gridColumnGap: `${largeVerticalGap * baseUnit}px`,
        gridRowGap: `${largeHorizontalGap * baseUnit}px`
      },
      [MEDIA_BREAKPOINTS.medium]: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gridTemplateRows: 'auto',
        gridColumnGap: `${mediumVerticalGap * baseUnit}px`,
        gridRowGap: `${mediumHorizontalGap * baseUnit}px`
      },
      [MEDIA_BREAKPOINTS.small]: {
        display: 'grid',
        gridTemplateColumns: 'auto',
        gridTemplateRows: 'auto',
        gridRowGap: `${smallHorizontalGap * baseUnit}px`
      }
    }
  });
};

const imagesWidths$1 = createImageWidths(360, 360, 360);
const ThreeVCExpandable = props => {
  const {
    cards,
    paddingFactors
  } = props;
  const paddingStyle = getLayoutComponentPadding(paddingFactors);
  const themeProps = useTheme(componentConfigThemeKey$12);
  const style = createStyle$F(themeProps);
  const {
    innerCardLargeVerticalGap,
    innerCardMediumVerticalGap,
    innerCardSmallVerticalGap,
    innerCardLargeHorizontalGap,
    innerCardMediumHorizontalGap,
    innerCardSmallHorizontalGap,
    titleFontSizeLarge,
    titleFontSizeMedium,
    titleFontSizeSmall,
    metadataFontSizeLarge,
    metadataFontSizeMedium,
    metadataFontSizeSmall
  } = themeProps;
  const themeContextVC = useThemeOverride({
    titleFontSizeLarge,
    titleFontSizeMedium,
    titleFontSizeSmall,
    metadataFontSizeLarge,
    metadataFontSizeMedium,
    metadataFontSizeSmall,
    descriptionFontSizeInLargeScreen: 'bigger',
    descriptionFontSizeInMediumScreen: 'bigger',
    descriptionFontSizeInSmallScreen: 'bigger',
    verticalGapLarge: innerCardLargeVerticalGap,
    verticalGapMedium: innerCardMediumVerticalGap,
    verticalGapSmall: innerCardSmallVerticalGap,
    horizontalGapLarge: innerCardLargeHorizontalGap,
    horizontalGapMedium: innerCardMediumHorizontalGap,
    horizontalGapSmall: innerCardSmallHorizontalGap
  }, componentConfigThemeKey$5);
  return /*#__PURE__*/createElement(ErrorBoundary, null, cards && cards.length > 0 && /*#__PURE__*/createElement("div", {
    className: css(paddingStyle, style.wrapper)
  }, /*#__PURE__*/createElement(ThemeContext.Provider, {
    value: themeContextVC
  }, cards.map((cardDataProps, index) => {
    return /*#__PURE__*/createElement(VerticalCard, _extends({}, cardDataProps, {
      imageWidths: imagesWidths$1,
      key: index
    }));
  }))));
};

const componentConfigThemeKey$13 = 'sectionOnTopTitle';

const createWrapperStyle = theme => StyleSheet.create({
  style: {
    display: 'flex',
    width: '100%',
    height: '100%',
    borderRadius: `${theme.borderRadius}px`,
    boxSizing: 'border-box',
    background: 'transparent',
    [MEDIA_BREAKPOINTS.large]: {
      padding: `${theme.wrapperGapLarge * baseUnit}px`
    },
    [MEDIA_BREAKPOINTS.medium]: {
      padding: `${theme.wrapperGapMedium * baseUnit}px`
    },
    [MEDIA_BREAKPOINTS.small]: {
      padding: `${theme.wrapperGapSmall * baseUnit}px`
    }
  }
}).style;

const createInnerWrapperStyle = theme => StyleSheet.create({
  style: {
    width: '100%',
    height: '100%',
    position: 'relative',
    textDecoration: 'none',
    color: theme.titleColorTypeNormal,
    display: 'block',
    '-webkit-mask-image': '-webkit-radial-gradient(white, black)',
    borderRadius: `${theme.borderRadius}px`
  }
}).style;

const createAdditionalInnerWrapperForLinkStyle = theme => StyleSheet.create({
  style: {
    ':hover': {
      color: theme.titleColorTypeHover
    },
    ':active': {
      color: theme.titleColorTypeActive
    }
  }
}).style;

const SectionOnTopTitleWrapper = props => {
  const {
    link,
    children
  } = props;
  const theme = useTheme(componentConfigThemeKey$13);
  const {
    boxShadowTypeNormal,
    boxShadowTypeHover,
    boxShadowTypeActive,
    borderColorTypeActive,
    borderColorTypeHover,
    borderColorTypeNormal,
    borderWidthTypeActive,
    borderWidthTypeNormal,
    borderWidthTypeHover
  } = theme;
  const borderBoxShadowForLink = {
    borderColorTypeActive,
    borderColorTypeHover,
    borderColorTypeNormal,
    borderWidthTypeActive,
    borderWidthTypeHover,
    borderWidthTypeNormal
  };
  const boxShadowForLinkStyle = createStyleForBoxShadow({
    boxShadowTypeNormal,
    boxShadowTypeHover,
    boxShadowTypeActive,
    borderBoxShadow: borderBoxShadowForLink
  });
  const borderBoxShadowForNotLink = {
    borderColorTypeActive: borderColorTypeNormal,
    borderColorTypeHover: borderColorTypeNormal,
    borderColorTypeNormal,
    borderWidthTypeActive: borderWidthTypeNormal,
    borderWidthTypeHover: borderWidthTypeNormal,
    borderWidthTypeNormal
  };
  const boxShadowForNotLinkStyle = createStyleForBoxShadow({
    boxShadowTypeNormal,
    boxShadowTypeHover: boxShadowTypeNormal,
    boxShadowTypeActive: boxShadowTypeNormal,
    borderBoxShadow: borderBoxShadowForNotLink
  });
  const wrapperStyle = createWrapperStyle(theme);
  const innerWrapperStyle = createInnerWrapperStyle(theme);
  const additionalInnerWrapperForLinkStyle = createAdditionalInnerWrapperForLinkStyle(theme);
  const boxShadowStyle = link ? boxShadowForLinkStyle.style : boxShadowForNotLinkStyle.style;
  return /*#__PURE__*/createElement("div", {
    className: css(boxShadowStyle, wrapperStyle)
  }, link ? /*#__PURE__*/createElement(Link, {
    className: css(innerWrapperStyle, additionalInnerWrapperForLinkStyle, boxShadowStyle),
    href: link
  }, children) : /*#__PURE__*/createElement("div", {
    className: css(innerWrapperStyle, boxShadowStyle)
  }, children));
};

const imageWidths$f = createImageWidths(540, 1080, 1080);

const createContentWrapperStyle = theme => StyleSheet.create({
  style: {
    overflow: 'hidden',
    width: '-webkit-fill-available',
    display: 'grid',
    background: 'transparent',
    position: 'absolute',
    bottom: 0,
    justifyContent: 'center',
    textAlign: 'center',
    justifyItems: 'center',
    [MEDIA_BREAKPOINTS.large]: {
      margin: `${baseUnit * theme.horizontalGapLarge}px ${baseUnit * theme.verticalGapLarge}px`,
      gridRowGap: `${baseUnit * theme.horizontalGapLarge}px`
    },
    [MEDIA_BREAKPOINTS.medium]: {
      margin: `${baseUnit * theme.horizontalGapMedium}px ${baseUnit * theme.verticalGapMedium}px`,
      gridRowGap: `${baseUnit * theme.horizontalGapMedium}px`
    },
    [MEDIA_BREAKPOINTS.small]: {
      margin: `${baseUnit * theme.horizontalGapSmall}px ${baseUnit * theme.verticalGapSmall}px`,
      gridRowGap: `${baseUnit * theme.horizontalGapSmall}px`
    }
  }
}).style;

const createLogoStyle = () => StyleSheet.create({
  style: {
    display: 'inline-flex',
    width: 'auto',
    [MEDIA_BREAKPOINTS.large]: {
      height: `${4 * baseUnit}px`
    },
    [MEDIA_BREAKPOINTS.medium]: {
      height: `${3 * baseUnit}px`
    },
    [MEDIA_BREAKPOINTS.small]: {
      height: `${3 * baseUnit}px`
    }
  }
}).style;

const SectionOnTopTitle = props => {
  const {
    title,
    subtitle,
    logo,
    link,
    image
  } = props;
  const theme = useTheme(componentConfigThemeKey$13);
  const {
    aspectRatioSmall,
    aspectRatioMedium,
    aspectRatioLarge,
    overlayColor,
    titleSemanticTag,
    titleFontSizeLarge,
    titleFontSizeMedium,
    titleFontSizeSmall,
    subtitleSemanticTag,
    subtitleFontSizeLarge,
    subtitleFontSizeMedium,
    subtitleFontSizeSmall
  } = theme;
  const contentWrapperStyle = createContentWrapperStyle(theme);
  const logoStyle = createLogoStyle();
  return /*#__PURE__*/createElement(SectionOnTopTitleWrapper, {
    link: link
  }, /*#__PURE__*/createElement(Image, _extends({}, image, {
    lazyLoad: false,
    imageAspectRatios: createImageAspectRatios(aspectRatioSmall, aspectRatioMedium, aspectRatioLarge),
    imageWidths: imageWidths$f
  })), /*#__PURE__*/createElement(CardOverlay, {
    overlayColor: overlayColor
  }), /*#__PURE__*/createElement("div", {
    className: css(contentWrapperStyle)
  }, logo ? /*#__PURE__*/createElement("img", {
    className: css(logoStyle),
    alt: logo.alt,
    src: `data:image/svg+xml,${encodeURIComponent(logo.src)}`
  }) : null, /*#__PURE__*/createElement(DynamicSemanticTag, {
    semanticTag: titleSemanticTag,
    fontSizeLarge: titleFontSizeLarge,
    fontSizeMedium: titleFontSizeMedium,
    fontSizeSmall: titleFontSizeSmall
  }, title), /*#__PURE__*/createElement(DynamicSemanticTag, {
    semanticTag: subtitleSemanticTag,
    fontSizeLarge: subtitleFontSizeLarge,
    fontSizeMedium: subtitleFontSizeMedium,
    fontSizeSmall: subtitleFontSizeSmall
  }, subtitle)));
};

const componentConfigThemeKey$14 = 'oneOTCThreeVC';

const createStyle$G = themeProps => {
  const {
    largeHorizontalGap,
    mediumHorizontalGap,
    smallHorizontalGap,
    baseUnit
  } = themeProps;
  return StyleSheet.create({
    wrapper: {
      display: 'grid',
      [MEDIA_BREAKPOINTS.large]: {
        gridRowGap: `${largeHorizontalGap * baseUnit}px`
      },
      [MEDIA_BREAKPOINTS.medium]: {
        gridRowGap: `${mediumHorizontalGap * baseUnit}px`
      },
      [MEDIA_BREAKPOINTS.small]: {
        gridRowGap: `${smallHorizontalGap * baseUnit}px`
      }
    }
  });
};

const imageWidthsHC$1 = createImageWidths(360, 720, 720);
const OneOTCThreeVC = props => {
  const {
    cards,
    paddingFactors
  } = props;
  const paddingStyle = getLayoutComponentPadding(paddingFactors);
  const themeProps = useTheme(componentConfigThemeKey$14);
  const style = createStyle$G(themeProps);
  const [firstCard, ...rest] = cards || [];
  const themeContextOTC = useThemeOverride({
    titleFontSizeLarge: 'gigantic',
    titleFontSizeMedium: 'bigger',
    titleFontSizeSmall: 'bigger',
    metadataFontSizeLarge: 'big',
    metadataFontSizeMedium: 'big',
    metadataFontSizeSmall: 'big',
    verticalGapLarge: 1,
    verticalGapMedium: 1,
    verticalGapSmall: 1,
    horizontalGapLarge: 2,
    horizontalGapMedium: 0.75,
    horizontalGapSmall: 0.75,
    aspectRatioSmall: {
      x: 4,
      y: 3
    },
    titleAlignment: 'CENTER',
    metadataAlignment: 'CENTER'
  }, componentConfigThemeKey$3);
  return /*#__PURE__*/createElement(ErrorBoundary, null, cards && cards.length > 0 && /*#__PURE__*/createElement("div", {
    className: css(paddingStyle, style.wrapper)
  }, /*#__PURE__*/createElement(ThemeContext.Provider, {
    value: themeContextOTC
  }, /*#__PURE__*/createElement(OnTopCard, _extends({}, firstCard, {
    imageWidths: imageWidthsHC$1
  }))), /*#__PURE__*/createElement(ThreeVC, {
    cards: rest,
    paddingFactors: {
      large: 0,
      medium: 0,
      small: 0
    }
  })));
};

const TRANSITION_LENGTH = 500;

const getShareIconStyles = (fillColor, isOpen) => {
  return StyleSheet.create({
    base: {
      fill: fillColor,
      opacity: 1,
      width: '100%',
      transition: `${TRANSITION_LENGTH}ms`,
      ...(isOpen && {
        transform: 'translateY(-30px)',
        opacity: 0
      })
    }
  });
};

const getCloseIconStyles = (fillColor, baseUnit, isOpen) => {
  return StyleSheet.create({
    base: {
      position: 'absolute',
      width: '100%',
      height: 0
    },
    line: {
      position: 'absolute',
      bottom: 0,
      height: '1px',
      backgroundColor: fillColor,
      opacity: 0,
      transition: `${TRANSITION_LENGTH}ms`,
      width: '100%'
    },
    leftLine: {
      left: 0,
      transformOrigin: 'left bottom',
      ...(isOpen && {
        opacity: 1,
        transform: 'rotate(-45deg)',
        width: '130%'
      })
    },
    rightLine: {
      right: 0,
      transformOrigin: 'right bottom',
      ...(isOpen && {
        opacity: 1,
        transform: 'rotate(45deg)',
        width: '130%'
      })
    }
  });
};

const SquareShareButton = ({
  shareIconColor,
  additionalStyle,
  onClick,
  isOpen
}) => {
  const {
    baseUnit
  } = useTheme();
  const shareIconStyles = getShareIconStyles(shareIconColor, isOpen);
  const closeIconStyles = getCloseIconStyles(shareIconColor, baseUnit, isOpen);
  return /*#__PURE__*/createElement("div", {
    className: css(additionalStyle.style),
    onClick: onClick
  }, /*#__PURE__*/createElement("svg", {
    xmlns: "http://www.w3.org/2000/svg",
    className: css(shareIconStyles.base),
    viewBox: "0 0 40 40"
  }, /*#__PURE__*/createElement("g", {
    fillRule: "evenodd"
  }, /*#__PURE__*/createElement("path", {
    d: "M18.75 0L8.125 10.709 9.658 12.247 17.655 4.229 17.655 31.25 19.846 31.25 19.846 4.229 27.841 12.247 29.375 10.709 18.751 0z",
    transform: "translate(1.25)"
  }), /*#__PURE__*/createElement("path", {
    d: "M35.357 20L35.357 37.778 2.143 37.778 2.143 20 0 20 0 40 37.5 40 37.5 20z",
    transform: "translate(1.25)"
  }))), /*#__PURE__*/createElement("div", {
    className: css(closeIconStyles.base)
  }, /*#__PURE__*/createElement("div", {
    className: css(closeIconStyles.line, closeIconStyles.leftLine)
  }), /*#__PURE__*/createElement("div", {
    className: css(closeIconStyles.line, closeIconStyles.rightLine)
  })));
};

const componentConfigurationKey$K = 'copyLinkButton';

const getStyles$x = () => {
  return StyleSheet.create({
    button: {
      position: 'relative',
      overflow: 'hidden',
      [MEDIA_BREAKPOINTS.large]: {
        width: '30px',
        height: '30px'
      },
      [MEDIA_BREAKPOINTS.medium]: {
        width: '30px',
        height: '30px'
      },
      [MEDIA_BREAKPOINTS.small]: {
        width: '20px',
        height: '20px'
      }
    },
    copyLinkIcon: {
      position: 'absolute',
      bottom: 0,
      left: '50%',
      transform: 'translateX(-50%)',
      transition: '200ms ease-in-out',
      [MEDIA_BREAKPOINTS.large]: {
        width: '30px'
      },
      [MEDIA_BREAKPOINTS.medium]: {
        width: '30px'
      },
      [MEDIA_BREAKPOINTS.small]: {
        width: '20px'
      }
    },
    copyLinkIconAction: {
      left: '-150%'
    },
    successIcon: {
      left: '150%'
    },
    successIconAction: {
      left: '50%'
    }
  });
};

const copyToClipboard$1 = text => {
  const el = document.createElement('textarea');
  el.value = text;
  el.setAttribute('readonly', '');
  el.style.position = 'absolute';
  el.style.opacity = '0';
  document.body.appendChild(el);
  el.select();
  document.execCommand('copy');
  document.body.removeChild(el);
};

const CopyLinkButton$1 = props => {
  const {
    canonical,
    buttonClass
  } = props;
  const {
    color
  } = useTheme(componentConfigurationKey$K);
  const styles = getStyles$x();
  let timeout;
  const [wasCopied, setWasCopied] = useState(false);

  const onClickCopy = () => {
    copyToClipboard$1(canonical);
    clearTimeout(timeout);
    setWasCopied(true);
    timeout = setTimeout(() => {
      setWasCopied(false);
    }, 1000);
  };

  const copyIconAction = wasCopied ? styles.copyLinkIconAction : styles.copyLinkIcon;
  const succcessIconAction = wasCopied ? styles.successIconAction : styles.successIcon;
  return /*#__PURE__*/createElement(ErrorBoundary, null, /*#__PURE__*/createElement("button", {
    type: "button",
    className: css(styles.button, buttonClass),
    onClick: onClickCopy
  }, /*#__PURE__*/createElement("svg", {
    xmlns: "http://www.w3.org/2000/svg",
    viewBox: "0 0 30 30",
    className: css(styles.copyLinkIcon, copyIconAction),
    fill: color
  }, /*#__PURE__*/createElement("path", {
    d: "M23.906 30H0V5.625h5.625V0H30v24.375h-1.875v-.001h-4.219V30zM5.625 24.375V7.5h-3.75v20.624h20.156v-3.75H7.5v.001H5.625zm22.5-22.501H7.5v20.625h20.625V1.874z"
  })), /*#__PURE__*/createElement("svg", {
    version: "1.1",
    xmlns: "http://www.w3.org/2000/svg",
    viewBox: "0 0 64 64",
    className: css(styles.copyLinkIcon, succcessIconAction)
  }, /*#__PURE__*/createElement("g", {
    fill: color
  }, /*#__PURE__*/createElement("path", {
    d: "M59.5,10.8"
  }), /*#__PURE__*/createElement("path", {
    d: "M23.1,56L1,32.9c-1.4-1.4-1.3-3.7,0.1-5.1c1.4-1.4,3.7-1.4,5,0.1l17,17.8L57.9,9.1c1.4-1.4,3.6-1.5,5-0.1 c1.4,1.4,1.4,3.7,0.1,5.1L23.1,56z"
  }), /*#__PURE__*/createElement("path", {
    d: "M23.4,43.4"
  })))));
};

const getAnimationDelayClass$1 = index => {
  return StyleSheet.create({
    animationModifier: {
      animationDelay: `${index * 50}ms`
    }
  });
};

const socialButtonAnimation$1 = {
  '0%': {
    opacity: 0
  },
  '100%': {
    opacity: 1
  }
};

const getStyles$y = baseUnit => {
  return StyleSheet.create({
    wrapper: {
      display: 'flex',
      flexDirection: 'column-reverse',
      position: 'absolute',
      alignItems: 'center',
      [MEDIA_BREAKPOINTS.large]: {
        bottom: `${baseUnit * 3.5}px`,
        right: `${baseUnit}px`,
        width: '30px'
      },
      [MEDIA_BREAKPOINTS.medium]: {
        bottom: `${baseUnit * 3.5}px`,
        right: `${baseUnit / 2}px`,
        width: '30px'
      },
      [MEDIA_BREAKPOINTS.small]: {
        bottom: `${baseUnit * 2}px`,
        right: '5px',
        width: '20px'
      }
    },
    socialButton: {
      opacity: 0,
      animationName: [socialButtonAnimation$1],
      animationDuration: '500ms',
      animationFillMode: 'forwards',
      cursor: 'pointer',
      textAlign: 'center',
      [MEDIA_BREAKPOINTS.large]: {
        height: `${baseUnit * 1.5}px`
      },
      [MEDIA_BREAKPOINTS.medium]: {
        height: `${baseUnit * 1.5}px`
      },
      [MEDIA_BREAKPOINTS.small]: {
        height: `${baseUnit}px`
      },
      ':not(:first-child):not(:last-child)': {
        [MEDIA_BREAKPOINTS.large]: {
          margin: `${baseUnit * 0.75}px 0`
        },
        [MEDIA_BREAKPOINTS.medium]: {
          margin: `${baseUnit * 0.75}px 0`
        },
        [MEDIA_BREAKPOINTS.small]: {
          margin: `${baseUnit * 0.5}px 0`
        }
      },
      ':first-child': {
        [MEDIA_BREAKPOINTS.large]: {
          margin: `${baseUnit * 0.75}px 0`
        },
        [MEDIA_BREAKPOINTS.medium]: {
          margin: `${baseUnit * 0.75}px 0`
        },
        [MEDIA_BREAKPOINTS.small]: {
          margin: `${baseUnit * 0.5}px 0`
        }
      },
      ':last-child': {
        [MEDIA_BREAKPOINTS.large]: {
          margin: `${baseUnit * 0.75}px 0`
        },
        [MEDIA_BREAKPOINTS.medium]: {
          margin: `${baseUnit * 0.75}px 0`
        },
        [MEDIA_BREAKPOINTS.small]: {
          margin: `${baseUnit * 0.5}px 0`
        }
      }
    }
  });
};

const getShareButtons$1 = (buttons, styles) => buttons.map((button, index) => {
  const {
    shareURL,
    src,
    type
  } = button;
  const animationDurationClass = getAnimationDelayClass$1(index);
  return (
    /*#__PURE__*/
    // eslint-disable-next-line jsx-a11y/anchor-is-valid
    createElement(Link, {
      className: css(styles.socialButton, animationDurationClass.animationModifier),
      key: index,
      target: "_blank",
      rel: "noopener noreferrer",
      onClick: () => {
        window.open(shareURL, '_blank', 'noopener');
        return false;
      }
    }, /*#__PURE__*/createElement("img", {
      src: src,
      alt: type,
      className: css(styles.socialIcon),
      height: "100%"
    }))
  );
});

const useToggleSocialMenu = () => {
  const [isOpen, setIsOpen] = useState(false);

  const onToggle = () => {
    setIsOpen(prev => !prev);
  };

  return [onToggle, isOpen];
};

const ArticleOnTopTitleShareButton = ({
  shareConfig,
  shareIconColor,
  shareIconStyle,
  canonical
}) => {
  const [onToggle, isOpen] = useToggleSocialMenu();
  const {
    baseUnit
  } = useTheme();
  const styles = getStyles$y(baseUnit);
  const copyLinkAnimationClass = getAnimationDelayClass$1(shareConfig.length);
  return /*#__PURE__*/createElement(Fragment, null, isOpen && /*#__PURE__*/createElement("div", {
    className: css(styles.wrapper)
  }, getShareButtons$1(shareConfig, styles), /*#__PURE__*/createElement(CopyLinkButton$1, {
    canonical: canonical,
    buttonClass: [styles.socialButton, copyLinkAnimationClass.animationModifier]
  })), /*#__PURE__*/createElement(SquareShareButton, {
    shareConfig: shareConfig,
    shareIconColor: shareIconColor,
    additionalStyle: shareIconStyle,
    onClick: onToggle,
    isOpen: isOpen
  }));
};

const componentConfigurationKey$L = 'articleOnTopTitle';

const getBlockStyle$6 = () => {
  return StyleSheet.create({
    style: {
      position: 'absolute',
      top: '0',
      left: '0',
      width: '100%',
      margin: 0
    }
  });
};

const getMedia$2 = (media, siteName, templateName) => {
  const additionalStyle = getBlockStyle$6();
  const {
    type
  } = media;

  switch (type) {
    case 'jw':
      {
        const {
          html,
          videoId
        } = media;
        return /*#__PURE__*/createElement(Jw, {
          html: html,
          additionalStyle: additionalStyle,
          videoId: videoId,
          siteName: siteName,
          templateName: templateName
        });
      }

    case 'youtube':
      {
        const {
          html
        } = media;
        return /*#__PURE__*/createElement(Iframe, {
          html: html,
          additionalStyle: additionalStyle
        });
      }

    case 'mmPlayer':
      {
        const {
          html
        } = media;
        return /*#__PURE__*/createElement(MMPlayer, {
          html: html,
          additionalStyle: additionalStyle
        });
      }

    default:
      return null;
  }
};

const imageWidths$g = createImageWidths(540, 720, 1440);

const getStyles$z = ({
  baseUnit,
  creditColor,
  titleColor,
  titleWithMediaColor
}) => {
  return StyleSheet.create({
    header: {
      position: 'relative'
    },
    creditWrapper: {
      position: 'absolute',
      textAlign: 'left',
      display: 'flex',
      alignItems: 'center',
      [MEDIA_BREAKPOINTS.large]: {
        bottom: `${baseUnit}px`,
        left: `${baseUnit}px`
      },
      [MEDIA_BREAKPOINTS.medium]: {
        bottom: `${baseUnit / 2}px`,
        left: `${baseUnit / 2}px`
      },
      [MEDIA_BREAKPOINTS.small]: {
        bottom: `${baseUnit / 4}px`,
        left: `${baseUnit / 4}px`
      }
    },
    credit: {
      color: creditColor,
      marginLeft: `${baseUnit / 2}px`
    },
    title: {
      color: titleColor,
      position: 'absolute',
      width: '100%',
      textAlign: 'center',
      boxSizing: 'border-box',
      [MEDIA_BREAKPOINTS.large]: {
        padding: `0 ${baseUnit * 2}px`,
        bottom: `${baseUnit * 4}px`
      },
      [MEDIA_BREAKPOINTS.medium]: {
        padding: `0 ${baseUnit * 2}px`,
        bottom: `${baseUnit * 4}px`
      },
      [MEDIA_BREAKPOINTS.small]: {
        padding: `0 ${baseUnit}px`,
        bottom: `${baseUnit * 2.5}px`
      }
    },
    titleWithMedia: {
      color: titleWithMediaColor,
      textAlign: 'center',
      [MEDIA_BREAKPOINTS.large]: {
        margin: `${baseUnit * 2}px`
      },
      [MEDIA_BREAKPOINTS.medium]: {
        margin: `${baseUnit}px`
      },
      [MEDIA_BREAKPOINTS.small]: {
        margin: `${baseUnit}px`
      }
    }
  });
};

const getShareIconStyle = baseUnit => {
  return StyleSheet.create({
    style: {
      cursor: 'pointer',
      position: 'absolute',
      [MEDIA_BREAKPOINTS.large]: {
        bottom: `${baseUnit}px`,
        right: `${baseUnit}px`,
        height: '30px',
        maxWidth: '30px'
      },
      [MEDIA_BREAKPOINTS.medium]: {
        bottom: `${baseUnit / 2}px`,
        right: `${baseUnit / 2}px`,
        height: '30px',
        maxWidth: '30px'
      },
      [MEDIA_BREAKPOINTS.small]: {
        bottom: '9px',
        right: `${baseUnit / 4}px`,
        height: '20px',
        maxWidth: '20px'
      }
    }
  });
};

const ArticleOnTopTitle = props => {
  const {
    image,
    title,
    shareConfig,
    canonical,
    media,
    siteName,
    templateName
  } = props;
  const themeProps = useTheme(componentConfigurationKey$L);
  const {
    creditColor
  } = themeProps;
  const {
    creditFontSizeLarge,
    creditFontSizeSmall,
    titleFontSizeLarge,
    titleFontSizeSmall,
    baseUnit,
    shareIconColor,
    shareIconColorForMediaCover,
    overlayColor,
    numberOfLinesForTitle
  } = themeProps;
  const hasCredit = typeof image.credit === 'string';
  const styles = getStyles$z(themeProps);
  const shareIconStyle = getShareIconStyle(baseUnit);
  const {
    style
  } = StyleSheet.create(ellipsify(numberOfLinesForTitle));
  const aspectRatios = {
    large: {
      x: 16,
      y: 9
    },
    medium: {
      x: 16,
      y: 9
    },
    small: media ? {
      x: 16,
      y: 9
    } : {
      x: 4,
      y: 3
    }
  };
  return /*#__PURE__*/createElement("header", {
    className: css(styles.header)
  }, /*#__PURE__*/createElement(Image, _extends({}, image, {
    imageWidths: imageWidths$g,
    imageAspectRatios: aspectRatios
  })), media && getMedia$2(media, siteName, templateName), !media && /*#__PURE__*/createElement(CardOverlay, {
    overlayColor: overlayColor
  }), /*#__PURE__*/createElement(H1, {
    styles: semanticCSS(media ? styles.titleWithMedia : styles.title, style),
    fontSizeLarge: titleFontSizeLarge,
    fontSizeMedium: titleFontSizeLarge,
    fontSizeSmall: titleFontSizeSmall
  }, title), hasCredit && /*#__PURE__*/createElement("div", {
    className: css(styles.creditWrapper)
  }, /*#__PURE__*/createElement(CameraIcon, {
    color: creditColor
  }), /*#__PURE__*/createElement(Span, {
    fontSizeLarge: creditFontSizeLarge,
    fontSizeMedium: creditFontSizeLarge,
    fontSizeSmall: creditFontSizeSmall,
    styles: semanticCSS(styles.credit)
  }, image.credit)), shareConfig.length > 0 && /*#__PURE__*/createElement(ArticleOnTopTitleShareButton, {
    canonical: canonical,
    shareConfig: shareConfig,
    shareIconColor: media ? shareIconColorForMediaCover : shareIconColor,
    shareIconStyle: shareIconStyle
  }));
};

const createImageAdditionalStyle = () => {
  return StyleSheet.create({
    style: {
      overflow: 'hidden',
      borderRadius: '100%',
      [MEDIA_BREAKPOINTS.large]: {
        height: '200px',
        width: '200px'
      },
      [MEDIA_BREAKPOINTS.medium]: {
        height: '160px',
        width: '160px'
      },
      [MEDIA_BREAKPOINTS.small]: {
        height: '80px',
        width: '80px'
      }
    }
  });
};

const AuthorPremiumImage = props => {
  const {
    image
  } = props;
  const aspectRatio = {
    x: 1,
    y: 1
  };
  const imageWidths = createImageWidths(360, 360, 360);
  const imageAdditionalStyle = createImageAdditionalStyle();
  return /*#__PURE__*/React__default.createElement(ErrorBoundary, null, /*#__PURE__*/React__default.createElement(Image, _extends({}, image, {
    aspectRatio: aspectRatio,
    imageWidths: imageWidths,
    additionalStyle: imageAdditionalStyle
  })));
};

const componentConfigThemeKey$15 = 'authorPremium';

const createAuthorPremiumTitleStyle = themeProps => {
  const {
    textColor
  } = themeProps;
  return StyleSheet.create({
    style: {
      color: textColor
    }
  }).style;
};

const AuthorPremiumTitle = props => {
  const {
    name
  } = props;
  const themeProps = useTheme(componentConfigThemeKey$15);
  const {
    fontSizeLarge,
    fontSizeSmall,
    fontSizeMedium
  } = themeProps;
  const authorPremiumTitleStyle = createAuthorPremiumTitleStyle(themeProps);
  return /*#__PURE__*/React__default.createElement(H1, {
    fontSizeLarge: fontSizeLarge,
    fontSizeSmall: fontSizeSmall,
    fontSizeMedium: fontSizeMedium,
    styles: semanticCSS(authorPremiumTitleStyle)
  }, name);
};

const createSocialButtonStyle$1 = themeProps => {
  const {
    socialButtonsNormalColor,
    socialButtonsHoverColor
  } = themeProps;
  return StyleSheet.create({
    style: {
      height: `${baseUnit * 1}px`,
      fill: socialButtonsNormalColor,
      ':hover': {
        fill: socialButtonsHoverColor
      }
    }
  }).style;
};

const createSocialButtonsWrapperStyle = numSocialLinks => {
  return StyleSheet.create({
    style: {
      marginTop: `${baseUnit * 1.5}px`,
      display: 'grid',
      gridColumnGap: `${baseUnit * 1.5}px`,
      gridTemplateColumns: `repeat(${numSocialLinks}, auto)`
    }
  }).style;
};

const AuthorPremiumSocialLinks = props => {
  const {
    socialButtons
  } = props;
  const themeProps = useTheme(componentConfigThemeKey$15);
  const socialButtonStyle = createSocialButtonStyle$1(themeProps);
  const socialButtonsWrapperStyle = createSocialButtonsWrapperStyle(socialButtons.length);
  return /*#__PURE__*/React__default.createElement(ErrorBoundary, null, /*#__PURE__*/React__default.createElement("div", {
    className: css(socialButtonsWrapperStyle)
  }, socialButtons.map((link, index) => {
    const svg = link.src.replace(/<svg/g, `<svg class="${css(socialButtonStyle)}"`);
    return /*#__PURE__*/React__default.createElement(Link, {
      href: link.href,
      key: index,
      rel: "noopener",
      target: "_blank",
      dangerouslySetInnerHTML: {
        __html: svg
      },
      alt: link.alt
    });
  })));
};

const componentConfigurationKey$M = 'authorPremiumByline';

const getStyles$A = (color, baseUnit) => StyleSheet.create({
  p: {
    color,
    marginTop: `${baseUnit / 2}px`
  }
});

const AuthorPremiumByline = ({
  byline
}) => {
  const {
    baseUnit,
    textColor,
    ...fontSizes
  } = useTheme(componentConfigurationKey$M);
  const styles = getStyles$A(textColor, baseUnit);
  return /*#__PURE__*/createElement(P, _extends({
    styles: semanticCSS(styles.p)
  }, fontSizes), byline);
};

const backgroundHeightForLargeScreen = 220;
const backgroundHeightForMediumScreen = 160;
const backgroundHeightForSmallScreen = 80;

const createBackgroundWrapperStyle = (themeProps, headerHeights) => {
  const {
    backgroundColor,
    maxWidth
  } = themeProps;
  return StyleSheet.create({
    style: {
      backgroundColor,
      position: 'absolute',
      top: 0,
      margin: 0,
      maxWidth: `${maxWidth}px`,
      width: '-webkit-fill-available',
      [MEDIA_BREAKPOINTS.large]: {
        paddingTop: `${headerHeights.large * baseUnit}px`,
        height: `${backgroundHeightForLargeScreen}px`
      },
      [MEDIA_BREAKPOINTS.medium]: {
        paddingTop: `${headerHeights.medium * baseUnit}px`,
        height: `${backgroundHeightForMediumScreen}px`
      },
      [MEDIA_BREAKPOINTS.small]: {
        paddingTop: `${headerHeights.small * baseUnit}px`,
        height: `${backgroundHeightForSmallScreen}px`
      }
    }
  }).style;
};

const createAuthorPremiumInfoWrapperStyle = () => {
  return StyleSheet.create({
    style: {
      display: 'grid',
      gridTemplateColumns: '1fr',
      justifyItems: 'center',
      marginBottom: `${baseUnit * 2}px`,
      [MEDIA_BREAKPOINTS.large]: {
        marginTop: `${backgroundHeightForLargeScreen * 0.5}px`
      },
      [MEDIA_BREAKPOINTS.medium]: {
        marginTop: `${backgroundHeightForMediumScreen * 0.5}px`
      },
      [MEDIA_BREAKPOINTS.small]: {
        marginTop: `${backgroundHeightForSmallScreen * 0.5}px`
      }
    }
  }).style;
};

const AuthorPremium = props => {
  const {
    image,
    name,
    byline,
    socialButtons
  } = props;
  const themeProps = useTheme(componentConfigThemeKey$15);
  const headerHeights = useHeaderHeights();
  const backgroundStyle = createBackgroundWrapperStyle(themeProps, headerHeights);
  const authorPremiumInfoWrapperStyle = createAuthorPremiumInfoWrapperStyle();
  return /*#__PURE__*/React__default.createElement(ErrorBoundary, null, /*#__PURE__*/React__default.createElement("div", {
    className: css(backgroundStyle)
  }), /*#__PURE__*/React__default.createElement("div", {
    className: css(authorPremiumInfoWrapperStyle)
  }, /*#__PURE__*/React__default.createElement(AuthorPremiumImage, {
    image: image
  }), /*#__PURE__*/React__default.createElement(AuthorPremiumTitle, {
    name: name
  }), byline ? /*#__PURE__*/React__default.createElement(AuthorPremiumByline, {
    byline: byline
  }) : null, socialButtons ? /*#__PURE__*/React__default.createElement(AuthorPremiumSocialLinks, {
    socialButtons: socialButtons
  }) : null));
};

const componentConfigThemeKey$16 = 'twoTwoOTC';

const createStyle$H = themeProps => {
  const {
    largeHorizontalGap,
    largeVerticalGap,
    mediumHorizontalGap,
    mediumVerticalGap,
    smallHorizontalGap,
    baseUnit
  } = themeProps;
  return StyleSheet.create({
    wrapper: {
      display: 'grid',
      [MEDIA_BREAKPOINTS.large]: {
        gridTemplateColumns: '1fr 1fr',
        gridTemplateRows: 'auto',
        gridRowGap: `${largeHorizontalGap * baseUnit}px`,
        gridColumnGap: `${largeVerticalGap * baseUnit}px`
      },
      [MEDIA_BREAKPOINTS.medium]: {
        gridTemplateColumns: '1fr 1fr',
        gridTemplateRows: 'auto',
        gridRowGap: `${mediumHorizontalGap * baseUnit}px`,
        gridColumnGap: `${mediumVerticalGap * baseUnit}px`
      },
      [MEDIA_BREAKPOINTS.small]: {
        gridTemplateColumns: '1fr',
        gridTemplateRows: 'auto',
        gridRowGap: `${smallHorizontalGap * baseUnit}px`
      }
    }
  });
};

const imageWidths$h = createImageWidths(360, 360, 540);
const TwoTwoOTC = props => {
  const {
    cards,
    paddingFactors
  } = props;
  const paddingStyle = getLayoutComponentPadding(paddingFactors);
  const themeProps = useTheme(componentConfigThemeKey$16);
  const style = createStyle$H(themeProps);
  const themeContextOTC = useThemeOverride({
    titleFontSizeLarge: 'large',
    titleFontSizeMedium: 'bigger',
    titleFontSizeSmall: 'bigger',
    metadataFontSizeLarge: 'big',
    metadataFontSizeMedium: 'big',
    metadataFontSizeSmall: 'big',
    verticalGapLarge: 1,
    verticalGapMedium: 1,
    verticalGapSmall: 0.75,
    horizontalGapLarge: 0.75,
    horizontalGapMedium: 0.75,
    horizontalGapSmall: 0.5
  }, componentConfigThemeKey$3);
  return /*#__PURE__*/createElement(ErrorBoundary, null, cards && cards.length > 0 && /*#__PURE__*/createElement("div", {
    className: css(paddingStyle, style.wrapper)
  }, /*#__PURE__*/createElement(ThemeContext.Provider, {
    value: themeContextOTC
  }, /*#__PURE__*/createElement(OnTopCard, _extends({}, cards[0], {
    imageWidths: imageWidths$h
  })), /*#__PURE__*/createElement(OnTopCard, _extends({}, cards[1], {
    imageWidths: imageWidths$h
  })), /*#__PURE__*/createElement(OnTopCard, _extends({}, cards[2], {
    imageWidths: imageWidths$h
  })), /*#__PURE__*/createElement(OnTopCard, _extends({}, cards[3], {
    imageWidths: imageWidths$h
  })))));
};

const componentConfigThemeKey$17 = 'fourHCFourVC';
const maximumNumberOfCards$3 = 4;

const createStyle$I = themeProps => {
  const {
    largeHorizontalGap,
    largeVerticalGap,
    mediumHorizontalGap,
    mediumVerticalGap,
    smallHorizontalGap
  } = themeProps;
  return StyleSheet.create({
    wrapperHC: {
      display: 'grid',
      marginTop: `${baseUnit}px`,
      [MEDIA_BREAKPOINTS.large]: {
        gridTemplateColumns: '1fr',
        gridTemplateRows: 'auto',
        gridRowGap: `${largeHorizontalGap * baseUnit}px`
      },
      [MEDIA_BREAKPOINTS.medium]: {
        gridTemplateColumns: '1fr',
        gridTemplateRows: 'auto',
        gridRowGap: `${mediumHorizontalGap * baseUnit}px`
      },
      [MEDIA_BREAKPOINTS.small]: {
        gridTemplateColumns: '1fr',
        gridTemplateRows: 'auto',
        gridRowGap: `${smallHorizontalGap * baseUnit}px`
      }
    },
    wrapperVC: {
      display: 'grid',
      marginTop: `${baseUnit}px`,
      [MEDIA_BREAKPOINTS.large]: {
        gridTemplateColumns: '1fr 1fr',
        gridTemplateRows: 'auto',
        gridRowGap: `${largeHorizontalGap * baseUnit}px`,
        gridColumnGap: `${largeVerticalGap * baseUnit}px`
      },
      [MEDIA_BREAKPOINTS.medium]: {
        gridTemplateColumns: '1fr 1fr',
        gridTemplateRows: 'auto',
        gridRowGap: `${mediumHorizontalGap * baseUnit}px`,
        gridColumnGap: `${mediumVerticalGap * baseUnit}px`
      },
      [MEDIA_BREAKPOINTS.small]: {
        gridTemplateColumns: '1fr',
        gridTemplateRows: 'auto',
        gridRowGap: `${smallHorizontalGap * baseUnit}px`
      }
    }
  });
};

const imageWidthsHC$2 = createImageWidths(180, 360, 540);
const imageWidthsVC = createImageWidths(360, 360, 540);
const FourHCFourVC = props => {
  const {
    cards,
    paddingFactors
  } = props;
  const themeProps = useTheme(componentConfigThemeKey$17);
  const style = createStyle$I(themeProps);
  const paddingStyle = getLayoutComponentPadding(paddingFactors);
  const themeContextHC = useThemeOverride({
    titleFontSizeLarge: 'large',
    titleFontSizeMedium: 'bigger',
    titleFontSizeSmall: 'bigger',
    metadataFontSizeLarge: 'big',
    metadataFontSizeMedium: 'big',
    metadataFontSizeSmall: 'big',
    descriptionFontSizeLarge: 'normal',
    descriptionFontSizeMedium: 'normal',
    descriptionFontSizeSmall: 'normal',
    showDescriptionInLargeScreen: true,
    showDescriptionInMediumScreen: true,
    verticalGapLarge: 1,
    verticalGapMedium: 1,
    verticalGapSmall: 0.75,
    horizontalGapLarge: 0.75,
    horizontalGapMedium: 0.75,
    horizontalGapSmall: 0.5,
    cardProportionsLarge: {
      x: 1,
      y: 1
    },
    cardProportionsMedium: {
      x: 1,
      y: 1
    },
    cardProportionsSmall: {
      x: 1,
      y: 1
    }
  }, componentConfigThemeKey$7);
  const themeContextVC = useThemeOverride({
    titleFontSizeLarge: 'large',
    titleFontSizeMedium: 'bigger',
    titleFontSizeSmall: 'bigger',
    metadataFontSizeLarge: 'big',
    metadataFontSizeMedium: 'big',
    metadataFontSizeSmall: 'big',
    verticalGapLarge: 1,
    verticalGapMedium: 1,
    verticalGapSmall: 0.75,
    horizontalGapLarge: 0.75,
    horizontalGapMedium: 0.75,
    horizontalGapSmall: 0.5,
    showDescriptionInLargeScreen: true,
    showDescriptionInMediumScreen: true
  }, componentConfigThemeKey$5);

  const createGrids = (cardsProps, index) => {
    return index % 2 === 0 ? /*#__PURE__*/createElement("div", {
      className: css(style.wrapperHC, paddingStyle),
      key: index
    }, /*#__PURE__*/createElement(ThemeContext.Provider, {
      value: themeContextHC
    }, cardsProps && cardsProps.map(card => /*#__PURE__*/createElement(HorizontalCard, _extends({
      key: card.id
    }, card, {
      imageWidths: imageWidthsHC$2
    }))).slice(0, maximumNumberOfCards$3))) : /*#__PURE__*/createElement("div", {
      className: css(style.wrapperVC, paddingStyle),
      key: index
    }, /*#__PURE__*/createElement(ThemeContext.Provider, {
      value: themeContextVC
    }, cardsProps && cardsProps.map(card => /*#__PURE__*/createElement(VerticalCard, _extends({
      key: card.id
    }, card, {
      imageWidths: imageWidthsVC
    }))).slice(0, maximumNumberOfCards$3)));
  };

  return /*#__PURE__*/createElement(ErrorBoundary, null, cards && cards.length > 0 && getArticlesInChunks(cards, 4).map(createGrids));
};

const imageWidths$i = createImageWidths(180, 360, 540);
const OneHC = props => {
  const {
    cards,
    paddingFactors
  } = props;
  const paddingStyle = getLayoutComponentPadding(paddingFactors);
  const themeContextVC = useThemeOverride({
    titleFontSizeLarge: 'large',
    titleFontSizeMedium: 'large',
    titleFontSizeSmall: 'large',
    metadataFontSizeLarge: 'big',
    metadataFontSizeMedium: 'big',
    metadataFontSizeSmall: 'big',
    shouldReverseImageAlignment: true,
    cardProportionsLarge: {
      x: 2,
      y: 1
    },
    cardProportionsMedium: {
      x: 2,
      y: 1
    },
    verticalGapLarge: 1,
    verticalGapMedium: 1,
    verticalGapSmall: 1,
    horizontalGapLarge: 0.75,
    horizontalGapMedium: 0.75,
    horizontalGapSmall: 0.75
  }, componentConfigThemeKey$7);
  return /*#__PURE__*/createElement(ErrorBoundary, null, cards.length > 0 && /*#__PURE__*/createElement("div", {
    className: css(paddingStyle)
  }, /*#__PURE__*/createElement(ThemeContext.Provider, {
    value: themeContextVC
  }, /*#__PURE__*/createElement(HorizontalCard, _extends({}, cards[0], {
    imageWidths: imageWidths$i
  })))));
};

export { AMPAnalytics, AMPAppContent, AMPArticleContent, AMPCover, AMPHeaderAndSideBar, AMPImage, AMPMainWrapper, AMPRecommendationsAdvertisement, AMPSocialContainer, AMPSponsor, Ad, AmpWebFonts, AnnouncementCard, AppContent, ArticleContent, ArticleImage, ArticleMainImage, ArticleOnTopTitle, ArticleTitle, ArticleTitleWithoutMetaData, Author$1 as Author, AuthorPremium, AuthorsWithRoundImage, BasicQuote, Button, ContentAndSidebar, ContentWithoutSidebar, DecoratedQuote, Divider, DividerWithQuoteIcon, EmbedPostMainContent, Error$1 as Error, ErrorBoundary, ErrorReporting, ExternalScripts, Footer, FourHC, FourHCFourVC, FourVC, FourVCForOneThird, FourVCOneOTC, FullWidthOTC, FullWidthPostMainContent, H1, H2, HUGE_SCREEN_SIZE, Header, HeaderWithSubMenus, HeadlinesArticleList, HighlightsArticleList, HoverAuthorsWithRoundImage, ImageWithCaptionAndIcon, LARGE_SCREEN_SIZE, LoadMoreButtonWrapper, MEDIA_BREAKPOINTS, MainWrapperForDefaultNavigation, MainWrapperForDoubleHeaderNavigation, MetaHeaderTags, NineHCForOneThird, OldFooter, OnTopCardsPostRecommendations, OneHC, OneHCThreeVC, OneOTCFourVC, OneOTCThreeVC, OneOnTopThreeHorizontalArticleList, OneThreeArticleList, OneThreeOTC, OneTwoOnTopFullWidth, OneTwoTwoOTC, PostAside, PostContentWithAside, PostMainContent, PostMetaHeaderTags, PostSidebar, PostWithoutSidebarMainContent, PrimarySectionTitle, RecommendationsAdvertisement, SafeArticleContentWrapper, ScrollableArticleList, SecondarySectionTitle, Section, SectionHeader, SectionOnTopTitle, SectionWithTitle, ShareButton, SixHC, SixHCForTwoThirds, SixSixArticleList, SixSixPostRecommendations, Sponsor, SwitchableShareMenu, ThemeProviderWithFonts, ThreeHC, ThreeHCOneOTC, ThreeThreeVC, ThreeVC, ThreeVCExpandable, TwoOTCFourHLC, TwoOneTwoArticleList, TwoThirdsOneThirdWrapper, TwoTwoOTC, WebFonts, baseUnit };
//# sourceMappingURL=components.bundle.esm.js.map
